// Widget + booking-page i18n. Scope: strings the CONSUMER sees on the
// CateringWidget, FloatingWidget, and /booking/[id] status page. Out of
// scope: dashboard (/proposals, /bookings, etc), venue profile, marketing.
// Keep keys flat and namespaced (widget.*, booking.*) so the dict reads
// as a spec, not a tree to traverse.
//
// HOW TO ADD A NEW STRING:
// 1. Pick a key namespaced by surface (widget.*, booking.*, booking.edit.*, etc.)
// 2. Add it to BOTH `en` and `fr` blocks — the key-parity vitest will fail
//    at CI otherwise.
// 3. Use {{placeholder}} interpolation for dynamic values; pass a values
//    object as the second arg to `t()`.
// 4. For visible numbers / dates / currency, reach for `formatDateLocal`,
//    `formatCurrencyLocal`, or Intl directly — don't hard-code "$123.45"
//    in a translation.

// Round 21 (2026-05-10): extended to 4 langs. Spanish is neutral Latin
// American (no `es-ES` Castilian forms). Italian is standard `it-IT`.
// ES/IT phone-prefix detection deferred per user direction — we can't deliver
// SMS to European numbers via Twilio without WhatsApp Business API. Lang
// detection still works via URL `?lang=` param + browser Accept-Language.
export type SupportedLang = 'en' | 'fr' | 'es' | 'it';
export const SUPPORTED_LANGS: SupportedLang[] = ['en', 'fr', 'es', 'it'];
export const DEFAULT_LANG: SupportedLang = 'en';

// Maps to the ElevenLabs agent's primary language vs. language_preset keys.
// `fr` → agent's default FR first_message. `en` → language_presets.en.
// Also forwarded to the SDK via `overrides.language` at session start.

// Dictionary: every key present in both languages. Missing keys fall back
// to English, then to the raw key. Strings use double-brace interpolation
// (`{{venueName}}`) — callers pass a values object to `t(key, values)`.
// Keep strings short and declarative; this isn't marketing copy.
export const DICT: Record<SupportedLang, Record<string, string>> = {
  en: {
    'widget.lang.toggle.aria':     'Change language',
    'widget.lang.en':              'English',
    'widget.lang.fr':              'Français',
    'widget.idle.title.default':   'Plan an event at {{venueName}}',
    'widget.group.other.label':    'Interested in our other locations?',
    'widget.group.other.aria':     'Other locations',
    'widget.idle.description':     'Call or chat with our team.',
    'widget.idle.call':            'Call',
    'widget.idle.chat':            'Chat',
    'widget.callMode.speakHint':   'You can type your details — or just say them out loud.',
    'widget.idle.loudroom':        'Loud Room — tap to talk',
    'widget.idle.loudroom.hint':   'For bars + restaurants — mic starts muted, press to speak.',
    'widget.idle.mic_blocked':     'Mic blocked — you can still chat using text below.',

    'widget.active.mic_muted':     'Muted',
    'widget.active.mic_unmuted':   'Listening',
    'widget.active.agent.speaking':'Agent speaking',
    'widget.active.agent.thinking':'Thinking…',
    'widget.active.type_hint':     'Type your message…',
    'widget.active.send':          'Send',
    'widget.active.end':           'End',
    'widget.active.mute':          'Mute',
    'widget.active.unmute':        'Unmute',
    'widget.active.hold_to_talk':  'Hold to talk',
    'widget.active.switch_to_chat':'Switch to chat',
    'widget.active.switch_to_voice':'Switch to voice',
    'widget.active.no_audio':      'No audio? Tap to switch to chat.',
    'widget.active.reconnect':     'Audio dropped — tap to reconnect.',

    'widget.ended.thanks':         'Thanks — the {{venueName}} team will be in touch.',
    'widget.ended.restart':        'Ask another question',
    'widget.ended.view_booking':   'View your booking',
    'widget.ended.status.sent':    'Sent to the team — we\u2019ll be in touch soon',
    'widget.ended.status.cancelled':'Conversation ended',

    // booking.* — consumer-facing booking status page (/booking/[id])
    'booking.title':               'Your booking with {{venueName}}',
    'booking.headline.default':    'Your Booking',
    'booking.headline.greeting':   'Hi {{name}}',
    'booking.headline.anon':       'Your booking request',
    'booking.headline.direct_event':    'Direct booking with',
    'booking.headline.direct_catering': 'Direct catering with',
    'booking.fallback.venue':      'venue',
    'booking.event_guide.cta':     'View {{location}} event guide',
    'booking.pricing.caveat':      'The events team will confirm final pricing over email.',
    'booking.venue_reply.heading': 'Reply from {{venue}}',
    'booking.venue_reply.via.email': 'Sent via email',
    'booking.venue_reply.via.sms':   'Sent via text',

    // pricing.* — shared between the venue dashboard's PerDatePricingTable and
    // the consumer awaiting-response card. Keep in sync with the FR block.
    'pricing.title':                  'Per-date pricing (auto-quoted)',
    'pricing.col.date':               'Date',
    'pricing.col.time_block':         'Time block',
    'pricing.col.min_spend':          'Min spend',
    'pricing.col.venue_rental_fee':   'Venue rental fee',
    'pricing.closed':                 'Venue closed — alternate date needed',
    'pricing.rates_pending':          'Rates pending — events team to confirm',
    'pricing.holiday':                'holiday',
    'pricing.closest_match':          'closest available time',
    'pricing.holiday_surcharge':      'includes holiday surcharge',
    // Phase 14.E (2026-05-07): caption removed per user feedback. The
    // auto-quote is venue-facing and "review and adjust" implied an action
    // the dashboard didn't surface clearly. Render side checks for empty
    // and skips the row.
    'pricing.dashboard_caveat':       '',

    // dashboard.proposals.lead.* — shadow-mode lead-inbox UX (Boxcar). Three
    // contact chips replace the Submit Proposal CTA. Submit stays reachable
    // via a small text link.
    'dashboard.proposals.lead.call':                'Call customer',
    'dashboard.proposals.lead.email':               'Email customer',
    'dashboard.proposals.lead.send_email':          'Send email reply',
    'dashboard.proposals.lead.send_text':           'Send text reply',
    'dashboard.proposals.lead.no_email':            "Customer didn't share an email address.",
    'dashboard.proposals.lead.no_phone':            "Customer didn't share a phone number.",
    'dashboard.proposals.lead.no_contact':          'No contact channel — submit a formal proposal instead.',
    'dashboard.proposals.lead.submit_formal_link':  'Submit a formal proposal instead',

    // Lead-status shortcut for shadow-mode bookings — venue marks the lead
    // as Booked or Completed without going through the formal proposal flow.
    'dashboard.proposals.lead.status_heading':         'Lead status',
    'dashboard.proposals.lead.status.mark_booked':     'Mark as booked',
    'dashboard.proposals.lead.status.mark_completed':  'Mark as completed',
    'dashboard.proposals.lead.status.confirm_booked':    'Confirm: lead is booked',
    'dashboard.proposals.lead.status.confirm_completed': 'Confirm: event completed',
    'dashboard.proposals.lead.status.notes_placeholder': 'Optional notes — e.g., booking ref, event details, follow-ups…',
    'dashboard.proposals.lead.status.event_datetime_label': 'Agreed event date & time (optional — pick from customer’s preferred dates)',
    'dashboard.proposals.lead.status.actual_spend_label':  'Actual spend (CAD, optional)',
    'dashboard.proposals.lead.status.hrs':                'hrs',
    'dashboard.proposals.lead.status.confirm':           'Confirm',
    'dashboard.proposals.lead.status.cancel':            'Cancel',
    'dashboard.proposals.lead.status.saving':            'Saving…',
    'dashboard.proposals.lead.status.toast_booked':      'Lead marked as booked',
    'dashboard.proposals.lead.status.toast_completed':   'Lead marked as completed',
    'dashboard.proposals.lead.status.toast_lost':        'Lead marked as lost',
    'dashboard.proposals.lead.status.toast_lost_failed': 'Couldn’t mark as lost — {{error}}',
    'dashboard.proposals.lead.status.mark_lost':         'Mark as lost',
    'dashboard.proposals.lead.status.mark_lost_heading': 'Mark as lost',
    'dashboard.proposals.lead.status.mark_lost_subtitle':'Log why this lead didn’t convert. Helps the team improve.',
    'dashboard.proposals.lead.status.toast_update_failed': 'Failed to update lead status',
    'dashboard.proposals.lead.status.toast_failed_log':  'Failed to log',
    'dashboard.proposals.lead.status.marked_booked':     'Booked · refreshing',
    'dashboard.proposals.lead.status.marked_completed':  'Completed · refreshing',

    // quick_reply.* — the inline composer that opens beneath a shadow-mode card.
    'quick_reply.heading.with_name':  'Reply to {{name}}',
    'quick_reply.heading.anon':       'Reply to customer',
    'quick_reply.close':              'Close composer',
    'quick_reply.channel.email':      'Email',
    'quick_reply.channel.sms':        'Text',
    'quick_reply.body_placeholder':   "Hi — thanks for the request. Wanted to share a few quick details before we lock anything in...",
    'quick_reply.send.email':         'Send email',
    'quick_reply.send.sms':           'Send text',
    'quick_reply.sending':            'Sending…',
    'quick_reply.toast.email_sent':   'Email sent',
    'quick_reply.toast.sms_sent':     'Text message sent',
    'quick_reply.sent.email':         'Emailed',
    'quick_reply.sent.sms':           'Texted',
    'quick_reply.sent.just_now':      'just now',
    'quick_reply.sent.minutes_ago':   '{{count}} min ago',
    'quick_reply.sent.hours_ago':     '{{count}} hr ago',

    'booking.step.intake_in_progress': 'Collecting Details',
    'booking.step.intake_complete':    'Details Complete',
    'booking.step.awaiting_response':  'Awaiting Response',
    'booking.step.finding_venues':     'Finding Venues',
    'booking.step.proposals_received': 'Proposals Received',
    'booking.step.consumer_selecting': 'Select Your Venue',
    'booking.step.consumer_confirmed': 'Confirmed — Payment Pending',
    'booking.step.booked':             'Matched!',

    'booking.status.pending':      'We received your request.',
    'booking.status.reviewing':    'Matching you with venues…',
    'booking.status.proposals':    'You have proposals to review.',
    'booking.status.selected':     'Booking confirmed.',
    'booking.status.completed':    'Event complete. Hope it was great!',

    'booking.loading':             'Loading your booking…',
    'booking.error.not_found':     "We can't find this booking. Check your link and try again.",
    'booking.error.load_failed':   'Unable to load booking details: {{message}}',
    'booking.not_found':           'Booking not found.',
    'booking.preparing.title':     'Preparing your booking',
    'booking.preparing.line1':     'Thanks for reaching out — putting the details together.',
    'booking.preparing.line2':     'This page will update on its own.',

    'booking.details.header.event':    'Event Details',
    'booking.details.header.catering': 'Catering Details',
    'booking.details.audience':        'Audience',
    'booking.details.audience.company':  'Company event',
    'booking.details.audience.personal': 'Personal event',
    'booking.details.date':            'Date',
    'booking.details.date.tbd':        'TBD',
    'booking.details.time':            'Time',
    'booking.details.guests':          'Guests',
    'booking.details.duration':        'Duration',
    'booking.details.duration_hours':  '{{hours}} hour',
    'booking.details.duration_hours.plural': '{{hours}} hours',
    'booking.details.food':            'Food',
    'booking.details.bar':             'Bar',
    'booking.details.music':           'Music',
    'booking.details.budget':          'Budget',
    'booking.details.booking_type':    'Booking Type',
    'booking.details.package':         'Package',
    'booking.details.email':           'Email',
    'booking.details.phone':           'Phone',
    'booking.details.company':         'Company',
    'booking.details.delivery_address':'Delivery Address',
    'booking.details.service_type':    'Service Type',
    'booking.details.event_vision':    'Event Vision',
    'booking.details.additional_notes':'Additional Notes',
    'booking.details.preferences':     'Your Preferences',
    'booking.details.dietary':         'Dietary',
    'booking.details.accessibility':   'Accessibility',
    'booking.details.booking_details': 'Booking Details',
    'booking.details.event_type':      'Event Type',
    'booking.details.preferred_dates': 'Preferred Dates',

    'booking.edit.event_type.placeholder':      'e.g. Birthday Party',
    'booking.edit.min_guests':                  'Min Guests',
    'booking.edit.max_guests':                  'Max Guests',
    'booking.edit.arrival_time':                'Arrival Time',
    'booking.edit.arrival_time.placeholder':    'e.g. 7:00 PM',
    'booking.edit.duration':                    'Duration (hours)',
    'booking.edit.duration.placeholder':        'e.g. 3',
    'booking.edit.email.placeholder':           'email@example.com',
    'booking.edit.company.placeholder':         'Company name or \u201Cpersonal\u201D',
    'booking.edit.delivery.placeholder':        'Start typing the delivery address\u2026',
    'booking.edit.select_placeholder':          'Select\u2026',
    'booking.edit.event_vision.placeholder':    'Describe your event vision\u2026',
    'booking.edit.notes.placeholder':           'Any additional details\u2026',
    'booking.edit.add_date':                    '+ Add',
    'booking.edit.save':                        'Save Changes',
    'booking.edit.cancel':                      'Cancel',
    'booking.edit.save_failed':                 'Failed to save changes',

    'booking.food.food_required':    'Food Required',
    'booking.food.drinks_only':      'Drinks Only',
    'booking.food.food_and_drinks':  'Food and Drinks',
    'booking.bar.cash_bar_minimum_spend': 'Cash Bar (Min. Spend)',
    'booking.bar.open_bar':           'Open Bar',
    'booking.bar.consumption_tab':    'Consumption Tab',
    'booking.bar.other':              'Other',
    'booking.music.spotify_playlist': 'Spotify Playlist',
    'booking.music.dj':               'DJ',
    'booking.music.none':             'No Preference',
    'booking.service.pickup':         'Pickup',
    'booking.service.delivery':       'Delivery',
    'booking.service.full_service':   'Full Service (with staff)',
    'booking.service.full_service.short': 'Full Service',

    'booking.proposal.heading':       'Proposals',
    'booking.proposal.review_heading':'Under Review — Venue is reviewing your updated details',
    'booking.proposal.caterer_prompt':'Questions or preferences for the caterer?',
    'booking.proposal.capacity':      'Capacity',
    'booking.proposal.min_spend':     'Minimum spend',
    'booking.proposal.min_spend_short':'MIN SPEND',
    'booking.proposal.per_person':    'PER PERSON',
    'booking.proposal.per_person_est':'PER PERSON (EST.)',
    'booking.proposal.fee':           'Booking fee',
    // Round 13 B7 (2026-05-10): Mark-as-Booked modal pricing field labels.
    // Generic across venues — "Deposit" replaces Boxcar-specific "Rental fee".
    'proposal.field.min_spend':       'Min spend ($)',
    'proposal.field.deposit':         'Deposit ($)',
    'booking.proposal.dates':         'Available dates',
    'booking.proposal.notes':         'Notes',
    'booking.proposal.book':          'Book & Pay',
    'booking.proposal.book_free':     'Confirm booking',
    'booking.proposal.confirm_match': 'Confirm & Book',
    'booking.proposal.confirm_match.named': 'Confirm & Book — {{spaceName}}',
    'booking.proposal.confirm_action_required': 'Tap below to officially confirm your booking — it isn\u2019t confirmed yet.',
    'booking.proposal.select_date_instruction': 'Pick the date and time that works best, then tap Confirm to lock it in.',
    'booking.proposal.complete_payment': 'Complete Payment',
    'booking.proposal.complete_payment.named': 'Complete Payment — {{fee}}',
    'booking.proposal.payment_prompt':'You\u2019ll have 24 hours to complete the {{fee}} booking fee payment.',
    'booking.proposal.pending':       'Waiting on venue response',
    'booking.proposal.space_options': '{{count}} space options',
    'booking.proposal.match_confirmed':'Match Confirmed!',
    'booking.proposal.view_venue':    'View venue profile',
    'booking.proposal.other_pending': 'Another proposal is confirmed — complete payment or wait for it to expire',
    'booking.proposal.delivered_from':'Delivered from',
    'booking.proposal.pickup_at':     'Pickup at',
    'booking.proposal.cta_catering_event':'The venue will be in touch to finalize your catering details.',
    'booking.proposal.cta_catering_order':'The venue will contact you to finalize your catering order.',

    'booking.payment.confirm_failed':'Failed to confirm booking',
    'booking.payment.checkout_failed':'Failed to start checkout',

    'booking.countdown.expired':      'Expired',
    'booking.countdown.remaining':    '{{hours}}h {{minutes}}m remaining to complete payment',

    'booking.cta.need_help':          'Need help? Reply to the booking confirmation email.',

    'booking.success.heading':        'You\u2019re Matched!',
    'booking.success.prompt':         'The venue will contact you to finalize your booking. Here are your details.',
    'booking.success.reference':      'BOOKING REFERENCE',
    'booking.success.venue':          'VENUE',
    'booking.success.event_type':     'EVENT TYPE',
    'booking.success.date':           'DATE',
    'booking.success.guests':         'GUESTS',
    'booking.success.time':           'TIME',
    'booking.success.payment':        'PAYMENT',
    'booking.success.booking_fee':    'Booking Fee',
    'booking.success.location':       'LOCATION',
    'booking.success.booking_type':   'RESERVATION TYPE',
    'booking.success.package':        'PACKAGE',
    'booking.success.min_spend':      'Minimum Spend (at venue)',
    'booking.success.fee_free':       'Free',
    'booking.success.view_details':   'View Booking Details',
    'booking.success.print':          'Print Receipt',
    'booking.success.confirming':     'Confirming Your Booking\u2026',
    'booking.success.just_a_moment':  'This will just take a moment\u2026',

    'booking.error_page.title':       'Something went wrong',
    'booking.error_page.fallback':    'We couldn\u2019t load your booking. Please try again.',
    'booking.error_page.retry':       'Try again',
    'booking.error_page.back':        'Back to home',

    'booking.not_found_page.title':   'Booking not found',
    'booking.not_found_page.body':    'We couldn\u2019t find a booking with that ID. It may have been removed or the link may be incorrect.',

    'booking.footer.tag':             'PRIVATE VENUE BOOKING — TORONTO, CA',

    // Venue dashboard chrome (sidebar nav, mobile nav, header, footer).
    'dashboard.nav.proposals':        'Proposals',
    'dashboard.nav.bookings':         'Bookings',
    'dashboard.nav.inquiries':        'Inquiries',
    'dashboard.nav.liveTransfers':    'Live Transfers',
    'dashboard.nav.analytics':        'Analytics',
    'dashboard.nav.settings':         'Settings',
    'dashboard.status.active':        'Active',
    'dashboard.status.paused':        'Paused',
    'dashboard.status.unknown':       'Unknown',
    'dashboard.header.switch_venue':  'Switch venue',
    'dashboard.header.admin_badge':   'Admin',
    'dashboard.header.admin_link':    '\u2190 Admin',
    'dashboard.header.signout':       'Sign out',
    'dashboard.footer.terms':         'Terms',
    'dashboard.footer.privacy':       'Privacy',
    'dashboard.footer.copyright':     '\u00A9 2026 BookOne.Party',

    // Common dashboard strings reused across pages.
    'dashboard.common.loading':       'Loading…',

    // Bookings page.
    'dashboard.bookings.subtitle':    '{{confirmed}} confirmed, {{completed}} completed',
    'dashboard.bookings.empty.title': 'No bookings yet',
    'dashboard.bookings.empty.body':  'When a customer selects your proposal, their booking will appear here with full contact details.',
    'dashboard.bookings.section.confirmed': 'Confirmed ({{count}})',
    'dashboard.bookings.section.completed': 'Completed ({{count}})',

    // Proposals page.
    'dashboard.proposals.subtitle.zero':  'No pending requests',
    'dashboard.proposals.subtitle.one':   '1 pending request',
    'dashboard.proposals.subtitle.many':  '{{count}} pending requests',
    'dashboard.proposals.search.placeholder': 'Search by event, name, date\u2026',
    'dashboard.proposals.filter.status.all':       'All',
    'dashboard.proposals.filter.status.pending':   'Pending',
    'dashboard.proposals.filter.status.submitted': 'Submitted',
    'dashboard.proposals.filter.status.selected':  'Selected',
    'dashboard.proposals.filter.source.all':         'All sources',
    'dashboard.proposals.filter.source.direct':      'Direct',
    'dashboard.proposals.filter.source.marketplace': 'Marketplace',
    'dashboard.proposals.filter.category.all':            'All',
    'dashboard.proposals.filter.category.private_events': 'Events',
    'dashboard.proposals.filter.category.catering':       'Catering',
    'dashboard.proposals.empty.no_requests.title': 'No requests yet',
    'dashboard.proposals.empty.no_requests.body':  'When a customer\u2019s event matches your space, their booking request will appear here. You\u2019ll also get an SMS notification.',
    'dashboard.proposals.empty.no_matches.title':  'No matching proposals',
    'dashboard.proposals.empty.no_matches.body.search': 'No proposals match your search. Try adjusting your criteria.',
    'dashboard.proposals.empty.no_matches.body.filter': 'No proposals match your filter. Try adjusting your criteria.',
    'dashboard.proposals.results.showing':  'Showing {{shown}} of {{total}} proposals',
    'dashboard.proposals.results.clear':    'Clear filters',
    'dashboard.proposals.section.needs_review':    'Needs review',
    'dashboard.proposals.section.pending':         'Pending requests',
    'dashboard.proposals.section.forwarded_crm':   'Forwarded to Tripleseat',
    'dashboard.proposals.section.active':          'Active',
    'dashboard.proposals.section.past':            'Past',
    'dashboard.proposals.toast.withdraw.success':       'Proposal withdrawn',
    'dashboard.proposals.toast.withdraw.error':         'Failed to withdraw proposal',
    'dashboard.proposals.toast.confirm_review.success': 'Proposal confirmed',
    'dashboard.proposals.toast.confirm_review.error':   'Failed to confirm proposal review',
    'dashboard.proposals.toast.dismiss.success':        'Proposal cleared from view',
    'dashboard.proposals.toast.dismiss.error':          'Failed to clear proposal',

    // General inquiries (Members Only only — gated by cateringConfig
    // .generalInquiriesEnabled). Dashboard Inquiries tab + per-inquiry card.
    'dashboard.inquiries.title':            'Inquiries',
    'dashboard.inquiries.subtitle.zero':    'No new questions',
    'dashboard.inquiries.subtitle.one':     '1 new question',
    'dashboard.inquiries.subtitle.many':    '{{count}} new questions',
    'dashboard.inquiries.empty':            'No questions yet. When a guest asks the team something through the widget or by phone, it lands here.',
    'dashboard.inquiries.mark_handled':     'Mark handled',
    'dashboard.inquiries.status.new':       'New',
    'dashboard.inquiries.voiceEmailCaveat': 'Captured by voice — may be misheard. The phone number is the reliable contact.',
    'dashboard.inquiries.status.handled':   'Handled',
    'dashboard.inquiries.toast.handled.success': 'Inquiry marked handled',
    'dashboard.inquiries.toast.handled.error':   'Failed to mark inquiry handled',
    'dashboard.inquiries.category.reservation': 'Reservation',
    'dashboard.inquiries.category.hours':       'Hours',
    'dashboard.inquiries.category.menu':        'Menu',
    'dashboard.inquiries.category.directions':  'Directions',
    'dashboard.inquiries.category.private_event': 'Private event',
    'dashboard.inquiries.category.catering':    'Catering',
    'dashboard.inquiries.category.general':     'General question',
    'dashboard.inquiries.category.other':       'Other',

    // Consumer-facing general-inquiry confirmation page (/inquiry/[id]).
    'inquiry.confirm.title':          'You asked the Members Only team a question',
    'inquiry.confirm.question_label': 'Your question',
    'inquiry.confirm.contact_label':  'We’ll reach you at',
    'inquiry.confirm.response_time':  'The team usually replies within a day.',
    'inquiry.edit.button':            'Edit',
    'inquiry.edit.name':              'Name',
    'inquiry.edit.email':             'Email',
    'inquiry.edit.phone':             'Phone',
    'inquiry.edit.add_email_hint':    'Add your email for a copy by email',
    'inquiry.edit.add_email_cta':     'Add your email to get a copy by email',
    'inquiry.edit.voice_hint':        'This email was captured by voice — double-check it\u2019s right.',
    'inquiry.edit.save':              'Save changes',
    'inquiry.edit.saving':            'Saving\u2026',
    'inquiry.edit.cancel':            'Cancel',
    'inquiry.edit.saved':             'Saved — the team will see your updated details.',
    'inquiry.edit.error':             'Couldn\u2019t save — please check your details and try again.',
    'inquiry.confirm.loading':        'Loading your inquiry…',
    'inquiry.confirm.not_found':      'We couldn’t find that inquiry — reach us at events@membersonly.com.',
    'inquiry.confirm.badge':          'Question received',

    // Multi-venue plan §1108-1134 — Route-to-sister-venue modal (Exodo parent
    // intake → hand-off to a sister venue). Visible only when active venue
    // belongs to a hospitality group AND ≥ 1 sibling is in caller scope.
    'proposals.routing.trigger':                        'Route to sister venue',
    'proposals.routing.title':                          'Route this booking from {{parent}}',
    'proposals.routing.choose_venue':                   'Choose a venue',
    'proposals.routing.no_sisters':                     'No sister venues available in your scope.',
    'proposals.routing.customer_unknown':               'Customer details unavailable',
    'proposals.routing.guests':                         '{{lo}}–{{hi}} guests',
    'proposals.routing.capacity':                       '{{count}}-cap',
    'proposals.routing.min_spend':                      'min {{amount}}',
    'proposals.routing.reason_label':                   'Reason (optional)',
    'proposals.routing.reason_placeholder':             'Logged to audit — why this venue?',
    'proposals.routing.cancel':                         'Cancel',
    'proposals.routing.confirm':                        'Route booking',
    'proposals.routing.confirm_named':                  'Route to {{venue}}',
    'proposals.routing.success':                        'Routed to {{venue}}',
    'proposals.routing.failure':                        'Couldn’t route, try again',
    'proposals.routing.tripleseat.disabled':            'Tripleseat: off',
    'proposals.routing.tripleseat.shadow':              'Tripleseat: shadow',
    'proposals.routing.tripleseat.external_crm':        'Tripleseat: CRM',
    'proposals.routing.skip_tripleseat_label':          'Skip Tripleseat for this routing',
    'proposals.routing.skip_tripleseat_help_enabled':   'Confirming routes the lead but won’t fire {{venue}}’s Tripleseat — handle it manually.',
    'proposals.routing.skip_tripleseat_help_disabled':  'The selected venue has no Tripleseat integration to skip.',
    'proposals.routed_from':                            'Routed from {{venueName}}',

    // Multi-venue plan §1108-1175 — View-As impersonation (platform_admin only).
    'dashboard.impersonate.view_as':                    'View as',
    'dashboard.impersonate.self':                       'Self (platform admin)',
    'dashboard.impersonate.exit':                       'Exit',
    'dashboard.impersonate.banner':                     'Impersonating: {{role}} for {{scope}}.',
    'dashboard.impersonate.role.group_manager':         'Group manager',
    'dashboard.impersonate.role.venue_manager':         'Venue manager',
    'dashboard.impersonate.role.location_manager':      'Location manager',

    // Proposal-card chrome (PENDING-card path; submitted-state labels deferred).
    'dashboard.proposals.card.view_details':         'View details',
    'dashboard.proposals.card.hide_details':         'Hide details',
    'dashboard.proposals.card.dietary_accessibility': 'Dietary / Accessibility',
    'dashboard.proposals.card.event_summary':        'Event Summary',
    'dashboard.proposals.card.catering_details':     'Catering Details',
    'dashboard.proposals.card.venue_specific':       'Venue-Specific Details',
    'dashboard.proposals.card.customer_contact':     'Customer Contact',
    'dashboard.proposals.card.submit':               'Submit Proposal',
    'dashboard.proposals.card.resubmit':             'Resubmit Proposal',
    'dashboard.proposals.card.confirm':              'Confirm Proposal',
    'dashboard.proposals.card.pending_review_banner': 'The customer updated their booking details. Please review and confirm your proposal.',
    'dashboard.proposals.card.direct_badge':         'Direct',
    'dashboard.proposals.card.guests_suffix':        'guests',
    'dashboard.proposals.card.status.pending':            'Pending',
    'dashboard.proposals.card.status.submitted':          'Submitted',
    'dashboard.proposals.card.status.consumer_confirmed': 'Payment Pending',
    'dashboard.proposals.card.status.consumer_confirmed_tripleseat': 'Booked',
    'dashboard.bookings.view.list':                       'List',
    'dashboard.bookings.view.calendar':                   'Calendar',
    'dashboard.proposals.card.status.pending_review':     'Needs Review',
    'dashboard.proposals.card.status.selected':           'Selected',
    'dashboard.proposals.card.status.completed':          'Completed',
    'dashboard.proposals.card.status.rejected':           'Not Selected',
    'dashboard.proposals.card.status.expired':            'Expired',
    'dashboard.proposals.card.status.withdrawn':          'Withdrawn',
    'dashboard.proposals.card.status.external_crm':       'Forwarded to CRM',
    'dashboard.proposals.card.status.lost_post_contact':  'Lost',
    'dashboard.proposals.card.field.allergy_concerns':    'Allergy Concerns',
    'dashboard.proposals.card.field.event_audience':      'Event Audience',
    'dashboard.proposals.card.field.location_preference': 'Location Preference',
    'dashboard.proposals.card.field.billing_setup':       'Billing',
    'dashboard.proposals.card.field.bar_restrictions':    'Bar Restrictions',
    'dashboard.proposals.card.field.pre_order_interest':  'Pre-order Menu',
    'dashboard.proposals.card.field.vendor_interest':     'Soda Pop-Up Events',
    'dashboard.proposals.card.field.budget_poc_name':     'Budget Contact',
    'dashboard.proposals.card.field.budget_landmarks':    'Budget Milestones',
    'dashboard.proposals.card.field.card_charge_confirmed': 'Card Charge',
    'dashboard.proposals.card.field.music_preference':    'Music / DJ',
    'dashboard.proposals.card.field.space_preference':    'Space Preference',

    // Submit-Proposal modal chrome.
    'dashboard.proposals.card.event_fallback':                  'Event',
    'dashboard.proposals.card.budget_label':                    'Budget',
    'dashboard.proposals.card.requested_label':                 'Requested',
    'dashboard.proposals.modal.ai_suggested_banner':            'AI-suggested from customer intake — feel free to adjust',
    'dashboard.proposals.modal.submitting':                     'Submitting...',
    'dashboard.proposals.modal.submit_multi':                   'Submit {{count}} Options',
    'dashboard.proposals.modal.add_option':                     'Add Another Space Option',
    'dashboard.proposals.modal.select_space_placeholder':       'Select space',
    'dashboard.proposals.modal.select_date_placeholder':        'Select date',
    'dashboard.proposals.modal.up_to':                          'up to {{n}}',
    'dashboard.proposals.modal.cross_location_warning':         'This space is at a different location than requested',
    'dashboard.proposals.modal.per_person_disclaimer':          'This is an estimate, not a binding quote. Final pricing confirmed after discussion.',
    'dashboard.proposals.modal.notes_placeholder':              'Any additional details for the customer...',
    'dashboard.proposals.modal.attachments_hint':               '(menus, terms & conditions, etc.)',
    'dashboard.proposals.modal.aria.remove_option':             'Remove option',
    'dashboard.proposals.modal.aria.remove_date_slot':          'Remove date slot',
    'dashboard.proposals.modal.aria.close_modal':               'Close modal',
    'dashboard.proposals.modal.label.space':                    'Space',
    'dashboard.proposals.modal.label.capacity':                 'Capacity',
    'dashboard.proposals.modal.label.min_spend':                'Min Spend ($)',
    'dashboard.proposals.modal.label.per_person':               'Per Person ($)',
    'dashboard.proposals.modal.label.booking_fee':              'Booking Fee ($)',
    'dashboard.proposals.modal.label.available_dates':          'Available Dates & Times',
    'dashboard.proposals.modal.label.notes':                    'Notes',
    'dashboard.proposals.modal.label.attachments':              'Attachments',

    // Landing page (apps/web/src/app/page.tsx).
    'marketing.nav.venue_login':         'Venue Login',
    'marketing.nav.call_now':            'Call Now',
    'marketing.hero.title.line1':        'BOOK YOUR',
    'marketing.hero.title.line2':        'PARTY.',
    'marketing.hero.tagline':            'Book private event spaces in {{city}}. Tell us what you need, we\u2019ll match you with venues.',
    'marketing.hero.cta.call':           'Call',
    'marketing.hero.cta.text':           'Text',
    'marketing.hero.cta.subline':        'CALL OR TEXT — SAME ASSISTANT, SAME NUMBER',
    'marketing.hero.cta.browse':         'Browse event spaces \u2192',
    'marketing.hero.legal':              'By calling or texting, you agree to receive SMS from BookOne.Party about your event inquiry. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.',
    'marketing.hero.legal.tos':          'Terms of Service',
    'marketing.hero.legal.privacy':      'Privacy Policy',
    'marketing.help.lead':               'I need a space for',
    'marketing.help.subline':            'TAP TO BROWSE PRIVATE EVENT SPACES',
    'marketing.help.phrase.birthday':    'a birthday party',
    'marketing.help.phrase.corporate':   'a corporate dinner',
    'marketing.help.phrase.anniversary': 'a surprise anniversary',
    'marketing.help.phrase.graduation':  'a graduation celebration',
    'marketing.help.phrase.offsite':     'a team offsite',
    'marketing.help.phrase.rehearsal':   'a rehearsal dinner',
    'marketing.help.phrase.holiday':     'a holiday party',
    'marketing.help.phrase.launch':      'a product launch',
    'marketing.help.phrase.shower':      'a baby shower brunch',
    'marketing.help.phrase.farewell':    'a farewell gathering',
    'marketing.help.phrase.watch':       'a watch party',
    'marketing.help.phrase.engagement':  'an engagement dinner',
    'marketing.how.eyebrow':             'HOW IT WORKS',
    'marketing.how.title.a':             'Three steps.',
    'marketing.how.title.b':             'One call.',
    'marketing.how.step':                'STEP',
    'marketing.how.step1.title':         'Tell Us About Your Event',
    'marketing.how.step1.desc':          'Browse private event spaces and share your event details — date, guest count, vibe. Shortlist the venues you like.',
    'marketing.how.step2.title':         'We Match You With Venues',
    'marketing.how.step2.desc':          'We find the best-fit private spaces for your event. Venues send proposals with space details, capacity, and pricing so you can compare.',
    'marketing.how.step3.title':         'Pick Your Favorite and Book',
    'marketing.how.step3.desc':          'Select the proposal you love and confirm your booking. We connect you directly with the venue to finalize everything.',
    'marketing.statement.a':             'Before BookOne, planning a private party meant 3 weeks and 12 emails.',
    'marketing.statement.b':             'Now it takes one search.',
    'marketing.statement.c':             'This is the shortcut.',
    'marketing.partners.eyebrow':        'PARTNERS',
    'marketing.partners.title':          'Trusted by {{city}}\u2019s best venues',
    'marketing.partners.coming_soon':    'Coming Soon',
    'marketing.partners.type.cocktail':  'Cocktail Lounge',
    'marketing.partners.type.restaurant': 'Restaurant & Bar',
    'marketing.partners.type.rooftop':   'Rooftop Venue',
    'marketing.partners.type.dining':    'Private Dining',
    'marketing.testimonial1.quote':      'BookOne made booking our company holiday party effortless. We had three proposals within the hour.',
    'marketing.testimonial1.role':       'Event Organizer',
    'marketing.testimonial2.quote':      'Finally, a service that understands what venues need. Qualified leads delivered straight to my phone.',
    'marketing.testimonial2.role':       'Venue Owner',
    'marketing.testimonial3.quote':      'I planned my entire birthday dinner in one afternoon. The venue was perfect for our group.',
    'marketing.testimonial3.role':       'Customer',
    'marketing.cta.title.a':             'Browse event spaces for your',
    'marketing.cta.title.b':             'party today.',
    'marketing.cta.tagline':             'No app. No account. No waiting.',
    'marketing.faq.eyebrow':             'FAQ',
    'marketing.faq.title':               'Common questions',
    'marketing.faq.q1.q':                'How fast will I get proposals?',
    'marketing.faq.q1.a':                'Most customers receive venue proposals within 30 minutes. We match your event with the best-fit venues and they respond fast because every inquiry is qualified.',
    'marketing.faq.q2.q':                'What does it cost?',
    'marketing.faq.q2.a':                'Booking through BookOne.Party is free for customers. You only pay the venue\u2019s booking fee when you select a proposal — no hidden charges, no platform fees.',
    'marketing.faq.q3.q':                'Can I text instead of calling?',
    'marketing.faq.q3.a':                'Yes. Same AI assistant handles voice and text through one number. No app required.',
    'marketing.faq.q4.q':                'Can I change my booking after selecting?',
    'marketing.faq.q4.a':                'Once you select a venue, we connect you directly with the venue manager. Any changes to dates, guest count, or details go through them.',
    'marketing.venue_cta.title':         'Host private events?',
    'marketing.venue_cta.body':          'Get private event bookings delivered straight to your phone. Free to sign up, no app required.',
    'marketing.venue_cta.button':        'Sign up your venue \u2192',
    'marketing.footer.tag':              'PRIVATE VENUE BOOKING \u2014 {{city}}, {{country}}',
    'marketing.footer.tagline':          'No app. No account. No waiting.',
    'marketing.footer.legal':            '\u00A9 2026 1001474668 ONTARIO INC.',

    // Analytics dashboard page.
    'dashboard.analytics.subtitle':         'Track your venue performance',
    'dashboard.analytics.period.7d':        '7d',
    'dashboard.analytics.period.30d':       '30d',
    'dashboard.analytics.period.90d':       '90d',
    'dashboard.analytics.period.all':       'All-time',
    'dashboard.analytics.empty.title':      'No data yet',
    'dashboard.analytics.empty.body':       'Analytics will populate as you receive and respond to booking requests.',
    'dashboard.analytics.stat.total_requests': 'Total Requests',
    'dashboard.analytics.stat.submitted':      'Submitted',
    'dashboard.analytics.stat.won':            'Won',
    'dashboard.analytics.stat.revenue':        'Revenue',
    'dashboard.analytics.funnel.title':        'Conversion Funnel',
    'dashboard.analytics.funnel.received':     'Received',
    'dashboard.analytics.funnel.submitted':    'Submitted',
    'dashboard.analytics.funnel.selected':     'Selected',
    'dashboard.analytics.funnel.completed':    'Completed',
    'dashboard.analytics.funnel.lost':         'Lost',
    'dashboard.analytics.funnel.lost.reasons': 'Reasons',
    'dashboard.analytics.funnel.conversion':   'Conversion Rate',
    'dashboard.analytics.stat.avg_party':      'Avg Party Size',
    'dashboard.analytics.stat.repeat':         'Repeat Customers',

    // Login page.
    'auth.login.title':                  'Venue Login',
    'auth.login.subtitle':               'Sign in to manage your booking requests.',
    'auth.login.tab.phone':              'Phone',
    'auth.login.tab.email':              'Email',
    'auth.login.label.phone':            'Phone number',
    'auth.login.label.email':            'Email address',
    'auth.login.label.code':             'Verification code',
    'auth.login.hint.phone':             'Enter the phone number you signed up with.',
    'auth.login.hint.email':             'We\u2019ll send a 6-digit code to verify it\u2019s you.',
    'auth.login.hint.code':              'Check your messages for a 6-digit code.',
    'auth.login.hint.code_email':        'Check your email for a code sent to {{email}}.',
    'auth.login.button.send':            'Send verification code',
    'auth.login.button.verify':          'Log in',
    'auth.login.button.resend':          'Resend code',
    'auth.login.button.different':       'Use a different number',
    'auth.login.button.different_email': 'Use a different email',
    'auth.login.error.phone_required':   'Please enter a phone number.',
    'auth.login.error.email_invalid':    'Please enter a valid email address.',
    'auth.login.error.phone_invalid':    'Please enter a valid phone number.',
    'auth.login.error.code_invalid':     'Invalid code. Try again or request a new one.',
    'auth.login.error.send_failed':      'Could not send code. Please try again.',
    'auth.login.error.session_expired':  'Your session has expired. Please log in again.',
    'auth.login.signup.prompt':          'New venue?',
    'auth.login.signup.cta':             'Sign up here',

    // Dashboard error/loading boundaries.
    'dashboard.error.title':             'Something went wrong',
    'dashboard.error.body':              'We hit an unexpected issue. Try again, or refresh the page.',
    'dashboard.error.retry':             'Try again',

    // Lang switcher / common UI.
    'common.lang.synced_from_profile':   'Switched to your saved dashboard language ({{lang}}).',

    // Login extras.
    'auth.login.placeholder.phone':      '(416) 555-1234',
    'auth.login.placeholder.email':      'owner@venue.com',
    'auth.login.hint.email_account':     'Enter the email address on your venue account.',
    'auth.login.hint.code_whatsapp':     'Check your WhatsApp for a code sent to {{phone}}.',
    'auth.login.hint.code_sms':          'We sent a 6-digit code to {{phone}}.',
    'auth.login.cooldown':               'Resend code in {{seconds}}s',
    'auth.login.toast.resent':           'Code resent!',
    'auth.login.toast.resend_failed':    'Failed to resend code',

    // Settings page chrome (subcomponents covered in follow-up commits).
    'dashboard.settings.subtitle':       'Manage your venue details, spaces, and availability',
    'dashboard.settings.action.preview_public':   'Preview Public',
    'dashboard.settings.action.preview_customer': 'Preview Customer',
    'dashboard.settings.action.pause':            'Pause',
    'dashboard.settings.action.resume':           'Resume',
    'dashboard.settings.toast.paused':            'Venue paused',
    'dashboard.settings.toast.resumed':           'Venue resumed',
    'dashboard.settings.toast.toggle_failed':     'Failed to toggle status',
    'dashboard.settings.toast.email_verified':    'Email verified successfully!',
    'dashboard.settings.toast.email_link_expired': 'Verification link expired. Please request a new one.',
    'dashboard.settings.signout':                 'Sign out',

    // Multi-venue plan — location_manager Settings scoping. When the
    // signed-in admin's role === 'location_manager', the Settings page
    // hides every global venue field and renders a single section with
    // only the seven location-scoped fields (address, postalCode, phone,
    // latitude, longitude, websiteUrl, instagramUrl). These strings drive
    // that surface plus the small explainer banner at the top.
    'settings.location_manager_banner':           'You’re a location manager for {{locationName}}. Only {{locationName}}-specific fields are editable here.',
    'settings.location_manager_section_title':    '{{locationName}} details',
    'settings.location_only_fields_notice':       'Editing global venue settings (brand, contact, integrations) requires venue-manager or higher access.',
    'settings.location_manager.field.address':    'Address',
    'settings.location_manager.field.postal_code':'Postal code',
    'settings.location_manager.field.phone':      'Phone',
    'settings.location_manager.field.latitude':   'Latitude',
    'settings.location_manager.field.longitude':  'Longitude',
    'settings.location_manager.field.website':    'Website URL',
    'settings.location_manager.field.instagram':  'Instagram URL',
    'settings.location_manager.action.save':      'Save changes',
    'settings.location_manager.action.saving':    'Saving…',
    'settings.location_manager.toast.saved':      'Location updated',
    'settings.location_manager.toast.save_failed':'Failed to save changes',
    'settings.location_manager.no_location':      'No location is currently in scope for this account. Contact your venue manager.',

    // Public venue profile (apps/web/src/app/venue/[id]).
    'venue_profile.error.title':         'Something went wrong',
    'venue_profile.error.body':          'We couldn\u2019t load this venue. Please try again.',
    'venue_profile.error.retry':         'Try again',
    'venue_profile.error.home':          'Back to home',
    'venue_profile.not_found.title':     'Venue not found',
    'venue_profile.not_found.body':      'We couldn\u2019t find a venue with that ID. It may have been removed or the link may be incorrect.',
    'venue_profile.not_found.home':      'Back to home',

    // R21 T3 (2026-05-10) — Booking management settings card toasts.
    'settings.booking_mgmt.toast_enabled':       'Booking management enabled',
    'settings.booking_mgmt.toast_disabled':      'Booking management disabled',
    'settings.booking_mgmt.toast_save_failed':   'Couldn’t save — {{error}}',
    'settings.booking_mgmt.error_save_default':  'Failed to save',

    // R21 T3 (2026-05-10) — Analytics customer-list error toasts (was alert()).
    'dashboard.analytics.customer_list.toast.update_reminder_failed': 'Failed to update reminder',
    'dashboard.analytics.customer_list.toast.save_notes_failed':      'Failed to save notes',

    // R21 T3 (2026-05-10) — Tripleseat venue settings card toasts + confirm dialog.
    'settings.tripleseat.toast.validation_errors':  'Please fix the validation errors before saving.',
    'settings.tripleseat.toast.saved':              'Tripleseat settings saved',
    'settings.tripleseat.toast.save_failed':        'Failed to save Tripleseat settings',
    'settings.tripleseat.confirm.switch_live':      'Switch to live Tripleseat submissions?\n\nThis will send real leads to your Tripleseat account. Make sure you have tested against the mock endpoint first.',

    // R21 T3 (2026-05-10) — Tripleseat preview modal toasts.
    'proposals.tripleseat_preview.toast.script_copied':    'Auto-fill script copied — paste in the form’s browser console',
    'proposals.tripleseat_preview.toast.copy_failed':      'Could not copy — try the per-field copy buttons below',
    'proposals.tripleseat_preview.toast.marked_forwarded': 'Marked as forwarded. Customer has been notified.',

    // R21 T3 (2026-05-10) — External CRM tracker (Tripleseat shadow-mode) toasts.
    'booking.external_crm_tracker.toast.note_required':    'Add a short note describing what happened',
    'booking.external_crm_tracker.toast.contact_logged':   'Contact logged',
    'booking.external_crm_tracker.toast.save_failed':      'Failed to save',
    'booking.external_crm_tracker.toast.status_advanced':  'Status: {{status}}',
    'booking.external_crm_tracker.toast.update_status_failed': 'Failed to update status',
    'booking.external_crm_tracker.toast.saved':            'Saved',

    // R21 T3 (2026-05-10) — Shared menu-upload component.
    'menu_upload.toast.file_too_large':                    'File too large ({{size}}MB). Maximum is 10MB.',
    'menu_upload.drop_or_click':                           'Drop PDF or image, or click to upload',
    'menu_upload.file_hint':                               'PDF, JPEG, PNG, or WebP — Max 10MB',

    // R21 Sweep Bug Fixes (2026-05-10) — surfaces flagged during the
    // multi-lang $B sweep that were leaking English on FR/ES/IT pages.
    'image_upload.drop_or_click':                          'Drop image or click to upload',
    'image_upload.file_hint':                              'JPEG, PNG, or WebP — Max 5MB',

    'browse_nav.button':                                   'Browse',
    'browse_nav.category.venues.label':                    'Venues',
    'browse_nav.category.venues.desc':                     'Book private event spaces and venues',
    'browse_nav.category.catering.label':                  'Catering',
    'browse_nav.category.catering.desc':                   'Order catering for your next event',
    'browse_nav.category.experiences.label':               'Experiences',
    'browse_nav.category.experiences.desc':                'Golf, racing, escape rooms & more',

    // R23 (2026-05-10) — browse hero titles + catering address filter
    // placeholder. Previously hardcoded English literals leaked into
    // FR/ES/IT sweeps. Pure wiring; the allowlist's "TODO wrap" entries
    // for these sites are now cleared.
    'browse.hero.back_to_home':                            '← Back to home',
    'browse.venues.title':                                 'Browse Venues',
    'browse.venues.subtitle':                              'Explore private event spaces and venues.',
    'browse.experiences.title':                            'Browse Experiences',
    'browse.experiences.subtitle':                         'Alternative event spaces and unique experiences.',
    'browse.catering.title':                               'Browse Catering',
    'browse.catering.subtitle':                            'Food and drinks delivered to your event.',
    'browse.catering.address_placeholder':                 'Enter your address to see who delivers to you...',

    // R21 Sweep batch 2 (2026-05-10) — venue profile + catering profile
    // public pages were rendering ~15 strings of hardcoded English on
    // every lang including FR.
    'venue_profile.back_to_venues':                        'Browse Venues',
    'venue_profile.back_to_catering':                      'Back to catering',
    'venue_profile.space.up_to':                           'Up to {{n}}',
    'venue_profile.section.private_spaces':                'Private Spaces',
    'venue_profile.section.menu_additional':               'Menu / Additional Details',
    'venue_profile.section.our_food':                      'Our Food',
    'venue_profile.section.our_locations':                 'Our Locations',
    'venue_profile.section.catering_menus':                'Catering Menus',
    'venue_profile.section.delivery_range':                'Delivery Range',
    'venue_profile.widget.title_event':                    'Book a private event at {{venueName}}',
    'venue_profile.widget.description_event':              'Chat with our assistant to plan your night.',
    'venue_profile.widget.title_catering':                 'Plan an event at {{venueName}}',
    'venue_profile.widget.description_catering':           'Get a quote — call or chat with the team.',
    'venue_profile.cta.call_to_book':                      'Call to Book',
    'venue_profile.cta.text_to_book':                      'Text to Book',
    'venue_profile.cta.ready_to_book':                     'Ready to book?',
    'venue_profile.cta.text_or_call_direct':               'Text or call {{venueName}} directly',
    'venue_profile.cta.text_to_book_direct':               'Text to book this venue directly',
    'venue_profile.locations.subtitle':                    '{{count}} locations across {{region}} — pick the closest to you, or check your delivery address below.',
    'venue_profile.locations.delivery_check':              'Check if we deliver to your address',
    'venue_profile.menu.online_menu':                      'Online Menu',

    // R21 T3 (2026-05-10) — Service types section in venue settings.
    'dashboard.settings.service_types.toast.updated':      'Service types updated',
    'dashboard.settings.service_types.toast.update_failed':'Failed to update',

    // R21 T3 (2026-05-10) — Live-transfers page dismiss/restore toasts.
    'dashboard.live_transfers.toast.dismissed':            'Dismissed',
    'dashboard.live_transfers.toast.dismiss_failed':       'Dismiss failed',
    'dashboard.live_transfers.toast.restored':             'Restored',
    'dashboard.live_transfers.toast.restore_failed':       'Restore failed',

    // Settings — VenueInfoForm.
    'settings.venue_info.title':              'Venue Information',
    'settings.venue_info.title_edit':         'Edit Venue Information',
    'settings.venue_info.action.edit':        'Edit',
    'settings.venue_info.action.cancel':      'Cancel',
    'settings.venue_info.action.save':        'Save',
    'settings.venue_info.action.saving':      'Saving\u2026',
    'settings.venue_info.hero.label':         'Hero Image',
    'settings.venue_info.hero.upload':        'Upload',
    'settings.venue_info.hero.change':        'Change',
    'settings.venue_info.hero.uploading':     'Uploading\u2026',
    'settings.venue_info.hero.hint':          'Recommended: 2400 \u00D7 1200px or larger',
    'settings.venue_info.hero.upload_failed': 'Failed to upload hero image',
    'settings.venue_info.field.name':         'Name',
    'settings.venue_info.field.name_full':    'Venue Name',
    'settings.venue_info.field.address':      'Address',
    'settings.venue_info.field.contact':      'Contact',
    'settings.venue_info.field.contact_name': 'Contact Name',
    'settings.venue_info.field.phone':        'Phone',
    'settings.venue_info.field.email':        'Contact Email',
    'settings.venue_info.field.tagline':      'Tagline',
    'settings.venue_info.field.website':      'Website',
    'settings.venue_info.field.instagram':    'Instagram',
    'settings.venue_info.field.notes':        'Notes',
    'settings.venue_info.placeholder.email':  'owner@venue.com',
    'settings.venue_info.placeholder.website': 'mybar.com',
    'settings.venue_info.placeholder.instagram': 'yourvenue',
    'settings.venue_info.maps':               'View on Google Maps',
    'settings.venue_info.notifications':      'Notifications',
    'settings.venue_info.no_email_set':       'No email set',
    'settings.venue_info.no_email_label':     'No email',
    'settings.venue_info.via_whatsapp':       '{{phone}} via WhatsApp',
    'settings.venue_info.via_sms':            '{{phone}} via SMS',
    'settings.venue_info.both':               '{{email}} + {{phone}}',
    'settings.venue_info.notifications.choose': 'Choose how you receive booking notifications and login codes.',
    'settings.venue_info.channel.phone':      'Phone',
    'settings.venue_info.channel.sms':        'SMS',
    'settings.venue_info.channel.whatsapp':   'WhatsApp',
    'settings.venue_info.channel.none_email': 'None (email only)',
    'settings.venue_info.channel.email_toggle': 'Email notifications',
    'settings.venue_info.channel.sending_to': 'Sending to {{email}}',
    'settings.venue_info.channel.add_email':  'Add a contact email above to enable email notifications.',
    'settings.venue_info.visibility.title':   'Public Profile Visibility',
    'settings.venue_info.visibility.menus':   'Show set menus on public profile',
    'settings.venue_info.visibility.spend':   'Show minimum spend on public profile',
    'settings.venue_info.visibility.contact': 'Show customer contact on proposals',
    'settings.venue_info.visibility.contact_help': 'Display customer phone and email on incoming booking requests',
    'settings.venue_info.accessibility':      'Wheelchair accessible',
    'settings.venue_info.toast.saved':        'Venue info saved',
    'settings.venue_info.toast.save_failed':  'Failed to save',
    'settings.venue_info.tagline_empty':      '\u2014',
    // Email verification badge.
    'settings.email_verify.verified':         'Verified',
    'settings.email_verify.not_verified':     'Not verified',
    'settings.email_verify.send':             'Send verification email',
    'settings.email_verify.sending':          'Sending\u2026',
    'settings.email_verify.toast.sent':       'Verification email sent. Check your inbox.',
    'settings.email_verify.toast.failed':     'Failed to send verification email',
    // Direct booking section.
    'settings.direct.title':                  'Direct Booking',
    'settings.direct.code_label':             'Venue Code',
    'settings.direct.code_help':              'Customers can text this code to BookOne to book directly with you',
    'settings.direct.line_label_one':         'Dedicated Phone Line',
    'settings.direct.line_label_many':        'Dedicated Phone Lines',
    'settings.direct.status.active':          'Active',
    'settings.direct.status.inactive':        'Inactive',
    'settings.direct.no_line':                'Want a dedicated phone number for your venue? Contact BookOne to get set up.',

    // Settings — LocationList.
    'settings.locations.title':               'Locations ({{count}})',
    'settings.locations.title_edit':          'Edit Locations',
    'settings.locations.empty':               'Single location venue. Click Edit to add additional locations.',
    'settings.locations.location_n':          'Location {{n}}',
    'settings.locations.field.name':          'Name',
    'settings.locations.field.city':          'City',
    'settings.locations.field.address':       'Address',
    'settings.locations.field.phone':         'Phone',
    'settings.locations.field.website':       'Website URL',
    'settings.locations.field.instagram':     'Instagram URL',
    'settings.locations.placeholder.name':    'e.g. Downtown Location',
    'settings.locations.placeholder.city':    'e.g. Toronto',
    'settings.locations.placeholder.address': 'Search for address\u2026',
    'settings.locations.placeholder.phone':   'e.g. (416) 555-1234',
    'settings.locations.placeholder.website': 'e.g. https://mybar.com/downtown',
    'settings.locations.placeholder.instagram': 'e.g. https://instagram.com/mybar',
    'settings.locations.add':                 'Add Location',
    'settings.locations.toast.saved':         'Locations saved',
    'settings.locations.toast.save_failed':   'Failed to save locations',
    'settings.locations.toast.missing_address': 'Location {{n}} needs an address before you can save.',
    'settings.locations.website_label':       'Website',

    // Settings — SpaceList + SpaceCard.
    'settings.spaces.title':                  'Spaces ({{count}})',
    'settings.spaces.title_edit':             'Edit Spaces',
    'settings.spaces.empty':                  'No spaces configured yet. Click Edit to add your first space.',
    'settings.spaces.space_n':                'Space {{n}}',
    'settings.spaces.field.name':             'Name',
    'settings.spaces.field.capacity':         'Max Capacity',
    'settings.spaces.field.min_spend':        'Min Spend ($)',
    'settings.spaces.field.booking_fee':      'Booking Fee ($)',
    'settings.spaces.field.location':         'Location',
    'settings.spaces.field.description':      'Description',
    'settings.spaces.field.images':           'Images',
    'settings.spaces.placeholder.name':       'e.g. The Snug',
    'settings.spaces.placeholder.min_spend':  'e.g. 1000',
    'settings.spaces.placeholder.booking_fee': 'e.g. 50',
    'settings.spaces.no_location':            'No specific location',
    'settings.spaces.add':                    'Add Space',
    'settings.spaces.toast.saved':            'Spaces saved',
    'settings.spaces.toast.save_failed':      'Failed to save spaces',
    'settings.spaces.toast.image_failed':     'Failed to upload image',
    'settings.space_card.up_to':              'Up to {{capacity}}',
    'settings.space_card.up_to_guests':       'Up to {{capacity}} guests',
    'settings.space_card.min_short':          '${{amount}} min',
    'settings.space_card.min_long':           '${{amount}} minimum spend',
    'settings.space_card.fee_short':          '${{amount}} fee',
    'settings.space_card.fee_long':           '${{amount}} booking fee',

    // Settings — MenuList.
    'settings.menus.title':                   'Menu / Additional Details ({{count}})',
    'settings.menus.title_edit':              'Edit Menu / Additional Details',
    'settings.menus.empty':                   'No menus or details uploaded yet. Click Edit to add menus or event details.',
    'settings.menus.menu_n':                  'Menu {{n}}',
    'settings.menus.field.name':              'Name',
    'settings.menus.field.description':       'Description',
    'settings.menus.field.location':          'Location',
    'settings.menus.field.source':            'Source',
    'settings.menus.placeholder.name':        'e.g. Set Menu, Event Package',
    'settings.menus.placeholder.description': 'Optional description',
    'settings.menus.placeholder.link':        'https://yoursite.com/menu.pdf',
    'settings.menus.placeholder.link_help':   'Paste a URL to your menu (website page, PDF link, etc.)',
    'settings.menus.no_location':             'No specific location',
    'settings.menus.mode.upload':             'Upload File',
    'settings.menus.mode.link':               'Paste Link',
    'settings.menus.view_current':            'View current file',
    'settings.menus.replace':                 'Replace',
    'settings.menus.add':                     'Add Menu ({{count}}/12)',
    'settings.menus.toast.saved':             'Menus saved',
    'settings.menus.toast.save_failed':       'Failed to save menus',
    'settings.menus.error.untitled':          'Untitled',
    'settings.menus.error.needs_link':        'Menu "{{name}}" needs a link URL',
    'settings.menus.error.too_large':         'File too large for "{{name}}". Max 10MB.',
    'settings.menus.error.bad_type':          'File type not allowed for "{{name}}". Use PDF, JPEG, PNG, or WebP.',
    'settings.menus.error.upload_failed':     'Upload failed for "{{name}}": {{msg}}',

    // Magic-link + verify-email pages.
    'auth.magic.signing_in':             'Signing you in\u2026',
    'auth.magic.redirecting':            'You\u2019ll be redirected to your dashboard',
    'auth.magic.expired':                'This magic link has expired or is invalid.',
    'auth.magic.redirecting_login':      'Redirecting to login\u2026',
    // Multi-venue plan \u00a71136-1154 \u2014 Access-changed state on /auth/magic.
    'auth.magic.access_changed.title':       'Access to this venue was changed',
    'auth.magic.access_changed.title_named': 'Access to {{venue}} was changed',
    'auth.magic.access_changed.message':     'Your role recently changed and you no longer have access to bookings at this venue. The booking is being handled by the venue. If this is unexpected, contact your group admin.',
    'auth.magic.access_changed.cta':         'Go to your dashboard',
    'auth.verify_email.verifying':       'Verifying your email\u2026',
    'auth.verify_email.short':           'Verifying\u2026',

    // Signup flow (apps/web/src/app/venue/signup).
    'signup.page.title':                  'List your venue',
    'signup.page.subtitle':               'Events, catering, or both — get bookings via SMS. No app needed, no fees to sign up.',
    'signup.type.question':               'What services does your venue offer?',
    'signup.type.events.label':           'Private Events only',
    'signup.type.events.desc':            'Consumers book your space for events',
    'signup.type.catering.label':         'Catering only',
    'signup.type.catering.desc':          'You deliver food; no on-site events',
    'signup.type.both.label':             'Both',
    'signup.type.both.desc':              'Host events on-site AND deliver catering',
    'signup.quickfill.title':             'Quick Fill from Google',
    'signup.quickfill.body':              'Search for your venue to auto-fill name, address, phone, and website.',
    'signup.quickfill.placeholder':       'Search your venue name or address\u2026',
    'signup.quickfill.success':           'Form auto-filled! Review and edit the details below.',
    'signup.venue.title':                 'Venue Details',
    'signup.venue.name_label':            'Venue Name *',
    'signup.venue.name_placeholder':      'Saint John\u2019s Tavern',
    'signup.venue.tagline_label':         'Tagline',
    'signup.venue.tagline_placeholder':   'Toronto\u2019s cosiest neighbourhood pub',
    'signup.venue.address_label':         'Address *',
    'signup.venue.accessibility':         'Venue is wheelchair accessible',
    'signup.photo.title':                 'Venue Photo',
    'signup.photo.body':                  'This will be the main photo on your venue profile',
    'signup.contact.title':               'Contact Info',
    'signup.contact.name_label':          'Your Name *',
    'signup.contact.phone_label':         'Phone (for SMS updates) *',
    'signup.contact.phone_placeholder':   '(416) 555-1234',
    'signup.contact.email_label':         'Email (optional)',
    'signup.online.title':                'Online Presence',
    'signup.online.body':                 'Help customers find you online (optional)',
    'signup.online.website_label':        'Website',
    'signup.online.website_placeholder':  'mybar.com',
    'signup.online.instagram_label':      'Instagram',
    'signup.online.instagram_placeholder': 'yourvenue',
    'signup.spaces.title':                'Private Spaces',
    'signup.spaces.body':                 'Add each private space available for booking.',
    'signup.spaces.space_n':              'Space {{n}}',
    'signup.spaces.name_placeholder':     'Space name (e.g. The Snug, Private Dining Room)',
    'signup.spaces.capacity_label':       'Max capacity *',
    'signup.spaces.capacity_placeholder': '50',
    'signup.spaces.min_spend_label':      'Min spend ($)',
    'signup.spaces.min_spend_placeholder': '2500',
    'signup.spaces.fee_label':            'Booking fee ($)',
    'signup.spaces.fee_placeholder':      '40',
    'signup.spaces.notes_placeholder':    'Additional notes (cancellation policy, etc.)',
    'signup.spaces.photo_label':          'Space Photo',
    'signup.spaces.add':                  'Add another space',
    'signup.catering.title':              'Catering Details',
    'signup.catering.body':               'A few quick details so we can match you with catering inquiries. You can add photos, menu packages, and more in Settings after signup.',
    'signup.catering.cuisines_label':     'Cuisine types',
    'signup.catering.cuisines_placeholder': 'Type a cuisine and press Enter (e.g. Italian, Mexican, Sushi)',
    'signup.catering.cuisines_remove':    'Remove {{tag}}',
    'signup.catering.radius_label':       'Delivery radius (km)',
    'signup.catering.radius_placeholder': '25',
    'signup.catering.minimum_label':      'Minimum order ($)',
    'signup.catering.minimum_placeholder': 'optional',
    'signup.catering.footer':             'All catering fields are optional at signup. You can add catering photos, menu packages, and more from your dashboard Settings after creating your venue.',
    'signup.menus.title':                 'Menu / Additional Details (Optional)',
    'signup.menus.body':                  'Upload menus or event details \u2014 PDF or image, up to 5.',
    'signup.menus.menu_n':                'Menu {{n}}',
    'signup.menus.name_placeholder':      'Name (e.g. Set Menu, Event Package)',
    'signup.menus.description_placeholder': 'Description (optional)',
    'signup.menus.add':                   'Add a set menu',
    'signup.notes.label':                 'Additional Notes (optional)',
    'signup.notes.placeholder':           'Anything else we should know?',
    'signup.consent.body':                'I consent to receive SMS from BookOne.Party about booking requests, event updates, account verification codes (OTP), and related service messages. Message frequency varies. Message & data rates may apply. Reply HELP for help, STOP to opt out. I have read and agree to the',
    'signup.consent.tos':                 'Terms of Service',
    'signup.consent.and':                 'and',
    'signup.consent.privacy':             'Privacy Policy',
    'signup.submit':                      'Sign Up Your Venue',
    'signup.error.name':                  'Venue name is required',
    'signup.error.address':               'Address is required',
    'signup.error.contact_name':          'Your name is required',
    'signup.error.phone_required':        'Phone number is required',
    'signup.error.phone_invalid':         'Enter a valid 10-digit North American phone number',
    'signup.error.hero_size':             'Hero image must be under 5 MB',
    'signup.error.space_name':            'Space name is required',
    'signup.error.space_capacity':        'Max capacity must be at least 1',
    'signup.error.space_image':           'Space image must be under 5 MB',
    'signup.error.menu_name':             'Menu name is required',
    'signup.error.menu_file':             'Menu file is required',
    'signup.error.menu_size':             'Menu file must be under 10 MB',
    'signup.error.delivery_negative':     'Delivery radius cannot be negative',
    'signup.error.minimum_negative':      'Minimum order cannot be negative',
    'signup.error.consent':               'You must agree to receive SMS to sign up a venue',
    'signup.error.generic':               'Something went wrong',

    // Signup success page.
    'signup.success.title':               'You\u2019re signed up!',
    'signup.success.intro':               'Your venue is now registered on BookOne.Party. Here\u2019s what happens next:',
    'signup.success.step1.title':         'Booking requests via SMS',
    'signup.success.step1.desc':          'When a customer\u2019s event matches your space, you\u2019ll receive a text with the details.',
    'signup.success.step2.title':         'Submit your proposal',
    'signup.success.step2.desc':          'Click the link in your SMS notification to submit your proposal through the dashboard.',
    'signup.success.step3.title':         'Get connected',
    'signup.success.step3.desc':          'If selected, we\u2019ll connect you directly with the customer to finalize.',
    'signup.success.cta.dashboard':       'Log in to dashboard',
    'signup.success.cta.home':            'Back to home',

    // Round 21 (2026-05-10) — customer-facing strings previously hard-coded
    // in booking-status-client.tsx, event-feedback-form.tsx, and the shared
    // InitialVsCurrentDiff component. Audit at /booking/[id]?lang=fr showed
    // these rendering in English even when lang=fr was set.
    'booking.pill.event_completed':       'EVENT COMPLETED',
    'booking.pill.match_confirmed':       'MATCH CONFIRMED',
    'booking.section.venue_contact':      'VENUE CONTACT',
    'booking.section.add_to_calendar':    'ADD TO CALENDAR',
    'booking.booked_for':                 'Booked for {{datetime}} · {{duration}}',
    'booking.calendar.google':            'Google Calendar',
    'booking.calendar.outlook':           'Outlook',
    'booking.calendar.download_ics':      'Download .ics',
    'booking.carousel.prev':              'Previous photo',
    'booking.carousel.next':              'Next photo',
    'booking.intake.notes_placeholder':   'e.g. We’d prefer crab over lobster, and need 5 vegan plates...',

    'feedback.heading.readonly':          'Your feedback',
    'feedback.heading.editable':          'How was your event?',
    'feedback.placeholder':               'Tell us more (optional)…',
    'feedback.submit':                    'Submit Feedback',
    'feedback.update':                    'Update Feedback',
    'feedback.edit':                      'Edit feedback',
    'feedback.submitting':                'Submitting…',
    'feedback.submitted_prefix':          'Submitted {{time}}',
    'feedback.hint':                      'Your feedback goes directly to the venue’s events team.',
    'feedback.error.no_rating':           'Please pick a rating from 1 to 5 wine glasses.',
    'feedback.error.submit_failed':       'Failed to submit feedback. Try again.',
    'feedback.relative.just_now':         'just now',
    'feedback.relative.minutes_ago':      '{{count}} min ago',
    'feedback.relative.hours_ago':        '{{count}} hr ago',
    'feedback.glass_aria':                'Rate {{n}} of 5',

    'diff.what_changed':                  'What changed',
    'diff.party_size':                    'Party size',
    'diff.dates':                         'Dates',
    'diff.budget':                        'Budget',
    'diff.time':                          'Time',
    'diff.duration':                      'Duration',
    'diff.guests_singular':               '{{n}} guest',
    'diff.guests_plural':                 '{{n}} guests',
    'diff.guests_range':                  '{{min}}–{{max}} guests',
    'diff.hours_singular':                '{{n}} hr',
    'diff.hours_plural':                  '{{n}} hrs',

    // Round 21 Phase 1B (2026-05-10) — venue dashboard hard-coded strings.
    'dashboard.analytics.stat.booked':         'Booked',
    'dashboard.analytics.stat.completed':      'Completed',
    'dashboard.analytics.stat.lost':           'Lost',
    'dashboard.analytics.lost_reasons.title':       'Why leads were lost',
    'dashboard.analytics.lost_reasons.description': 'Includes leads that didn’t convert (no follow-up, went elsewhere) and bookings cancelled after confirmation.',
    'dashboard.analytics.lost_reasons.show_less':   'Show less',
    'dashboard.analytics.detail.event_summary':     'Event summary',
    'dashboard.analytics.detail.event_notes':       'Event notes',
    'dashboard.analytics.detail.min_spend':         'Min spend',
    'dashboard.analytics.detail.actual_spend':      'Actual spend',
    'dashboard.analytics.detail.rental_fee':        'Deposit',
    'dashboard.analytics.detail.confirmed_for':     'Confirmed for {{date}} · {{time}}{{duration}}',
    'dashboard.analytics.lost_reasons.show_more':   'Show all {{total}} reasons (+{{overflow}} more)',

    // R24 (2026-05-11) — analytics customer-list i18n: badges, status pills,
    // cancel-reason labels. Separate namespace from dashboard.proposals.status.*
    // because the semantics differ (proposal-workflow state vs historical
    // booking outcome).
    'dashboard.analytics.customer_list.heading_count':                'Customers ({{count}})',
    'dashboard.analytics.customer_list.history.heading_count':        'Booking history ({{count}})',
    'dashboard.analytics.customer_list.badge.re_engaged':             'RE-ENGAGED',
    'dashboard.analytics.customer_list.badge.repeat_count':           'REPEAT · {{count}}',
    'dashboard.analytics.customer_list.badge.corporate_count':        'CORPORATE × {{count}}',
    'dashboard.analytics.customer_list.badge.personal_count':         'PERSONAL × {{count}}',
    'dashboard.analytics.customer_list.status.completed':             'Completed',
    'dashboard.analytics.customer_list.status.booked':                'Booked',
    'dashboard.analytics.customer_list.status.lost':                  'Lost',
    'dashboard.analytics.customer_list.status.lost_with_reason':      'Lost · {{reason}}',
    'dashboard.analytics.customer_list.status.rejected':              'Not selected',
    'dashboard.analytics.customer_list.status.expired':               'Expired',
    'dashboard.analytics.customer_list.status.withdrawn':             'Withdrawn',
    'dashboard.analytics.customer_list.cancel_reason.unspecified':    'Unspecified',
    'dashboard.analytics.customer_list.cancel_reason.no_response':    "Didn't follow up",
    'dashboard.analytics.customer_list.cancel_reason.chose_elsewhere':'Went elsewhere',
    'dashboard.analytics.customer_list.cancel_reason.price_disagreement':'Price disagreement',
    'dashboard.analytics.customer_list.cancel_reason.date_unavailable':'Date unavailable',
    'dashboard.analytics.customer_list.cancel_reason.event_postponed':'Event postponed',
    'dashboard.analytics.customer_list.cancel_reason.event_cancelled':'Customer cancelled',
    'dashboard.analytics.customer_list.cancel_reason.other':          'Other',
    'dashboard.analytics.customer_list.last_corporate.label':         'Last corporate booking',
    'dashboard.analytics.customer_list.toggle.history':               'History',
    'dashboard.analytics.customer_list.toggle.hide':                  'Hide',

    'dashboard.feedback.title':            'Customer Feedback',
    'dashboard.feedback.review_singular':  'review',
    'dashboard.feedback.review_plural':    'reviews',
    'dashboard.feedback.highlights':       'Highlights (5★)',
    'dashboard.feedback.concerns':         'Could improve (1-2★)',
    'dashboard.feedback.view_all':         'View all {{total}} reviews',
    'dashboard.feedback.view_less':        'Hide',

    'dashboard.proposals.section.in_negotiation': 'In Negotiation',

    'dashboard.settings.transfer.email_prompt': 'Please enter an email address',
    'dashboard.settings.transfer.phone_prompt': 'Please enter a phone number',
    'dashboard.settings.transfer.success':      'Venue access transferred! The new manager will receive a login link via {{via}}.',
    'dashboard.settings.transfer.error':        'Failed to transfer venue access',
    'dashboard.settings.cleanup.success':       'Cleaned up {{proposals}} proposals and {{bookings}} booking requests',
    'dashboard.settings.cleanup.error':         'Failed to clean up',

    'dashboard.bookings.event_completed':       'Event marked as complete',
    'dashboard.bookings.complete_error':        'Failed to mark complete',

    'dashboard.error.load_venue':               'Failed to load venue',
    'dashboard.error.login_retry':              'Please try logging in again.',

    // Round 21 Phase 5A (2026-05-10) — venue language preference dropdown.
    'settings.venue_info.language':                  'Notification Language',
    'settings.venue_info.language.hint':             'Language used for your dashboard notifications (OTP, booking alerts, magic-link emails). Defaults to auto-detect from your phone area code.',
    'settings.venue_info.language.auto':             'Auto (based on phone)',
    'settings.venue_info.language.en':               'English',
    'settings.venue_info.language.fr':               'Français',
    'settings.venue_info.language.es':               'Español',
    'settings.venue_info.language.it':               'Italiano',

    // Round 21 Phase 1C (2026-05-10) — modal + heavy-form strings.
    // EditBookingModal
    'edit_booking.title':              'Edit booking details',
    'edit_booking.close':              'Close',
    'edit_booking.guest_count':        'Guest count',
    'edit_booking.agreed_date':        'Agreed event date',
    'edit_booking.start_time':         'Start time',
    'edit_booking.duration_hrs':       'Duration (hrs)',
    'edit_booking.contact_locked':     'Customer name, phone, and email are locked. To update customer contact info, edit in {{crmName}}.',
    'edit_booking.notes':              'Event notes',
    'edit_booking.notes_placeholder':  'Customer feedback, follow-ups, what to remember next time…',
    'edit_booking.saving':             'Saving…',
    'edit_booking.save':               'Save changes',
    'edit_booking.cancel':             'Cancel',
    'edit_booking.toast.saved':        'Booking updated',
    'edit_booking.toast.error':        'Failed to save changes',

    // ProposalSubmitModal (photo section only — rest already i18n'd)
    'proposal_submit.fulfilling_from':       'Fulfilling from',
    'proposal_submit.location_placeholder':  'Choose a location…',
    'proposal_submit.customer_asked_for':    'Customer asked for:',
    'proposal_submit.photos.heading':        'Proposal Photos',
    'proposal_submit.photos.label':          'Proposal Photo',
    'proposal_submit.photos.default_hero':   'Default hero image',
    'proposal_submit.photos.empty_settings_hint': 'Add catering photos in Settings to pick specific images for each proposal.',
    'proposal_submit.photos.none_selected':  'None selected — the venue hero image will be shown on the proposal. Click photos to include them instead.',
    'proposal_submit.photos.deselect_hint':  'Click a photo to deselect. Selected photos will appear on the proposal card the consumer sees.',

    // CateringConfigForm
    'catering_config.heading':             'Catering Configuration',
    'catering_config.cuisine.heading':     'Cuisine Types',
    'catering_config.cuisine.placeholder': 'Type cuisine and press Enter',
    'catering_config.min_order':           'Minimum Order',
    'catering_config.service_area':        'Service Area',
    'catering_config.service_area.placeholder': 'e.g., Greater Toronto Area',
    'catering_config.delivery_radius':     'Delivery Radius:',
    'catering_config.km':                  '{{km}} km',
    'catering_config.radius_min':          '1 km',
    'catering_config.radius_max':          '100 km',
    'catering_config.show_service_area':   'Show service area on catering profile',
    'catering_config.show_service_area.hint': 'Consumers can type their address to check if you deliver to them',
    'catering_config.packages.heading':    'Menu Packages',
    'catering_config.packages.add':        '+ Add Package',
    'catering_config.packages.name_placeholder': 'Package name',
    'catering_config.packages.desc_placeholder': 'Description (items included, dietary info, etc.)',
    'catering_config.packages.per_person': '$/person',
    'catering_config.packages.empty':      'No menu packages yet. Add one to showcase your catering options.',
    'catering_config.photos.heading':      'Food Photos',
    'catering_config.photos.caption_placeholder': 'Add a caption...',
    'catering_config.photos.uploading':    'Uploading...',
    'catering_config.photos.add_button':   'Add Food Photos',
    'catering_config.photos.hint':         'Showcase your dishes. Photos display as a slideshow on your catering profile.',
    'catering_config.toast.max_photos':    'Maximum 20 photos',
    'catering_config.toast.file_type_invalid': '{{filename}}: only JPEG, PNG, WebP allowed',
    'catering_config.toast.upload_success': '{{count}} photo(s) uploaded',
    'catering_config.toast.saved':         'Catering settings saved',
    'catering_config.toast.upload_failed': 'Upload failed',
    'catering_config.toast.max_menus':     'Maximum 10 menus',
    'catering_config.toast.menu_uploaded': 'Menu uploaded',
    'catering_config.toast.save_failed':   'Failed to save',

    // /admin/groups/new — 8-step hospitality-group setup wizard (platform_admin only)
    'admin.groups.new.title':              'Create a new group',
    'admin.groups.new.subtitle':           'Set up a hospitality group, parent venue, sisters, and staff.',
    'admin.groups.new.progress':           'Step {{current}} of {{total}}',
    'admin.groups.new.back':               'Back',
    'admin.groups.new.continue':           'Continue',
    'admin.groups.new.submit':             'Open staging PR',
    'admin.groups.new.draft.creating':     'Creating draft…',
    'admin.groups.new.draft.saving':       'Saving…',
    'admin.groups.new.draft.error':        'Could not save your progress. Retry?',
    'admin.groups.new.draft.load_error':   'Could not load draft.',
    'admin.groups.new.required':           'Required',
    'admin.groups.new.step1.title':        'Pick a setup archetype',
    'admin.groups.new.step1.intro':        'One venue or many? This drives the rest of the wizard.',
    'admin.groups.new.step1.standalone.label':       'Standalone venue',
    'admin.groups.new.step1.standalone.description': 'One venue, one phone, one widget. Use /admin/venues/new instead.',
    'admin.groups.new.step1.hospitality.label':      'Hospitality group',
    'admin.groups.new.step1.hospitality.description':'Parent venue plus sister venues, shared events team. (Recommended)',
    'admin.groups.new.step1.franchise.label':        'Franchise',
    'admin.groups.new.step1.franchise.description':  'One brand, multiple locations, per-location managers. Use /admin/venues/new (franchise mode).',
    'admin.groups.new.step1.redirect_notice':        'This archetype is handled by /admin/venues/new. We will redirect you there.',
    'admin.groups.new.step2.title':        'Group identity',
    'admin.groups.new.step2.name.label':   'Group name',
    'admin.groups.new.step2.name.help':    'The legal or trading name customers will see in their dashboard breadcrumb.',
    'admin.groups.new.step2.slug.label':   'Slug',
    'admin.groups.new.step2.slug.help':    'URL-safe identifier. Auto-derived from the name; edit if needed.',
    'admin.groups.new.step2.parent.label': 'Parent venue',
    'admin.groups.new.step2.parent.yes':   'Yes — the group has a parent venue that takes its own bookings',
    'admin.groups.new.step2.parent.no':    'No — the group is an organisational container only',
    'admin.groups.new.step2.error.slug':   'Slug must be lowercase letters, digits, or hyphens.',
    'admin.groups.new.step2.error.name':   'Name is required.',
    'admin.groups.new.step3.title':        'Parent venue',
    'admin.groups.new.step3.intro':        'Link to an existing venue or create a new one in a follow-up step.',
    'admin.groups.new.step3.link.label':   'Existing venue',
    'admin.groups.new.step3.link.placeholder': 'Choose an existing venue…',
    'admin.groups.new.step3.new.label':    'Create a new parent venue after this wizard',
    'admin.groups.new.step3.skip':         'Skipping — group has no parent venue.',
    'admin.groups.new.step4.title':        'Sister venues',
    'admin.groups.new.step4.intro':        'Add every venue that belongs in this group. Each one auto-inherits the group ID.',
    'admin.groups.new.step4.add':          'Add sister venue',
    'admin.groups.new.step4.placeholder':  'Sister venue slug (e.g. akiro)',
    'admin.groups.new.step4.existing.label':  'Link to existing venue',
    'admin.groups.new.step4.new.label':    'Placeholder — create later',
    'admin.groups.new.step4.empty':        'No sisters added yet.',
    'admin.groups.new.step4.error.duplicate': 'Duplicate slug.',
    'admin.groups.new.step4.error.invalid':   'Slug must be lowercase letters, digits, or hyphens.',
    'admin.groups.new.step4.remove.aria':  'Remove sister venue',
    'admin.groups.new.step5.title':        'Staff',
    'admin.groups.new.step5.intro':        'Who manages this group? Add at least one events manager.',
    'admin.groups.new.step5.add_group':    'Add events manager',
    'admin.groups.new.step5.add_venue':    'Add venue manager',
    'admin.groups.new.step5.name.label':   'Name',
    'admin.groups.new.step5.phone.label':  'Phone (E.164)',
    'admin.groups.new.step5.phone.placeholder': '+15551234567',
    'admin.groups.new.step5.email.label':  'Email',
    'admin.groups.new.step5.venue.label':  'Venue',
    'admin.groups.new.step5.channel.label':'Notification channel',
    'admin.groups.new.step5.channel.sms':  'SMS',
    'admin.groups.new.step5.channel.email':'Email',
    'admin.groups.new.step5.channel.both': 'Both',
    'admin.groups.new.step5.empty':        'No staff added yet.',
    'admin.groups.new.step5.remove.aria':  'Remove staff entry',
    'admin.groups.new.step5.role.group_manager':    'Events manager (group scope)',
    'admin.groups.new.step5.role.venue_manager':    'Venue manager',
    'admin.groups.new.step5.role.location_manager': 'Location manager',
    'admin.groups.new.step5.error.required':        'At least one events manager is required.',
    'admin.groups.new.step5.error.phone':           'Phone must be in E.164 format (+1...).',
    'admin.groups.new.step5.error.duplicate_phone': 'This phone number is already on the staff list.',
    'admin.groups.new.step6.title':        'Review',
    'admin.groups.new.step6.intro':        'Confirm the assembled manifest below. Navigate back to fix anything.',
    'admin.groups.new.step6.preview':      'Generated manifest',
    'admin.groups.new.step6.validation.title': 'Manifest validation',
    'admin.groups.new.step6.validation.ok':    'Manifest is valid.',
    'admin.groups.new.step6.validation.errors':'Fix the following before submitting:',
    'admin.groups.new.step6.submit_hint':   'Submitting opens a GitHub PR into the staging branch. Merging it triggers the staging deploy.',
    'admin.groups.new.step7.title':        'Staging PR',
    'admin.groups.new.step7.intro':        'The wizard opened this PR. Review the diff, merge to staging, watch CI.',
    'admin.groups.new.step7.pr_url.label': 'Pull request',
    'admin.groups.new.step7.pr_url.open':  'Open on GitHub',
    'admin.groups.new.step7.refresh':      'Refresh PR status',
    'admin.groups.new.step7.checking':     'Checking…',
    'admin.groups.new.step7.error':        'Submission failed:',
    'admin.groups.new.step8.title':        'Production',
    'admin.groups.new.step8.intro':        'Once the staging PR is merged and staging passes verification, run /go-live to promote to production.',
    'admin.groups.new.step8.gate':         'The wizard intentionally does not auto-promote to production — staging soak first.',
    'admin.groups.new.step8.done':         'Done',

    // /admin/groups/[id] — group detail page (members / staff / audit / manifest tabs)
    'admin.groups.detail.title':           '{{name}}',
    'admin.groups.detail.subtitle':        'Group slug: {{slug}}',
    'admin.groups.detail.archetype':       'Archetype',
    'admin.groups.detail.parent':          'Parent venue',
    'admin.groups.detail.no_parent':       'No parent venue',
    'admin.groups.detail.loading':         'Loading group…',
    'admin.groups.detail.error':           'Could not load group',
    'admin.groups.detail.back':            '← All groups',
    'admin.groups.detail.tab.members':     'Members',
    'admin.groups.detail.tab.staff':       'Staff',
    'admin.groups.detail.tab.audit':       'Audit',
    'admin.groups.detail.tab.manifest':    'Manifest',
    'admin.groups.detail.members.empty':   'No member venues yet.',
    'admin.groups.detail.members.col.name':   'Name',
    'admin.groups.detail.members.col.slug':   'Slug',
    'admin.groups.detail.members.col.status': 'Status',
    'admin.groups.detail.members.manage':  'Manage',
    'admin.groups.detail.staff.empty':     'No staff scoped to this group yet.',
    'admin.groups.detail.staff.col.name':    'Name',
    'admin.groups.detail.staff.col.phone':   'Phone',
    'admin.groups.detail.staff.col.role':    'Role',
    'admin.groups.detail.staff.col.channel': 'Notify via',
    'admin.groups.detail.staff.edit':      'Edit',
    'admin.groups.detail.audit.empty':     'No audit events for this group yet.',
    'admin.groups.detail.manifest.empty':  'No manifest preview available.',
    'admin.groups.detail.unlink_cta':      'Unlink venue from group',
    'admin.groups.detail.unlink.confirm.title': 'Unlink this venue?',
    'admin.groups.detail.unlink.confirm.body':  'Unlinking removes the venue from this group. The venue keeps its data; you can re-link it later.',
    'admin.groups.detail.unlink.confirm.cta':   'Unlink',
    'admin.groups.detail.unlink.cancel':   'Cancel',

    // /admin/staff — list page
    'admin.staff.list.add_button':         '+ Add staff',
    'admin.staff.list.row.tooltip':        'Open staff member',

    // /admin/staff/new — create staff form (PR #91 v3)
    'admin.staff.new.title':               'Add new staff member',
    'admin.staff.new.subtitle':            'Create a new admin row. You can fine-tune notification settings on the detail page after creation.',
    'admin.staff.new.field.name.label':    'Name',
    'admin.staff.new.field.name.placeholder': 'Display name',
    'admin.staff.new.field.phone.label':   'Login phone (E.164)',
    'admin.staff.new.field.phone.placeholder': '+15551234567',
    'admin.staff.new.field.phone.help':    'Must be E.164 — country code + digits, no spaces.',
    'admin.staff.new.field.notification_email.label': 'Notification email',
    'admin.staff.new.field.notification_email.placeholder': 'staff@example.com',
    'admin.staff.new.field.notification_channel.label': 'Notification channel',
    'admin.staff.new.channel.sms':         'SMS',
    'admin.staff.new.channel.email':       'Email',
    'admin.staff.new.channel.both':        'SMS + Email',
    'admin.staff.new.field.role.label':    'Role',
    'admin.staff.new.role.group_manager':  'Group manager',
    'admin.staff.new.role.venue_manager':  'Venue manager',
    'admin.staff.new.role.location_manager': 'Location manager',
    'admin.staff.new.role.help.group_manager':    'Sees every venue in the selected groups.',
    'admin.staff.new.role.help.venue_manager':    'Sees every booking for the selected venues.',
    'admin.staff.new.role.help.location_manager': 'Sees only bookings for the listed location pairs.',
    'admin.staff.new.field.scope.groups.label':   'Managed groups',
    'admin.staff.new.field.scope.groups.help':    'Pick one or more groups. The manager sees every venue inside them.',
    'admin.staff.new.field.scope.venues.label':   'Managed venues',
    'admin.staff.new.field.scope.venues.help':    'Pick one or more venues. The manager sees all bookings for them.',
    'admin.staff.new.field.scope.locations.label': 'Managed locations',
    'admin.staff.new.field.scope.locations.help':  'Pick a venue, then a location, and add the pair. Repeat for each location the manager handles.',
    'admin.staff.new.picker.search.groups':        'Search groups…',
    'admin.staff.new.picker.search.venues':        'Search venues…',
    'admin.staff.new.picker.loading.groups':       'Loading groups…',
    'admin.staff.new.picker.loading.venues':       'Loading venues…',
    'admin.staff.new.picker.error.groups':         "Couldn't load groups. Refresh to retry.",
    'admin.staff.new.picker.error.venues':         "Couldn't load venues. Refresh to retry.",
    'admin.staff.new.picker.empty.groups':         'No groups found.',
    'admin.staff.new.picker.empty.venues':         'No venues found.',
    'admin.staff.new.picker.empty.locations':      'None of the available venues have configured locations yet.',
    'admin.staff.new.picker.location.venue.label': 'Venue',
    'admin.staff.new.picker.location.venue.placeholder': 'Select a venue…',
    'admin.staff.new.picker.location.slug.label':  'Location',
    'admin.staff.new.picker.location.slug.placeholder': 'Select a location…',
    'admin.staff.new.picker.location.add':         '+ Add location',
    'admin.staff.new.picker.location.added':       'Added locations',
    'admin.staff.new.picker.location.remove.aria': 'Remove location',
    'admin.staff.new.picker.selected.count':       '{{count}} selected',
    'admin.staff.new.submit':              'Create staff',
    'admin.staff.new.submitting':          'Creating…',
    'admin.staff.new.cancel':              'Cancel',
    'admin.staff.new.error.generic':       'Could not create staff — please check the fields and try again.',
    'admin.staff.new.error.conflict':      'A staff member with that phone already exists.',
    'admin.staff.new.error.scope_required.group_manager':    'Pick at least one group for a group manager.',
    'admin.staff.new.error.scope_required.venue_manager':    'Pick at least one venue for a venue manager.',
    'admin.staff.new.error.scope_required.location_manager': 'Add at least one {venueId, locationSlug} pair.',
    'admin.staff.new.error.invalid_phone':  'Phone must be E.164 format (e.g. +15551234567).',
    'admin.staff.new.error.invalid_locations_json': 'Managed locations must be valid JSON — an array of objects with venueId and locationSlug.',
    'admin.staff.new.success':              'Staff created.',

    // /admin/staff/[id] — staff detail page (role + scope + actions)
    'admin.staff.detail.title':            '{{name}}',
    'admin.staff.detail.subtitle':         'Staff member detail',
    'admin.staff.detail.loading':          'Loading staff…',
    'admin.staff.detail.error':            'Could not load staff',
    'admin.staff.detail.back':             '← All staff',
    'admin.staff.detail.section.identity': 'Identity',
    'admin.staff.detail.section.access':   'Role and scope',
    'admin.staff.detail.section.notifications': 'Notifications',
    'admin.staff.detail.section.login_history': 'Login history',
    'admin.staff.detail.section.audit':    'Recent activity',
    'admin.staff.detail.section.actions':  'Actions',
    'admin.staff.detail.field.name':       'Name',
    'admin.staff.detail.field.phone':      'Phone',
    'admin.staff.detail.field.email':      'Email',
    'admin.staff.detail.field.role':       'Role',
    'admin.staff.detail.field.disabled':   'Disabled',
    'admin.staff.detail.field.scope.venues': 'Managed venues',
    'admin.staff.detail.field.scope.groups': 'Managed groups',
    'admin.staff.detail.field.scope.locations': 'Managed locations',
    'admin.staff.detail.field.scope.empty':  'No scope assigned',
    'admin.staff.detail.field.notify_phone': 'Notification phone',
    'admin.staff.detail.field.notify_email': 'Notification email',
    'admin.staff.detail.field.notify_channel': 'Notification channel',
    'admin.staff.detail.field.last_login':  'Last login',
    'admin.staff.detail.field.created':     'Created',
    'admin.staff.detail.login_history.empty':   'Login history not yet tracked.',
    'admin.staff.detail.audit.empty':       'No recent activity.',
    'admin.staff.detail.role.platform_admin':  'Platform admin',
    'admin.staff.detail.role.group_manager':   'Group manager',
    'admin.staff.detail.role.venue_manager':   'Venue manager',
    'admin.staff.detail.role.location_manager':'Location manager',
    'admin.staff.detail.channel.sms':       'SMS',
    'admin.staff.detail.channel.email':     'Email',
    'admin.staff.detail.channel.both':      'SMS + Email',
    'admin.staff.detail.save':              'Save changes',
    'admin.staff.detail.saving':            'Saving…',
    'admin.staff.detail.save_ok':           'Changes saved',
    'admin.staff.detail.save_fail':         'Could not save changes',
    'admin.staff.detail.disable':           'Disable staff',
    'admin.staff.detail.disable.confirm':   'Disable this staff member? They will lose all access until re-enabled.',
    'admin.staff.detail.enable':            'Enable staff',
    'admin.staff.detail.rotate_phone':      'Rotate phone',
    'admin.staff.detail.rotate_phone.confirm': 'Rotate this staff member’s login phone? They will need to log in with the new number.',
    'admin.staff.detail.rotate_phone.prompt':  'New phone number (E.164 format, e.g. +15551234567):',
    'admin.staff.detail.force_reauth':       'Force re-authentication',
    'admin.staff.detail.force_reauth.confirm': 'Force this staff member to log in again? Their current sessions will be invalidated.',
    'admin.staff.detail.force_reauth.ok':    'Sessions invalidated',
    'admin.staff.detail.delete':             'Delete staff',
    'admin.staff.detail.delete.confirm':     'Delete this staff member permanently? This cannot be undone. Type the staff member’s phone number to confirm.',

    // /admin/audit — filters + CSV export + live tail (extension of existing list page)
    'admin.audit.filter.title':            'Filters',
    'admin.audit.filter.actor':            'Actor',
    'admin.audit.filter.actor.placeholder':'Actor id or role',
    'admin.audit.filter.action':           'Action',
    'admin.audit.filter.action.placeholder':'e.g. submit_proposal',
    'admin.audit.filter.target_type':      'Target type',
    'admin.audit.filter.target_type.all':  'All target types',
    'admin.audit.filter.date_from':        'From',
    'admin.audit.filter.date_to':          'To',
    'admin.audit.filter.apply':            'Apply',
    'admin.audit.filter.reset':            'Reset',
    'admin.audit.filter.open':             'Filter',
    'admin.audit.csv_export':              'Export CSV',
    'admin.audit.csv_export.empty':        'Nothing to export.',
    'admin.audit.live_toggle':             'Live',
    'admin.audit.live_toggle.aria':        'Toggle real-time tail mode',
    'admin.audit.row.expand':              'View details',
    'admin.audit.row.collapse':            'Close',
    'admin.audit.row.payload':             'Payload',
    'admin.audit.row.impersonated':        'Impersonated as',
  },
  fr: {
    'widget.lang.toggle.aria':     'Changer de langue',
    'widget.lang.en':              'English',
    'widget.lang.fr':              'Français',
    'widget.idle.title.default':   'Planifier un événement chez {{venueName}}',
    'widget.group.other.label':    'Envie de découvrir nos autres adresses?',
    'widget.group.other.aria':     'Autres adresses',
    'widget.idle.description':     'Appelez ou clavardez avec notre équipe.',
    'widget.idle.call':            'Appeler',
    'widget.idle.chat':            'Clavarder',
    'widget.callMode.speakHint':   'Vous pouvez écrire vos coordonnées — ou simplement les dire à voix haute.',
    'widget.idle.loudroom':        'Endroit bruyant — touchez pour parler',
    'widget.idle.loudroom.hint':   'Pour bars et restos — le micro démarre coupé, touchez pour parler.',
    'widget.idle.mic_blocked':     'Micro bloqué — vous pouvez tout de même clavarder par texte ci-dessous.',

    'widget.active.mic_muted':     'Coupé',
    'widget.active.mic_unmuted':   'À l\u2019écoute',
    'widget.active.agent.speaking':'L\u2019hôtesse parle',
    'widget.active.agent.thinking':'Réflexion…',
    'widget.active.type_hint':     'Écrivez votre message…',
    'widget.active.send':          'Envoyer',
    'widget.active.end':           'Terminer',
    'widget.active.mute':          'Couper le micro',
    'widget.active.unmute':        'Activer le micro',
    'widget.active.hold_to_talk':  'Maintenez pour parler',
    'widget.active.switch_to_chat':'Passer au clavardage',
    'widget.active.switch_to_voice':'Passer à la voix',
    'widget.active.no_audio':      'Pas de son? Touchez pour passer au clavardage.',
    'widget.active.reconnect':     'Le son a décroché — touchez pour reconnecter.',

    'widget.ended.thanks':         'Merci — l\u2019équipe {{venueName}} va vous répondre bientôt.',
    'widget.ended.restart':        'Poser une autre question',
    'widget.ended.view_booking':   'Voir votre réservation',
    'widget.ended.status.sent':    'Envoyé à l\u2019équipe — nous vous répondrons bientôt',
    'widget.ended.status.cancelled':'Conversation terminée',

    // booking.* — page réservation (/booking/[id])
    'booking.title':               'Votre réservation avec {{venueName}}',
    'booking.headline.default':    'Votre réservation',
    'booking.headline.greeting':   'Bonjour {{name}}',
    'booking.headline.anon':       'Votre demande de réservation',
    'booking.headline.direct_event':    'Réservation directe avec',
    'booking.headline.direct_catering': 'Traiteur direct avec',
    'booking.fallback.venue':      'l\u2019endroit',
    'booking.event_guide.cta':     'Voir le guide événements de {{location}}',
    'booking.pricing.caveat':      'L\u2019équipe événementielle confirmera la tarification finale par courriel.',
    'booking.venue_reply.heading': 'Réponse de {{venue}}',
    'booking.venue_reply.via.email': 'Envoyé par courriel',
    'booking.venue_reply.via.sms':   'Envoyé par texto',

    // pricing.* — partagé entre le dashboard venue et la carte d'attente de réponse.
    'pricing.title':                  'Tarification par date (devis automatique)',
    'pricing.col.date':               'Date',
    'pricing.col.time_block':         'Plage horaire',
    'pricing.col.min_spend':          'Dépense min.',
    'pricing.col.venue_rental_fee':   'Frais de location',
    'pricing.closed':                 'Salle fermée — date alternative requise',
    'pricing.rates_pending':          'Tarifs à confirmer — équipe événementielle',
    'pricing.holiday':                'férié',
    'pricing.closest_match':          'plage horaire la plus proche',
    'pricing.holiday_surcharge':      'supplément jour férié inclus',
    // Phase 14.E mirror: empty in FR too.
    'pricing.dashboard_caveat':       "",

    // dashboard.proposals.lead.* — UX boîte de leads pour les venues en shadow.
    'dashboard.proposals.lead.call':                'Appeler le client',
    'dashboard.proposals.lead.email':               'Envoyer un courriel',
    'dashboard.proposals.lead.send_email':          'Envoyer une réponse par courriel',
    'dashboard.proposals.lead.send_text':           'Envoyer une réponse par texto',
    'dashboard.proposals.lead.no_email':            "Le client n'a pas partagé d'adresse courriel.",
    'dashboard.proposals.lead.no_phone':            "Le client n'a pas partagé de numéro de téléphone.",
    'dashboard.proposals.lead.no_contact':          'Aucun canal de contact — soumettre une proposition formelle à la place.',
    'dashboard.proposals.lead.submit_formal_link':  'Soumettre une proposition formelle',

    // Lead-status shortcut FR
    'dashboard.proposals.lead.status_heading':         'Statut du lead',
    'dashboard.proposals.lead.status.mark_booked':     'Marquer comme réservé',
    'dashboard.proposals.lead.status.mark_completed':  'Marquer comme complété',
    'dashboard.proposals.lead.status.confirm_booked':    'Confirmer : lead réservé',
    'dashboard.proposals.lead.status.confirm_completed': 'Confirmer : événement complété',
    'dashboard.proposals.lead.status.notes_placeholder': 'Notes facultatives — réf. de réservation, détails, suivis…',
    'dashboard.proposals.lead.status.event_datetime_label': 'Date et heure d’événement convenues (facultatif — choisir parmi les dates du client)',
    'dashboard.proposals.lead.status.actual_spend_label':  'Dépense réelle (CAD, facultatif)',
    'dashboard.proposals.lead.status.hrs':                'h',
    'dashboard.proposals.lead.status.confirm':           'Confirmer',
    'dashboard.proposals.lead.status.cancel':            'Annuler',
    'dashboard.proposals.lead.status.saving':            'Enregistrement…',
    'dashboard.proposals.lead.status.toast_booked':      'Lead marqué comme réservé',
    'dashboard.proposals.lead.status.toast_completed':   'Lead marqué comme complété',
    'dashboard.proposals.lead.status.toast_lost':        'Lead marqué comme perdu',
    'dashboard.proposals.lead.status.toast_lost_failed': 'Impossible de marquer comme perdu — {{error}}',
    'dashboard.proposals.lead.status.mark_lost':         'Marquer comme perdu',
    'dashboard.proposals.lead.status.mark_lost_heading': 'Marquer comme perdu',
    'dashboard.proposals.lead.status.mark_lost_subtitle':'Notez pourquoi ce lead n’a pas converti. Ça aide l’équipe à s’améliorer.',
    'dashboard.proposals.lead.status.toast_update_failed': 'Échec de la mise à jour du statut',
    'dashboard.proposals.lead.status.toast_failed_log':  'Échec de l’enregistrement',
    'dashboard.proposals.lead.status.marked_booked':     'Réservé · actualisation',
    'dashboard.proposals.lead.status.marked_completed':  'Complété · actualisation',

    // quick_reply.* — le composeur en ligne qui s\u2019ouvre sous une carte shadow.
    'quick_reply.heading.with_name':  'Répondre à {{name}}',
    'quick_reply.heading.anon':       'Répondre au client',
    'quick_reply.close':              'Fermer le composeur',
    'quick_reply.channel.email':      'Courriel',
    'quick_reply.channel.sms':        'Texto',
    'quick_reply.body_placeholder':   'Bonjour — merci pour votre demande. Quelques précisions avant de finaliser...',
    'quick_reply.send.email':         'Envoyer le courriel',
    'quick_reply.send.sms':           'Envoyer le texto',
    'quick_reply.sending':            'Envoi…',
    'quick_reply.toast.email_sent':   'Courriel envoyé',
    'quick_reply.toast.sms_sent':     'Texto envoyé',
    'quick_reply.sent.email':         'Courriel',
    'quick_reply.sent.sms':           'Texto',
    'quick_reply.sent.just_now':      'à l\u2019instant',
    'quick_reply.sent.minutes_ago':   'il y a {{count}} min',
    'quick_reply.sent.hours_ago':     'il y a {{count}} h',

    'booking.step.intake_in_progress': 'Collecte des informations',
    'booking.step.intake_complete':    'Informations complètes',
    'booking.step.awaiting_response':  'En attente de réponse',
    'booking.step.finding_venues':     'Recherche d\u2019endroits',
    'booking.step.proposals_received': 'Propositions reçues',
    'booking.step.consumer_selecting': 'Choisissez votre endroit',
    'booking.step.consumer_confirmed': 'Confirmé — paiement en attente',
    'booking.step.booked':             'Jumelé!',

    'booking.status.pending':      'Nous avons reçu votre demande.',
    'booking.status.reviewing':    'Nous vous jumelons avec des endroits\u2026',
    'booking.status.proposals':    'Vous avez des propositions à consulter.',
    'booking.status.selected':     'Réservation confirmée.',
    'booking.status.completed':    'Événement terminé. On espère que ça s\u2019est bien passé!',

    'booking.loading':             'Chargement de votre réservation\u2026',
    'booking.error.not_found':     'Nous ne trouvons pas cette réservation. Vérifiez votre lien et réessayez.',
    'booking.error.load_failed':   'Impossible de charger les détails de la réservation : {{message}}',
    'booking.not_found':           'Réservation introuvable.',
    'booking.preparing.title':     'Préparation de votre réservation',
    'booking.preparing.line1':     'Merci d\u2019avoir pris contact — nous assemblons les détails.',
    'booking.preparing.line2':     'Cette page se mettra à jour toute seule.',

    'booking.details.header.event':    'Détails de l\u2019événement',
    'booking.details.header.catering': 'Détails du traiteur',
    'booking.details.audience':        'Type',
    'booking.details.audience.company':  'Événement d\u2019entreprise',
    'booking.details.audience.personal': 'Événement personnel',
    'booking.details.date':            'Date',
    'booking.details.date.tbd':        'À déterminer',
    'booking.details.time':            'Heure',
    'booking.details.guests':          'Invités',
    'booking.details.duration':        'Durée',
    'booking.details.duration_hours':  '{{hours}} heure',
    'booking.details.duration_hours.plural': '{{hours}} heures',
    'booking.details.food':            'Repas',
    'booking.details.bar':             'Bar',
    'booking.details.music':           'Musique',
    'booking.details.budget':          'Budget',
    'booking.details.booking_type':    'Type de réservation',
    'booking.details.package':         'Forfait',
    'booking.details.email':           'Courriel',
    'booking.details.phone':           'Téléphone',
    'booking.details.company':         'Entreprise',
    'booking.details.delivery_address':'Adresse de livraison',
    'booking.details.service_type':    'Type de service',
    'booking.details.event_vision':    'Votre vision',
    'booking.details.additional_notes':'Notes additionnelles',
    'booking.details.preferences':     'Vos préférences',
    'booking.details.dietary':         'Restrictions alimentaires',
    'booking.details.accessibility':   'Accessibilité',
    'booking.details.booking_details': 'Détails de la réservation',
    'booking.details.event_type':      'Type d\u2019événement',
    'booking.details.preferred_dates': 'Dates préférées',

    'booking.edit.event_type.placeholder':      'ex. fête d\u2019anniversaire',
    'booking.edit.min_guests':                  'Min. invités',
    'booking.edit.max_guests':                  'Max. invités',
    'booking.edit.arrival_time':                'Heure d\u2019arrivée',
    'booking.edit.arrival_time.placeholder':    'ex. 19h00',
    'booking.edit.duration':                    'Durée (heures)',
    'booking.edit.duration.placeholder':        'ex. 3',
    'booking.edit.email.placeholder':           'courriel@exemple.com',
    'booking.edit.company.placeholder':         'Nom de l\u2019entreprise ou « personnel »',
    'booking.edit.delivery.placeholder':        'Tapez l\u2019adresse de livraison\u2026',
    'booking.edit.select_placeholder':          'Sélectionner\u2026',
    'booking.edit.event_vision.placeholder':    'Décrivez votre vision de l\u2019événement\u2026',
    'booking.edit.notes.placeholder':           'Détails additionnels\u2026',
    'booking.edit.add_date':                    '+ Ajouter',
    'booking.edit.save':                        'Enregistrer',
    'booking.edit.cancel':                      'Annuler',
    'booking.edit.save_failed':                 'Impossible d\u2019enregistrer les modifications',

    'booking.food.food_required':    'Repas requis',
    'booking.food.drinks_only':      'Boissons seulement',
    'booking.food.food_and_drinks':  'Repas et boissons',
    'booking.bar.cash_bar_minimum_spend': 'Bar payant (dépense minimum)',
    'booking.bar.open_bar':           'Bar ouvert',
    'booking.bar.consumption_tab':    'Note de consommation',
    'booking.bar.other':              'Autre',
    'booking.music.spotify_playlist': 'Playlist Spotify',
    'booking.music.dj':               'DJ',
    'booking.music.none':             'Aucune préférence',
    'booking.service.pickup':         'Ramassage',
    'booking.service.delivery':       'Livraison',
    'booking.service.full_service':   'Service complet (avec personnel)',
    'booking.service.full_service.short': 'Service complet',

    'booking.proposal.heading':       'Propositions',
    'booking.proposal.review_heading':'En révision — l\u2019endroit examine vos nouveaux détails',
    'booking.proposal.caterer_prompt':'Questions ou préférences pour le traiteur?',
    'booking.proposal.capacity':      'Capacité',
    'booking.proposal.min_spend':     'Dépense minimum',
    'booking.proposal.min_spend_short':'DÉP. MIN.',
    // Round 13 B7 (2026-05-10): "Acompte" = down payment / deposit en français.
    'proposal.field.min_spend':       'Dépense minimum ($)',
    'proposal.field.deposit':         'Acompte ($)',
    'booking.proposal.per_person':    'PAR PERSONNE',
    'booking.proposal.per_person_est':'PAR PERSONNE (EST.)',
    'booking.proposal.fee':           'Frais de réservation',
    'booking.proposal.dates':         'Dates disponibles',
    'booking.proposal.notes':         'Notes',
    'booking.proposal.book':          'Réserver et payer',
    'booking.proposal.book_free':     'Confirmer la réservation',
    'booking.proposal.confirm_match': 'Confirmer et réserver',
    'booking.proposal.confirm_match.named': 'Confirmer et réserver — {{spaceName}}',
    'booking.proposal.confirm_action_required': 'Touchez ci-dessous pour confirmer officiellement votre réservation — elle n\u2019est pas encore confirmée.',
    'booking.proposal.select_date_instruction': 'Choisissez la date et l’heure qui vous conviennent le mieux, puis touchez Confirmer pour réserver.',
    'booking.proposal.complete_payment': 'Compléter le paiement',
    'booking.proposal.complete_payment.named': 'Compléter le paiement — {{fee}}',
    'booking.proposal.payment_prompt':'Vous aurez 24 heures pour compléter le paiement des frais de réservation de {{fee}}.',
    'booking.proposal.pending':       'En attente de la réponse de l\u2019endroit',
    'booking.proposal.space_options': '{{count}} options d\u2019espace',
    'booking.proposal.match_confirmed':'Jumelage confirmé!',
    'booking.proposal.view_venue':    'Voir le profil',
    'booking.proposal.other_pending': 'Une autre proposition est confirmée — complétez le paiement ou attendez qu\u2019elle expire',
    'booking.proposal.delivered_from':'Livraison depuis',
    'booking.proposal.pickup_at':     'Ramassage à',
    'booking.proposal.cta_catering_event':'L\u2019endroit vous contactera pour finaliser votre événement traiteur.',
    'booking.proposal.cta_catering_order':'L\u2019endroit vous contactera pour finaliser votre commande traiteur.',

    'booking.payment.confirm_failed':'Impossible de confirmer la réservation',
    'booking.payment.checkout_failed':'Impossible de démarrer le paiement',

    'booking.countdown.expired':      'Expiré',
    'booking.countdown.remaining':    '{{hours}}h {{minutes}}m restants pour compléter le paiement',

    'booking.cta.need_help':          'Besoin d\u2019aide? Répondez au courriel de confirmation.',

    'booking.success.heading':        'Vous êtes jumelé!',
    'booking.success.prompt':         'L\u2019endroit vous contactera pour finaliser votre réservation. Voici vos détails.',
    'booking.success.reference':      'N\u00B0 DE RÉSERVATION',
    'booking.success.venue':          'ENDROIT',
    'booking.success.event_type':     'TYPE D\u2019ÉVÉNEMENT',
    'booking.success.date':           'DATE',
    'booking.success.guests':         'INVITÉS',
    'booking.success.time':           'HEURE',
    'booking.success.payment':        'PAIEMENT',
    'booking.success.booking_fee':    'Frais de réservation',
    'booking.success.location':       'EMPLACEMENT',
    'booking.success.booking_type':   'TYPE DE RÉSERVATION',
    'booking.success.package':        'FORFAIT',
    'booking.success.min_spend':      'Dépense minimum (à l\u2019endroit)',
    'booking.success.fee_free':       'Gratuit',
    'booking.success.view_details':   'Voir les détails',
    'booking.success.print':          'Imprimer le reçu',
    'booking.success.confirming':     'Confirmation de votre réservation\u2026',
    'booking.success.just_a_moment':  'Quelques instants\u2026',

    'booking.error_page.title':       'Un problème est survenu',
    'booking.error_page.fallback':    'Nous n\u2019avons pas pu charger votre réservation. Veuillez réessayer.',
    'booking.error_page.retry':       'Réessayer',
    'booking.error_page.back':        'Retour à l\u2019accueil',

    'booking.not_found_page.title':   'Réservation introuvable',
    'booking.not_found_page.body':    'Nous n\u2019avons trouvé aucune réservation avec cet identifiant. Elle a peut-être été supprimée ou le lien est incorrect.',

    'booking.footer.tag':             'RÉSERVATION D\u2019ENDROIT PRIVÉ — TORONTO, CA',

    // Venue dashboard chrome (sidebar nav, mobile nav, header, footer).
    'dashboard.nav.proposals':        'Propositions',
    'dashboard.nav.bookings':         'Réservations',
    'dashboard.nav.inquiries':        'Demandes',
    'dashboard.nav.liveTransfers':    'Transferts en direct',
    'dashboard.nav.analytics':        'Analytique',
    'dashboard.nav.settings':         'Paramètres',
    'dashboard.status.active':        'Actif',
    'dashboard.status.paused':        'En pause',
    'dashboard.status.unknown':       'Inconnu',
    'dashboard.header.switch_venue':  'Changer d\u2019endroit',
    'dashboard.header.admin_badge':   'Admin',
    'dashboard.header.admin_link':    '\u2190 Admin',
    'dashboard.header.signout':       'Se déconnecter',
    'dashboard.footer.terms':         'Conditions',
    'dashboard.footer.privacy':       'Confidentialité',
    'dashboard.footer.copyright':     '\u00A9 2026 BookOne.Party',

    // Common dashboard strings reused across pages.
    'dashboard.common.loading':       'Chargement\u2026',

    // Bookings page.
    'dashboard.bookings.subtitle':    '{{confirmed}} confirmée(s), {{completed}} terminée(s)',
    'dashboard.bookings.empty.title': 'Aucune réservation',
    'dashboard.bookings.empty.body':  'Lorsqu\u2019un client choisit votre proposition, sa réservation apparaîtra ici avec ses coordonnées complètes.',
    'dashboard.bookings.section.confirmed': 'Confirmées ({{count}})',
    'dashboard.bookings.section.completed': 'Terminées ({{count}})',

    // Proposals page.
    'dashboard.proposals.subtitle.zero':  'Aucune demande en attente',
    'dashboard.proposals.subtitle.one':   '1 demande en attente',
    'dashboard.proposals.subtitle.many':  '{{count}} demandes en attente',
    'dashboard.proposals.search.placeholder': 'Rechercher par événement, nom, date\u2026',
    'dashboard.proposals.filter.status.all':       'Toutes',
    'dashboard.proposals.filter.status.pending':   'En attente',
    'dashboard.proposals.filter.status.submitted': 'Soumises',
    'dashboard.proposals.filter.status.selected':  'Sélectionnées',
    'dashboard.proposals.filter.source.all':         'Toutes les sources',
    'dashboard.proposals.filter.source.direct':      'Direct',
    'dashboard.proposals.filter.source.marketplace': 'Marketplace',
    'dashboard.proposals.filter.category.all':            'Toutes',
    'dashboard.proposals.filter.category.private_events': 'Événements',
    'dashboard.proposals.filter.category.catering':       'Traiteur',
    'dashboard.proposals.empty.no_requests.title': 'Aucune demande pour l\u2019instant',
    'dashboard.proposals.empty.no_requests.body':  'Lorsqu\u2019un événement correspondra à votre espace, la demande apparaîtra ici. Vous recevrez aussi une notification SMS.',
    'dashboard.proposals.empty.no_matches.title':  'Aucune proposition correspondante',
    'dashboard.proposals.empty.no_matches.body.search': 'Aucune proposition ne correspond à votre recherche. Ajustez vos critères.',
    'dashboard.proposals.empty.no_matches.body.filter': 'Aucune proposition ne correspond à votre filtre. Ajustez vos critères.',
    'dashboard.proposals.results.showing':  'Affichage de {{shown}} sur {{total}} propositions',
    'dashboard.proposals.results.clear':    'Effacer les filtres',
    'dashboard.proposals.section.needs_review':    'À réviser',
    'dashboard.proposals.section.pending':         'Demandes en attente',
    'dashboard.proposals.section.forwarded_crm':   'Transférées à Tripleseat',
    'dashboard.proposals.section.active':          'Actives',
    'dashboard.proposals.section.past':            'Passées',
    'dashboard.proposals.toast.withdraw.success':       'Proposition retirée',
    'dashboard.proposals.toast.withdraw.error':         'Échec du retrait de la proposition',
    'dashboard.proposals.toast.confirm_review.success': 'Proposition confirmée',
    'dashboard.proposals.toast.confirm_review.error':   'Échec de la confirmation de la révision',
    'dashboard.proposals.toast.dismiss.success':        'Proposition retirée de la vue',
    'dashboard.proposals.toast.dismiss.error':          'Échec du retrait de la proposition',

    // Demandes générales (Members Only seulement — FR).
    'dashboard.inquiries.title':            'Demandes',
    'dashboard.inquiries.subtitle.zero':    'Aucune nouvelle question',
    'dashboard.inquiries.subtitle.one':     '1 nouvelle question',
    'dashboard.inquiries.subtitle.many':    '{{count}} nouvelles questions',
    'dashboard.inquiries.empty':            'Aucune question pour l’instant. Lorsqu’un client pose une question à l’équipe via le widget ou par téléphone, elle apparaît ici.',
    'dashboard.inquiries.mark_handled':     'Marquer comme traité',
    'dashboard.inquiries.status.new':       'Nouveau',
    'dashboard.inquiries.voiceEmailCaveat': 'Saisi à la voix — peut être mal transcrit. Le numéro de téléphone est le contact fiable.',
    'dashboard.inquiries.status.handled':   'Traité',
    'dashboard.inquiries.toast.handled.success': 'Demande marquée comme traitée',
    'dashboard.inquiries.toast.handled.error':   'Échec du marquage de la demande',
    'dashboard.inquiries.category.reservation': 'Réservation',
    'dashboard.inquiries.category.hours':       'Heures d’ouverture',
    'dashboard.inquiries.category.menu':        'Menu',
    'dashboard.inquiries.category.directions':  'Itinéraire',
    'dashboard.inquiries.category.private_event': 'Événement privé',
    'dashboard.inquiries.category.catering':    'Traiteur',
    'dashboard.inquiries.category.general':     'Question générale',
    'dashboard.inquiries.category.other':       'Autre',

    // Page de confirmation de demande générale (/inquiry/[id]) — FR.
    'inquiry.confirm.title':          'Vous avez posé une question à l’équipe Members Only',
    'inquiry.confirm.question_label': 'Votre question',
    'inquiry.confirm.contact_label':  'Nous vous joindrons à',
    'inquiry.confirm.response_time':  'L’équipe répond généralement dans la journée.',
    'inquiry.edit.button':            'Modifier',
    'inquiry.edit.name':              'Nom',
    'inquiry.edit.email':             'Courriel',
    'inquiry.edit.phone':             'Téléphone',
    'inquiry.edit.add_email_hint':    'Ajoutez votre courriel pour recevoir une copie',
    'inquiry.edit.add_email_cta':     'Ajoutez votre courriel pour recevoir une copie',
    'inquiry.edit.voice_hint':        'Ce courriel a été saisi à la voix — vérifiez qu\u2019il est exact.',
    'inquiry.edit.save':              'Enregistrer',
    'inquiry.edit.saving':            'Enregistrement\u2026',
    'inquiry.edit.cancel':            'Annuler',
    'inquiry.edit.saved':             'Enregistré — l\u2019équipe verra vos informations à jour.',
    'inquiry.edit.error':             'Échec de l\u2019enregistrement — vérifiez vos informations et réessayez.',
    'inquiry.confirm.loading':        'Chargement de votre demande…',
    'inquiry.confirm.not_found':      'Nous n’avons pas trouvé cette demande — écrivez-nous à events@membersonly.com.',
    'inquiry.confirm.badge':          'Question reçue',

    // Multi-venue plan §1108-1134 — Modal d'attribution à un établissement
    // partenaire (FR).
    'proposals.routing.trigger':                        'Rediriger vers un établissement partenaire',
    'proposals.routing.title':                          'Rediriger cette réservation depuis {{parent}}',
    'proposals.routing.choose_venue':                   'Choisir un établissement',
    'proposals.routing.no_sisters':                     'Aucun établissement partenaire disponible dans votre périmètre.',
    'proposals.routing.customer_unknown':               'Détails du client indisponibles',
    'proposals.routing.guests':                         '{{lo}}–{{hi}} invités',
    'proposals.routing.capacity':                       'capacité {{count}}',
    'proposals.routing.min_spend':                      'min {{amount}}',
    'proposals.routing.reason_label':                   'Motif (facultatif)',
    'proposals.routing.reason_placeholder':             'Consigné à l’audit — pourquoi cet établissement ?',
    'proposals.routing.cancel':                         'Annuler',
    'proposals.routing.confirm':                        'Rediriger la réservation',
    'proposals.routing.confirm_named':                  'Rediriger vers {{venue}}',
    'proposals.routing.success':                        'Redirigée vers {{venue}}',
    'proposals.routing.failure':                        'Échec de la redirection, réessayez',
    'proposals.routing.tripleseat.disabled':            'Tripleseat : désactivé',
    'proposals.routing.tripleseat.shadow':              'Tripleseat : shadow',
    'proposals.routing.tripleseat.external_crm':        'Tripleseat : CRM',
    'proposals.routing.skip_tripleseat_label':          'Ne pas envoyer à Tripleseat pour cette redirection',
    'proposals.routing.skip_tripleseat_help_enabled':   'La redirection est enregistrée mais Tripleseat de {{venue}} ne sera pas déclenché — à traiter manuellement.',
    'proposals.routing.skip_tripleseat_help_disabled':  'L’établissement sélectionné n’a pas d’intégration Tripleseat à ignorer.',
    'proposals.routed_from':                            'Redirigée depuis {{venueName}}',

    // Multi-venue plan §1108-1175 — Mode "Voir en tant que" (FR).
    'dashboard.impersonate.view_as':                    'Voir en tant que',
    'dashboard.impersonate.self':                       'Moi (admin plateforme)',
    'dashboard.impersonate.exit':                       'Quitter',
    'dashboard.impersonate.banner':                     'Usurpation : {{role}} pour {{scope}}.',
    'dashboard.impersonate.role.group_manager':         'Gestionnaire de groupe',
    'dashboard.impersonate.role.venue_manager':         'Gestionnaire d’établissement',
    'dashboard.impersonate.role.location_manager':      'Gestionnaire de site',

    // Proposal-card chrome.
    'dashboard.proposals.card.view_details':         'Voir les détails',
    'dashboard.proposals.card.hide_details':         'Masquer les détails',
    'dashboard.proposals.card.dietary_accessibility': 'Régime / Accessibilité',
    'dashboard.proposals.card.event_summary':        'Résumé de l’événement',
    'dashboard.proposals.card.catering_details':     'Détails du traiteur',
    'dashboard.proposals.card.venue_specific':       'Détails spécifiques à l’établissement',
    'dashboard.proposals.card.customer_contact':     'Coordonnées du client',
    'dashboard.proposals.card.submit':               'Soumettre la proposition',
    'dashboard.proposals.card.resubmit':             'Resoumettre la proposition',
    'dashboard.proposals.card.confirm':              'Confirmer la proposition',
    'dashboard.proposals.card.pending_review_banner': 'Le client a mis à jour les détails de sa réservation. Veuillez réviser et confirmer votre proposition.',
    'dashboard.proposals.card.direct_badge':         'Direct',
    'dashboard.proposals.card.guests_suffix':        'invités',
    'dashboard.proposals.card.status.pending':            'En attente',
    'dashboard.proposals.card.status.submitted':          'Soumise',
    'dashboard.proposals.card.status.consumer_confirmed': 'Paiement en attente',
    'dashboard.proposals.card.status.consumer_confirmed_tripleseat': 'Réservé',
    'dashboard.bookings.view.list':                       'Liste',
    'dashboard.bookings.view.calendar':                   'Calendrier',
    'dashboard.proposals.card.status.pending_review':     'À réviser',
    'dashboard.proposals.card.status.selected':           'Sélectionnée',
    'dashboard.proposals.card.status.completed':          'Terminée',
    'dashboard.proposals.card.status.rejected':           'Non retenue',
    'dashboard.proposals.card.status.expired':            'Expirée',
    'dashboard.proposals.card.status.withdrawn':          'Retirée',
    'dashboard.proposals.card.status.external_crm':       'Transférée au CRM',
    'dashboard.proposals.card.status.lost_post_contact':  'Perdue',
    'dashboard.proposals.card.field.allergy_concerns':    'Allergies',
    'dashboard.proposals.card.field.event_audience':      'Public de l’événement',
    'dashboard.proposals.card.field.location_preference': 'Préférence d’emplacement',
    'dashboard.proposals.card.field.billing_setup':       'Facturation',
    'dashboard.proposals.card.field.bar_restrictions':    'Restrictions du bar',
    'dashboard.proposals.card.field.pre_order_interest':  'Pré-commande de menu',
    'dashboard.proposals.card.field.vendor_interest':     'Événements pop-up Soda',
    'dashboard.proposals.card.field.budget_poc_name':     'Contact budgétaire',
    'dashboard.proposals.card.field.budget_landmarks':    'Jalons de budget',
    'dashboard.proposals.card.field.card_charge_confirmed': 'Frais sur carte',
    'dashboard.proposals.card.field.music_preference':    'Musique / DJ',
    'dashboard.proposals.card.field.space_preference':    'Préférence d’espace',

    // Submit-Proposal modal chrome.
    'dashboard.proposals.card.event_fallback':                  'Événement',
    'dashboard.proposals.card.budget_label':                    'Budget',
    'dashboard.proposals.card.requested_label':                 'Demandé',
    'dashboard.proposals.modal.ai_suggested_banner':            'Suggéré par IA depuis la demande du client — n’hésitez pas à ajuster',
    'dashboard.proposals.modal.submitting':                     'Envoi en cours...',
    'dashboard.proposals.modal.submit_multi':                   'Soumettre {{count}} options',
    'dashboard.proposals.modal.add_option':                     'Ajouter une autre option d’espace',
    'dashboard.proposals.modal.select_space_placeholder':       'Sélectionner un espace',
    'dashboard.proposals.modal.select_date_placeholder':        'Sélectionner une date',
    'dashboard.proposals.modal.up_to':                          'jusqu’à {{n}}',
    'dashboard.proposals.modal.cross_location_warning':         'Cet espace est à un emplacement différent de celui demandé',
    'dashboard.proposals.modal.per_person_disclaimer':          'Estimation seulement, pas une cotation ferme. Le prix final sera confirmé après discussion.',
    'dashboard.proposals.modal.notes_placeholder':              'Détails supplémentaires pour le client...',
    'dashboard.proposals.modal.attachments_hint':               '(menus, conditions, etc.)',
    'dashboard.proposals.modal.aria.remove_option':             'Retirer l’option',
    'dashboard.proposals.modal.aria.remove_date_slot':          'Retirer le créneau',
    'dashboard.proposals.modal.aria.close_modal':               'Fermer la fenêtre',
    'dashboard.proposals.modal.label.space':                    'Espace',
    'dashboard.proposals.modal.label.capacity':                 'Capacité',
    'dashboard.proposals.modal.label.min_spend':                'Dépense minimale ($)',
    'dashboard.proposals.modal.label.per_person':               'Par personne ($)',
    'dashboard.proposals.modal.label.booking_fee':              'Frais de réservation ($)',
    'dashboard.proposals.modal.label.available_dates':          'Dates et heures disponibles',
    'dashboard.proposals.modal.label.notes':                    'Notes',
    'dashboard.proposals.modal.label.attachments':              'Pièces jointes',

    // Landing page (apps/web/src/app/page.tsx).
    'marketing.nav.venue_login':         'Connexion endroit',
    'marketing.nav.call_now':            'Appeler',
    'marketing.hero.title.line1':        'RÉSERVEZ VOTRE',
    'marketing.hero.title.line2':        'SOIRÉE.',
    'marketing.hero.tagline':            'Réservez un espace d\u2019événement privé à {{city}}. Dites-nous ce qu\u2019il vous faut et nous trouvons des endroits pour vous.',
    'marketing.hero.cta.call':           'Appeler',
    'marketing.hero.cta.text':           'Texter',
    'marketing.hero.cta.subline':        'APPELEZ OU TEXTEZ — MÊME ASSISTANT, MÊME NUMÉRO',
    'marketing.hero.cta.browse':         'Parcourir les espaces \u2192',
    'marketing.hero.legal':              'En appelant ou textant, vous acceptez de recevoir des SMS de BookOne.Party concernant votre demande. Fréquence variable. Frais de message et de données peuvent s\u2019appliquer. Répondez HELP pour de l\u2019aide, STOP pour vous désabonner.',
    'marketing.hero.legal.tos':          'Conditions d\u2019utilisation',
    'marketing.hero.legal.privacy':      'Politique de confidentialité',
    'marketing.help.lead':               'Je cherche un espace pour',
    'marketing.help.subline':            'TOUCHEZ POUR PARCOURIR LES ESPACES ÉVÉNEMENTIELS',
    'marketing.help.phrase.birthday':    'une fête d\u2019anniversaire',
    'marketing.help.phrase.corporate':   'un souper d\u2019entreprise',
    'marketing.help.phrase.anniversary': 'un anniversaire surprise',
    'marketing.help.phrase.graduation':  'une célébration de remise des diplômes',
    'marketing.help.phrase.offsite':     'une retraite d\u2019équipe',
    'marketing.help.phrase.rehearsal':   'un souper de répétition',
    'marketing.help.phrase.holiday':     'un party des fêtes',
    'marketing.help.phrase.launch':      'un lancement de produit',
    'marketing.help.phrase.shower':      'un brunch baby shower',
    'marketing.help.phrase.farewell':    'un party d\u2019adieu',
    'marketing.help.phrase.watch':       'un party d\u2019écoute',
    'marketing.help.phrase.engagement':  'un souper de fiançailles',
    'marketing.how.eyebrow':             'COMMENT \u00C7A MARCHE',
    'marketing.how.title.a':             'Trois étapes.',
    'marketing.how.title.b':             'Un appel.',
    'marketing.how.step':                '\u00C9TAPE',
    'marketing.how.step1.title':         'Parlez-nous de votre événement',
    'marketing.how.step1.desc':          'Parcourez des espaces événementiels et partagez vos détails — date, nombre d\u2019invités, ambiance. Ajoutez vos lieux préférés à votre liste.',
    'marketing.how.step2.title':         'On vous jumelle avec des endroits',
    'marketing.how.step2.desc':          'Nous trouvons les espaces privés qui correspondent le mieux. Les endroits envoient des propositions avec détails, capacité et prix pour comparer.',
    'marketing.how.step3.title':         'Choisissez votre préféré et réservez',
    'marketing.how.step3.desc':          'Sélectionnez la proposition qui vous plaît et confirmez votre réservation. Nous vous mettons en contact direct avec l\u2019endroit pour finaliser le tout.',
    'marketing.statement.a':             'Avant BookOne, organiser un party privé prenait 3 semaines et 12 courriels.',
    'marketing.statement.b':             'Maintenant, ça ne prend qu\u2019une recherche.',
    'marketing.statement.c':             'C\u2019est le raccourci.',
    'marketing.partners.eyebrow':        'PARTENAIRES',
    'marketing.partners.title':          'La confiance des meilleurs endroits de {{city}}',
    'marketing.partners.coming_soon':    'À venir',
    'marketing.partners.type.cocktail':  'Bar à cocktails',
    'marketing.partners.type.restaurant': 'Restaurant et bar',
    'marketing.partners.type.rooftop':   'Endroit sur le toit',
    'marketing.partners.type.dining':    'Salle privée',
    'marketing.testimonial1.quote':      'BookOne a rendu la réservation de notre party des fêtes vraiment facile. Nous avons reçu trois propositions en moins d\u2019une heure.',
    'marketing.testimonial1.role':       'Organisatrice d\u2019événement',
    'marketing.testimonial2.quote':      'Enfin un service qui comprend les besoins des endroits. Des prospects qualifiés livrés directement à mon téléphone.',
    'marketing.testimonial2.role':       'Propriétaire d\u2019endroit',
    'marketing.testimonial3.quote':      'J\u2019ai planifié tout mon souper d\u2019anniversaire en une seule après-midi. L\u2019endroit était parfait pour notre groupe.',
    'marketing.testimonial3.role':       'Client',
    'marketing.cta.title.a':             'Parcourez les espaces pour votre',
    'marketing.cta.title.b':             'party aujourd\u2019hui.',
    'marketing.cta.tagline':             'Pas d\u2019application. Pas de compte. Pas d\u2019attente.',
    'marketing.faq.eyebrow':             'FAQ',
    'marketing.faq.title':               'Questions fréquentes',
    'marketing.faq.q1.q':                'À quelle vitesse vais-je recevoir des propositions?',
    'marketing.faq.q1.a':                'La plupart des clients reçoivent des propositions d\u2019endroits en moins de 30 minutes. Nous jumelons votre événement aux meilleurs endroits, qui répondent vite car chaque demande est qualifiée.',
    'marketing.faq.q2.q':                'Combien ça coûte?',
    'marketing.faq.q2.a':                'Réserver via BookOne.Party est gratuit pour les clients. Vous payez seulement les frais de réservation de l\u2019endroit lorsque vous choisissez une proposition — aucuns frais cachés, aucuns frais de plateforme.',
    'marketing.faq.q3.q':                'Puis-je texter au lieu d\u2019appeler?',
    'marketing.faq.q3.a':                'Oui. Le même assistant IA gère la voix et le texto via un seul numéro. Aucune application requise.',
    'marketing.faq.q4.q':                'Puis-je modifier ma réservation après l\u2019avoir choisie?',
    'marketing.faq.q4.a':                'Une fois l\u2019endroit choisi, nous vous mettons en contact direct avec le gérant. Tout changement (date, nombre d\u2019invités, détails) passe par lui.',
    'marketing.venue_cta.title':         'Vous accueillez des événements privés?',
    'marketing.venue_cta.body':          'Recevez des réservations d\u2019événements privés directement sur votre téléphone. Inscription gratuite, aucune application requise.',
    'marketing.venue_cta.button':        'Inscrivez votre endroit \u2192',
    'marketing.footer.tag':              'RÉSERVATION D\u2019ENDROIT PRIVÉ \u2014 {{city}}, {{country}}',
    'marketing.footer.tagline':          'Pas d\u2019application. Pas de compte. Pas d\u2019attente.',
    'marketing.footer.legal':            '\u00A9 2026 1001474668 ONTARIO INC.',

    // Analytics dashboard page.
    'dashboard.analytics.subtitle':         'Suivez la performance de votre endroit',
    'dashboard.analytics.period.7d':        '7j',
    'dashboard.analytics.period.30d':       '30j',
    'dashboard.analytics.period.90d':       '90j',
    'dashboard.analytics.period.all':       'Tout',
    'dashboard.analytics.empty.title':      'Aucune donnée pour l\u2019instant',
    'dashboard.analytics.empty.body':       'Les analyses se rempliront au fur et à mesure que vous recevez et répondez aux demandes.',
    'dashboard.analytics.stat.total_requests': 'Total des demandes',
    'dashboard.analytics.stat.submitted':      'Soumises',
    'dashboard.analytics.stat.won':            'Gagnées',
    'dashboard.analytics.stat.revenue':        'Revenus',
    'dashboard.analytics.funnel.title':        'Entonnoir de conversion',
    'dashboard.analytics.funnel.received':     'Reçues',
    'dashboard.analytics.funnel.submitted':    'Soumises',
    'dashboard.analytics.funnel.selected':     'Sélectionnées',
    'dashboard.analytics.funnel.completed':    'Terminées',
    'dashboard.analytics.funnel.lost':         'Perdues',
    'dashboard.analytics.funnel.lost.reasons': 'Raisons',
    'dashboard.analytics.funnel.conversion':   'Taux de conversion',
    'dashboard.analytics.stat.avg_party':      'Taille moyenne du groupe',
    'dashboard.analytics.stat.repeat':         'Clients récurrents',

    // Login page.
    'auth.login.title':                  'Connexion endroit',
    'auth.login.subtitle':               'Connectez-vous pour gérer vos demandes de réservation.',
    'auth.login.tab.phone':              'Téléphone',
    'auth.login.tab.email':              'Courriel',
    'auth.login.label.phone':            'Numéro de téléphone',
    'auth.login.label.email':            'Adresse courriel',
    'auth.login.label.code':             'Code de vérification',
    'auth.login.hint.phone':             'Entrez le numéro de téléphone que vous avez utilisé à l\u2019inscription.',
    'auth.login.hint.email':             'Nous enverrons un code à 6 chiffres pour vérifier votre identité.',
    'auth.login.hint.code':              'Consultez vos messages pour un code à 6 chiffres.',
    'auth.login.hint.code_email':        'Vérifiez votre courriel pour un code envoyé à {{email}}.',
    'auth.login.button.send':            'Envoyer le code de vérification',
    'auth.login.button.verify':          'Se connecter',
    'auth.login.button.resend':          'Renvoyer le code',
    'auth.login.button.different':       'Utiliser un autre numéro',
    'auth.login.button.different_email': 'Utiliser un autre courriel',
    'auth.login.error.phone_required':   'Veuillez entrer un numéro de téléphone.',
    'auth.login.error.email_invalid':    'Veuillez entrer une adresse courriel valide.',
    'auth.login.error.phone_invalid':    'Veuillez entrer un numéro de téléphone valide.',
    'auth.login.error.code_invalid':     'Code invalide. Réessayez ou demandez un nouveau code.',
    'auth.login.error.send_failed':      'Impossible d\u2019envoyer le code. Veuillez réessayer.',
    'auth.login.error.session_expired':  'Votre session a expiré. Veuillez vous reconnecter.',
    'auth.login.signup.prompt':          'Nouvel endroit?',
    'auth.login.signup.cta':             'Inscrivez-vous ici',

    // Dashboard error/loading boundaries.
    'dashboard.error.title':             'Une erreur est survenue',
    'dashboard.error.body':              'Nous avons rencontré un problème inattendu. Réessayez ou rafraîchissez la page.',
    'dashboard.error.retry':             'Réessayer',

    // Lang switcher / common UI.
    'common.lang.synced_from_profile':   'Langue synchronisée avec votre profil ({{lang}}).',

    // Login extras.
    'auth.login.placeholder.phone':      '(514) 555-1234',
    'auth.login.placeholder.email':      'proprietaire@endroit.com',
    'auth.login.hint.email_account':     'Entrez l\u2019adresse courriel associée à votre endroit.',
    'auth.login.hint.code_whatsapp':     'Vérifiez votre WhatsApp pour un code envoyé à {{phone}}.',
    'auth.login.hint.code_sms':          'Nous avons envoyé un code à 6 chiffres à {{phone}}.',
    'auth.login.cooldown':               'Renvoyer le code dans {{seconds}}s',
    'auth.login.toast.resent':           'Code renvoyé!',
    'auth.login.toast.resend_failed':    'Échec du renvoi du code',

    // Settings page chrome (subcomponents covered in follow-up commits).
    'dashboard.settings.subtitle':       'Gérez les détails, espaces et disponibilité de votre endroit',
    'dashboard.settings.action.preview_public':   'Aperçu public',
    'dashboard.settings.action.preview_customer': 'Aperçu client',
    'dashboard.settings.action.pause':            'Mettre en pause',
    'dashboard.settings.action.resume':           'Réactiver',
    'dashboard.settings.toast.paused':            'Endroit mis en pause',
    'dashboard.settings.toast.resumed':           'Endroit réactivé',
    'dashboard.settings.toast.toggle_failed':     'Échec du changement de statut',
    'dashboard.settings.toast.email_verified':    'Courriel vérifié avec succès!',
    'dashboard.settings.toast.email_link_expired': 'Le lien de vérification a expiré. Demandez-en un nouveau.',
    'dashboard.settings.signout':                 'Se déconnecter',

    // Plan multi-établissements — vue Réglages limitée au gérant
    // d'établissement (location_manager). Tous les champs globaux sont
    // masqués; seule la section « détails de cet établissement » reste
    // modifiable.
    'settings.location_manager_banner':           'Vous êtes gérant·e de l’établissement {{locationName}}. Seuls les champs propres à {{locationName}} sont modifiables ici.',
    'settings.location_manager_section_title':    'Détails de {{locationName}}',
    'settings.location_only_fields_notice':       'La modification des réglages globaux (marque, contact, intégrations) nécessite un accès gérant·e d’endroit ou supérieur.',
    'settings.location_manager.field.address':    'Adresse',
    'settings.location_manager.field.postal_code':'Code postal',
    'settings.location_manager.field.phone':      'Téléphone',
    'settings.location_manager.field.latitude':   'Latitude',
    'settings.location_manager.field.longitude':  'Longitude',
    'settings.location_manager.field.website':    'URL du site Web',
    'settings.location_manager.field.instagram':  'URL Instagram',
    'settings.location_manager.action.save':      'Enregistrer',
    'settings.location_manager.action.saving':    'Enregistrement…',
    'settings.location_manager.toast.saved':      'Établissement mis à jour',
    'settings.location_manager.toast.save_failed':'Échec de l’enregistrement',
    'settings.location_manager.no_location':      'Aucun établissement n’est associé à ce compte. Contactez votre gérant·e d’endroit.',

    // Public venue profile (apps/web/src/app/venue/[id]).
    'venue_profile.error.title':         'Une erreur est survenue',
    'venue_profile.error.body':          'Impossible de charger cet endroit. Veuillez réessayer.',
    'venue_profile.error.retry':         'Réessayer',
    'venue_profile.error.home':          'Retour à l\u2019accueil',
    'venue_profile.not_found.title':     'Endroit introuvable',
    'venue_profile.not_found.body':      'Aucun endroit trouvé avec cet identifiant. Il a peut-être été supprimé ou le lien est incorrect.',
    'venue_profile.not_found.home':      'Retour à l\u2019accueil',

    // Settings — VenueInfoForm.
    'settings.booking_mgmt.toast_enabled':       'Gestion des r\u00e9servations activ\u00e9e',
    'settings.booking_mgmt.toast_disabled':      'Gestion des r\u00e9servations d\u00e9sactiv\u00e9e',
    'settings.booking_mgmt.toast_save_failed':   '\u00c9chec de l\u2019enregistrement \u2014 {{error}}',
    'settings.booking_mgmt.error_save_default':  '\u00c9chec de l\u2019enregistrement',

    'dashboard.analytics.customer_list.toast.update_reminder_failed': '\u00c9chec de la mise \u00e0 jour du rappel',
    'dashboard.analytics.customer_list.toast.save_notes_failed':      '\u00c9chec de l\u2019enregistrement des notes',

    'settings.tripleseat.toast.validation_errors':  'Veuillez corriger les erreurs de validation avant d\u2019enregistrer.',
    'settings.tripleseat.toast.saved':              'Param\u00e8tres Tripleseat enregistr\u00e9s',
    'settings.tripleseat.toast.save_failed':        '\u00c9chec de l\u2019enregistrement des param\u00e8tres Tripleseat',
    'settings.tripleseat.confirm.switch_live':      'Basculer vers les soumissions Tripleseat en direct ?\n\nCela enverra de vraies demandes \u00e0 votre compte Tripleseat. Assurez-vous d\u2019avoir test\u00e9 avec le point de terminaison fictif d\u2019abord.',

    'proposals.tripleseat_preview.toast.script_copied':    'Script de remplissage copi\u00e9 \u2014 collez-le dans la console du formulaire',
    'proposals.tripleseat_preview.toast.copy_failed':      'Impossible de copier \u2014 utilisez les boutons de copie individuels ci-dessous',
    'proposals.tripleseat_preview.toast.marked_forwarded': 'Marqu\u00e9 comme transf\u00e9r\u00e9. Le client a \u00e9t\u00e9 notifi\u00e9.',

    'booking.external_crm_tracker.toast.note_required':    'Ajoutez une courte note d\u00e9crivant ce qui s\u2019est pass\u00e9',
    'booking.external_crm_tracker.toast.contact_logged':   'Contact enregistr\u00e9',
    'booking.external_crm_tracker.toast.save_failed':      '\u00c9chec de l\u2019enregistrement',
    'booking.external_crm_tracker.toast.status_advanced':  'Statut : {{status}}',
    'booking.external_crm_tracker.toast.update_status_failed': '\u00c9chec de la mise \u00e0 jour du statut',
    'booking.external_crm_tracker.toast.saved':            'Enregistr\u00e9',

    'menu_upload.toast.file_too_large':                    'Fichier trop volumineux ({{size}} Mo). Maximum : 10 Mo.',
    'menu_upload.drop_or_click':                           'Déposez un PDF ou une image, ou cliquez pour téléverser',
    'menu_upload.file_hint':                               'PDF, JPEG, PNG ou WebP — Max 10 Mo',

    'image_upload.drop_or_click':                          'Déposez une image ou cliquez pour téléverser',
    'image_upload.file_hint':                              'JPEG, PNG ou WebP — Max 5 Mo',

    'browse_nav.button':                                   'Parcourir',
    'browse_nav.category.venues.label':                    'Endroits',
    'browse_nav.category.venues.desc':                     'Réservez des espaces et endroits pour événements privés',
    'browse_nav.category.catering.label':                  'Traiteur',
    'browse_nav.category.catering.desc':                   'Commandez un traiteur pour votre prochain événement',
    'browse_nav.category.experiences.label':               'Expériences',
    'browse_nav.category.experiences.desc':                'Golf, course, escape rooms et plus',

    'browse.hero.back_to_home':                            '← Retour à l\'accueil',
    'browse.venues.title':                                 'Parcourir les endroits',
    'browse.venues.subtitle':                              'Explorez les espaces et endroits pour événements privés.',
    'browse.experiences.title':                            'Parcourir les expériences',
    'browse.experiences.subtitle':                         'Espaces événementiels alternatifs et expériences uniques.',
    'browse.catering.title':                               'Parcourir les traiteurs',
    'browse.catering.subtitle':                            'Plats et boissons livrés à votre événement.',
    'browse.catering.address_placeholder':                 'Entrez votre adresse pour voir qui livre chez vous...',

    'venue_profile.back_to_venues':                        'Parcourir les endroits',
    'venue_profile.back_to_catering':                      'Retour au traiteur',
    'venue_profile.space.up_to':                           'Jusqu’à {{n}}',
    'venue_profile.section.private_spaces':                'Espaces privés',
    'venue_profile.section.menu_additional':               'Menu / détails supplémentaires',
    'venue_profile.section.our_food':                      'Notre cuisine',
    'venue_profile.section.our_locations':                 'Nos emplacements',
    'venue_profile.section.catering_menus':                'Menus de traiteur',
    'venue_profile.section.delivery_range':                'Zone de livraison',
    'venue_profile.widget.title_event':                    'Réservez un événement privé chez {{venueName}}',
    'venue_profile.widget.description_event':              'Discutez avec notre assistant pour planifier votre soirée.',
    'venue_profile.widget.title_catering':                 'Planifiez un événement chez {{venueName}}',
    'venue_profile.widget.description_catering':           'Obtenez un devis — appelez ou clavardez avec l’équipe.',
    'venue_profile.cta.call_to_book':                      'Appeler pour réserver',
    'venue_profile.cta.text_to_book':                      'Texter pour réserver',
    'venue_profile.cta.ready_to_book':                     'Prêt à réserver?',
    'venue_profile.cta.text_or_call_direct':               'Texter ou appeler {{venueName}} directement',
    'venue_profile.cta.text_to_book_direct':               'Texter pour réserver cet endroit directement',
    'venue_profile.locations.subtitle':                    '{{count}} emplacements à travers {{region}} — choisissez le plus proche, ou vérifiez votre adresse de livraison ci-dessous.',
    'venue_profile.locations.delivery_check':              'Vérifier si nous livrons à votre adresse',
    'venue_profile.menu.online_menu':                      'Menu en ligne',

    'dashboard.settings.service_types.toast.updated':      'Types de service mis à jour',
    'dashboard.settings.service_types.toast.update_failed':'Échec de la mise à jour',

    'dashboard.live_transfers.toast.dismissed':            'Ignoré',
    'dashboard.live_transfers.toast.dismiss_failed':       'Échec de l’ignore',
    'dashboard.live_transfers.toast.restored':             'Restauré',
    'dashboard.live_transfers.toast.restore_failed':       'Échec de la restauration',

    'settings.venue_info.title':              'Informations de l\u2019endroit',
    'settings.venue_info.title_edit':         'Modifier les informations',
    'settings.venue_info.action.edit':        'Modifier',
    'settings.venue_info.action.cancel':      'Annuler',
    'settings.venue_info.action.save':        'Enregistrer',
    'settings.venue_info.action.saving':      'Enregistrement\u2026',
    'settings.venue_info.hero.label':         'Image principale',
    'settings.venue_info.hero.upload':        'Téléverser',
    'settings.venue_info.hero.change':        'Changer',
    'settings.venue_info.hero.uploading':     'Téléversement\u2026',
    'settings.venue_info.hero.hint':          'Recommandé : 2400 \u00D7 1200px ou plus grand',
    'settings.venue_info.hero.upload_failed': 'Échec du téléversement de l\u2019image',
    'settings.venue_info.field.name':         'Nom',
    'settings.venue_info.field.name_full':    'Nom de l\u2019endroit',
    'settings.venue_info.field.address':      'Adresse',
    'settings.venue_info.field.contact':      'Contact',
    'settings.venue_info.field.contact_name': 'Nom du contact',
    'settings.venue_info.field.phone':        'Téléphone',
    'settings.venue_info.field.email':        'Courriel de contact',
    'settings.venue_info.field.tagline':      'Slogan',
    'settings.venue_info.field.website':      'Site web',
    'settings.venue_info.field.instagram':    'Instagram',
    'settings.venue_info.field.notes':        'Notes',
    'settings.venue_info.placeholder.email':  'proprietaire@endroit.com',
    'settings.venue_info.placeholder.website': 'monendroit.com',
    'settings.venue_info.placeholder.instagram': 'votre_endroit',
    'settings.venue_info.maps':               'Voir sur Google Maps',
    'settings.venue_info.notifications':      'Notifications',
    'settings.venue_info.no_email_set':       'Aucun courriel défini',
    'settings.venue_info.no_email_label':     'Aucun courriel',
    'settings.venue_info.via_whatsapp':       '{{phone}} via WhatsApp',
    'settings.venue_info.via_sms':            '{{phone}} via SMS',
    'settings.venue_info.both':               '{{email}} + {{phone}}',
    'settings.venue_info.notifications.choose': 'Choisissez comment recevoir les notifications de réservation et les codes de connexion.',
    'settings.venue_info.channel.phone':      'Téléphone',
    'settings.venue_info.channel.sms':        'SMS',
    'settings.venue_info.channel.whatsapp':   'WhatsApp',
    'settings.venue_info.channel.none_email': 'Aucun (courriel seulement)',
    'settings.venue_info.channel.email_toggle': 'Notifications par courriel',
    'settings.venue_info.channel.sending_to': 'Envoi à {{email}}',
    'settings.venue_info.channel.add_email':  'Ajoutez un courriel ci-dessus pour activer les notifications par courriel.',
    'settings.venue_info.visibility.title':   'Visibilité du profil public',
    'settings.venue_info.visibility.menus':   'Afficher les menus sur le profil public',
    'settings.venue_info.visibility.spend':   'Afficher la dépense minimale sur le profil public',
    'settings.venue_info.visibility.contact': 'Afficher les coordonnées du client sur les propositions',
    'settings.venue_info.visibility.contact_help': 'Affiche le téléphone et le courriel du client sur les demandes entrantes',
    'settings.venue_info.accessibility':      'Accessible aux fauteuils roulants',
    'settings.venue_info.toast.saved':        'Informations enregistrées',
    'settings.venue_info.toast.save_failed':  'Échec de l\u2019enregistrement',
    'settings.venue_info.tagline_empty':      '\u2014',
    // Email verification badge.
    'settings.email_verify.verified':         'Vérifié',
    'settings.email_verify.not_verified':     'Non vérifié',
    'settings.email_verify.send':             'Envoyer le courriel de vérification',
    'settings.email_verify.sending':          'Envoi\u2026',
    'settings.email_verify.toast.sent':       'Courriel de vérification envoyé. Vérifiez votre boîte de réception.',
    'settings.email_verify.toast.failed':     'Échec de l\u2019envoi du courriel de vérification',
    // Direct booking section.
    'settings.direct.title':                  'Réservation directe',
    'settings.direct.code_label':             'Code d\u2019endroit',
    'settings.direct.code_help':              'Les clients peuvent texter ce code à BookOne pour réserver directement avec vous',
    'settings.direct.line_label_one':         'Ligne téléphonique dédiée',
    'settings.direct.line_label_many':        'Lignes téléphoniques dédiées',
    'settings.direct.status.active':          'Active',
    'settings.direct.status.inactive':        'Inactive',
    'settings.direct.no_line':                'Vous voulez un numéro dédié pour votre endroit? Contactez BookOne pour configurer.',

    // Settings — LocationList.
    'settings.locations.title':               'Lieux ({{count}})',
    'settings.locations.title_edit':          'Modifier les lieux',
    'settings.locations.empty':               'Endroit à un seul emplacement. Cliquez sur Modifier pour ajouter d\u2019autres lieux.',
    'settings.locations.location_n':          'Lieu {{n}}',
    'settings.locations.field.name':          'Nom',
    'settings.locations.field.city':          'Ville',
    'settings.locations.field.address':       'Adresse',
    'settings.locations.field.phone':         'Téléphone',
    'settings.locations.field.website':       'URL du site web',
    'settings.locations.field.instagram':     'URL Instagram',
    'settings.locations.placeholder.name':    'p. ex. Centre-ville',
    'settings.locations.placeholder.city':    'p. ex. Montréal',
    'settings.locations.placeholder.address': 'Rechercher une adresse\u2026',
    'settings.locations.placeholder.phone':   'p. ex. (514) 555-1234',
    'settings.locations.placeholder.website': 'p. ex. https://monendroit.com/centre',
    'settings.locations.placeholder.instagram': 'p. ex. https://instagram.com/monendroit',
    'settings.locations.add':                 'Ajouter un lieu',
    'settings.locations.toast.saved':         'Lieux enregistrés',
    'settings.locations.toast.save_failed':   'Échec de l\u2019enregistrement des lieux',
    'settings.locations.toast.missing_address': 'Le lieu {{n}} a besoin d\u2019une adresse avant l\u2019enregistrement.',
    'settings.locations.website_label':       'Site web',

    // Settings — SpaceList + SpaceCard.
    'settings.spaces.title':                  'Espaces ({{count}})',
    'settings.spaces.title_edit':             'Modifier les espaces',
    'settings.spaces.empty':                  'Aucun espace configuré. Cliquez sur Modifier pour ajouter votre premier espace.',
    'settings.spaces.space_n':                'Espace {{n}}',
    'settings.spaces.field.name':             'Nom',
    'settings.spaces.field.capacity':         'Capacité maximale',
    'settings.spaces.field.min_spend':        'Dépense minimale ($)',
    'settings.spaces.field.booking_fee':      'Frais de réservation ($)',
    'settings.spaces.field.location':         'Lieu',
    'settings.spaces.field.description':      'Description',
    'settings.spaces.field.images':           'Images',
    'settings.spaces.placeholder.name':       'p. ex. Le Salon',
    'settings.spaces.placeholder.min_spend':  'p. ex. 1000',
    'settings.spaces.placeholder.booking_fee': 'p. ex. 50',
    'settings.spaces.no_location':            'Aucun lieu spécifique',
    'settings.spaces.add':                    'Ajouter un espace',
    'settings.spaces.toast.saved':            'Espaces enregistrés',
    'settings.spaces.toast.save_failed':      'Échec de l\u2019enregistrement des espaces',
    'settings.spaces.toast.image_failed':     'Échec du téléversement de l\u2019image',
    'settings.space_card.up_to':              'Jusqu\u2019à {{capacity}}',
    'settings.space_card.up_to_guests':       'Jusqu\u2019à {{capacity}} invités',
    'settings.space_card.min_short':          '{{amount}} $ min',
    'settings.space_card.min_long':           '{{amount}} $ de dépense minimale',
    'settings.space_card.fee_short':          '{{amount}} $ de frais',
    'settings.space_card.fee_long':           '{{amount}} $ de frais de réservation',

    // Settings — MenuList.
    'settings.menus.title':                   'Menus / Détails supplémentaires ({{count}})',
    'settings.menus.title_edit':              'Modifier les menus / détails',
    'settings.menus.empty':                   'Aucun menu ni détail téléversé. Cliquez sur Modifier pour ajouter des menus ou des détails d\u2019événement.',
    'settings.menus.menu_n':                  'Menu {{n}}',
    'settings.menus.field.name':              'Nom',
    'settings.menus.field.description':       'Description',
    'settings.menus.field.location':          'Lieu',
    'settings.menus.field.source':            'Source',
    'settings.menus.placeholder.name':        'p. ex. Menu fixe, Forfait événement',
    'settings.menus.placeholder.description': 'Description facultative',
    'settings.menus.placeholder.link':        'https://votresite.com/menu.pdf',
    'settings.menus.placeholder.link_help':   'Collez un URL vers votre menu (page web, lien PDF, etc.)',
    'settings.menus.no_location':             'Aucun lieu spécifique',
    'settings.menus.mode.upload':             'Téléverser un fichier',
    'settings.menus.mode.link':               'Coller un lien',
    'settings.menus.view_current':            'Voir le fichier actuel',
    'settings.menus.replace':                 'Remplacer',
    'settings.menus.add':                     'Ajouter un menu ({{count}}/12)',
    'settings.menus.toast.saved':             'Menus enregistrés',
    'settings.menus.toast.save_failed':       'Échec de l\u2019enregistrement des menus',
    'settings.menus.error.untitled':          'Sans titre',
    'settings.menus.error.needs_link':        'Le menu « {{name}} » a besoin d\u2019un URL',
    'settings.menus.error.too_large':         'Fichier trop volumineux pour « {{name}} ». Max 10 Mo.',
    'settings.menus.error.bad_type':          'Type de fichier non autorisé pour « {{name}} ». Utilisez PDF, JPEG, PNG ou WebP.',
    'settings.menus.error.upload_failed':     'Échec du téléversement pour « {{name}} » : {{msg}}',

    // Magic-link + verify-email pages.
    'auth.magic.signing_in':             'Connexion en cours\u2026',
    'auth.magic.redirecting':            'Vous serez redirigé vers votre tableau de bord',
    'auth.magic.expired':                'Ce lien magique a expiré ou est invalide.',
    'auth.magic.redirecting_login':      'Redirection vers la connexion\u2026',
    // Multi-venue plan §1136-1154 — État « Accès modifié » (FR).
    'auth.magic.access_changed.title':       'L’accès à cet établissement a été modifié',
    'auth.magic.access_changed.title_named': 'L’accès à {{venue}} a été modifié',
    'auth.magic.access_changed.message':     'Votre rôle a récemment changé et vous n’avez plus accès aux réservations de cet établissement. La réservation est prise en charge par l’établissement. Si c’est inattendu, contactez votre administrateur de groupe.',
    'auth.magic.access_changed.cta':         'Aller à votre tableau de bord',
    'auth.verify_email.verifying':       'Vérification de votre courriel\u2026',
    'auth.verify_email.short':           'Vérification\u2026',

    // Signup flow (apps/web/src/app/venue/signup).
    'signup.page.title':                  'Inscrivez votre endroit',
    'signup.page.subtitle':               'Événements, traiteur ou les deux — recevez des réservations par SMS. Aucune application requise, aucuns frais d\u2019inscription.',
    'signup.type.question':               'Quels services votre endroit offre-t-il?',
    'signup.type.events.label':           'Événements privés seulement',
    'signup.type.events.desc':            'Les clients réservent votre espace pour des événements',
    'signup.type.catering.label':         'Traiteur seulement',
    'signup.type.catering.desc':          'Vous livrez de la nourriture; aucun événement sur place',
    'signup.type.both.label':             'Les deux',
    'signup.type.both.desc':              'Accueillir des événements ET livrer du traiteur',
    'signup.quickfill.title':             'Remplissage rapide depuis Google',
    'signup.quickfill.body':              'Recherchez votre endroit pour remplir automatiquement le nom, l\u2019adresse, le téléphone et le site web.',
    'signup.quickfill.placeholder':       'Recherchez le nom ou l\u2019adresse de votre endroit\u2026',
    'signup.quickfill.success':           'Formulaire pré-rempli! Vérifiez et modifiez les détails ci-dessous.',
    'signup.venue.title':                 'Détails de l\u2019endroit',
    'signup.venue.name_label':            'Nom de l\u2019endroit *',
    'signup.venue.name_placeholder':      'Le Saint-Jean',
    'signup.venue.tagline_label':         'Slogan',
    'signup.venue.tagline_placeholder':   'Le pub de quartier le plus chaleureux de Montréal',
    'signup.venue.address_label':         'Adresse *',
    'signup.venue.accessibility':         'Endroit accessible aux fauteuils roulants',
    'signup.photo.title':                 'Photo de l\u2019endroit',
    'signup.photo.body':                  'Ce sera la photo principale de votre profil',
    'signup.contact.title':               'Coordonnées',
    'signup.contact.name_label':          'Votre nom *',
    'signup.contact.phone_label':         'Téléphone (pour les SMS) *',
    'signup.contact.phone_placeholder':   '(514) 555-1234',
    'signup.contact.email_label':         'Courriel (facultatif)',
    'signup.online.title':                'Présence en ligne',
    'signup.online.body':                 'Aidez les clients à vous trouver en ligne (facultatif)',
    'signup.online.website_label':        'Site web',
    'signup.online.website_placeholder':  'monendroit.com',
    'signup.online.instagram_label':      'Instagram',
    'signup.online.instagram_placeholder': 'votre_endroit',
    'signup.spaces.title':                'Espaces privés',
    'signup.spaces.body':                 'Ajoutez chaque espace privé disponible pour réservation.',
    'signup.spaces.space_n':              'Espace {{n}}',
    'signup.spaces.name_placeholder':     'Nom de l\u2019espace (p. ex. Le Salon, Salle privée)',
    'signup.spaces.capacity_label':       'Capacité maximale *',
    'signup.spaces.capacity_placeholder': '50',
    'signup.spaces.min_spend_label':      'Dépense minimale ($)',
    'signup.spaces.min_spend_placeholder': '2500',
    'signup.spaces.fee_label':            'Frais de réservation ($)',
    'signup.spaces.fee_placeholder':      '40',
    'signup.spaces.notes_placeholder':    'Notes supplémentaires (politique d\u2019annulation, etc.)',
    'signup.spaces.photo_label':          'Photo de l\u2019espace',
    'signup.spaces.add':                  'Ajouter un autre espace',
    'signup.catering.title':              'Détails du traiteur',
    'signup.catering.body':               'Quelques détails rapides pour qu\u2019on puisse vous jumeler aux demandes de traiteur. Vous pourrez ajouter photos, forfaits-menus et plus depuis les Paramètres après l\u2019inscription.',
    'signup.catering.cuisines_label':     'Types de cuisine',
    'signup.catering.cuisines_placeholder': 'Tapez une cuisine et appuyez sur Entrée (p. ex. Italienne, Mexicaine, Sushi)',
    'signup.catering.cuisines_remove':    'Retirer {{tag}}',
    'signup.catering.radius_label':       'Rayon de livraison (km)',
    'signup.catering.radius_placeholder': '25',
    'signup.catering.minimum_label':      'Commande minimale ($)',
    'signup.catering.minimum_placeholder': 'facultatif',
    'signup.catering.footer':             'Tous les champs traiteur sont facultatifs à l\u2019inscription. Vous pourrez ajouter photos, forfaits-menus et plus depuis les Paramètres après avoir créé votre endroit.',
    'signup.menus.title':                 'Menu / détails supplémentaires (facultatif)',
    'signup.menus.body':                  'Téléversez des menus ou détails d\u2019événement \u2014 PDF ou image, jusqu\u2019à 5.',
    'signup.menus.menu_n':                'Menu {{n}}',
    'signup.menus.name_placeholder':      'Nom (p. ex. Menu fixe, Forfait événement)',
    'signup.menus.description_placeholder': 'Description (facultatif)',
    'signup.menus.add':                   'Ajouter un menu',
    'signup.notes.label':                 'Notes supplémentaires (facultatif)',
    'signup.notes.placeholder':           'Autre chose à savoir?',
    'signup.consent.body':                'Je consens à recevoir des SMS de BookOne.Party concernant les demandes de réservation, les mises à jour d\u2019événement, les codes de vérification (OTP) et autres messages de service. Fréquence variable. Frais de message et de données peuvent s\u2019appliquer. Répondez HELP pour de l\u2019aide, STOP pour vous désabonner. J\u2019ai lu et accepte les',
    'signup.consent.tos':                 'Conditions d\u2019utilisation',
    'signup.consent.and':                 'et la',
    'signup.consent.privacy':             'Politique de confidentialité',
    'signup.submit':                      'Inscrire mon endroit',
    'signup.error.name':                  'Le nom de l\u2019endroit est requis',
    'signup.error.address':               'L\u2019adresse est requise',
    'signup.error.contact_name':          'Votre nom est requis',
    'signup.error.phone_required':        'Le numéro de téléphone est requis',
    'signup.error.phone_invalid':         'Entrez un numéro de téléphone nord-américain valide à 10 chiffres',
    'signup.error.hero_size':             'L\u2019image principale doit être inférieure à 5 Mo',
    'signup.error.space_name':            'Le nom de l\u2019espace est requis',
    'signup.error.space_capacity':        'La capacité maximale doit être d\u2019au moins 1',
    'signup.error.space_image':           'L\u2019image de l\u2019espace doit être inférieure à 5 Mo',
    'signup.error.menu_name':             'Le nom du menu est requis',
    'signup.error.menu_file':             'Le fichier du menu est requis',
    'signup.error.menu_size':             'Le fichier du menu doit être inférieur à 10 Mo',
    'signup.error.delivery_negative':     'Le rayon de livraison ne peut pas être négatif',
    'signup.error.minimum_negative':      'La commande minimale ne peut pas être négative',
    'signup.error.consent':               'Vous devez accepter de recevoir des SMS pour inscrire un endroit',
    'signup.error.generic':               'Une erreur est survenue',

    // Signup success page.
    'signup.success.title':               'Vous êtes inscrit!',
    'signup.success.intro':               'Votre endroit est maintenant inscrit sur BookOne.Party. Voici la suite :',
    'signup.success.step1.title':         'Demandes de réservation par SMS',
    'signup.success.step1.desc':          'Lorsqu\u2019un événement correspond à votre espace, vous recevrez un texto avec les détails.',
    'signup.success.step2.title':         'Soumettez votre proposition',
    'signup.success.step2.desc':          'Cliquez sur le lien dans votre SMS pour soumettre votre proposition via le tableau de bord.',
    'signup.success.step3.title':         'Mise en contact',
    'signup.success.step3.desc':          'Si choisi, nous vous mettrons en contact direct avec le client pour finaliser.',
    'signup.success.cta.dashboard':       'Connexion au tableau de bord',

    // Round 21 (2026-05-10) — FR mirrors of Phase 1A customer-facing keys.
    'booking.pill.event_completed':       'ÉVÉNEMENT TERMINÉ',
    'booking.pill.match_confirmed':       'JUMELAGE CONFIRMÉ',
    'booking.section.venue_contact':      'CONTACT DU LIEU',
    'booking.section.add_to_calendar':    'AJOUTER AU CALENDRIER',
    'booking.booked_for':                 'Réservé pour {{datetime}} · {{duration}}',
    'booking.calendar.google':            'Google Agenda',
    'booking.calendar.outlook':           'Outlook',
    'booking.calendar.download_ics':      'Télécharger .ics',
    'booking.carousel.prev':              'Photo précédente',
    'booking.carousel.next':              'Photo suivante',
    'booking.intake.notes_placeholder':   'ex. Nous préférons le crabe au homard, et il nous faut 5 plats véganes...',

    'feedback.heading.readonly':          'Votre avis',
    'feedback.heading.editable':          'Comment était votre événement ?',
    'feedback.placeholder':               'Dites-nous-en plus (facultatif)…',
    'feedback.submit':                    'Envoyer mon avis',
    'feedback.update':                    'Mettre à jour',
    'feedback.edit':                      'Modifier l’avis',
    'feedback.submitting':                'Envoi en cours…',
    'feedback.submitted_prefix':          'Envoyé {{time}}',
    'feedback.hint':                      'Votre avis est transmis directement à l’équipe événements du lieu.',
    'feedback.error.no_rating':           'Veuillez choisir une note de 1 à 5 verres de vin.',
    'feedback.error.submit_failed':       'Échec de l’envoi de l’avis. Réessayez.',
    'feedback.relative.just_now':         'à l’instant',
    'feedback.relative.minutes_ago':      'il y a {{count}} min',
    'feedback.relative.hours_ago':        'il y a {{count}} h',
    'feedback.glass_aria':                'Noter {{n}} sur 5',

    'diff.what_changed':                  'Ce qui a changé',
    'diff.party_size':                    'Nombre d’invités',
    'diff.dates':                         'Dates',
    'diff.budget':                        'Budget',
    'diff.time':                          'Heure',
    'diff.duration':                      'Durée',
    'diff.guests_singular':               '{{n}} invité',
    'diff.guests_plural':                 '{{n}} invités',
    'diff.guests_range':                  '{{min}}–{{max}} invités',
    'diff.hours_singular':                '{{n}} h',
    'diff.hours_plural':                  '{{n}} h',

    // Round 21 Phase 1B (2026-05-10) — FR mirror of venue dashboard strings.
    'dashboard.analytics.stat.booked':         'Réservés',
    'dashboard.analytics.stat.completed':      'Terminés',
    'dashboard.analytics.stat.lost':           'Perdus',
    'dashboard.analytics.lost_reasons.title':       'Pourquoi les pistes ont été perdues',
    'dashboard.analytics.lost_reasons.description': 'Inclut les pistes qui n’ont pas converti (sans suivi, parties ailleurs) et les réservations annulées après confirmation.',
    'dashboard.analytics.lost_reasons.show_less':   'Afficher moins',
    'dashboard.analytics.detail.event_summary':     'Résumé de l’événement',
    'dashboard.analytics.detail.event_notes':       'Notes de l’événement',
    'dashboard.analytics.detail.min_spend':         'Dépense min.',
    'dashboard.analytics.detail.actual_spend':      'Dépense réelle',
    'dashboard.analytics.detail.rental_fee':        'Acompte',
    'dashboard.analytics.detail.confirmed_for':     'Confirmé pour {{date}} · {{time}}{{duration}}',
    'dashboard.analytics.lost_reasons.show_more':   'Voir les {{total}} raisons (+{{overflow}} de plus)',

    'dashboard.analytics.customer_list.heading_count':                'Clients ({{count}})',
    'dashboard.analytics.customer_list.history.heading_count':        'Historique de réservations ({{count}})',
    'dashboard.analytics.customer_list.badge.re_engaged':             'RÉACTIVÉ',
    'dashboard.analytics.customer_list.badge.repeat_count':           'RÉCURRENT · {{count}}',
    'dashboard.analytics.customer_list.badge.corporate_count':        'CORPORATIF × {{count}}',
    'dashboard.analytics.customer_list.badge.personal_count':         'PERSONNEL × {{count}}',
    'dashboard.analytics.customer_list.status.completed':             'Terminé',
    'dashboard.analytics.customer_list.status.booked':                'Réservé',
    'dashboard.analytics.customer_list.status.lost':                  'Perdu',
    'dashboard.analytics.customer_list.status.lost_with_reason':      'Perdu · {{reason}}',
    'dashboard.analytics.customer_list.status.rejected':              'Non sélectionné',
    'dashboard.analytics.customer_list.status.expired':               'Expiré',
    'dashboard.analytics.customer_list.status.withdrawn':             'Retiré',
    'dashboard.analytics.customer_list.cancel_reason.unspecified':    'Non spécifié',
    'dashboard.analytics.customer_list.cancel_reason.no_response':    'Pas de suivi',
    'dashboard.analytics.customer_list.cancel_reason.chose_elsewhere':'A choisi ailleurs',
    'dashboard.analytics.customer_list.cancel_reason.price_disagreement':'Désaccord sur le prix',
    'dashboard.analytics.customer_list.cancel_reason.date_unavailable':'Date indisponible',
    'dashboard.analytics.customer_list.cancel_reason.event_postponed':'Événement reporté',
    'dashboard.analytics.customer_list.cancel_reason.event_cancelled':'Client annulé',
    'dashboard.analytics.customer_list.cancel_reason.other':          'Autre',
    'dashboard.analytics.customer_list.last_corporate.label':         'Dernière réservation corporative',
    'dashboard.analytics.customer_list.toggle.history':               'Historique',
    'dashboard.analytics.customer_list.toggle.hide':                  'Masquer',

    'dashboard.feedback.title':            'Avis des clients',
    'dashboard.feedback.review_singular':  'avis',
    'dashboard.feedback.review_plural':    'avis',
    'dashboard.feedback.highlights':       'Faits saillants (5★)',
    'dashboard.feedback.concerns':         'À améliorer (1-2★)',
    'dashboard.feedback.view_all':         'Voir les {{total}} avis',
    'dashboard.feedback.view_less':        'Masquer',

    'dashboard.proposals.section.in_negotiation': 'En négociation',

    'dashboard.settings.transfer.email_prompt': 'Veuillez saisir une adresse courriel',
    'dashboard.settings.transfer.phone_prompt': 'Veuillez saisir un numéro de téléphone',
    'dashboard.settings.transfer.success':      'Accès au lieu transféré ! Le nouveau gestionnaire recevra un lien de connexion par {{via}}.',
    'dashboard.settings.transfer.error':        'Échec du transfert de l’accès au lieu',
    'dashboard.settings.cleanup.success':       '{{proposals}} propositions et {{bookings}} demandes de réservation nettoyées',
    'dashboard.settings.cleanup.error':         'Échec du nettoyage',

    'dashboard.bookings.event_completed':       'Événement marqué comme terminé',
    'dashboard.bookings.complete_error':        'Échec du marquage comme terminé',

    'dashboard.error.load_venue':               'Échec du chargement du lieu',
    'dashboard.error.login_retry':              'Veuillez vous reconnecter.',

    // Round 21 Phase 5A (2026-05-10) — FR mirror.
    'settings.venue_info.language':                  'Langue des notifications',
    'settings.venue_info.language.hint':             'Langue utilisée pour vos notifications du tableau de bord (OTP, alertes de réservation, courriels de lien magique). Détection automatique par défaut selon l’indicatif téléphonique.',
    'settings.venue_info.language.auto':             'Auto (selon le téléphone)',
    'settings.venue_info.language.en':               'English',
    'settings.venue_info.language.fr':               'Français',
    'settings.venue_info.language.es':               'Español',
    'settings.venue_info.language.it':               'Italiano',

    // Round 21 Phase 1C (2026-05-10) — FR mirror of modal + heavy-form strings.
    'edit_booking.title':              'Modifier les détails de la réservation',
    'edit_booking.close':              'Fermer',
    'edit_booking.guest_count':        'Nombre d’invités',
    'edit_booking.agreed_date':        'Date de l’événement convenue',
    'edit_booking.start_time':         'Heure de début',
    'edit_booking.duration_hrs':       'Durée (h)',
    'edit_booking.contact_locked':     'Le nom, le téléphone et le courriel du client sont verrouillés. Pour mettre à jour les coordonnées, modifiez dans {{crmName}}.',
    'edit_booking.notes':              'Notes sur l’événement',
    'edit_booking.notes_placeholder':  'Commentaires du client, suivis, à retenir pour la prochaine fois…',
    'edit_booking.saving':             'Enregistrement…',
    'edit_booking.save':               'Enregistrer',
    'edit_booking.cancel':             'Annuler',
    'edit_booking.toast.saved':        'Réservation mise à jour',
    'edit_booking.toast.error':        'Échec de l’enregistrement',

    'proposal_submit.fulfilling_from':       'Livré depuis',
    'proposal_submit.location_placeholder':  'Choisir un lieu…',
    'proposal_submit.customer_asked_for':    'Le client a demandé :',
    'proposal_submit.photos.heading':        'Photos de la proposition',
    'proposal_submit.photos.label':          'Photo de la proposition',
    'proposal_submit.photos.default_hero':   'Image principale par défaut',
    'proposal_submit.photos.empty_settings_hint': 'Ajoutez des photos de traiteur dans Paramètres pour en choisir pour chaque proposition.',
    'proposal_submit.photos.none_selected':  'Aucune sélectionnée — l’image principale du lieu sera affichée. Cliquez sur les photos pour les inclure.',
    'proposal_submit.photos.deselect_hint':  'Cliquez sur une photo pour la désélectionner. Les photos sélectionnées apparaîtront sur la carte de proposition vue par le client.',

    'catering_config.heading':             'Configuration du traiteur',
    'catering_config.cuisine.heading':     'Types de cuisine',
    'catering_config.cuisine.placeholder': 'Saisir un type et appuyer sur Entrée',
    'catering_config.min_order':           'Commande minimum',
    'catering_config.service_area':        'Zone de service',
    'catering_config.service_area.placeholder': 'ex. Région du Grand Toronto',
    'catering_config.delivery_radius':     'Rayon de livraison :',
    'catering_config.km':                  '{{km}} km',
    'catering_config.radius_min':          '1 km',
    'catering_config.radius_max':          '100 km',
    'catering_config.show_service_area':   'Afficher la zone de service sur le profil traiteur',
    'catering_config.show_service_area.hint': 'Les clients peuvent saisir leur adresse pour vérifier si vous livrez chez eux',
    'catering_config.packages.heading':    'Forfaits de menu',
    'catering_config.packages.add':        '+ Ajouter un forfait',
    'catering_config.packages.name_placeholder': 'Nom du forfait',
    'catering_config.packages.desc_placeholder': 'Description (plats inclus, infos diététiques, etc.)',
    'catering_config.packages.per_person': '$/personne',
    'catering_config.packages.empty':      'Aucun forfait pour l’instant. Ajoutez-en un pour présenter vos options.',
    'catering_config.photos.heading':      'Photos des plats',
    'catering_config.photos.caption_placeholder': 'Ajouter une légende...',
    'catering_config.photos.uploading':    'Téléversement...',
    'catering_config.photos.add_button':   'Ajouter des photos',
    'catering_config.photos.hint':         'Mettez vos plats en vedette. Les photos s’affichent en diaporama sur votre profil traiteur.',
    'catering_config.toast.max_photos':    'Maximum 20 photos',
    'catering_config.toast.file_type_invalid': '{{filename}} : seuls JPEG, PNG et WebP sont autorisés',
    'catering_config.toast.upload_success': '{{count}} photo(s) téléversée(s)',
    'catering_config.toast.saved':         'Paramètres traiteur enregistrés',
    'catering_config.toast.upload_failed': 'Échec du téléversement',
    'catering_config.toast.max_menus':     'Maximum 10 menus',
    'catering_config.toast.menu_uploaded': 'Menu téléversé',
    'catering_config.toast.save_failed':   'Échec de l’enregistrement',
    'signup.success.cta.home':            'Retour à l\u2019accueil',

    // /admin/groups/new — Assistant de configuration des groupes d\u2019hospitalité
    'admin.groups.new.title':              'Créer un nouveau groupe',
    'admin.groups.new.subtitle':           'Configurez un groupe, son lieu parent, ses lieux sœurs et le personnel.',
    'admin.groups.new.progress':           'Étape {{current}} sur {{total}}',
    'admin.groups.new.back':               'Précédent',
    'admin.groups.new.continue':           'Continuer',
    'admin.groups.new.submit':             'Ouvrir la PR de staging',
    'admin.groups.new.draft.creating':     'Création du brouillon\u2026',
    'admin.groups.new.draft.saving':       'Enregistrement\u2026',
    'admin.groups.new.draft.error':        'Impossible d\u2019enregistrer votre progression. Réessayer ?',
    'admin.groups.new.draft.load_error':   'Impossible de charger le brouillon.',
    'admin.groups.new.required':           'Obligatoire',
    'admin.groups.new.step1.title':        'Choisissez un archétype',
    'admin.groups.new.step1.intro':        'Un lieu ou plusieurs ? Cette réponse oriente la suite de l\u2019assistant.',
    'admin.groups.new.step1.standalone.label':       'Lieu autonome',
    'admin.groups.new.step1.standalone.description': 'Un seul lieu, un téléphone, un widget. Utilisez plutôt /admin/venues/new.',
    'admin.groups.new.step1.hospitality.label':      'Groupe d\u2019hospitalité',
    'admin.groups.new.step1.hospitality.description':'Lieu parent + lieux sœurs, équipe événements partagée. (Recommandé)',
    'admin.groups.new.step1.franchise.label':        'Franchise',
    'admin.groups.new.step1.franchise.description':  'Une marque, plusieurs emplacements, gestionnaires par emplacement. Utilisez /admin/venues/new (mode franchise).',
    'admin.groups.new.step1.redirect_notice':        'Cet archétype est géré par /admin/venues/new. Nous vous redirigerons.',
    'admin.groups.new.step2.title':        'Identité du groupe',
    'admin.groups.new.step2.name.label':   'Nom du groupe',
    'admin.groups.new.step2.name.help':    'Le nom légal ou commercial affiché dans le fil d\u2019Ariane du tableau de bord.',
    'admin.groups.new.step2.slug.label':   'Identifiant (slug)',
    'admin.groups.new.step2.slug.help':    'Identifiant compatible URL. Dérivé automatiquement du nom; modifiable.',
    'admin.groups.new.step2.parent.label': 'Lieu parent',
    'admin.groups.new.step2.parent.yes':   'Oui — le groupe a un lieu parent qui reçoit ses propres réservations',
    'admin.groups.new.step2.parent.no':    'Non — le groupe est un conteneur organisationnel uniquement',
    'admin.groups.new.step2.error.slug':   'Le slug doit contenir seulement des lettres minuscules, chiffres ou tirets.',
    'admin.groups.new.step2.error.name':   'Le nom est obligatoire.',
    'admin.groups.new.step3.title':        'Lieu parent',
    'admin.groups.new.step3.intro':        'Liez à un lieu existant ou créez-en un dans une étape suivante.',
    'admin.groups.new.step3.link.label':   'Lieu existant',
    'admin.groups.new.step3.link.placeholder': 'Choisir un lieu existant\u2026',
    'admin.groups.new.step3.new.label':    'Créer un nouveau lieu parent après cet assistant',
    'admin.groups.new.step3.skip':         'Ignoré — le groupe n\u2019a pas de lieu parent.',
    'admin.groups.new.step4.title':        'Lieux sœurs',
    'admin.groups.new.step4.intro':        'Ajoutez chaque lieu qui appartient à ce groupe. L\u2019ID du groupe est hérité automatiquement.',
    'admin.groups.new.step4.add':          'Ajouter un lieu sœur',
    'admin.groups.new.step4.placeholder':  'Slug du lieu sœur (ex. akiro)',
    'admin.groups.new.step4.existing.label':  'Lier à un lieu existant',
    'admin.groups.new.step4.new.label':    'Espace réservé — à créer plus tard',
    'admin.groups.new.step4.empty':        'Aucun lieu sœur ajouté.',
    'admin.groups.new.step4.error.duplicate': 'Slug en doublon.',
    'admin.groups.new.step4.error.invalid':   'Le slug doit contenir seulement des lettres minuscules, chiffres ou tirets.',
    'admin.groups.new.step4.remove.aria':  'Retirer le lieu sœur',
    'admin.groups.new.step5.title':        'Personnel',
    'admin.groups.new.step5.intro':        'Qui gère ce groupe ? Ajoutez au moins un gestionnaire d\u2019événements.',
    'admin.groups.new.step5.add_group':    'Ajouter un gestionnaire d\u2019événements',
    'admin.groups.new.step5.add_venue':    'Ajouter un gestionnaire de lieu',
    'admin.groups.new.step5.name.label':   'Nom',
    'admin.groups.new.step5.phone.label':  'Téléphone (E.164)',
    'admin.groups.new.step5.phone.placeholder': '+15551234567',
    'admin.groups.new.step5.email.label':  'Courriel',
    'admin.groups.new.step5.venue.label':  'Lieu',
    'admin.groups.new.step5.channel.label':'Canal de notification',
    'admin.groups.new.step5.channel.sms':  'SMS',
    'admin.groups.new.step5.channel.email':'Courriel',
    'admin.groups.new.step5.channel.both': 'Les deux',
    'admin.groups.new.step5.empty':        'Aucun membre du personnel ajouté.',
    'admin.groups.new.step5.remove.aria':  'Retirer l\u2019entrée',
    'admin.groups.new.step5.role.group_manager':    'Gestionnaire d\u2019événements (groupe)',
    'admin.groups.new.step5.role.venue_manager':    'Gestionnaire de lieu',
    'admin.groups.new.step5.role.location_manager': 'Gestionnaire d\u2019emplacement',
    'admin.groups.new.step5.error.required':        'Au moins un gestionnaire d\u2019événements est requis.',
    'admin.groups.new.step5.error.phone':           'Le téléphone doit être au format E.164 (+1\u2026).',
    'admin.groups.new.step5.error.duplicate_phone': 'Ce numéro est déjà dans la liste.',
    'admin.groups.new.step6.title':        'Révision',
    'admin.groups.new.step6.intro':        'Confirmez le manifeste assemblé ci-dessous. Revenez en arrière pour corriger.',
    'admin.groups.new.step6.preview':      'Manifeste généré',
    'admin.groups.new.step6.validation.title': 'Validation du manifeste',
    'admin.groups.new.step6.validation.ok':    'Manifeste valide.',
    'admin.groups.new.step6.validation.errors':'Corrigez les éléments suivants avant de soumettre :',
    'admin.groups.new.step6.submit_hint':   'La soumission ouvre une PR GitHub vers la branche staging. Fusionner déclenche le déploiement.',
    'admin.groups.new.step7.title':        'PR de staging',
    'admin.groups.new.step7.intro':        'L\u2019assistant a ouvert cette PR. Vérifiez le diff, fusionnez, observez la CI.',
    'admin.groups.new.step7.pr_url.label': 'Pull request',
    'admin.groups.new.step7.pr_url.open':  'Ouvrir sur GitHub',
    'admin.groups.new.step7.refresh':      'Actualiser le statut',
    'admin.groups.new.step7.checking':     'Vérification\u2026',
    'admin.groups.new.step7.error':        'Échec de la soumission :',
    'admin.groups.new.step8.title':        'Production',
    'admin.groups.new.step8.intro':        'Une fois la PR de staging fusionnée et la vérification réussie, lancez /go-live pour promouvoir en production.',
    'admin.groups.new.step8.gate':         'L\u2019assistant ne promeut pas en production automatiquement — staging d\u2019abord.',
    'admin.groups.new.step8.done':         'Terminé',

    // /admin/groups/[id]
    'admin.groups.detail.title':           '{{name}}',
    'admin.groups.detail.subtitle':        'Identifiant du groupe : {{slug}}',
    'admin.groups.detail.archetype':       'Archétype',
    'admin.groups.detail.parent':          'Établissement principal',
    'admin.groups.detail.no_parent':       'Aucun établissement principal',
    'admin.groups.detail.loading':         'Chargement du groupe…',
    'admin.groups.detail.error':           'Impossible de charger le groupe',
    'admin.groups.detail.back':            '← Tous les groupes',
    'admin.groups.detail.tab.members':     'Membres',
    'admin.groups.detail.tab.staff':       'Personnel',
    'admin.groups.detail.tab.audit':       'Audit',
    'admin.groups.detail.tab.manifest':    'Manifeste',
    'admin.groups.detail.members.empty':   'Aucun établissement membre.',
    'admin.groups.detail.members.col.name':   'Nom',
    'admin.groups.detail.members.col.slug':   'Identifiant',
    'admin.groups.detail.members.col.status': 'Statut',
    'admin.groups.detail.members.manage':  'Gérer',
    'admin.groups.detail.staff.empty':     'Aucun membre du personnel rattaché.',
    'admin.groups.detail.staff.col.name':    'Nom',
    'admin.groups.detail.staff.col.phone':   'Téléphone',
    'admin.groups.detail.staff.col.role':    'Rôle',
    'admin.groups.detail.staff.col.channel': 'Notification',
    'admin.groups.detail.staff.edit':      'Modifier',
    'admin.groups.detail.audit.empty':     'Aucun événement d’audit pour ce groupe.',
    'admin.groups.detail.manifest.empty':  'Aucun aperçu de manifeste disponible.',
    'admin.groups.detail.unlink_cta':      'Dissocier l’établissement du groupe',
    'admin.groups.detail.unlink.confirm.title': 'Dissocier cet établissement ?',
    'admin.groups.detail.unlink.confirm.body':  'La dissociation retire l’établissement de ce groupe. Ses données sont conservées et la liaison peut être rétablie plus tard.',
    'admin.groups.detail.unlink.confirm.cta':   'Dissocier',
    'admin.groups.detail.unlink.cancel':   'Annuler',

    // /admin/staff — liste
    'admin.staff.list.add_button':         '+ Ajouter du personnel',
    'admin.staff.list.row.tooltip':        'Ouvrir le membre',

    // /admin/staff/new
    'admin.staff.new.title':               'Ajouter un membre du personnel',
    'admin.staff.new.subtitle':            'Créer une nouvelle ligne admin. Vous pourrez affiner les notifications après création.',
    'admin.staff.new.field.name.label':    'Nom',
    'admin.staff.new.field.name.placeholder': 'Nom affiché',
    'admin.staff.new.field.phone.label':   'Téléphone de connexion (E.164)',
    'admin.staff.new.field.phone.placeholder': '+15551234567',
    'admin.staff.new.field.phone.help':    'Format E.164 — indicatif + chiffres, sans espaces.',
    'admin.staff.new.field.notification_email.label': 'E-mail de notification',
    'admin.staff.new.field.notification_email.placeholder': 'staff@example.com',
    'admin.staff.new.field.notification_channel.label': 'Canal de notification',
    'admin.staff.new.channel.sms':         'SMS',
    'admin.staff.new.channel.email':       'E-mail',
    'admin.staff.new.channel.both':        'SMS + E-mail',
    'admin.staff.new.field.role.label':    'Rôle',
    'admin.staff.new.role.group_manager':  'Gestionnaire de groupe',
    'admin.staff.new.role.venue_manager':  'Gestionnaire de lieu',
    'admin.staff.new.role.location_manager': 'Gestionnaire d’établissement',
    'admin.staff.new.role.help.group_manager':    'Voit tous les lieux des groupes sélectionnés.',
    'admin.staff.new.role.help.venue_manager':    'Voit toutes les réservations des lieux sélectionnés.',
    'admin.staff.new.role.help.location_manager': 'Voit uniquement les réservations des paires listées.',
    'admin.staff.new.field.scope.groups.label':   'Groupes gérés',
    'admin.staff.new.field.scope.groups.help':    'Sélectionnez un ou plusieurs groupes. Le manager voit tous leurs lieux.',
    'admin.staff.new.field.scope.venues.label':   'Lieux gérés',
    'admin.staff.new.field.scope.venues.help':    'Sélectionnez un ou plusieurs lieux. Le manager voit toutes leurs réservations.',
    'admin.staff.new.field.scope.locations.label': 'Établissements gérés',
    'admin.staff.new.field.scope.locations.help':  'Choisissez un lieu, puis un établissement, puis ajoutez la paire. Répétez pour chaque établissement.',
    'admin.staff.new.picker.search.groups':        'Rechercher des groupes…',
    'admin.staff.new.picker.search.venues':        'Rechercher des lieux…',
    'admin.staff.new.picker.loading.groups':       'Chargement des groupes…',
    'admin.staff.new.picker.loading.venues':       'Chargement des lieux…',
    'admin.staff.new.picker.error.groups':         'Impossible de charger les groupes. Rafraîchissez pour réessayer.',
    'admin.staff.new.picker.error.venues':         'Impossible de charger les lieux. Rafraîchissez pour réessayer.',
    'admin.staff.new.picker.empty.groups':         'Aucun groupe trouvé.',
    'admin.staff.new.picker.empty.venues':         'Aucun lieu trouvé.',
    'admin.staff.new.picker.empty.locations':      'Aucun des lieux disponibles n’a d’établissements configurés.',
    'admin.staff.new.picker.location.venue.label': 'Lieu',
    'admin.staff.new.picker.location.venue.placeholder': 'Sélectionnez un lieu…',
    'admin.staff.new.picker.location.slug.label':  'Établissement',
    'admin.staff.new.picker.location.slug.placeholder': 'Sélectionnez un établissement…',
    'admin.staff.new.picker.location.add':         '+ Ajouter l’établissement',
    'admin.staff.new.picker.location.added':       'Établissements ajoutés',
    'admin.staff.new.picker.location.remove.aria': 'Retirer l’établissement',
    'admin.staff.new.picker.selected.count':       '{{count}} sélectionnés',
    'admin.staff.new.submit':              'Créer le membre',
    'admin.staff.new.submitting':          'Création…',
    'admin.staff.new.cancel':              'Annuler',
    'admin.staff.new.error.generic':       'Impossible de créer le membre — vérifiez les champs.',
    'admin.staff.new.error.conflict':      'Un membre avec ce téléphone existe déjà.',
    'admin.staff.new.error.scope_required.group_manager':    'Sélectionnez au moins un groupe.',
    'admin.staff.new.error.scope_required.venue_manager':    'Sélectionnez au moins un lieu.',
    'admin.staff.new.error.scope_required.location_manager': 'Ajoutez au moins une paire {venueId, locationSlug}.',
    'admin.staff.new.error.invalid_phone':  'Le téléphone doit être au format E.164 (ex. +15551234567).',
    'admin.staff.new.error.invalid_locations_json': 'Les établissements doivent être un JSON valide — tableau d’objets {venueId, locationSlug}.',
    'admin.staff.new.success':              'Membre créé.',

    // /admin/staff/[id]
    'admin.staff.detail.title':            '{{name}}',
    'admin.staff.detail.subtitle':         'Détail du membre du personnel',
    'admin.staff.detail.loading':          'Chargement du personnel…',
    'admin.staff.detail.error':            'Impossible de charger le membre',
    'admin.staff.detail.back':             '← Tout le personnel',
    'admin.staff.detail.section.identity': 'Identité',
    'admin.staff.detail.section.access':   'Rôle et périmètre',
    'admin.staff.detail.section.notifications': 'Notifications',
    'admin.staff.detail.section.login_history': 'Historique de connexion',
    'admin.staff.detail.section.audit':    'Activité récente',
    'admin.staff.detail.section.actions':  'Actions',
    'admin.staff.detail.field.name':       'Nom',
    'admin.staff.detail.field.phone':      'Téléphone',
    'admin.staff.detail.field.email':      'E-mail',
    'admin.staff.detail.field.role':       'Rôle',
    'admin.staff.detail.field.disabled':   'Désactivé',
    'admin.staff.detail.field.scope.venues': 'Établissements gérés',
    'admin.staff.detail.field.scope.groups': 'Groupes gérés',
    'admin.staff.detail.field.scope.locations': 'Sites gérés',
    'admin.staff.detail.field.scope.empty':  'Aucun périmètre attribué',
    'admin.staff.detail.field.notify_phone': 'Téléphone de notification',
    'admin.staff.detail.field.notify_email': 'E-mail de notification',
    'admin.staff.detail.field.notify_channel': 'Canal de notification',
    'admin.staff.detail.field.last_login':  'Dernière connexion',
    'admin.staff.detail.field.created':     'Créé le',
    'admin.staff.detail.login_history.empty':   'L’historique de connexion n’est pas encore suivi.',
    'admin.staff.detail.audit.empty':       'Aucune activité récente.',
    'admin.staff.detail.role.platform_admin':  'Admin plateforme',
    'admin.staff.detail.role.group_manager':   'Gestionnaire de groupe',
    'admin.staff.detail.role.venue_manager':   'Gestionnaire d’établissement',
    'admin.staff.detail.role.location_manager':'Gestionnaire de site',
    'admin.staff.detail.channel.sms':       'SMS',
    'admin.staff.detail.channel.email':     'E-mail',
    'admin.staff.detail.channel.both':      'SMS + E-mail',
    'admin.staff.detail.save':              'Enregistrer',
    'admin.staff.detail.saving':            'Enregistrement…',
    'admin.staff.detail.save_ok':           'Modifications enregistrées',
    'admin.staff.detail.save_fail':         'Impossible d’enregistrer',
    'admin.staff.detail.disable':           'Désactiver',
    'admin.staff.detail.disable.confirm':   'Désactiver ce membre ? Il perdra tout accès jusqu’à réactivation.',
    'admin.staff.detail.enable':            'Activer',
    'admin.staff.detail.rotate_phone':      'Changer le téléphone',
    'admin.staff.detail.rotate_phone.confirm': 'Changer le téléphone de connexion ? Le membre devra se connecter avec le nouveau numéro.',
    'admin.staff.detail.rotate_phone.prompt':  'Nouveau numéro (format E.164, ex. +15551234567) :',
    'admin.staff.detail.force_reauth':       'Forcer la reconnexion',
    'admin.staff.detail.force_reauth.confirm': 'Forcer ce membre à se reconnecter ? Ses sessions actuelles seront invalidées.',
    'admin.staff.detail.force_reauth.ok':    'Sessions invalidées',
    'admin.staff.detail.delete':             'Supprimer',
    'admin.staff.detail.delete.confirm':     'Supprimer définitivement ce membre ? Action irréversible. Tapez son téléphone pour confirmer.',

    // /admin/audit — filtres + export CSV + flux temps réel
    'admin.audit.filter.title':            'Filtres',
    'admin.audit.filter.actor':            'Acteur',
    'admin.audit.filter.actor.placeholder':'ID ou rôle d’acteur',
    'admin.audit.filter.action':           'Action',
    'admin.audit.filter.action.placeholder':'ex. submit_proposal',
    'admin.audit.filter.target_type':      'Type de cible',
    'admin.audit.filter.target_type.all':  'Tous les types',
    'admin.audit.filter.date_from':        'Du',
    'admin.audit.filter.date_to':          'Au',
    'admin.audit.filter.apply':            'Appliquer',
    'admin.audit.filter.reset':            'Réinitialiser',
    'admin.audit.filter.open':             'Filtrer',
    'admin.audit.csv_export':              'Exporter en CSV',
    'admin.audit.csv_export.empty':        'Rien à exporter.',
    'admin.audit.live_toggle':             'Direct',
    'admin.audit.live_toggle.aria':        'Activer le suivi en direct',
    'admin.audit.row.expand':              'Voir le détail',
    'admin.audit.row.collapse':            'Fermer',
    'admin.audit.row.payload':             'Charge utile',
    'admin.audit.row.impersonated':        'Impersonné en tant que',
  },
  // Round 21 (2026-05-10) — Spanish (neutral Latin-American) translations.
  // Auto-generated by Claude; spot-review recommended for marketing copy.
  // Placeholders ({{name}}, {{count}}, etc.) preserved exactly.
  // Coverage: high-traffic namespaces (booking.*, feedback.*, diff.*, widget.*,
  // pricing.*, dashboard.*, edit_booking.*, catering_config.*, proposal_submit.*,
  // settings.venue_info.language.*). Other namespaces (marketing.*, auth.login.*,
  // signup.*, venue_profile.*, settings.* sub-forms) fall back to EN — to be
  // backfilled in a follow-up batch.
  es: {
    // widget.* — voice/chat widget chrome
    'widget.lang.toggle.aria':     'Cambiar idioma',
    'widget.lang.en':              'English',
    'widget.lang.fr':              'Français',
    'widget.idle.title.default':   'Planea un evento en {{venueName}}',
    'widget.group.other.label':    '¿Te interesan nuestras otras ubicaciones?',
    'widget.group.other.aria':     'Otras ubicaciones',
    'widget.idle.description':     'Llama o chatea con nuestro equipo.',
    'widget.idle.call':            'Llamar',
    'widget.idle.chat':            'Chatear',
    'widget.callMode.speakHint':   'Puedes escribir tus datos — o simplemente decirlos en voz alta.',
    'widget.idle.loudroom':        'Lugar Ruidoso — toca para hablar',
    'widget.idle.loudroom.hint':   'Para bares + restaurantes — el micrófono inicia silenciado, presiona para hablar.',
    'widget.idle.mic_blocked':     'Micrófono bloqueado — puedes chatear con texto abajo.',
    'widget.active.mic_muted':     'Silenciado',
    'widget.active.mic_unmuted':   'Escuchando',
    'widget.active.agent.speaking':'Asistente hablando',
    'widget.active.agent.thinking':'Pensando…',
    'widget.active.type_hint':     'Escribe tu mensaje…',
    'widget.active.send':          'Enviar',
    'widget.active.end':           'Finalizar',
    'widget.active.mute':          'Silenciar',
    'widget.active.unmute':        'Activar',
    'widget.active.hold_to_talk':  'Mantener para hablar',
    'widget.active.switch_to_chat':'Cambiar a chat',
    'widget.active.switch_to_voice':'Cambiar a voz',
    'widget.active.no_audio':      '¿Sin audio? Toca para cambiar a chat.',
    'widget.active.reconnect':     'Audio interrumpido — toca para reconectar.',
    'widget.ended.thanks':         'Gracias — el equipo de {{venueName}} se pondrá en contacto.',
    'widget.ended.restart':        'Hacer otra pregunta',
    'widget.ended.view_booking':   'Ver tu reserva',
    'widget.ended.status.sent':    'Enviado al equipo — nos pondremos en contacto pronto',
    'widget.ended.status.cancelled':'Conversación finalizada',

    // booking.* — customer booking status page (/booking/[id])
    'booking.title':               'Tu reserva con {{venueName}}',
    'booking.headline.default':    'Tu reserva',
    'booking.headline.greeting':   'Hola {{name}}',
    'booking.headline.anon':       'Tu solicitud de reserva',
    'booking.headline.direct_event':    'Reserva directa con',
    'booking.headline.direct_catering': 'Catering directo con',
    'booking.fallback.venue':      'lugar',
    'booking.event_guide.cta':     'Ver la guía de eventos de {{location}}',
    'booking.pricing.caveat':      'El equipo de eventos confirmará el precio final por correo.',
    'booking.venue_reply.heading': 'Respuesta de {{venue}}',
    'booking.venue_reply.via.email': 'Enviado por correo',
    'booking.venue_reply.via.sms':   'Enviado por mensaje',

    'booking.step.intake_in_progress': 'Recopilando detalles',
    'booking.step.intake_complete':    'Detalles completos',
    'booking.step.awaiting_response':  'Esperando respuesta',
    'booking.step.finding_venues':     'Buscando lugares',
    'booking.step.proposals_received': 'Propuestas recibidas',
    'booking.step.consumer_selecting': 'Selecciona tu lugar',
    'booking.step.consumer_confirmed': 'Confirmado — pago pendiente',
    'booking.step.booked':             '¡Emparejado!',

    'booking.status.pending':      'Recibimos tu solicitud.',
    'booking.status.reviewing':    'Buscando lugares para ti…',
    'booking.status.proposals':    'Tienes propuestas para revisar.',
    'booking.status.selected':     'Reserva confirmada.',
    'booking.status.completed':    'Evento completado. ¡Esperamos que haya sido genial!',

    'booking.loading':             'Cargando tu reserva…',
    'booking.error.not_found':     'No encontramos esta reserva. Revisa tu enlace e intenta de nuevo.',
    'booking.error.load_failed':   'No se pudieron cargar los detalles: {{message}}',
    'booking.not_found':           'Reserva no encontrada.',
    'booking.preparing.title':     'Preparando tu reserva',
    'booking.preparing.line1':     'Gracias por contactarnos — estamos armando los detalles.',
    'booking.preparing.line2':     'Esta página se actualizará sola.',

    'booking.details.header.event':    'Detalles del evento',
    'booking.details.header.catering': 'Detalles del catering',
    'booking.details.audience':        'Audiencia',
    'booking.details.audience.company':  'Evento corporativo',
    'booking.details.audience.personal': 'Evento personal',
    'booking.details.date':            'Fecha',
    'booking.details.date.tbd':        'Por definir',
    'booking.details.time':            'Hora',
    'booking.details.guests':          'Invitados',
    'booking.details.duration':        'Duración',
    'booking.details.duration_hours':  '{{hours}} hora',
    'booking.details.duration_hours.plural': '{{hours}} horas',
    'booking.details.food':            'Comida',
    'booking.details.bar':             'Bar',
    'booking.details.music':           'Música',
    'booking.details.budget':          'Presupuesto',
    'booking.details.booking_type':    'Tipo de reserva',
    'booking.details.package':         'Paquete',
    'booking.details.email':           'Correo',
    'booking.details.phone':           'Teléfono',
    'booking.details.company':         'Empresa',
    'booking.details.delivery_address':'Dirección de entrega',
    'booking.details.service_type':    'Tipo de servicio',
    'booking.details.event_vision':    'Visión del evento',
    'booking.details.additional_notes':'Notas adicionales',
    'booking.details.preferences':     'Tus preferencias',
    'booking.details.dietary':         'Dietéticas',
    'booking.details.accessibility':   'Accesibilidad',
    'booking.details.booking_details': 'Detalles de la reserva',
    'booking.details.event_type':      'Tipo de evento',
    'booking.details.preferred_dates': 'Fechas preferidas',

    // R21 T2 (2026-05-10) — Customer-side intake field labels (ES).
    'booking.edit.event_type.placeholder':      'ej. Fiesta de cumpleaños',
    'booking.edit.min_guests':                  'Invitados mínimos',
    'booking.edit.max_guests':                  'Invitados máximos',
    'booking.edit.arrival_time':                'Hora de llegada',
    'booking.edit.arrival_time.placeholder':    'ej. 19:00',
    'booking.edit.duration':                    'Duración (horas)',
    'booking.edit.duration.placeholder':        'ej. 3',
    'booking.edit.email.placeholder':           'email@ejemplo.com',
    'booking.edit.company.placeholder':         'Nombre de empresa o "personal"',
    'booking.edit.delivery.placeholder':        'Empieza a escribir la dirección de entrega…',
    'booking.edit.select_placeholder':          'Selecciona…',
    'booking.edit.event_vision.placeholder':    'Describe tu visión del evento…',
    'booking.edit.notes.placeholder':           'Detalles adicionales…',
    'booking.edit.add_date':                    '+ Agregar',
    'booking.edit.save':                        'Guardar cambios',
    'booking.edit.cancel':                      'Cancelar',
    'booking.edit.save_failed':                 'Error al guardar los cambios',

    'booking.food.food_required':    'Comida requerida',
    'booking.food.drinks_only':      'Solo bebidas',
    'booking.food.food_and_drinks':  'Comida y bebidas',
    'booking.bar.cash_bar_minimum_spend': 'Bar pagado (consumo mínimo)',
    'booking.bar.open_bar':           'Barra libre',
    'booking.bar.consumption_tab':    'Cuenta abierta',
    'booking.bar.other':              'Otro',
    'booking.music.spotify_playlist': 'Lista de Spotify',
    'booking.music.dj':               'DJ',
    'booking.music.none':             'Sin preferencia',
    'booking.service.pickup':         'Recogida',
    'booking.service.delivery':       'Entrega',
    'booking.service.full_service':   'Servicio completo (con personal)',
    'booking.service.full_service.short': 'Servicio completo',

    // R21 T2 (2026-05-10) — Venue staff login + magic link auth (ES).
    'auth.login.title':                  'Acceso de lugar',
    'auth.login.subtitle':               'Inicia sesión para gestionar tus solicitudes.',
    'auth.login.tab.phone':              'Teléfono',
    'auth.login.tab.email':              'Correo',
    'auth.login.label.phone':            'Número de teléfono',
    'auth.login.label.email':            'Correo electrónico',
    'auth.login.label.code':             'Código de verificación',
    'auth.login.hint.phone':             'Ingresa el teléfono con el que te registraste.',
    'auth.login.hint.email':             'Te enviaremos un código de 6 dígitos para verificarte.',
    'auth.login.hint.code':              'Revisa tus mensajes por un código de 6 dígitos.',
    'auth.login.hint.code_email':        'Revisa tu correo, te enviamos un código a {{email}}.',
    'auth.login.button.send':            'Enviar código de verificación',
    'auth.login.button.verify':          'Iniciar sesión',
    'auth.login.button.resend':          'Reenviar código',
    'auth.login.button.different':       'Usar otro número',
    'auth.login.button.different_email': 'Usar otro correo',
    'auth.login.error.phone_required':   'Por favor ingresa un número de teléfono.',
    'auth.login.error.email_invalid':    'Por favor ingresa un correo válido.',
    'auth.login.error.phone_invalid':    'Por favor ingresa un teléfono válido.',
    'auth.login.error.code_invalid':     'Código inválido. Intenta de nuevo o solicita otro.',
    'auth.login.error.send_failed':      'No se pudo enviar el código. Intenta de nuevo.',
    'auth.login.error.session_expired':  'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
    'auth.login.signup.prompt':          '¿Eres un nuevo lugar?',
    'auth.login.signup.cta':             'Regístrate aquí',
    'auth.login.placeholder.phone':      '(555) 123-4567',
    'auth.login.placeholder.email':      'dueno@lugar.com',
    'auth.login.hint.email_account':     'Ingresa el correo de tu cuenta de lugar.',
    'auth.login.hint.code_whatsapp':     'Revisa tu WhatsApp, te enviamos un código a {{phone}}.',
    'auth.login.hint.code_sms':          'Te enviamos un código de 6 dígitos a {{phone}}.',
    'auth.login.cooldown':               'Reenviar código en {{seconds}}s',
    'auth.login.toast.resent':           '¡Código reenviado!',
    'auth.login.toast.resend_failed':    'Error al reenviar el código',

    'auth.magic.signing_in':             'Iniciando sesión…',
    'auth.magic.redirecting':            'Te redirigiremos a tu panel',
    'auth.magic.expired':                'Este enlace mágico ha expirado o es inválido.',
    'auth.magic.redirecting_login':      'Redirigiendo al inicio de sesión…',
    // Multi-venue plan §1136-1154 — Estado "Acceso modificado" (ES).
    'auth.magic.access_changed.title':       'Se modificó el acceso a este local',
    'auth.magic.access_changed.title_named': 'Se modificó el acceso a {{venue}}',
    'auth.magic.access_changed.message':     'Tu rol cambió recientemente y ya no tienes acceso a las reservas de este local. La reserva la está gestionando el propio local. Si esto es inesperado, contacta al administrador de tu grupo.',
    'auth.magic.access_changed.cta':         'Ir a tu panel',
    'auth.verify_email.verifying':       'Verificando tu correo…',
    'auth.verify_email.short':           'Verificando…',

    // R21 T2 (2026-05-10) — Dashboard chrome (nav, header, footer, error, status) (ES).
    'dashboard.nav.proposals':           'Propuestas',
    'dashboard.nav.bookings':            'Reservas',
    'dashboard.nav.inquiries':           'Consultas',
    'dashboard.nav.analytics':           'Analítica',
    'dashboard.nav.settings':            'Ajustes',
    'dashboard.header.admin_badge':      'Admin',
    'dashboard.header.admin_link':       '← Admin',
    'dashboard.header.signout':          'Cerrar sesión',
    'dashboard.header.switch_venue':     'Cambiar de lugar',
    'dashboard.footer.copyright':        '© 2026 BookOne.Party',
    'dashboard.footer.privacy':          'Privacidad',
    'dashboard.footer.terms':            'Términos',
    'dashboard.error.title':             'Algo salió mal',
    'dashboard.error.body':              'Encontramos un problema inesperado. Intenta de nuevo o recarga la página.',
    'dashboard.error.load_venue':        'Error al cargar el lugar',
    'dashboard.error.login_retry':       'Por favor intenta iniciar sesión de nuevo.',
    'dashboard.error.retry':             'Intentar de nuevo',
    'dashboard.status.active':           'Activo',
    'dashboard.status.paused':           'Pausado',
    'dashboard.status.unknown':          'Desconocido',
    'dashboard.common.loading':          'Cargando…',

    'booking.proposal.heading':       'Propuestas',
    'booking.proposal.review_heading':'En revisión — el lugar está revisando tus detalles actualizados',
    'booking.proposal.caterer_prompt':'¿Preguntas o preferencias para el catering?',
    'booking.proposal.capacity':      'Capacidad',
    'booking.proposal.min_spend':     'Consumo mínimo',
    'booking.proposal.min_spend_short':'CONSUMO MÍN.',
    'booking.proposal.per_person':    'POR PERSONA',
    'booking.proposal.per_person_est':'POR PERSONA (EST.)',
    'booking.proposal.fee':           'Cuota de reserva',
    'booking.proposal.dates':         'Fechas disponibles',
    'booking.proposal.notes':         'Notas',
    'booking.proposal.book':          'Reservar y pagar',
    'booking.proposal.book_free':     'Confirmar reserva',
    'booking.proposal.confirm_match': 'Confirmar y reservar',
    'booking.proposal.confirm_match.named': 'Confirmar y reservar — {{spaceName}}',
    'booking.proposal.confirm_action_required': 'Toca abajo para confirmar oficialmente tu reserva — todavía no está confirmada.',
    'booking.proposal.select_date_instruction': 'Elige la fecha y hora que mejor te convengan, luego toca Confirmar para reservar.',
    'booking.proposal.complete_payment': 'Completar pago',
    'booking.proposal.complete_payment.named': 'Completar pago — {{fee}}',
    'booking.proposal.payment_prompt':'Tendrás 24 horas para completar la cuota de reserva de {{fee}}.',
    'booking.proposal.pending':       'Esperando respuesta del lugar',
    'booking.proposal.space_options': '{{count}} opciones de espacio',
    'booking.proposal.match_confirmed':'¡Coincidencia confirmada!',
    'booking.proposal.view_venue':    'Ver perfil del lugar',
    'booking.proposal.other_pending': 'Otra propuesta está confirmada — completa el pago o espera a que expire',
    'booking.proposal.delivered_from':'Entregado desde',
    'booking.proposal.pickup_at':     'Recoger en',
    'booking.proposal.cta_catering_event':'El lugar se pondrá en contacto para finalizar los detalles del catering.',
    'booking.proposal.cta_catering_order':'El lugar te contactará para finalizar tu pedido de catering.',

    'proposal.field.min_spend':       'Consumo mín. ($)',
    'proposal.field.deposit':         'Depósito ($)',

    'booking.payment.confirm_failed':'No se pudo confirmar la reserva',
    'booking.payment.checkout_failed':'No se pudo iniciar el pago',

    'booking.countdown.expired':      'Expirado',
    'booking.countdown.remaining':    '{{hours}}h {{minutes}}m restantes para completar el pago',

    'booking.cta.need_help':          '¿Necesitas ayuda? Responde al correo de confirmación.',

    // booking.success.* — payment confirmation + status labels
    'booking.success.heading':        '¡Estás emparejado!',
    'booking.success.prompt':         'El lugar te contactará para finalizar tu reserva. Aquí están tus detalles.',
    'booking.success.reference':      'REFERENCIA DE RESERVA',
    'booking.success.venue':          'LUGAR',
    'booking.success.event_type':     'TIPO DE EVENTO',
    'booking.success.date':           'FECHA',
    'booking.success.guests':         'INVITADOS',
    'booking.success.time':           'HORA',
    'booking.success.payment':        'PAGO',
    'booking.success.booking_fee':    'Cuota de reserva',
    'booking.success.location':       'UBICACIÓN',
    'booking.success.booking_type':   'TIPO DE RESERVA',
    'booking.success.package':        'PAQUETE',
    'booking.success.min_spend':      'Consumo mínimo (en el lugar)',
    'booking.success.fee_free':       'Gratis',
    'booking.success.view_details':   'Ver detalles de la reserva',
    'booking.success.print':          'Imprimir recibo',
    'booking.success.confirming':     'Confirmando tu reserva…',
    'booking.success.just_a_moment':  'Esto tomará solo un momento…',

    'booking.error_page.title':       'Algo salió mal',
    'booking.error_page.fallback':    'No pudimos cargar tu reserva. Por favor intenta de nuevo.',
    'booking.error_page.retry':       'Intentar de nuevo',
    'booking.error_page.back':        'Volver al inicio',
    'booking.not_found_page.title':   'Reserva no encontrada',
    'booking.not_found_page.body':    'No encontramos una reserva con ese ID. Puede haber sido eliminada o el enlace puede ser incorrecto.',
    'booking.footer.tag':             'RESERVA DE LUGAR PRIVADO — TORONTO, CA',

    // R21 Phase 1A — customer-facing
    'booking.pill.event_completed':       'EVENTO COMPLETADO',
    'booking.pill.match_confirmed':       'COINCIDENCIA CONFIRMADA',
    'booking.section.venue_contact':      'CONTACTO DEL LUGAR',
    'booking.section.add_to_calendar':    'AGREGAR AL CALENDARIO',
    'booking.booked_for':                 'Reservado para {{datetime}} · {{duration}}',
    'booking.calendar.google':            'Google Calendar',
    'booking.calendar.outlook':           'Outlook',
    'booking.calendar.download_ics':      'Descargar .ics',
    'booking.carousel.prev':              'Foto anterior',
    'booking.carousel.next':              'Foto siguiente',
    'booking.intake.notes_placeholder':   'ej. Preferimos cangrejo sobre langosta, y necesitamos 5 platos veganos...',

    // feedback.* — post-event feedback form
    'feedback.heading.readonly':          'Tu comentario',
    'feedback.heading.editable':          '¿Cómo estuvo tu evento?',
    'feedback.placeholder':               'Cuéntanos más (opcional)…',
    'feedback.submit':                    'Enviar comentario',
    'feedback.update':                    'Actualizar comentario',
    'feedback.edit':                      'Editar comentario',
    'feedback.submitting':                'Enviando…',
    'feedback.submitted_prefix':          'Enviado {{time}}',
    'feedback.hint':                      'Tu comentario va directamente al equipo de eventos del lugar.',
    'feedback.error.no_rating':           'Por favor elige una calificación de 1 a 5 copas de vino.',
    'feedback.error.submit_failed':       'No se pudo enviar el comentario. Intenta de nuevo.',
    'feedback.relative.just_now':         'justo ahora',
    'feedback.relative.minutes_ago':      'hace {{count}} min',
    'feedback.relative.hours_ago':        'hace {{count}} h',
    'feedback.glass_aria':                'Calificar {{n}} de 5',

    // diff.* — InitialVsCurrentDiff component
    'diff.what_changed':                  'Qué cambió',
    'diff.party_size':                    'Número de invitados',
    'diff.dates':                         'Fechas',
    'diff.budget':                        'Presupuesto',
    'diff.time':                          'Hora',
    'diff.duration':                      'Duración',
    'diff.guests_singular':               '{{n}} invitado',
    'diff.guests_plural':                 '{{n}} invitados',
    'diff.guests_range':                  '{{min}}–{{max}} invitados',
    'diff.hours_singular':                '{{n}} h',
    'diff.hours_plural':                  '{{n}} h',

    // R21 T3 (2026-05-10) — Venue dashboard proposal lead toasts.
    'dashboard.proposals.lead.status.toast_lost':         'Lead marcado como perdido',
    'dashboard.proposals.lead.status.toast_lost_failed':  'No se pudo marcar como perdido — {{error}}',
    'dashboard.proposals.lead.status.mark_lost':          'Marcar como perdido',
    'dashboard.proposals.lead.status.mark_lost_heading':  'Marcar como perdido',
    'dashboard.proposals.lead.status.mark_lost_subtitle': 'Anota por qué no se convirtió este lead. Ayuda al equipo a mejorar.',
    'dashboard.proposals.lead.status.toast_update_failed':'Error al actualizar el estado del lead',
    'dashboard.proposals.lead.status.toast_failed_log':   'Error al registrar',

    // R21 T2 batch 3 (2026-05-10) — Full dashboard.proposals.* coverage (ES).
    'dashboard.proposals.card.budget_label':              'Presupuesto',
    'dashboard.proposals.card.catering_details':          'Detalles del catering',
    'dashboard.proposals.card.confirm':                   'Confirmar propuesta',
    'dashboard.proposals.card.customer_contact':          'Contacto del cliente',
    'dashboard.proposals.card.dietary_accessibility':     'Dieta / accesibilidad',
    'dashboard.proposals.card.direct_badge':              'Directo',
    'dashboard.proposals.card.event_fallback':            'Evento',
    'dashboard.proposals.card.event_summary':             'Resumen del evento',
    'dashboard.proposals.card.field.allergy_concerns':    'Alergias',
    'dashboard.proposals.card.field.bar_restrictions':    'Restricciones de bar',
    'dashboard.proposals.card.field.billing_setup':       'Facturación',
    'dashboard.proposals.card.field.budget_landmarks':    'Hitos de presupuesto',
    'dashboard.proposals.card.field.budget_poc_name':     'Contacto de presupuesto',
    'dashboard.proposals.card.field.card_charge_confirmed': 'Cargo a tarjeta',
    'dashboard.proposals.card.field.event_audience':      'Audiencia del evento',
    'dashboard.proposals.card.field.location_preference': 'Preferencia de ubicación',
    'dashboard.proposals.card.field.music_preference':    'Música / DJ',
    'dashboard.proposals.card.field.pre_order_interest':  'Menú preordenado',
    'dashboard.proposals.card.field.space_preference':    'Preferencia de espacio',
    'dashboard.proposals.card.field.vendor_interest':     'Eventos pop-up',
    'dashboard.proposals.card.guests_suffix':             'invitados',
    'dashboard.proposals.card.hide_details':              'Ocultar detalles',
    'dashboard.proposals.card.pending_review_banner':     'El cliente actualizó los detalles de su reserva. Por favor revisa y confirma tu propuesta.',
    'dashboard.proposals.card.requested_label':           'Solicitado',
    'dashboard.proposals.card.resubmit':                  'Reenviar propuesta',
    'dashboard.proposals.card.status.completed':          'Completado',
    'dashboard.proposals.card.status.consumer_confirmed': 'Pago pendiente',
    'dashboard.proposals.card.status.consumer_confirmed_tripleseat': 'Reservado',
    'dashboard.proposals.card.status.expired':            'Expirado',
    'dashboard.proposals.card.status.external_crm':       'Enviado al CRM',
    'dashboard.proposals.card.status.lost_post_contact':  'Perdido',
    'dashboard.proposals.card.status.pending':            'Pendiente',
    'dashboard.proposals.card.status.pending_review':     'Requiere revisión',
    'dashboard.proposals.card.status.rejected':           'No seleccionado',
    'dashboard.proposals.card.status.selected':           'Seleccionado',
    'dashboard.proposals.card.status.submitted':          'Enviado',
    'dashboard.proposals.card.status.withdrawn':          'Retirado',
    'dashboard.proposals.card.submit':                    'Enviar propuesta',
    'dashboard.proposals.card.venue_specific':            'Detalles específicos del lugar',
    'dashboard.proposals.card.view_details':              'Ver detalles',
    'dashboard.proposals.empty.no_matches.body.filter':   'Ninguna propuesta coincide con tu filtro. Intenta ajustar los criterios.',
    'dashboard.proposals.empty.no_matches.body.search':   'Ninguna propuesta coincide con tu búsqueda. Intenta ajustar los criterios.',
    'dashboard.proposals.empty.no_matches.title':         'Sin propuestas coincidentes',
    'dashboard.proposals.empty.no_requests.body':         'Cuando el evento de un cliente coincida con tu espacio, su solicitud de reserva aparecerá aquí. También recibirás una notificación SMS.',
    'dashboard.proposals.empty.no_requests.title':        'Sin solicitudes todavía',
    'dashboard.proposals.filter.category.all':            'Todas',
    'dashboard.proposals.filter.category.catering':       'Catering',
    'dashboard.proposals.filter.category.private_events': 'Eventos',
    'dashboard.proposals.filter.source.all':              'Todas las fuentes',
    'dashboard.proposals.filter.source.direct':           'Directo',
    'dashboard.proposals.filter.source.marketplace':      'Marketplace',
    'dashboard.proposals.filter.status.all':              'Todos',
    'dashboard.proposals.filter.status.pending':          'Pendiente',
    'dashboard.proposals.filter.status.selected':         'Seleccionado',
    'dashboard.proposals.filter.status.submitted':        'Enviado',
    'dashboard.proposals.lead.call':                      'Llamar al cliente',
    'dashboard.proposals.lead.email':                     'Enviar correo al cliente',
    'dashboard.proposals.lead.no_contact':                'Sin canal de contacto — envía una propuesta formal en su lugar.',
    'dashboard.proposals.lead.no_email':                  'El cliente no compartió un correo electrónico.',
    'dashboard.proposals.lead.no_phone':                  'El cliente no compartió un número de teléfono.',
    'dashboard.proposals.lead.send_email':                'Enviar respuesta por correo',
    'dashboard.proposals.lead.send_text':                 'Enviar respuesta por texto',
    'dashboard.proposals.lead.status.cancel':             'Cancelar',
    'dashboard.proposals.lead.status.confirm':            'Confirmar',
    'dashboard.proposals.lead.status.confirm_booked':     'Confirmar: lead reservado',
    'dashboard.proposals.lead.status.confirm_completed':  'Confirmar: evento completado',
    'dashboard.proposals.lead.status.event_datetime_label': 'Fecha y hora acordadas del evento (opcional — elige entre las fechas preferidas del cliente)',
    'dashboard.proposals.lead.status.actual_spend_label':  'Consumo real (CAD, opcional)',
    'dashboard.proposals.lead.status.hrs':                'hrs',
    'dashboard.proposals.lead.status.mark_booked':        'Marcar como reservado',
    'dashboard.proposals.lead.status.mark_completed':     'Marcar como completado',
    'dashboard.proposals.lead.status.marked_booked':      'Reservado · actualizando',
    'dashboard.proposals.lead.status.marked_completed':   'Completado · actualizando',
    'dashboard.proposals.lead.status.notes_placeholder':  'Notas opcionales — ej., referencia de reserva, detalles del evento, seguimientos…',
    'dashboard.proposals.lead.status.saving':             'Guardando…',
    'dashboard.proposals.lead.status.toast_booked':       'Lead marcado como reservado',
    'dashboard.proposals.lead.status.toast_completed':    'Lead marcado como completado',
    'dashboard.proposals.lead.status_heading':            'Estado del lead',
    'dashboard.proposals.lead.submit_formal_link':        'Envía una propuesta formal en su lugar',
    'dashboard.proposals.modal.add_option':               'Agregar otra opción de espacio',
    'dashboard.proposals.modal.ai_suggested_banner':      'Sugerido por IA basándose en la intake del cliente — siéntete libre de ajustar',
    'dashboard.proposals.modal.aria.close_modal':         'Cerrar modal',
    'dashboard.proposals.modal.aria.remove_date_slot':    'Eliminar fecha',
    'dashboard.proposals.modal.aria.remove_option':       'Eliminar opción',
    'dashboard.proposals.modal.attachments_hint':         '(menús, términos y condiciones, etc.)',
    'dashboard.proposals.modal.cross_location_warning':   'Este espacio está en una ubicación diferente a la solicitada',
    'dashboard.proposals.modal.label.attachments':        'Adjuntos',
    'dashboard.proposals.modal.label.available_dates':    'Fechas y horas disponibles',
    'dashboard.proposals.modal.label.booking_fee':        'Cuota de reserva ($)',
    'dashboard.proposals.modal.label.capacity':           'Capacidad',
    'dashboard.proposals.modal.label.min_spend':          'Consumo mínimo ($)',
    'dashboard.proposals.modal.label.notes':              'Notas',
    'dashboard.proposals.modal.label.per_person':         'Por persona ($)',
    'dashboard.proposals.modal.label.space':              'Espacio',
    'dashboard.proposals.modal.notes_placeholder':        'Detalles adicionales para el cliente...',
    'dashboard.proposals.modal.per_person_disclaimer':    'Es una estimación, no una cotización vinculante. El precio final se confirma después de la conversación.',
    'dashboard.proposals.modal.select_date_placeholder':  'Selecciona fecha',
    'dashboard.proposals.modal.select_space_placeholder': 'Selecciona espacio',
    'dashboard.proposals.modal.submit_multi':             'Enviar {{count}} opciones',
    'dashboard.proposals.modal.submitting':               'Enviando...',
    'dashboard.proposals.modal.up_to':                    'hasta {{n}}',
    'dashboard.proposals.results.clear':                  'Limpiar filtros',
    'dashboard.proposals.results.showing':                'Mostrando {{shown}} de {{total}} propuestas',
    'dashboard.proposals.search.placeholder':             'Buscar por evento, nombre, fecha…',
    'dashboard.proposals.section.active':                 'Activas',
    'dashboard.proposals.section.forwarded_crm':          'Enviadas a Tripleseat',
    'dashboard.proposals.section.in_negotiation':         'En negociación',
    'dashboard.proposals.section.needs_review':           'Requiere revisión',
    'dashboard.proposals.section.past':                   'Pasadas',
    'dashboard.proposals.section.pending':                'Solicitudes pendientes',
    'dashboard.proposals.subtitle.many':                  '{{count}} solicitudes pendientes',
    'dashboard.proposals.subtitle.one':                   '1 solicitud pendiente',
    'dashboard.proposals.subtitle.zero':                  'Sin solicitudes pendientes',
    'dashboard.proposals.toast.confirm_review.error':     'Error al confirmar la revisión de la propuesta',
    'dashboard.proposals.toast.confirm_review.success':   'Propuesta confirmada',
    'dashboard.proposals.toast.dismiss.error':            'Error al eliminar la propuesta',
    'dashboard.proposals.toast.dismiss.success':          'Propuesta eliminada de la vista',
    'dashboard.proposals.toast.withdraw.error':           'Error al retirar la propuesta',
    'dashboard.proposals.toast.withdraw.success':         'Propuesta retirada',

    // Consultas generales (solo Members Only — ES).
    'dashboard.inquiries.title':            'Consultas',
    'dashboard.inquiries.subtitle.zero':    'Sin preguntas nuevas',
    'dashboard.inquiries.subtitle.one':     '1 pregunta nueva',
    'dashboard.inquiries.subtitle.many':    '{{count}} preguntas nuevas',
    'dashboard.inquiries.empty':            'Aún no hay preguntas. Cuando un cliente le hace una pregunta al equipo a través del widget o por teléfono, aparece aquí.',
    'dashboard.inquiries.mark_handled':     'Marcar como atendida',
    'dashboard.inquiries.status.new':       'Nueva',
    'dashboard.inquiries.voiceEmailCaveat': 'Capturado por voz — puede estar mal transcrito. El número de teléfono es el contacto fiable.',
    'dashboard.inquiries.status.handled':   'Atendida',
    'dashboard.inquiries.toast.handled.success': 'Consulta marcada como atendida',
    'dashboard.inquiries.toast.handled.error':   'Error al marcar la consulta',
    'dashboard.inquiries.category.reservation': 'Reserva',
    'dashboard.inquiries.category.hours':       'Horario',
    'dashboard.inquiries.category.menu':        'Menú',
    'dashboard.inquiries.category.directions':  'Cómo llegar',
    'dashboard.inquiries.category.private_event': 'Evento privado',
    'dashboard.inquiries.category.catering':    'Catering',
    'dashboard.inquiries.category.general':     'Pregunta general',
    'dashboard.inquiries.category.other':       'Otra',

    // Página de confirmación de consulta general (/inquiry/[id]) — ES.
    'inquiry.confirm.title':          'Le hiciste una pregunta al equipo de Members Only',
    'inquiry.confirm.question_label': 'Tu pregunta',
    'inquiry.confirm.contact_label':  'Te contactaremos en',
    'inquiry.confirm.response_time':  'El equipo suele responder en un día.',
    'inquiry.edit.button':            'Editar',
    'inquiry.edit.name':              'Nombre',
    'inquiry.edit.email':             'Correo electrónico',
    'inquiry.edit.phone':             'Teléfono',
    'inquiry.edit.add_email_hint':    'Añade tu correo para recibir una copia',
    'inquiry.edit.add_email_cta':     'Añade tu correo para recibir una copia',
    'inquiry.edit.voice_hint':        'Este correo se capturó por voz — comprueba que sea correcto.',
    'inquiry.edit.save':              'Guardar cambios',
    'inquiry.edit.saving':            'Guardando\u2026',
    'inquiry.edit.cancel':            'Cancelar',
    'inquiry.edit.saved':             'Guardado — el equipo verá tus datos actualizados.',
    'inquiry.edit.error':             'No se pudo guardar — revisa tus datos e inténtalo de nuevo.',
    'inquiry.confirm.loading':        'Cargando tu consulta…',
    'inquiry.confirm.not_found':      'No pudimos encontrar esa consulta — escríbenos a events@membersonly.com.',
    'inquiry.confirm.badge':          'Pregunta recibida',

    // Multi-venue plan §1108-1134 — Modal de derivación a un local hermano (ES).
    'proposals.routing.trigger':                          'Derivar a un local hermano',
    'proposals.routing.title':                            'Derivar esta reserva desde {{parent}}',
    'proposals.routing.choose_venue':                     'Elige un local',
    'proposals.routing.no_sisters':                       'No hay locales hermanos disponibles en tu ámbito.',
    'proposals.routing.customer_unknown':                 'Datos del cliente no disponibles',
    'proposals.routing.guests':                           '{{lo}}–{{hi}} invitados',
    'proposals.routing.capacity':                         'capacidad {{count}}',
    'proposals.routing.min_spend':                        'mín {{amount}}',
    'proposals.routing.reason_label':                     'Motivo (opcional)',
    'proposals.routing.reason_placeholder':               'Registrado en la auditoría — ¿por qué este local?',
    'proposals.routing.cancel':                           'Cancelar',
    'proposals.routing.confirm':                          'Derivar reserva',
    'proposals.routing.confirm_named':                    'Derivar a {{venue}}',
    'proposals.routing.success':                          'Derivada a {{venue}}',
    'proposals.routing.failure':                          'No se pudo derivar, inténtalo de nuevo',
    'proposals.routing.tripleseat.disabled':              'Tripleseat: desactivado',
    'proposals.routing.tripleseat.shadow':                'Tripleseat: en sombra',
    'proposals.routing.tripleseat.external_crm':          'Tripleseat: CRM',
    'proposals.routing.skip_tripleseat_label':            'Omitir Tripleseat para esta derivación',
    'proposals.routing.skip_tripleseat_help_enabled':     'La derivación se registra pero no se enviará el lead al Tripleseat de {{venue}} — gestiónalo manualmente.',
    'proposals.routing.skip_tripleseat_help_disabled':    'El local seleccionado no tiene integración con Tripleseat que omitir.',
    'proposals.routed_from':                              'Derivada desde {{venueName}}',

    // Multi-venue plan §1108-1175 — Modo "Ver como" (ES).
    'dashboard.impersonate.view_as':                      'Ver como',
    'dashboard.impersonate.self':                         'Yo (admin de plataforma)',
    'dashboard.impersonate.exit':                         'Salir',
    'dashboard.impersonate.banner':                       'Suplantando: {{role}} para {{scope}}.',
    'dashboard.impersonate.role.group_manager':           'Gestor de grupo',
    'dashboard.impersonate.role.venue_manager':           'Gestor de local',
    'dashboard.impersonate.role.location_manager':        'Gestor de sede',

    // R21 T2 batch 4 (2026-05-10) — dashboard.analytics + bookings +
    // feedback + settings (ES).
    'dashboard.analytics.subtitle':                       'Sigue el rendimiento de tu lugar',
    'dashboard.analytics.empty.title':                    'Sin datos todavía',
    'dashboard.analytics.empty.body':                     'Las analíticas se llenarán a medida que recibas y respondas solicitudes de reserva.',
    'dashboard.analytics.period.7d':                      '7d',
    'dashboard.analytics.period.30d':                     '30d',
    'dashboard.analytics.period.90d':                     '90d',
    'dashboard.analytics.period.all':                     'Todo el tiempo',
    'dashboard.analytics.stat.total_requests':            'Total de solicitudes',
    'dashboard.analytics.stat.submitted':                 'Enviadas',
    'dashboard.analytics.stat.won':                       'Ganadas',
    'dashboard.analytics.stat.lost':                      'Perdidas',
    'dashboard.analytics.stat.booked':                    'Reservadas',
    'dashboard.analytics.stat.completed':                 'Completadas',
    'dashboard.analytics.stat.revenue':                   'Ingresos',
    'dashboard.analytics.stat.avg_party':                 'Tamaño promedio',
    'dashboard.analytics.stat.repeat':                    'Clientes recurrentes',
    'dashboard.analytics.funnel.title':                   'Embudo de conversión',
    'dashboard.analytics.funnel.received':                'Recibidas',
    'dashboard.analytics.funnel.submitted':               'Enviadas',
    'dashboard.analytics.funnel.selected':                'Seleccionadas',
    'dashboard.analytics.funnel.completed':               'Completadas',
    'dashboard.analytics.funnel.lost':                    'Perdidas',
    'dashboard.analytics.funnel.lost.reasons':            'Motivos',
    'dashboard.analytics.funnel.conversion':              'Tasa de conversión',
    'dashboard.analytics.lost_reasons.title':             'Por qué se perdieron los leads',
    'dashboard.analytics.lost_reasons.description':       'Incluye leads que no convirtieron (sin seguimiento, eligieron otro lugar) y reservas canceladas después de confirmar.',
    'dashboard.analytics.lost_reasons.show_more':         'Mostrar los {{total}} motivos (+{{overflow}} más)',
    'dashboard.analytics.lost_reasons.show_less':         'Mostrar menos',
    'dashboard.analytics.detail.event_summary':           'Resumen del evento',
    'dashboard.analytics.detail.event_notes':             'Notas del evento',
    'dashboard.analytics.detail.min_spend':               'Consumo mínimo',
    'dashboard.analytics.detail.actual_spend':            'Consumo real',
    'dashboard.analytics.detail.rental_fee':              'Depósito',
    'dashboard.analytics.detail.confirmed_for':           'Confirmado para {{date}} · {{time}}{{duration}}',

    'dashboard.bookings.subtitle':                        '{{confirmed}} confirmadas, {{completed}} completadas',
    'dashboard.bookings.empty.title':                     'Sin reservas todavía',
    'dashboard.bookings.empty.body':                      'Cuando un cliente selecciona tu propuesta, su reserva aparecerá aquí con los detalles de contacto completos.',
    'dashboard.bookings.event_completed':                 'Evento marcado como completado',
    'dashboard.bookings.complete_error':                  'Error al marcar como completado',
    'dashboard.bookings.section.confirmed':               'Confirmadas ({{count}})',
    'dashboard.bookings.section.completed':               'Completadas ({{count}})',
    'dashboard.bookings.view.list':                       'Lista',
    'dashboard.bookings.view.calendar':                   'Calendario',

    'dashboard.analytics.customer_list.heading_count':                'Clientes ({{count}})',
    'dashboard.analytics.customer_list.history.heading_count':        'Historial de reservas ({{count}})',
    'dashboard.analytics.customer_list.badge.re_engaged':             'REACTIVADO',
    'dashboard.analytics.customer_list.badge.repeat_count':           'RECURRENTE · {{count}}',
    'dashboard.analytics.customer_list.badge.corporate_count':        'CORPORATIVO × {{count}}',
    'dashboard.analytics.customer_list.badge.personal_count':         'PERSONAL × {{count}}',
    'dashboard.analytics.customer_list.status.completed':             'Completado',
    'dashboard.analytics.customer_list.status.booked':                'Reservado',
    'dashboard.analytics.customer_list.status.lost':                  'Perdido',
    'dashboard.analytics.customer_list.status.lost_with_reason':      'Perdido · {{reason}}',
    'dashboard.analytics.customer_list.status.rejected':              'No seleccionado',
    'dashboard.analytics.customer_list.status.expired':               'Expirado',
    'dashboard.analytics.customer_list.status.withdrawn':             'Retirado',
    'dashboard.analytics.customer_list.cancel_reason.unspecified':    'No especificado',
    'dashboard.analytics.customer_list.cancel_reason.no_response':    'Sin seguimiento',
    'dashboard.analytics.customer_list.cancel_reason.chose_elsewhere':'Eligió otro lugar',
    'dashboard.analytics.customer_list.cancel_reason.price_disagreement':'Desacuerdo de precio',
    'dashboard.analytics.customer_list.cancel_reason.date_unavailable':'Fecha no disponible',
    'dashboard.analytics.customer_list.cancel_reason.event_postponed':'Evento pospuesto',
    'dashboard.analytics.customer_list.cancel_reason.event_cancelled':'Cliente canceló',
    'dashboard.analytics.customer_list.cancel_reason.other':          'Otro',
    'dashboard.analytics.customer_list.last_corporate.label':         'Última reserva corporativa',
    'dashboard.analytics.customer_list.toggle.history':               'Historial',
    'dashboard.analytics.customer_list.toggle.hide':                  'Ocultar',

    'dashboard.feedback.title':                           'Comentarios de clientes',
    'dashboard.feedback.highlights':                      'Destacados (5★)',
    'dashboard.feedback.concerns':                        'Podría mejorar (1-2★)',
    'dashboard.feedback.review_singular':                 'reseña',
    'dashboard.feedback.review_plural':                   'reseñas',
    'dashboard.feedback.view_all':                        'Ver todas las {{total}} reseñas',
    'dashboard.feedback.view_less':                       'Ocultar',

    'dashboard.settings.subtitle':                        'Administra los detalles, espacios y disponibilidad de tu lugar',
    'dashboard.settings.action.preview_public':           'Vista pública',
    'dashboard.settings.action.preview_customer':         'Vista cliente',
    'dashboard.settings.action.pause':                    'Pausar',
    'dashboard.settings.action.resume':                   'Reanudar',
    'dashboard.settings.signout':                         'Cerrar sesión',

    // Plan multi-locales — vista de Ajustes limitada al gerente de local
    // (location_manager). Se ocultan todos los campos globales; solo
    // queda editable el bloque de detalles del local en cuestión.
    'settings.location_manager_banner':                   'Eres gerente del local {{locationName}}. Solo los campos específicos de {{locationName}} pueden editarse aquí.',
    'settings.location_manager_section_title':            'Detalles de {{locationName}}',
    'settings.location_only_fields_notice':               'Editar la configuración global del lugar (marca, contacto, integraciones) requiere acceso de gerente del lugar o superior.',
    'settings.location_manager.field.address':            'Dirección',
    'settings.location_manager.field.postal_code':        'Código postal',
    'settings.location_manager.field.phone':              'Teléfono',
    'settings.location_manager.field.latitude':           'Latitud',
    'settings.location_manager.field.longitude':          'Longitud',
    'settings.location_manager.field.website':            'URL del sitio web',
    'settings.location_manager.field.instagram':          'URL de Instagram',
    'settings.location_manager.action.save':              'Guardar cambios',
    'settings.location_manager.action.saving':            'Guardando…',
    'settings.location_manager.toast.saved':              'Local actualizado',
    'settings.location_manager.toast.save_failed':        'Error al guardar los cambios',
    'settings.location_manager.no_location':              'No hay ningún local en el alcance de esta cuenta. Contacta a tu gerente del lugar.',
    'dashboard.settings.toast.paused':                    'Lugar pausado',
    'dashboard.settings.toast.resumed':                   'Lugar reanudado',
    'dashboard.settings.toast.toggle_failed':             'Error al cambiar el estado',
    'dashboard.settings.toast.email_verified':            '¡Correo verificado correctamente!',
    'dashboard.settings.toast.email_link_expired':        'El enlace de verificación expiró. Por favor solicita uno nuevo.',
    'dashboard.settings.transfer.email_prompt':           'Por favor ingresa una dirección de correo',
    'dashboard.settings.transfer.phone_prompt':           'Por favor ingresa un número de teléfono',
    'dashboard.settings.transfer.success':                '¡Acceso al lugar transferido! El nuevo administrador recibirá un enlace de inicio de sesión por {{via}}.',
    'dashboard.settings.transfer.error':                  'Error al transferir el acceso al lugar',
    'dashboard.settings.cleanup.success':                 'Limpieza: {{proposals}} propuestas y {{bookings}} solicitudes',
    'dashboard.settings.cleanup.error':                   'Error al limpiar',

    // R21 T2 batch 5 (2026-05-10) — settings.* sub-forms (ES). Venue
    // weekly-traffic surface — venue info, spaces, locations, menus,
    // direct booking, email verification.
    'settings.venue_info.title':                          'Información del lugar',
    'settings.venue_info.title_edit':                     'Editar información del lugar',
    'settings.venue_info.action.edit':                    'Editar',
    'settings.venue_info.action.cancel':                  'Cancelar',
    'settings.venue_info.action.save':                    'Guardar',
    'settings.venue_info.action.saving':                  'Guardando…',
    'settings.venue_info.hero.label':                     'Imagen principal',
    'settings.venue_info.hero.upload':                    'Subir',
    'settings.venue_info.hero.change':                    'Cambiar',
    'settings.venue_info.hero.uploading':                 'Subiendo…',
    'settings.venue_info.hero.hint':                      'Recomendado: 2400 × 1200px o más',
    'settings.venue_info.hero.upload_failed':             'Error al subir la imagen principal',
    'settings.venue_info.field.name':                     'Nombre',
    'settings.venue_info.field.name_full':                'Nombre del lugar',
    'settings.venue_info.field.contact':                  'Contacto',
    'settings.venue_info.field.contact_name':             'Nombre de contacto',
    'settings.venue_info.field.email':                    'Correo de contacto',
    'settings.venue_info.field.phone':                    'Teléfono',
    'settings.venue_info.field.address':                  'Dirección',
    'settings.venue_info.field.tagline':                  'Eslogan',
    'settings.venue_info.field.website':                  'Sitio web',
    'settings.venue_info.field.instagram':                'Instagram',
    'settings.venue_info.field.notes':                    'Notas',
    'settings.venue_info.placeholder.email':              'dueno@lugar.com',
    'settings.venue_info.placeholder.website':            'milugar.com',
    'settings.venue_info.placeholder.instagram':          'tulugar',
    'settings.venue_info.tagline_empty':                  '—',
    'settings.venue_info.accessibility':                  'Accesible para silla de ruedas',
    'settings.venue_info.maps':                           'Ver en Google Maps',
    'settings.venue_info.notifications':                  'Notificaciones',
    'settings.venue_info.notifications.choose':           'Elige cómo recibir notificaciones de reserva y códigos de acceso.',
    'settings.venue_info.channel.sms':                    'SMS',
    'settings.venue_info.channel.whatsapp':               'WhatsApp',
    'settings.venue_info.channel.phone':                  'Teléfono',
    'settings.venue_info.channel.email_toggle':           'Notificaciones por correo',
    'settings.venue_info.channel.add_email':              'Agrega un correo de contacto arriba para habilitar las notificaciones por correo.',
    'settings.venue_info.channel.none_email':             'Ninguno (solo correo)',
    'settings.venue_info.channel.sending_to':             'Enviando a {{email}}',
    'settings.venue_info.via_sms':                        '{{phone}} por SMS',
    'settings.venue_info.via_whatsapp':                   '{{phone}} por WhatsApp',
    'settings.venue_info.both':                           '{{email}} + {{phone}}',
    'settings.venue_info.no_email_label':                 'Sin correo',
    'settings.venue_info.no_email_set':                   'Sin correo configurado',
    'settings.venue_info.visibility.title':               'Visibilidad del perfil público',
    'settings.venue_info.visibility.contact':             'Mostrar contacto del cliente en propuestas',
    'settings.venue_info.visibility.contact_help':        'Muestra el teléfono y correo del cliente en las solicitudes entrantes',
    'settings.venue_info.visibility.menus':               'Mostrar menús en el perfil público',
    'settings.venue_info.visibility.spend':               'Mostrar consumo mínimo en el perfil público',
    'settings.venue_info.language':                       'Idioma de notificaciones',
    'settings.venue_info.language.auto':                  'Automático (según teléfono)',
    'settings.venue_info.language.en':                    'English',
    'settings.venue_info.language.fr':                    'Français',
    'settings.venue_info.language.es':                    'Español',
    'settings.venue_info.language.it':                    'Italiano',
    'settings.venue_info.language.hint':                  'Idioma usado para tus notificaciones (OTP, alertas de reserva, correos de enlace mágico). Por defecto detecta desde el área de tu teléfono.',
    'settings.venue_info.toast.saved':                    'Información del lugar guardada',
    'settings.venue_info.toast.save_failed':              'Error al guardar',

    'settings.spaces.title':                              'Espacios ({{count}})',
    'settings.spaces.title_edit':                         'Editar espacios',
    'settings.spaces.add':                                'Agregar espacio',
    'settings.spaces.empty':                              'Aún no hay espacios configurados. Haz clic en Editar para agregar tu primer espacio.',
    'settings.spaces.space_n':                            'Espacio {{n}}',
    'settings.spaces.no_location':                        'Sin ubicación específica',
    'settings.spaces.field.name':                         'Nombre',
    'settings.spaces.field.location':                     'Ubicación',
    'settings.spaces.field.capacity':                     'Capacidad máxima',
    'settings.spaces.field.min_spend':                    'Consumo mínimo ($)',
    'settings.spaces.field.booking_fee':                  'Cuota de reserva ($)',
    'settings.spaces.field.description':                  'Descripción',
    'settings.spaces.field.images':                       'Imágenes',
    'settings.spaces.placeholder.name':                   'ej. The Snug',
    'settings.spaces.placeholder.min_spend':              'ej. 1000',
    'settings.spaces.placeholder.booking_fee':            'ej. 50',
    'settings.spaces.toast.saved':                        'Espacios guardados',
    'settings.spaces.toast.save_failed':                  'Error al guardar los espacios',
    'settings.spaces.toast.image_failed':                 'Error al subir la imagen',

    'settings.space_card.up_to':                          'Hasta {{capacity}}',
    'settings.space_card.up_to_guests':                   'Hasta {{capacity}} invitados',
    'settings.space_card.min_short':                      '${{amount}} mín.',
    'settings.space_card.min_long':                       '${{amount}} consumo mínimo',
    'settings.space_card.fee_short':                      '${{amount}} cuota',
    'settings.space_card.fee_long':                       '${{amount}} de cuota de reserva',

    'settings.locations.title':                           'Ubicaciones ({{count}})',
    'settings.locations.title_edit':                      'Editar ubicaciones',
    'settings.locations.add':                             'Agregar ubicación',
    'settings.locations.empty':                           'Lugar con una sola ubicación. Haz clic en Editar para agregar ubicaciones.',
    'settings.locations.location_n':                      'Ubicación {{n}}',
    'settings.locations.website_label':                   'Sitio web',
    'settings.locations.field.name':                      'Nombre',
    'settings.locations.field.city':                      'Ciudad',
    'settings.locations.field.address':                   'Dirección',
    'settings.locations.field.phone':                     'Teléfono',
    'settings.locations.field.website':                   'URL del sitio web',
    'settings.locations.field.instagram':                 'URL de Instagram',
    'settings.locations.placeholder.name':                'ej. Sede centro',
    'settings.locations.placeholder.city':                'ej. Toronto',
    'settings.locations.placeholder.address':             'Busca una dirección…',
    'settings.locations.placeholder.phone':               'ej. (555) 123-4567',
    'settings.locations.placeholder.website':             'ej. https://milugar.com/centro',
    'settings.locations.placeholder.instagram':           'ej. https://instagram.com/milugar',
    'settings.locations.toast.saved':                     'Ubicaciones guardadas',
    'settings.locations.toast.save_failed':               'Error al guardar las ubicaciones',
    'settings.locations.toast.missing_address':           'La ubicación {{n}} necesita una dirección antes de guardar.',

    'settings.menus.title':                               'Menús / Detalles adicionales ({{count}})',
    'settings.menus.title_edit':                          'Editar menús / detalles adicionales',
    'settings.menus.add':                                 'Agregar menú ({{count}}/12)',
    'settings.menus.empty':                               'Aún no hay menús o detalles cargados. Haz clic en Editar para agregar menús o detalles del evento.',
    'settings.menus.menu_n':                              'Menú {{n}}',
    'settings.menus.replace':                             'Reemplazar',
    'settings.menus.view_current':                        'Ver archivo actual',
    'settings.menus.no_location':                         'Sin ubicación específica',
    'settings.menus.mode.upload':                         'Subir archivo',
    'settings.menus.mode.link':                           'Pegar enlace',
    'settings.menus.field.name':                          'Nombre',
    'settings.menus.field.description':                   'Descripción',
    'settings.menus.field.location':                      'Ubicación',
    'settings.menus.field.source':                        'Fuente',
    'settings.menus.placeholder.name':                    'ej. Menú degustación, Paquete de evento',
    'settings.menus.placeholder.description':             'Descripción opcional',
    'settings.menus.placeholder.link':                    'https://tusitio.com/menu.pdf',
    'settings.menus.placeholder.link_help':               'Pega una URL a tu menú (página web, enlace a PDF, etc.)',
    'settings.menus.toast.saved':                         'Menús guardados',
    'settings.menus.toast.save_failed':                   'Error al guardar los menús',
    'settings.menus.error.untitled':                      'Sin título',
    'settings.menus.error.needs_link':                    'El menú "{{name}}" necesita una URL',
    'settings.menus.error.too_large':                     'Archivo demasiado grande para "{{name}}". Máximo 10MB.',
    'settings.menus.error.bad_type':                      'Tipo de archivo no permitido para "{{name}}". Usa PDF, JPEG, PNG o WebP.',
    'settings.menus.error.upload_failed':                 'Error al subir "{{name}}": {{msg}}',

    'settings.direct.title':                              'Reserva directa',
    'settings.direct.code_label':                         'Código del lugar',
    'settings.direct.code_help':                          'Los clientes pueden enviar este código por SMS a BookOne para reservar directamente contigo',
    'settings.direct.line_label_one':                     'Línea telefónica dedicada',
    'settings.direct.line_label_many':                    'Líneas telefónicas dedicadas',
    'settings.direct.no_line':                            '¿Quieres un número dedicado para tu lugar? Contacta a BookOne para configurarlo.',
    'settings.direct.status.active':                      'Activo',
    'settings.direct.status.inactive':                    'Inactivo',

    'settings.email_verify.verified':                     'Verificado',
    'settings.email_verify.not_verified':                 'No verificado',
    'settings.email_verify.send':                         'Enviar correo de verificación',
    'settings.email_verify.sending':                      'Enviando…',
    'settings.email_verify.toast.sent':                   'Correo de verificación enviado. Revisa tu bandeja de entrada.',
    'settings.email_verify.toast.failed':                 'Error al enviar el correo de verificación',

    // R21 T2 batch 6 (2026-05-10) — R21 namespaces (edit_booking,
    // catering_config, proposal_submit, pricing, quick_reply) (ES).
    'edit_booking.title':                                 'Editar detalles de la reserva',
    'edit_booking.close':                                 'Cerrar',
    'edit_booking.cancel':                                'Cancelar',
    'edit_booking.save':                                  'Guardar cambios',
    'edit_booking.saving':                                'Guardando…',
    'edit_booking.guest_count':                           'Número de invitados',
    'edit_booking.agreed_date':                           'Fecha del evento acordada',
    'edit_booking.start_time':                            'Hora de inicio',
    'edit_booking.duration_hrs':                          'Duración (hrs)',
    'edit_booking.notes':                                 'Notas del evento',
    'edit_booking.notes_placeholder':                     'Comentarios del cliente, seguimientos, qué recordar la próxima vez…',
    'edit_booking.contact_locked':                        'El nombre, teléfono y correo del cliente están bloqueados. Para actualizar la info de contacto, edita en {{crmName}}.',
    'edit_booking.toast.saved':                           'Reserva actualizada',
    'edit_booking.toast.error':                           'Error al guardar los cambios',

    'catering_config.heading':                            'Configuración de catering',
    'catering_config.cuisine.heading':                    'Tipos de cocina',
    'catering_config.cuisine.placeholder':                'Escribe el tipo y presiona Enter',
    'catering_config.min_order':                          'Pedido mínimo',
    'catering_config.service_area':                       'Área de servicio',
    'catering_config.service_area.placeholder':           'ej. Área metropolitana',
    'catering_config.delivery_radius':                    'Radio de entrega:',
    'catering_config.km':                                 '{{km}} km',
    'catering_config.radius_min':                         '1 km',
    'catering_config.radius_max':                         '100 km',
    'catering_config.show_service_area':                  'Mostrar área de servicio en el perfil de catering',
    'catering_config.show_service_area.hint':             'Los clientes pueden ingresar su dirección para verificar si entregas ahí',
    'catering_config.packages.heading':                   'Paquetes de menú',
    'catering_config.packages.add':                       '+ Agregar paquete',
    'catering_config.packages.name_placeholder':          'Nombre del paquete',
    'catering_config.packages.desc_placeholder':          'Descripción (platos incluidos, info dietética, etc.)',
    'catering_config.packages.per_person':                '$/persona',
    'catering_config.packages.empty':                     'Aún no hay paquetes. Agrega uno para mostrar tus opciones de catering.',
    'catering_config.photos.heading':                     'Fotos de comida',
    'catering_config.photos.caption_placeholder':         'Agregar leyenda...',
    'catering_config.photos.uploading':                   'Subiendo...',
    'catering_config.photos.add_button':                  'Agregar fotos de comida',
    'catering_config.photos.hint':                        'Muestra tus platos. Las fotos aparecen como un slideshow en tu perfil de catering.',
    'catering_config.toast.max_photos':                   'Máximo 20 fotos',
    'catering_config.toast.file_type_invalid':            '{{filename}}: solo JPEG, PNG, WebP permitidos',
    'catering_config.toast.upload_success':               '{{count}} foto(s) subida(s)',
    'catering_config.toast.saved':                        'Configuración de catering guardada',

    'proposal_submit.customer_asked_for':                 'El cliente pidió:',
    'proposal_submit.fulfilling_from':                    'Despachado desde',
    'proposal_submit.location_placeholder':               'Elige una ubicación…',
    'proposal_submit.photos.heading':                     'Fotos de la propuesta',
    'proposal_submit.photos.label':                       'Foto de la propuesta',
    'proposal_submit.photos.default_hero':                'Imagen principal predeterminada',
    'proposal_submit.photos.none_selected':               'Ninguna seleccionada — la imagen principal del lugar se mostrará en la propuesta. Haz clic en fotos para incluirlas.',
    'proposal_submit.photos.deselect_hint':               'Haz clic en una foto para deseleccionar. Las fotos seleccionadas aparecerán en la tarjeta de propuesta que el cliente ve.',
    'proposal_submit.photos.empty_settings_hint':         'Agrega fotos de catering en Configuración para elegir imágenes específicas para cada propuesta.',

    'pricing.title':                                      'Precio por fecha (cotización automática)',
    'pricing.col.date':                                   'Fecha',
    'pricing.col.time_block':                             'Bloque horario',
    'pricing.col.min_spend':                              'Consumo mínimo',
    'pricing.col.venue_rental_fee':                       'Renta del lugar',
    'pricing.closed':                                     'Lugar cerrado — fecha alternativa necesaria',
    'pricing.closest_match':                              'horario disponible más cercano',
    'pricing.holiday':                                    'feriado',
    'pricing.holiday_surcharge':                          'incluye recargo por feriado',
    'pricing.rates_pending':                              'Tarifas pendientes — el equipo de eventos confirmará',
    'pricing.dashboard_caveat':                           '',

    'quick_reply.heading.with_name':                      'Responder a {{name}}',
    'quick_reply.heading.anon':                           'Responder al cliente',
    'quick_reply.close':                                  'Cerrar compositor',
    'quick_reply.channel.email':                          'Correo',
    'quick_reply.channel.sms':                            'Texto',
    'quick_reply.body_placeholder':                       'Hola — gracias por la solicitud. Quería compartir algunos detalles rápidos antes de cerrar nada...',
    'quick_reply.send.email':                             'Enviar correo',
    'quick_reply.send.sms':                               'Enviar texto',
    'quick_reply.sending':                                'Enviando…',
    'quick_reply.toast.email_sent':                       'Correo enviado',
    'quick_reply.toast.sms_sent':                         'Mensaje de texto enviado',
    'quick_reply.sent.email':                             'Enviado por correo',
    'quick_reply.sent.sms':                               'Enviado por texto',
    'quick_reply.sent.just_now':                          'recién',
    'quick_reply.sent.minutes_ago':                       'hace {{count}} min',
    'quick_reply.sent.hours_ago':                         'hace {{count}} h',

    // R21 T2 batch 7 (2026-05-10) — anonymous landing + venue signup +
    // public venue page error states (ES).
    'common.lang.synced_from_profile':                    'Cambiado al idioma guardado en tu panel ({{lang}}).',

    'marketing.nav.call_now':                             'Llamar ahora',
    'marketing.nav.venue_login':                          'Acceso de lugar',
    'marketing.hero.title.line1':                         'RESERVA TU',
    'marketing.hero.title.line2':                         'FIESTA.',
    'marketing.hero.tagline':                             'Reserva espacios para eventos privados en {{city}}. Dinos qué necesitas, te emparejaremos con lugares.',
    'marketing.hero.cta.call':                            'Llamar',
    'marketing.hero.cta.text':                            'Texto',
    'marketing.hero.cta.subline':                         'LLAMA O ESCRIBE — MISMO ASISTENTE, MISMO NÚMERO',
    'marketing.hero.cta.browse':                          'Explorar espacios para eventos →',
    'marketing.hero.legal':                               'Al llamar o escribir, aceptas recibir SMS de BookOne.Party sobre tu solicitud de evento. La frecuencia de mensajes varía. Pueden aplicar tarifas de mensajes y datos. Responde HELP para ayuda, STOP para darte de baja.',
    'marketing.hero.legal.privacy':                       'Política de privacidad',
    'marketing.hero.legal.tos':                           'Términos de servicio',
    'marketing.help.lead':                                'Necesito un espacio para',
    'marketing.help.subline':                             'TOCA PARA EXPLORAR ESPACIOS PARA EVENTOS',
    'marketing.help.phrase.birthday':                     'una fiesta de cumpleaños',
    'marketing.help.phrase.corporate':                    'una cena corporativa',
    'marketing.help.phrase.engagement':                   'una cena de compromiso',
    'marketing.help.phrase.anniversary':                  'un aniversario sorpresa',
    'marketing.help.phrase.holiday':                      'una fiesta de fin de año',
    'marketing.help.phrase.farewell':                     'una despedida',
    'marketing.help.phrase.shower':                       'un brunch de baby shower',
    'marketing.help.phrase.graduation':                   'una celebración de graduación',
    'marketing.help.phrase.rehearsal':                    'una cena de ensayo',
    'marketing.help.phrase.offsite':                      'un retiro de equipo',
    'marketing.help.phrase.watch':                        'una fiesta para ver el partido',
    'marketing.help.phrase.launch':                       'un lanzamiento de producto',
    'marketing.statement.a':                              'Antes de BookOne, planear una fiesta privada significaba 3 semanas y 12 correos.',
    'marketing.statement.b':                              'Ahora toma una sola búsqueda.',
    'marketing.statement.c':                              'Este es el atajo.',
    'marketing.how.eyebrow':                              'CÓMO FUNCIONA',
    'marketing.how.title.a':                              'Tres pasos.',
    'marketing.how.title.b':                              'Una llamada.',
    'marketing.how.step':                                 'PASO',
    'marketing.how.step1.title':                          'Cuéntanos sobre tu evento',
    'marketing.how.step1.desc':                           'Explora espacios para eventos y comparte los detalles — fecha, número de invitados, ambiente. Guarda los lugares que te gusten.',
    'marketing.how.step2.title':                          'Te emparejamos con lugares',
    'marketing.how.step2.desc':                           'Encontramos los espacios privados que mejor se ajustan a tu evento. Los lugares envían propuestas con detalles del espacio, capacidad y precios para que puedas comparar.',
    'marketing.how.step3.title':                          'Elige tu favorito y reserva',
    'marketing.how.step3.desc':                           'Selecciona la propuesta que te encanta y confirma tu reserva. Te conectamos directamente con el lugar para finalizar todo.',
    'marketing.partners.eyebrow':                         'SOCIOS',
    'marketing.partners.title':                           'La confianza de los mejores lugares de {{city}}',
    'marketing.partners.coming_soon':                     'Próximamente',
    'marketing.partners.type.cocktail':                   'Salón de cócteles',
    'marketing.partners.type.dining':                     'Comedor privado',
    'marketing.partners.type.restaurant':                 'Restaurante y bar',
    'marketing.partners.type.rooftop':                    'Terraza',
    'marketing.testimonial1.quote':                       'BookOne hizo que reservar nuestra fiesta de fin de año fuera muy fácil. Tuvimos tres propuestas en menos de una hora.',
    'marketing.testimonial1.role':                        'Organizadora de eventos',
    'marketing.testimonial2.quote':                       'Por fin un servicio que entiende lo que necesitan los lugares. Leads calificados directo a mi teléfono.',
    'marketing.testimonial2.role':                        'Dueña de lugar',
    'marketing.testimonial3.quote':                       'Planeé toda mi cena de cumpleaños en una sola tarde. El lugar fue perfecto para nuestro grupo.',
    'marketing.testimonial3.role':                        'Cliente',
    'marketing.faq.eyebrow':                              'PREGUNTAS FRECUENTES',
    'marketing.faq.title':                                'Preguntas comunes',
    'marketing.faq.q1.q':                                 '¿Qué tan rápido recibiré las propuestas?',
    'marketing.faq.q1.a':                                 'La mayoría de los clientes reciben propuestas en 30 minutos. Emparejamos tu evento con los lugares que mejor encajan y responden rápido porque cada consulta está calificada.',
    'marketing.faq.q2.q':                                 '¿Cuál es el costo?',
    'marketing.faq.q2.a':                                 'Reservar a través de BookOne.Party es gratis para los clientes. Solo pagas la cuota de reserva del lugar cuando seleccionas una propuesta — sin cargos ocultos ni cuotas de plataforma.',
    'marketing.faq.q3.q':                                 '¿Puedo escribir en lugar de llamar?',
    'marketing.faq.q3.a':                                 'Sí. El mismo asistente IA maneja voz y texto a través de un solo número. No se requiere app.',
    'marketing.faq.q4.q':                                 '¿Puedo cambiar mi reserva después de seleccionar?',
    'marketing.faq.q4.a':                                 'Una vez que seleccionas un lugar, te conectamos directamente con el gerente del lugar. Cualquier cambio de fechas, número de invitados o detalles pasa por ellos.',
    'marketing.cta.title.a':                              'Explora espacios para tu',
    'marketing.cta.title.b':                              'fiesta hoy.',
    'marketing.cta.tagline':                              'Sin app. Sin cuenta. Sin esperas.',
    'marketing.venue_cta.title':                          '¿Organizas eventos privados?',
    'marketing.venue_cta.body':                           'Recibe solicitudes de eventos privados directamente en tu teléfono. Registro gratis, sin app.',
    'marketing.venue_cta.button':                         'Registra tu lugar →',
    'marketing.footer.tag':                               'RESERVA DE LUGAR PRIVADO — {{city}}, {{country}}',
    'marketing.footer.tagline':                           'Sin app. Sin cuenta. Sin esperas.',
    'marketing.footer.legal':                             '© 2026 1001474668 ONTARIO INC.',

    'signup.page.title':                                  'Registra tu lugar',
    'signup.page.subtitle':                               'Eventos, catering o ambos — recibe reservas por SMS. Sin app, sin cuotas de registro.',
    'signup.quickfill.title':                             'Rellenado rápido desde Google',
    'signup.quickfill.body':                              'Busca tu lugar para autocompletar nombre, dirección, teléfono y sitio web.',
    'signup.quickfill.placeholder':                       'Busca el nombre o dirección de tu lugar…',
    'signup.quickfill.success':                           '¡Formulario autocompletado! Revisa y edita los detalles abajo.',
    'signup.type.question':                               '¿Qué servicios ofrece tu lugar?',
    'signup.type.events.label':                           'Solo eventos privados',
    'signup.type.events.desc':                            'Los clientes reservan tu espacio para eventos',
    'signup.type.catering.label':                         'Solo catering',
    'signup.type.catering.desc':                          'Entregas comida; sin eventos en sitio',
    'signup.type.both.label':                             'Ambos',
    'signup.type.both.desc':                              'Realizas eventos en sitio Y entregas catering',
    'signup.venue.title':                                 'Detalles del lugar',
    'signup.venue.name_label':                            'Nombre del lugar *',
    'signup.venue.name_placeholder':                      'Taberna San Juan',
    'signup.venue.tagline_label':                         'Eslogan',
    'signup.venue.tagline_placeholder':                   'El pub más acogedor del barrio',
    'signup.venue.address_label':                         'Dirección *',
    'signup.venue.accessibility':                         'El lugar es accesible para silla de ruedas',
    'signup.contact.title':                               'Información de contacto',
    'signup.contact.name_label':                          'Tu nombre *',
    'signup.contact.phone_label':                         'Teléfono (para actualizaciones SMS) *',
    'signup.contact.phone_placeholder':                   '(555) 123-4567',
    'signup.contact.email_label':                         'Correo (opcional)',
    'signup.photo.title':                                 'Foto del lugar',
    'signup.photo.body':                                  'Esta será la foto principal en el perfil de tu lugar',
    'signup.online.title':                                'Presencia en línea',
    'signup.online.body':                                 'Ayuda a los clientes a encontrarte en línea (opcional)',
    'signup.online.website_label':                        'Sitio web',
    'signup.online.website_placeholder':                  'milugar.com',
    'signup.online.instagram_label':                      'Instagram',
    'signup.online.instagram_placeholder':                'tulugar',
    'signup.spaces.title':                                'Espacios privados',
    'signup.spaces.body':                                 'Agrega cada espacio privado disponible para reservar.',
    'signup.spaces.space_n':                              'Espacio {{n}}',
    'signup.spaces.name_placeholder':                     'Nombre del espacio (ej. The Snug, Sala privada)',
    'signup.spaces.capacity_label':                       'Capacidad máxima *',
    'signup.spaces.capacity_placeholder':                 '50',
    'signup.spaces.min_spend_label':                      'Consumo mínimo ($)',
    'signup.spaces.min_spend_placeholder':                '2500',
    'signup.spaces.fee_label':                            'Cuota de reserva ($)',
    'signup.spaces.fee_placeholder':                      '40',
    'signup.spaces.notes_placeholder':                    'Notas adicionales (política de cancelación, etc.)',
    'signup.spaces.photo_label':                          'Foto del espacio',
    'signup.spaces.add':                                  'Agregar otro espacio',
    'signup.catering.title':                              'Detalles de catering',
    'signup.catering.body':                               'Algunos detalles rápidos para emparejarte con solicitudes de catering. Puedes agregar fotos, paquetes de menú y más en Configuración después del registro.',
    'signup.catering.cuisines_label':                     'Tipos de cocina',
    'signup.catering.cuisines_placeholder':               'Escribe un tipo y presiona Enter (ej. italiana, mexicana, sushi)',
    'signup.catering.cuisines_remove':                    'Eliminar {{tag}}',
    'signup.catering.radius_label':                       'Radio de entrega (km)',
    'signup.catering.radius_placeholder':                 '25',
    'signup.catering.minimum_label':                      'Pedido mínimo ($)',
    'signup.catering.minimum_placeholder':                'opcional',
    'signup.catering.footer':                             'Todos los campos de catering son opcionales al registrarse. Puedes agregar fotos de catering, paquetes de menú y más desde Configuración después de crear tu lugar.',
    'signup.menus.title':                                 'Menús / Detalles adicionales (opcional)',
    'signup.menus.body':                                  'Sube menús o detalles del evento — PDF o imagen, hasta 5.',
    'signup.menus.menu_n':                                'Menú {{n}}',
    'signup.menus.name_placeholder':                      'Nombre (ej. Menú degustación, Paquete de evento)',
    'signup.menus.description_placeholder':               'Descripción (opcional)',
    'signup.menus.add':                                   'Agregar un menú',
    'signup.notes.label':                                 'Notas adicionales (opcional)',
    'signup.notes.placeholder':                           '¿Algo más que debamos saber?',
    'signup.consent.body':                                'Acepto recibir SMS de BookOne.Party sobre solicitudes de reserva, actualizaciones de eventos, códigos de verificación (OTP) y mensajes relacionados con el servicio. La frecuencia de mensajes varía. Pueden aplicar tarifas de mensajes y datos. Responde HELP para ayuda, STOP para darte de baja. He leído y acepto los',
    'signup.consent.tos':                                 'Términos de servicio',
    'signup.consent.and':                                 'y',
    'signup.consent.privacy':                             'Política de privacidad',
    'signup.submit':                                      'Registrar tu lugar',
    'signup.error.generic':                               'Algo salió mal',
    'signup.error.consent':                               'Debes aceptar recibir SMS para registrar un lugar',
    'signup.error.name':                                  'El nombre del lugar es obligatorio',
    'signup.error.address':                               'La dirección es obligatoria',
    'signup.error.contact_name':                          'Tu nombre es obligatorio',
    'signup.error.phone_required':                        'El número de teléfono es obligatorio',
    'signup.error.phone_invalid':                         'Ingresa un número de teléfono válido de Norteamérica',
    'signup.error.hero_size':                             'La imagen principal debe ser menor a 5 MB',
    'signup.error.space_name':                            'El nombre del espacio es obligatorio',
    'signup.error.space_capacity':                        'La capacidad máxima debe ser al menos 1',
    'signup.error.space_image':                           'La imagen del espacio debe ser menor a 5 MB',
    'signup.error.menu_name':                             'El nombre del menú es obligatorio',
    'signup.error.menu_file':                             'El archivo del menú es obligatorio',
    'signup.error.menu_size':                             'El archivo del menú debe ser menor a 10 MB',
    'signup.error.minimum_negative':                      'El pedido mínimo no puede ser negativo',
    'signup.error.delivery_negative':                     'El radio de entrega no puede ser negativo',
    'signup.success.title':                               '¡Estás registrado!',
    'signup.success.intro':                               'Tu lugar está registrado en BookOne.Party. Esto es lo que sigue:',
    'signup.success.step1.title':                         'Solicitudes de reserva por SMS',
    'signup.success.step1.desc':                          'Cuando el evento de un cliente coincida con tu espacio, recibirás un texto con los detalles.',
    'signup.success.step2.title':                         'Envía tu propuesta',
    'signup.success.step2.desc':                          'Haz clic en el enlace de la notificación SMS para enviar tu propuesta desde el panel.',
    'signup.success.step3.title':                         'Conéctate',
    'signup.success.step3.desc':                          'Si te seleccionan, te conectamos directamente con el cliente para finalizar.',
    'signup.success.cta.dashboard':                       'Iniciar sesión en el panel',
    'signup.success.cta.home':                            'Volver al inicio',

    'venue_profile.error.title':                          'Algo salió mal',
    'venue_profile.error.body':                           'No pudimos cargar este lugar. Por favor intenta de nuevo.',
    'venue_profile.error.retry':                          'Intentar de nuevo',
    'venue_profile.error.home':                           'Volver al inicio',
    'venue_profile.not_found.title':                      'Lugar no encontrado',
    'venue_profile.not_found.body':                       'No encontramos un lugar con ese ID. Puede haber sido eliminado o el enlace puede ser incorrecto.',
    'venue_profile.not_found.home':                       'Volver al inicio',

    'settings.booking_mgmt.toast_enabled':                'Gestión de reservas activada',
    'settings.booking_mgmt.toast_disabled':               'Gestión de reservas desactivada',
    'settings.booking_mgmt.toast_save_failed':            'No se pudo guardar — {{error}}',
    'settings.booking_mgmt.error_save_default':           'Error al guardar',

    'dashboard.analytics.customer_list.toast.update_reminder_failed': 'Error al actualizar el recordatorio',
    'dashboard.analytics.customer_list.toast.save_notes_failed':      'Error al guardar las notas',

    'catering_config.toast.upload_failed':                'Error al subir',
    'catering_config.toast.max_menus':                    'Máximo 10 menús',
    'catering_config.toast.menu_uploaded':                'Menú subido',
    'catering_config.toast.save_failed':                  'Error al guardar',

    'settings.tripleseat.toast.validation_errors':        'Por favor corrige los errores de validación antes de guardar.',
    'settings.tripleseat.toast.saved':                    'Configuración de Tripleseat guardada',
    'settings.tripleseat.toast.save_failed':              'Error al guardar la configuración de Tripleseat',
    'settings.tripleseat.confirm.switch_live':            '¿Cambiar a envíos en vivo de Tripleseat?\n\nEsto enviará leads reales a tu cuenta de Tripleseat. Asegúrate de haber probado con el endpoint simulado primero.',

    'proposals.tripleseat_preview.toast.script_copied':    'Script de autocompletado copiado — pégalo en la consola del navegador del formulario',
    'proposals.tripleseat_preview.toast.copy_failed':      'No se pudo copiar — usa los botones de copia individuales abajo',
    'proposals.tripleseat_preview.toast.marked_forwarded': 'Marcado como enviado. El cliente ha sido notificado.',

    'booking.external_crm_tracker.toast.note_required':    'Agrega una nota breve describiendo qué pasó',
    'booking.external_crm_tracker.toast.contact_logged':   'Contacto registrado',
    'booking.external_crm_tracker.toast.save_failed':      'Error al guardar',
    'booking.external_crm_tracker.toast.status_advanced':  'Estado: {{status}}',
    'booking.external_crm_tracker.toast.update_status_failed': 'Error al actualizar el estado',
    'booking.external_crm_tracker.toast.saved':            'Guardado',

    'menu_upload.toast.file_too_large':                    'Archivo demasiado grande ({{size}}MB). Máximo: 10MB.',
    'menu_upload.drop_or_click':                           'Suelta un PDF o imagen, o haz clic para subir',
    'menu_upload.file_hint':                               'PDF, JPEG, PNG o WebP — Máx 10MB',

    'image_upload.drop_or_click':                          'Suelta una imagen o haz clic para subir',
    'image_upload.file_hint':                              'JPEG, PNG o WebP — Máx 5MB',

    'browse_nav.button':                                   'Explorar',
    'browse_nav.category.venues.label':                    'Lugares',
    'browse_nav.category.venues.desc':                     'Reserva espacios y lugares para eventos privados',
    'browse_nav.category.catering.label':                  'Catering',
    'browse_nav.category.catering.desc':                   'Pide catering para tu próximo evento',
    'browse_nav.category.experiences.label':               'Experiencias',
    'browse_nav.category.experiences.desc':                'Golf, carreras, escape rooms y más',

    'browse.hero.back_to_home':                            '← Volver al inicio',
    'browse.venues.title':                                 'Explorar lugares',
    'browse.venues.subtitle':                              'Explora espacios y lugares para eventos privados.',
    'browse.experiences.title':                            'Explorar experiencias',
    'browse.experiences.subtitle':                         'Espacios alternativos y experiencias únicas para eventos.',
    'browse.catering.title':                               'Explorar catering',
    'browse.catering.subtitle':                            'Comida y bebidas entregadas a tu evento.',
    'browse.catering.address_placeholder':                 'Ingresa tu dirección para ver quién entrega...',

    'venue_profile.back_to_venues':                        'Explorar lugares',
    'venue_profile.back_to_catering':                      'Volver a catering',
    'venue_profile.space.up_to':                           'Hasta {{n}}',
    'venue_profile.section.private_spaces':                'Espacios privados',
    'venue_profile.section.menu_additional':               'Menú / Detalles adicionales',
    'venue_profile.section.our_food':                      'Nuestra comida',
    'venue_profile.section.our_locations':                 'Nuestras ubicaciones',
    'venue_profile.section.catering_menus':                'Menús de catering',
    'venue_profile.section.delivery_range':                'Rango de entrega',
    'venue_profile.widget.title_event':                    'Reserva un evento privado en {{venueName}}',
    'venue_profile.widget.description_event':              'Chatea con nuestro asistente para planear tu noche.',
    'venue_profile.widget.title_catering':                 'Planea un evento en {{venueName}}',
    'venue_profile.widget.description_catering':           'Solicita una cotización — llama o chatea con el equipo.',
    'venue_profile.cta.call_to_book':                      'Llamar para reservar',
    'venue_profile.cta.text_to_book':                      'Escribir para reservar',
    'venue_profile.cta.ready_to_book':                     '¿Listo para reservar?',
    'venue_profile.cta.text_or_call_direct':               'Escribe o llama a {{venueName}} directamente',
    'venue_profile.cta.text_to_book_direct':               'Escribe para reservar este lugar directamente',
    'venue_profile.locations.subtitle':                    '{{count}} ubicaciones en {{region}} — elige la más cercana, o verifica tu dirección de entrega abajo.',
    'venue_profile.locations.delivery_check':              'Verifica si entregamos a tu dirección',
    'venue_profile.menu.online_menu':                      'Menú en línea',

    'dashboard.settings.service_types.toast.updated':      'Tipos de servicio actualizados',
    'dashboard.settings.service_types.toast.update_failed':'Error al actualizar',

    'dashboard.live_transfers.toast.dismissed':            'Descartado',
    'dashboard.live_transfers.toast.dismiss_failed':       'Error al descartar',
    'dashboard.live_transfers.toast.restored':             'Restaurado',
    'dashboard.live_transfers.toast.restore_failed':       'Error al restaurar',

    // /admin/groups/new — Asistente de configuración de grupos hosteleros
    'admin.groups.new.title':              'Crear un nuevo grupo',
    'admin.groups.new.subtitle':           'Configura un grupo hostelero, un local principal, sus locales hermanos y el personal.',
    'admin.groups.new.progress':           'Paso {{current}} de {{total}}',
    'admin.groups.new.back':               'Atrás',
    'admin.groups.new.continue':           'Continuar',
    'admin.groups.new.submit':             'Abrir PR de staging',
    'admin.groups.new.draft.creating':     'Creando borrador\u2026',
    'admin.groups.new.draft.saving':       'Guardando\u2026',
    'admin.groups.new.draft.error':        'No se pudo guardar tu progreso. ¿Reintentar?',
    'admin.groups.new.draft.load_error':   'No se pudo cargar el borrador.',
    'admin.groups.new.required':           'Obligatorio',
    'admin.groups.new.step1.title':        'Elige un arquetipo',
    'admin.groups.new.step1.intro':        '¿Un local o varios? Esta respuesta guía el resto del asistente.',
    'admin.groups.new.step1.standalone.label':       'Local independiente',
    'admin.groups.new.step1.standalone.description': 'Un local, un teléfono, un widget. Usa /admin/venues/new.',
    'admin.groups.new.step1.hospitality.label':      'Grupo hostelero',
    'admin.groups.new.step1.hospitality.description':'Local principal + locales hermanos, equipo de eventos compartido. (Recomendado)',
    'admin.groups.new.step1.franchise.label':        'Franquicia',
    'admin.groups.new.step1.franchise.description':  'Una marca, varias ubicaciones, gerentes por ubicación. Usa /admin/venues/new (modo franquicia).',
    'admin.groups.new.step1.redirect_notice':        'Este arquetipo se gestiona en /admin/venues/new. Te redirigiremos.',
    'admin.groups.new.step2.title':        'Identidad del grupo',
    'admin.groups.new.step2.name.label':   'Nombre del grupo',
    'admin.groups.new.step2.name.help':    'El nombre legal o comercial que verán los clientes en su panel.',
    'admin.groups.new.step2.slug.label':   'Identificador (slug)',
    'admin.groups.new.step2.slug.help':    'Identificador apto para URL. Se deriva del nombre; editable.',
    'admin.groups.new.step2.parent.label': 'Local principal',
    'admin.groups.new.step2.parent.yes':   'Sí — el grupo tiene un local principal que recibe sus propias reservas',
    'admin.groups.new.step2.parent.no':    'No — el grupo es solo un contenedor organizacional',
    'admin.groups.new.step2.error.slug':   'El slug debe contener solo minúsculas, dígitos o guiones.',
    'admin.groups.new.step2.error.name':   'El nombre es obligatorio.',
    'admin.groups.new.step3.title':        'Local principal',
    'admin.groups.new.step3.intro':        'Enlaza con un local existente o crea uno más adelante.',
    'admin.groups.new.step3.link.label':   'Local existente',
    'admin.groups.new.step3.link.placeholder': 'Elegir un local existente\u2026',
    'admin.groups.new.step3.new.label':    'Crear un nuevo local principal después del asistente',
    'admin.groups.new.step3.skip':         'Omitido — el grupo no tiene local principal.',
    'admin.groups.new.step4.title':        'Locales hermanos',
    'admin.groups.new.step4.intro':        'Agrega cada local del grupo. Cada uno hereda el ID del grupo.',
    'admin.groups.new.step4.add':          'Agregar local hermano',
    'admin.groups.new.step4.placeholder':  'Slug del local hermano (ej. akiro)',
    'admin.groups.new.step4.existing.label':  'Enlazar con local existente',
    'admin.groups.new.step4.new.label':    'Marcador — crear después',
    'admin.groups.new.step4.empty':        'Aún no se agregaron hermanos.',
    'admin.groups.new.step4.error.duplicate': 'Slug duplicado.',
    'admin.groups.new.step4.error.invalid':   'El slug debe contener solo minúsculas, dígitos o guiones.',
    'admin.groups.new.step4.remove.aria':  'Quitar local hermano',
    'admin.groups.new.step5.title':        'Personal',
    'admin.groups.new.step5.intro':        '¿Quién gestiona este grupo? Agrega al menos un gerente de eventos.',
    'admin.groups.new.step5.add_group':    'Agregar gerente de eventos',
    'admin.groups.new.step5.add_venue':    'Agregar gerente de local',
    'admin.groups.new.step5.name.label':   'Nombre',
    'admin.groups.new.step5.phone.label':  'Teléfono (E.164)',
    'admin.groups.new.step5.phone.placeholder': '+15551234567',
    'admin.groups.new.step5.email.label':  'Correo',
    'admin.groups.new.step5.venue.label':  'Local',
    'admin.groups.new.step5.channel.label':'Canal de notificación',
    'admin.groups.new.step5.channel.sms':  'SMS',
    'admin.groups.new.step5.channel.email':'Correo',
    'admin.groups.new.step5.channel.both': 'Ambos',
    'admin.groups.new.step5.empty':        'Aún no hay personal agregado.',
    'admin.groups.new.step5.remove.aria':  'Quitar entrada',
    'admin.groups.new.step5.role.group_manager':    'Gerente de eventos (grupo)',
    'admin.groups.new.step5.role.venue_manager':    'Gerente de local',
    'admin.groups.new.step5.role.location_manager': 'Gerente de ubicación',
    'admin.groups.new.step5.error.required':        'Se requiere al menos un gerente de eventos.',
    'admin.groups.new.step5.error.phone':           'El teléfono debe estar en formato E.164 (+1\u2026).',
    'admin.groups.new.step5.error.duplicate_phone': 'Este teléfono ya está en la lista.',
    'admin.groups.new.step6.title':        'Revisión',
    'admin.groups.new.step6.intro':        'Confirma el manifiesto generado abajo. Regresa para corregir.',
    'admin.groups.new.step6.preview':      'Manifiesto generado',
    'admin.groups.new.step6.validation.title': 'Validación del manifiesto',
    'admin.groups.new.step6.validation.ok':    'Manifiesto válido.',
    'admin.groups.new.step6.validation.errors':'Corrige lo siguiente antes de enviar:',
    'admin.groups.new.step6.submit_hint':   'Enviar abre un PR de GitHub al branch staging. Mergear dispara el deploy.',
    'admin.groups.new.step7.title':        'PR de staging',
    'admin.groups.new.step7.intro':        'El asistente abrió este PR. Revisa el diff, mergea, observa CI.',
    'admin.groups.new.step7.pr_url.label': 'Pull request',
    'admin.groups.new.step7.pr_url.open':  'Abrir en GitHub',
    'admin.groups.new.step7.refresh':      'Actualizar estado',
    'admin.groups.new.step7.checking':     'Verificando\u2026',
    'admin.groups.new.step7.error':        'La envío falló:',
    'admin.groups.new.step8.title':        'Producción',
    'admin.groups.new.step8.intro':        'Una vez mergeado el PR de staging y verificado, ejecuta /go-live para promover a producción.',
    'admin.groups.new.step8.gate':         'El asistente no promueve a producción automáticamente — primero staging.',
    'admin.groups.new.step8.done':         'Listo',

    // /admin/groups/[id]
    'admin.groups.detail.title':           '{{name}}',
    'admin.groups.detail.subtitle':        'Slug del grupo: {{slug}}',
    'admin.groups.detail.archetype':       'Arquetipo',
    'admin.groups.detail.parent':          'Sede principal',
    'admin.groups.detail.no_parent':       'Sin sede principal',
    'admin.groups.detail.loading':         'Cargando grupo…',
    'admin.groups.detail.error':           'No se pudo cargar el grupo',
    'admin.groups.detail.back':            '← Todos los grupos',
    'admin.groups.detail.tab.members':     'Miembros',
    'admin.groups.detail.tab.staff':       'Personal',
    'admin.groups.detail.tab.audit':       'Auditoría',
    'admin.groups.detail.tab.manifest':    'Manifiesto',
    'admin.groups.detail.members.empty':   'Aún no hay sedes miembro.',
    'admin.groups.detail.members.col.name':   'Nombre',
    'admin.groups.detail.members.col.slug':   'Slug',
    'admin.groups.detail.members.col.status': 'Estado',
    'admin.groups.detail.members.manage':  'Gestionar',
    'admin.groups.detail.staff.empty':     'Sin personal vinculado a este grupo.',
    'admin.groups.detail.staff.col.name':    'Nombre',
    'admin.groups.detail.staff.col.phone':   'Teléfono',
    'admin.groups.detail.staff.col.role':    'Rol',
    'admin.groups.detail.staff.col.channel': 'Notificar por',
    'admin.groups.detail.staff.edit':      'Editar',
    'admin.groups.detail.audit.empty':     'Sin eventos de auditoría para este grupo.',
    'admin.groups.detail.manifest.empty':  'Sin previsualización de manifiesto.',
    'admin.groups.detail.unlink_cta':      'Desvincular sede del grupo',
    'admin.groups.detail.unlink.confirm.title': '¿Desvincular esta sede?',
    'admin.groups.detail.unlink.confirm.body':  'Al desvincular se quita la sede del grupo. Sus datos se conservan y puedes volver a vincularla.',
    'admin.groups.detail.unlink.confirm.cta':   'Desvincular',
    'admin.groups.detail.unlink.cancel':   'Cancelar',

    // /admin/staff — lista
    'admin.staff.list.add_button':         '+ Añadir personal',
    'admin.staff.list.row.tooltip':        'Abrir miembro',

    // /admin/staff/new
    'admin.staff.new.title':               'Añadir miembro del personal',
    'admin.staff.new.subtitle':            'Crear una nueva fila de administrador. Podrás ajustar las notificaciones en la página de detalle.',
    'admin.staff.new.field.name.label':    'Nombre',
    'admin.staff.new.field.name.placeholder': 'Nombre visible',
    'admin.staff.new.field.phone.label':   'Teléfono de inicio de sesión (E.164)',
    'admin.staff.new.field.phone.placeholder': '+15551234567',
    'admin.staff.new.field.phone.help':    'Formato E.164 — código de país + dígitos, sin espacios.',
    'admin.staff.new.field.notification_email.label': 'Correo de notificación',
    'admin.staff.new.field.notification_email.placeholder': 'staff@example.com',
    'admin.staff.new.field.notification_channel.label': 'Canal de notificación',
    'admin.staff.new.channel.sms':         'SMS',
    'admin.staff.new.channel.email':       'Correo',
    'admin.staff.new.channel.both':        'SMS + Correo',
    'admin.staff.new.field.role.label':    'Rol',
    'admin.staff.new.role.group_manager':  'Gerente de grupo',
    'admin.staff.new.role.venue_manager':  'Gerente de local',
    'admin.staff.new.role.location_manager': 'Gerente de ubicación',
    'admin.staff.new.role.help.group_manager':    'Ve todos los locales de los grupos seleccionados.',
    'admin.staff.new.role.help.venue_manager':    'Ve todas las reservas de los locales seleccionados.',
    'admin.staff.new.role.help.location_manager': 'Ve solo las reservas de los pares listados.',
    'admin.staff.new.field.scope.groups.label':   'Grupos gestionados',
    'admin.staff.new.field.scope.groups.help':    'Elige uno o varios grupos. El gerente verá todos sus locales.',
    'admin.staff.new.field.scope.venues.label':   'Locales gestionados',
    'admin.staff.new.field.scope.venues.help':    'Elige uno o varios locales. El gerente verá todas sus reservas.',
    'admin.staff.new.field.scope.locations.label': 'Ubicaciones gestionadas',
    'admin.staff.new.field.scope.locations.help':  'Elige un local, luego una ubicación, y añade el par. Repite para cada ubicación.',
    'admin.staff.new.picker.search.groups':        'Buscar grupos…',
    'admin.staff.new.picker.search.venues':        'Buscar locales…',
    'admin.staff.new.picker.loading.groups':       'Cargando grupos…',
    'admin.staff.new.picker.loading.venues':       'Cargando locales…',
    'admin.staff.new.picker.error.groups':         'No se pudieron cargar los grupos. Refresca para reintentar.',
    'admin.staff.new.picker.error.venues':         'No se pudieron cargar los locales. Refresca para reintentar.',
    'admin.staff.new.picker.empty.groups':         'No se encontraron grupos.',
    'admin.staff.new.picker.empty.venues':         'No se encontraron locales.',
    'admin.staff.new.picker.empty.locations':      'Ninguno de los locales disponibles tiene ubicaciones configuradas.',
    'admin.staff.new.picker.location.venue.label': 'Local',
    'admin.staff.new.picker.location.venue.placeholder': 'Selecciona un local…',
    'admin.staff.new.picker.location.slug.label':  'Ubicación',
    'admin.staff.new.picker.location.slug.placeholder': 'Selecciona una ubicación…',
    'admin.staff.new.picker.location.add':         '+ Añadir ubicación',
    'admin.staff.new.picker.location.added':       'Ubicaciones añadidas',
    'admin.staff.new.picker.location.remove.aria': 'Quitar ubicación',
    'admin.staff.new.picker.selected.count':       '{{count}} seleccionados',
    'admin.staff.new.submit':              'Crear miembro',
    'admin.staff.new.submitting':          'Creando…',
    'admin.staff.new.cancel':              'Cancelar',
    'admin.staff.new.error.generic':       'No se pudo crear el miembro — revisa los campos.',
    'admin.staff.new.error.conflict':      'Ya existe un miembro con ese teléfono.',
    'admin.staff.new.error.scope_required.group_manager':    'Elige al menos un grupo.',
    'admin.staff.new.error.scope_required.venue_manager':    'Elige al menos un local.',
    'admin.staff.new.error.scope_required.location_manager': 'Añade al menos un par {venueId, locationSlug}.',
    'admin.staff.new.error.invalid_phone':  'El teléfono debe estar en formato E.164 (ej. +15551234567).',
    'admin.staff.new.error.invalid_locations_json': 'Ubicaciones debe ser JSON válido — un arreglo de objetos {venueId, locationSlug}.',
    'admin.staff.new.success':              'Miembro creado.',

    // /admin/staff/[id]
    'admin.staff.detail.title':            '{{name}}',
    'admin.staff.detail.subtitle':         'Detalle del miembro del personal',
    'admin.staff.detail.loading':          'Cargando personal…',
    'admin.staff.detail.error':            'No se pudo cargar el miembro',
    'admin.staff.detail.back':             '← Todo el personal',
    'admin.staff.detail.section.identity': 'Identidad',
    'admin.staff.detail.section.access':   'Rol y ámbito',
    'admin.staff.detail.section.notifications': 'Notificaciones',
    'admin.staff.detail.section.login_history': 'Historial de inicios de sesión',
    'admin.staff.detail.section.audit':    'Actividad reciente',
    'admin.staff.detail.section.actions':  'Acciones',
    'admin.staff.detail.field.name':       'Nombre',
    'admin.staff.detail.field.phone':      'Teléfono',
    'admin.staff.detail.field.email':      'Correo',
    'admin.staff.detail.field.role':       'Rol',
    'admin.staff.detail.field.disabled':   'Deshabilitado',
    'admin.staff.detail.field.scope.venues': 'Sedes gestionadas',
    'admin.staff.detail.field.scope.groups': 'Grupos gestionados',
    'admin.staff.detail.field.scope.locations': 'Ubicaciones gestionadas',
    'admin.staff.detail.field.scope.empty':  'Sin ámbito asignado',
    'admin.staff.detail.field.notify_phone': 'Teléfono de notificación',
    'admin.staff.detail.field.notify_email': 'Correo de notificación',
    'admin.staff.detail.field.notify_channel': 'Canal de notificación',
    'admin.staff.detail.field.last_login':  'Último inicio de sesión',
    'admin.staff.detail.field.created':     'Creado',
    'admin.staff.detail.login_history.empty':   'El historial de inicios de sesión aún no se registra.',
    'admin.staff.detail.audit.empty':       'Sin actividad reciente.',
    'admin.staff.detail.role.platform_admin':  'Admin de plataforma',
    'admin.staff.detail.role.group_manager':   'Gestor de grupo',
    'admin.staff.detail.role.venue_manager':   'Gestor de sede',
    'admin.staff.detail.role.location_manager':'Gestor de ubicación',
    'admin.staff.detail.channel.sms':       'SMS',
    'admin.staff.detail.channel.email':     'Correo',
    'admin.staff.detail.channel.both':      'SMS + Correo',
    'admin.staff.detail.save':              'Guardar cambios',
    'admin.staff.detail.saving':            'Guardando…',
    'admin.staff.detail.save_ok':           'Cambios guardados',
    'admin.staff.detail.save_fail':         'No se pudieron guardar los cambios',
    'admin.staff.detail.disable':           'Deshabilitar',
    'admin.staff.detail.disable.confirm':   '¿Deshabilitar a este miembro? Perderá todo acceso hasta que se reactive.',
    'admin.staff.detail.enable':            'Habilitar',
    'admin.staff.detail.rotate_phone':      'Rotar teléfono',
    'admin.staff.detail.rotate_phone.confirm': '¿Rotar el teléfono de inicio de sesión? El miembro deberá usar el nuevo número.',
    'admin.staff.detail.rotate_phone.prompt':  'Nuevo teléfono (formato E.164, p. ej. +15551234567):',
    'admin.staff.detail.force_reauth':       'Forzar reautenticación',
    'admin.staff.detail.force_reauth.confirm': '¿Forzar reinicio de sesión? Las sesiones actuales se invalidarán.',
    'admin.staff.detail.force_reauth.ok':    'Sesiones invalidadas',
    'admin.staff.detail.delete':             'Eliminar',
    'admin.staff.detail.delete.confirm':     '¿Eliminar permanentemente? Acción irreversible. Escribe el teléfono del miembro para confirmar.',

    // /admin/audit — filtros + exportación CSV + modo en vivo
    'admin.audit.filter.title':            'Filtros',
    'admin.audit.filter.actor':            'Actor',
    'admin.audit.filter.actor.placeholder':'ID o rol del actor',
    'admin.audit.filter.action':           'Acción',
    'admin.audit.filter.action.placeholder':'p. ej. submit_proposal',
    'admin.audit.filter.target_type':      'Tipo de objetivo',
    'admin.audit.filter.target_type.all':  'Todos los tipos',
    'admin.audit.filter.date_from':        'Desde',
    'admin.audit.filter.date_to':          'Hasta',
    'admin.audit.filter.apply':            'Aplicar',
    'admin.audit.filter.reset':            'Restablecer',
    'admin.audit.filter.open':             'Filtrar',
    'admin.audit.csv_export':              'Exportar CSV',
    'admin.audit.csv_export.empty':        'Nada para exportar.',
    'admin.audit.live_toggle':             'En vivo',
    'admin.audit.live_toggle.aria':        'Activar el modo en vivo',
    'admin.audit.row.expand':              'Ver detalles',
    'admin.audit.row.collapse':            'Cerrar',
    'admin.audit.row.payload':             'Carga útil',
    'admin.audit.row.impersonated':        'Suplantado como',
  },
  // Round 21 (2026-05-10) — Italian translations.
  it: {
    // widget.*
    'widget.lang.toggle.aria':     'Cambia lingua',
    'widget.lang.en':              'English',
    'widget.lang.fr':              'Français',
    'widget.idle.title.default':   'Pianifica un evento presso {{venueName}}',
    'widget.group.other.label':    'Ti interessano le nostre altre sedi?',
    'widget.group.other.aria':     'Altre sedi',
    'widget.idle.description':     'Chiama o chatta con il nostro team.',
    'widget.idle.call':            'Chiama',
    'widget.idle.chat':            'Chatta',
    'widget.callMode.speakHint':   'Puoi scrivere i tuoi dati — oppure dirli semplicemente ad alta voce.',
    'widget.idle.loudroom':        'Locale Rumoroso — tocca per parlare',
    'widget.idle.loudroom.hint':   'Per bar + ristoranti — il microfono parte muto, premi per parlare.',
    'widget.idle.mic_blocked':     'Microfono bloccato — puoi chattare con il testo qui sotto.',
    'widget.active.mic_muted':     'Muto',
    'widget.active.mic_unmuted':   'In ascolto',
    'widget.active.agent.speaking':'Assistente parla',
    'widget.active.agent.thinking':'Sto pensando…',
    'widget.active.type_hint':     'Scrivi il tuo messaggio…',
    'widget.active.send':          'Invia',
    'widget.active.end':           'Termina',
    'widget.active.mute':          'Muta',
    'widget.active.unmute':        'Riattiva',
    'widget.active.hold_to_talk':  'Tieni premuto per parlare',
    'widget.active.switch_to_chat':'Passa alla chat',
    'widget.active.switch_to_voice':'Passa alla voce',
    'widget.active.no_audio':      'Niente audio? Tocca per passare alla chat.',
    'widget.active.reconnect':     'Audio interrotto — tocca per riconnettere.',
    'widget.ended.thanks':         'Grazie — il team di {{venueName}} ti contatterà.',
    'widget.ended.restart':        'Fai un’altra domanda',
    'widget.ended.view_booking':   'Vedi la tua prenotazione',
    'widget.ended.status.sent':    'Inviato al team — ci sentiremo presto',
    'widget.ended.status.cancelled':'Conversazione terminata',

    // booking.*
    'booking.title':               'La tua prenotazione con {{venueName}}',
    'booking.headline.default':    'La tua prenotazione',
    'booking.headline.greeting':   'Ciao {{name}}',
    'booking.headline.anon':       'La tua richiesta di prenotazione',
    'booking.headline.direct_event':    'Prenotazione diretta con',
    'booking.headline.direct_catering': 'Catering diretto con',
    'booking.fallback.venue':      'sede',
    'booking.event_guide.cta':     'Vedi la guida agli eventi di {{location}}',
    'booking.pricing.caveat':      'Il team eventi confermerà il prezzo finale via email.',
    'booking.venue_reply.heading': 'Risposta da {{venue}}',
    'booking.venue_reply.via.email': 'Inviato via email',
    'booking.venue_reply.via.sms':   'Inviato via SMS',

    'booking.step.intake_in_progress': 'Raccolta dei dettagli',
    'booking.step.intake_complete':    'Dettagli completati',
    'booking.step.awaiting_response':  'In attesa di risposta',
    'booking.step.finding_venues':     'Ricerca delle sedi',
    'booking.step.proposals_received': 'Proposte ricevute',
    'booking.step.consumer_selecting': 'Seleziona la tua sede',
    'booking.step.consumer_confirmed': 'Confermato — pagamento in sospeso',
    'booking.step.booked':             'Abbinato!',

    'booking.status.pending':      'Abbiamo ricevuto la tua richiesta.',
    'booking.status.reviewing':    'Stiamo cercando sedi per te…',
    'booking.status.proposals':    'Hai proposte da rivedere.',
    'booking.status.selected':     'Prenotazione confermata.',
    'booking.status.completed':    'Evento completato. Speriamo sia andato benissimo!',

    'booking.loading':             'Caricamento della prenotazione…',
    'booking.error.not_found':     'Non troviamo questa prenotazione. Verifica il link e riprova.',
    'booking.error.load_failed':   'Impossibile caricare i dettagli: {{message}}',
    'booking.not_found':           'Prenotazione non trovata.',
    'booking.preparing.title':     'Preparazione della prenotazione',
    'booking.preparing.line1':     'Grazie per averci contattato — stiamo mettendo insieme i dettagli.',
    'booking.preparing.line2':     'Questa pagina si aggiornerà da sola.',

    'booking.details.header.event':    'Dettagli evento',
    'booking.details.header.catering': 'Dettagli catering',
    'booking.details.audience':        'Pubblico',
    'booking.details.audience.company':  'Evento aziendale',
    'booking.details.audience.personal': 'Evento personale',
    'booking.details.date':            'Data',
    'booking.details.date.tbd':        'Da definire',
    'booking.details.time':            'Ora',
    'booking.details.guests':          'Ospiti',
    'booking.details.duration':        'Durata',
    'booking.details.duration_hours':  '{{hours}} ora',
    'booking.details.duration_hours.plural': '{{hours}} ore',
    'booking.details.food':            'Cibo',
    'booking.details.bar':             'Bar',
    'booking.details.music':           'Musica',
    'booking.details.budget':          'Budget',
    'booking.details.booking_type':    'Tipo di prenotazione',
    'booking.details.package':         'Pacchetto',
    'booking.details.email':           'Email',
    'booking.details.phone':           'Telefono',
    'booking.details.company':         'Azienda',
    'booking.details.delivery_address':'Indirizzo di consegna',
    'booking.details.service_type':    'Tipo di servizio',
    'booking.details.event_vision':    'Visione dell’evento',
    'booking.details.additional_notes':'Note aggiuntive',
    'booking.details.preferences':     'Le tue preferenze',
    'booking.details.dietary':         'Alimentari',
    'booking.details.accessibility':   'Accessibilità',
    'booking.details.booking_details': 'Dettagli della prenotazione',
    'booking.details.event_type':      'Tipo di evento',
    'booking.details.preferred_dates': 'Date preferite',

    // R21 T2 (2026-05-10) — Customer-side intake field labels (IT).
    'booking.edit.event_type.placeholder':      'es. Festa di compleanno',
    'booking.edit.min_guests':                  'Ospiti minimi',
    'booking.edit.max_guests':                  'Ospiti massimi',
    'booking.edit.arrival_time':                'Orario di arrivo',
    'booking.edit.arrival_time.placeholder':    'es. 19:00',
    'booking.edit.duration':                    'Durata (ore)',
    'booking.edit.duration.placeholder':        'es. 3',
    'booking.edit.email.placeholder':           'email@esempio.com',
    'booking.edit.company.placeholder':         'Nome dell’azienda o "personale"',
    'booking.edit.delivery.placeholder':        'Inizia a digitare l’indirizzo di consegna…',
    'booking.edit.select_placeholder':          'Seleziona…',
    'booking.edit.event_vision.placeholder':    'Descrivi la tua visione dell’evento…',
    'booking.edit.notes.placeholder':           'Dettagli aggiuntivi…',
    'booking.edit.add_date':                    '+ Aggiungi',
    'booking.edit.save':                        'Salva modifiche',
    'booking.edit.cancel':                      'Annulla',
    'booking.edit.save_failed':                 'Errore nel salvare le modifiche',

    'booking.food.food_required':    'Cibo richiesto',
    'booking.food.drinks_only':      'Solo bevande',
    'booking.food.food_and_drinks':  'Cibo e bevande',
    'booking.bar.cash_bar_minimum_spend': 'Bar a pagamento (spesa minima)',
    'booking.bar.open_bar':           'Open bar',
    'booking.bar.consumption_tab':    'Conto a consumo',
    'booking.bar.other':              'Altro',
    'booking.music.spotify_playlist': 'Playlist Spotify',
    'booking.music.dj':               'DJ',
    'booking.music.none':             'Nessuna preferenza',
    'booking.service.pickup':         'Ritiro',
    'booking.service.delivery':       'Consegna',
    'booking.service.full_service':   'Servizio completo (con personale)',
    'booking.service.full_service.short': 'Servizio completo',

    // R21 T2 (2026-05-10) — Venue staff login + magic link auth (IT).
    'auth.login.title':                  'Accesso sede',
    'auth.login.subtitle':               'Accedi per gestire le tue richieste di prenotazione.',
    'auth.login.tab.phone':              'Telefono',
    'auth.login.tab.email':              'Email',
    'auth.login.label.phone':            'Numero di telefono',
    'auth.login.label.email':            'Indirizzo email',
    'auth.login.label.code':             'Codice di verifica',
    'auth.login.hint.phone':             'Inserisci il numero di telefono con cui ti sei registrato.',
    'auth.login.hint.email':             'Ti invieremo un codice di 6 cifre per verificarti.',
    'auth.login.hint.code':              'Controlla i messaggi per un codice di 6 cifre.',
    'auth.login.hint.code_email':        'Controlla la tua email, abbiamo inviato un codice a {{email}}.',
    'auth.login.button.send':            'Invia codice di verifica',
    'auth.login.button.verify':          'Accedi',
    'auth.login.button.resend':          'Rinvia codice',
    'auth.login.button.different':       'Usa un altro numero',
    'auth.login.button.different_email': 'Usa un’altra email',
    'auth.login.error.phone_required':   'Per favore inserisci un numero di telefono.',
    'auth.login.error.email_invalid':    'Per favore inserisci un’email valida.',
    'auth.login.error.phone_invalid':    'Per favore inserisci un numero di telefono valido.',
    'auth.login.error.code_invalid':     'Codice non valido. Riprova o richiedine uno nuovo.',
    'auth.login.error.send_failed':      'Impossibile inviare il codice. Riprova.',
    'auth.login.error.session_expired':  'La tua sessione è scaduta. Per favore accedi di nuovo.',
    'auth.login.signup.prompt':          'Nuova sede?',
    'auth.login.signup.cta':             'Registrati qui',
    'auth.login.placeholder.phone':      '(02) 1234-5678',
    'auth.login.placeholder.email':      'titolare@sede.com',
    'auth.login.hint.email_account':     'Inserisci l’email del tuo account sede.',
    'auth.login.hint.code_whatsapp':     'Controlla WhatsApp, abbiamo inviato un codice a {{phone}}.',
    'auth.login.hint.code_sms':          'Abbiamo inviato un codice di 6 cifre a {{phone}}.',
    'auth.login.cooldown':               'Rinvia codice tra {{seconds}}s',
    'auth.login.toast.resent':           'Codice rinviato!',
    'auth.login.toast.resend_failed':    'Errore nel rinvio del codice',

    'auth.magic.signing_in':             'Accesso in corso…',
    'auth.magic.redirecting':            'Verrai reindirizzato alla tua dashboard',
    'auth.magic.expired':                'Questo link magico è scaduto o non valido.',
    'auth.magic.redirecting_login':      'Reindirizzamento al login…',
    // Multi-venue plan §1136-1154 — Stato "Accesso modificato" (IT).
    'auth.magic.access_changed.title':       'L’accesso a questa sede è stato modificato',
    'auth.magic.access_changed.title_named': 'L’accesso a {{venue}} è stato modificato',
    'auth.magic.access_changed.message':     'Il tuo ruolo è cambiato di recente e non hai più accesso alle prenotazioni di questa sede. La prenotazione viene gestita dalla sede stessa. Se è inatteso, contatta l’amministratore del tuo gruppo.',
    'auth.magic.access_changed.cta':         'Vai alla tua dashboard',
    'auth.verify_email.verifying':       'Verifica della tua email…',
    'auth.verify_email.short':           'Verifica…',

    // R21 T2 (2026-05-10) — Dashboard chrome (nav, header, footer, error, status) (IT).
    'dashboard.nav.proposals':           'Proposte',
    'dashboard.nav.bookings':            'Prenotazioni',
    'dashboard.nav.inquiries':           'Richieste',
    'dashboard.nav.analytics':           'Analisi',
    'dashboard.nav.settings':            'Impostazioni',
    'dashboard.header.admin_badge':      'Admin',
    'dashboard.header.admin_link':       '← Admin',
    'dashboard.header.signout':          'Esci',
    'dashboard.header.switch_venue':     'Cambia sede',
    'dashboard.footer.copyright':        '© 2026 BookOne.Party',
    'dashboard.footer.privacy':          'Privacy',
    'dashboard.footer.terms':            'Termini',
    'dashboard.error.title':             'Qualcosa è andato storto',
    'dashboard.error.body':              'Abbiamo riscontrato un problema imprevisto. Riprova o ricarica la pagina.',
    'dashboard.error.load_venue':        'Errore nel caricare la sede',
    'dashboard.error.login_retry':       'Per favore prova ad accedere di nuovo.',
    'dashboard.error.retry':             'Riprova',
    'dashboard.status.active':           'Attivo',
    'dashboard.status.paused':           'In pausa',
    'dashboard.status.unknown':          'Sconosciuto',
    'dashboard.common.loading':          'Caricamento…',

    'booking.proposal.heading':       'Proposte',
    'booking.proposal.review_heading':'In revisione — la sede sta rivedendo i tuoi dettagli aggiornati',
    'booking.proposal.caterer_prompt':'Domande o preferenze per il catering?',
    'booking.proposal.capacity':      'Capacità',
    'booking.proposal.min_spend':     'Spesa minima',
    'booking.proposal.min_spend_short':'SPESA MIN.',
    'booking.proposal.per_person':    'A PERSONA',
    'booking.proposal.per_person_est':'A PERSONA (STIMA)',
    'booking.proposal.fee':           'Costo di prenotazione',
    'booking.proposal.dates':         'Date disponibili',
    'booking.proposal.notes':         'Note',
    'booking.proposal.book':          'Prenota e paga',
    'booking.proposal.book_free':     'Conferma prenotazione',
    'booking.proposal.confirm_match': 'Conferma e prenota',
    'booking.proposal.confirm_match.named': 'Conferma e prenota — {{spaceName}}',
    'booking.proposal.confirm_action_required': 'Tocca qui sotto per confermare ufficialmente la tua prenotazione — non è ancora confermata.',
    'booking.proposal.select_date_instruction': 'Scegli la data e l’ora che preferisci, poi tocca Conferma per prenotare.',
    'booking.proposal.complete_payment': 'Completa pagamento',
    'booking.proposal.complete_payment.named': 'Completa pagamento — {{fee}}',
    'booking.proposal.payment_prompt':'Avrai 24 ore per completare il pagamento di {{fee}}.',
    'booking.proposal.pending':       'In attesa della risposta della sede',
    'booking.proposal.space_options': '{{count}} opzioni di spazio',
    'booking.proposal.match_confirmed':'Abbinamento confermato!',
    'booking.proposal.view_venue':    'Vedi profilo sede',
    'booking.proposal.other_pending': 'Un’altra proposta è confermata — completa il pagamento o aspetta che scada',
    'booking.proposal.delivered_from':'Consegnato da',
    'booking.proposal.pickup_at':     'Ritiro presso',
    'booking.proposal.cta_catering_event':'La sede ti contatterà per finalizzare i dettagli del catering.',
    'booking.proposal.cta_catering_order':'La sede ti contatterà per finalizzare il tuo ordine di catering.',

    'proposal.field.min_spend':       'Spesa min. ($)',
    'proposal.field.deposit':         'Deposito ($)',

    'booking.payment.confirm_failed':'Conferma della prenotazione fallita',
    'booking.payment.checkout_failed':'Avvio del pagamento fallito',

    'booking.countdown.expired':      'Scaduto',
    'booking.countdown.remaining':    '{{hours}}h {{minutes}}m rimanenti per completare il pagamento',

    'booking.cta.need_help':          'Serve aiuto? Rispondi all’email di conferma.',

    // booking.success.*
    'booking.success.heading':        'Sei abbinato!',
    'booking.success.prompt':         'La sede ti contatterà per finalizzare la tua prenotazione. Ecco i tuoi dettagli.',
    'booking.success.reference':      'RIFERIMENTO PRENOTAZIONE',
    'booking.success.venue':          'SEDE',
    'booking.success.event_type':     'TIPO DI EVENTO',
    'booking.success.date':           'DATA',
    'booking.success.guests':         'OSPITI',
    'booking.success.time':           'ORA',
    'booking.success.payment':        'PAGAMENTO',
    'booking.success.booking_fee':    'Costo di prenotazione',
    'booking.success.location':       'POSIZIONE',
    'booking.success.booking_type':   'TIPO DI PRENOTAZIONE',
    'booking.success.package':        'PACCHETTO',
    'booking.success.min_spend':      'Spesa minima (presso la sede)',
    'booking.success.fee_free':       'Gratis',
    'booking.success.view_details':   'Vedi dettagli prenotazione',
    'booking.success.print':          'Stampa ricevuta',
    'booking.success.confirming':     'Conferma della prenotazione in corso…',
    'booking.success.just_a_moment':  'Ci vorrà solo un momento…',

    'booking.error_page.title':       'Qualcosa è andato storto',
    'booking.error_page.fallback':    'Non siamo riusciti a caricare la tua prenotazione. Riprova.',
    'booking.error_page.retry':       'Riprova',
    'booking.error_page.back':        'Torna alla home',
    'booking.not_found_page.title':   'Prenotazione non trovata',
    'booking.not_found_page.body':    'Non abbiamo trovato una prenotazione con quell’ID. Potrebbe essere stata rimossa o il link potrebbe essere errato.',
    'booking.footer.tag':             'PRENOTAZIONE SEDE PRIVATA — TORONTO, CA',

    // R21 Phase 1A — customer-facing
    'booking.pill.event_completed':       'EVENTO COMPLETATO',
    'booking.pill.match_confirmed':       'ABBINAMENTO CONFERMATO',
    'booking.section.venue_contact':      'CONTATTO SEDE',
    'booking.section.add_to_calendar':    'AGGIUNGI AL CALENDARIO',
    'booking.booked_for':                 'Prenotato per {{datetime}} · {{duration}}',
    'booking.calendar.google':            'Google Calendar',
    'booking.calendar.outlook':           'Outlook',
    'booking.calendar.download_ics':      'Scarica .ics',
    'booking.carousel.prev':              'Foto precedente',
    'booking.carousel.next':              'Foto successiva',
    'booking.intake.notes_placeholder':   'es. Preferiamo il granchio all’aragosta, e servono 5 piatti vegani...',

    // feedback.*
    'feedback.heading.readonly':          'Il tuo commento',
    'feedback.heading.editable':          'Com’è andato il tuo evento?',
    'feedback.placeholder':               'Raccontaci di più (facoltativo)…',
    'feedback.submit':                    'Invia commento',
    'feedback.update':                    'Aggiorna commento',
    'feedback.edit':                      'Modifica commento',
    'feedback.submitting':                'Invio…',
    'feedback.submitted_prefix':          'Inviato {{time}}',
    'feedback.hint':                      'Il tuo commento va direttamente al team eventi della sede.',
    'feedback.error.no_rating':           'Per favore scegli un punteggio da 1 a 5 calici di vino.',
    'feedback.error.submit_failed':       'Invio del commento fallito. Riprova.',
    'feedback.relative.just_now':         'proprio ora',
    'feedback.relative.minutes_ago':      '{{count}} min fa',
    'feedback.relative.hours_ago':        '{{count}} ore fa',
    'feedback.glass_aria':                'Valuta {{n}} su 5',

    // diff.*
    'diff.what_changed':                  'Cosa è cambiato',
    'diff.party_size':                    'Numero di ospiti',
    'diff.dates':                         'Date',
    'diff.budget':                        'Budget',
    'diff.time':                          'Ora',
    'diff.duration':                      'Durata',
    'diff.guests_singular':               '{{n}} ospite',
    'diff.guests_plural':                 '{{n}} ospiti',
    'diff.guests_range':                  '{{min}}–{{max}} ospiti',
    'diff.hours_singular':                '{{n}} h',
    'diff.hours_plural':                  '{{n}} h',

    // R21 T3 (2026-05-10) — Venue dashboard proposal lead toasts.
    'dashboard.proposals.lead.status.toast_lost':         'Lead segnato come perso',
    'dashboard.proposals.lead.status.toast_lost_failed':  'Impossibile segnare come perso — {{error}}',
    'dashboard.proposals.lead.status.mark_lost':          'Segna come perso',
    'dashboard.proposals.lead.status.mark_lost_heading':  'Segna come perso',
    'dashboard.proposals.lead.status.mark_lost_subtitle': 'Annota perché questo lead non si è convertito. Aiuta il team a migliorare.',
    'dashboard.proposals.lead.status.toast_update_failed':'Errore aggiornando lo stato del lead',
    'dashboard.proposals.lead.status.toast_failed_log':   'Errore registrando',

    // R21 T2 batch 3 (2026-05-10) — Full dashboard.proposals.* coverage (IT).
    'dashboard.proposals.card.budget_label':              'Budget',
    'dashboard.proposals.card.catering_details':          'Dettagli catering',
    'dashboard.proposals.card.confirm':                   'Conferma proposta',
    'dashboard.proposals.card.customer_contact':          'Contatto cliente',
    'dashboard.proposals.card.dietary_accessibility':     'Dieta / accessibilità',
    'dashboard.proposals.card.direct_badge':              'Diretta',
    'dashboard.proposals.card.event_fallback':            'Evento',
    'dashboard.proposals.card.event_summary':             'Riepilogo evento',
    'dashboard.proposals.card.field.allergy_concerns':    'Allergie',
    'dashboard.proposals.card.field.bar_restrictions':    'Restrizioni bar',
    'dashboard.proposals.card.field.billing_setup':       'Fatturazione',
    'dashboard.proposals.card.field.budget_landmarks':    'Tappe del budget',
    'dashboard.proposals.card.field.budget_poc_name':     'Contatto budget',
    'dashboard.proposals.card.field.card_charge_confirmed': 'Addebito su carta',
    'dashboard.proposals.card.field.event_audience':      'Pubblico dell’evento',
    'dashboard.proposals.card.field.location_preference': 'Preferenza di location',
    'dashboard.proposals.card.field.music_preference':    'Musica / DJ',
    'dashboard.proposals.card.field.pre_order_interest':  'Menu preordinato',
    'dashboard.proposals.card.field.space_preference':    'Preferenza di spazio',
    'dashboard.proposals.card.field.vendor_interest':     'Eventi pop-up',
    'dashboard.proposals.card.guests_suffix':             'ospiti',
    'dashboard.proposals.card.hide_details':              'Nascondi dettagli',
    'dashboard.proposals.card.pending_review_banner':     'Il cliente ha aggiornato i dettagli della prenotazione. Per favore rivedi e conferma la tua proposta.',
    'dashboard.proposals.card.requested_label':           'Richiesto',
    'dashboard.proposals.card.resubmit':                  'Reinvia proposta',
    'dashboard.proposals.card.status.completed':          'Completato',
    'dashboard.proposals.card.status.consumer_confirmed': 'Pagamento in attesa',
    'dashboard.proposals.card.status.consumer_confirmed_tripleseat': 'Prenotato',
    'dashboard.proposals.card.status.expired':            'Scaduto',
    'dashboard.proposals.card.status.external_crm':       'Inoltrato al CRM',
    'dashboard.proposals.card.status.lost_post_contact':  'Perso',
    'dashboard.proposals.card.status.pending':            'In sospeso',
    'dashboard.proposals.card.status.pending_review':     'Richiede revisione',
    'dashboard.proposals.card.status.rejected':           'Non selezionato',
    'dashboard.proposals.card.status.selected':           'Selezionato',
    'dashboard.proposals.card.status.submitted':          'Inviato',
    'dashboard.proposals.card.status.withdrawn':          'Ritirato',
    'dashboard.proposals.card.submit':                    'Invia proposta',
    'dashboard.proposals.card.venue_specific':            'Dettagli specifici della sede',
    'dashboard.proposals.card.view_details':              'Vedi dettagli',
    'dashboard.proposals.empty.no_matches.body.filter':   'Nessuna proposta corrisponde al tuo filtro. Prova ad aggiustare i criteri.',
    'dashboard.proposals.empty.no_matches.body.search':   'Nessuna proposta corrisponde alla tua ricerca. Prova ad aggiustare i criteri.',
    'dashboard.proposals.empty.no_matches.title':         'Nessuna proposta corrispondente',
    'dashboard.proposals.empty.no_requests.body':         'Quando l’evento di un cliente corrisponde al tuo spazio, la sua richiesta di prenotazione apparirà qui. Riceverai anche una notifica SMS.',
    'dashboard.proposals.empty.no_requests.title':        'Nessuna richiesta ancora',
    'dashboard.proposals.filter.category.all':            'Tutte',
    'dashboard.proposals.filter.category.catering':       'Catering',
    'dashboard.proposals.filter.category.private_events': 'Eventi',
    'dashboard.proposals.filter.source.all':              'Tutte le fonti',
    'dashboard.proposals.filter.source.direct':           'Diretta',
    'dashboard.proposals.filter.source.marketplace':      'Marketplace',
    'dashboard.proposals.filter.status.all':              'Tutti',
    'dashboard.proposals.filter.status.pending':          'In sospeso',
    'dashboard.proposals.filter.status.selected':         'Selezionato',
    'dashboard.proposals.filter.status.submitted':        'Inviato',
    'dashboard.proposals.lead.call':                      'Chiama il cliente',
    'dashboard.proposals.lead.email':                     'Manda email al cliente',
    'dashboard.proposals.lead.no_contact':                'Nessun canale di contatto — invia una proposta formale.',
    'dashboard.proposals.lead.no_email':                  'Il cliente non ha condiviso un’email.',
    'dashboard.proposals.lead.no_phone':                  'Il cliente non ha condiviso un numero di telefono.',
    'dashboard.proposals.lead.send_email':                'Invia risposta via email',
    'dashboard.proposals.lead.send_text':                 'Invia risposta via SMS',
    'dashboard.proposals.lead.status.cancel':             'Annulla',
    'dashboard.proposals.lead.status.confirm':            'Conferma',
    'dashboard.proposals.lead.status.confirm_booked':     'Conferma: lead prenotato',
    'dashboard.proposals.lead.status.confirm_completed':  'Conferma: evento completato',
    'dashboard.proposals.lead.status.event_datetime_label': 'Data e ora dell’evento concordate (opzionale — scegli tra le date preferite del cliente)',
    'dashboard.proposals.lead.status.actual_spend_label':  'Spesa effettiva (CAD, opzionale)',
    'dashboard.proposals.lead.status.hrs':                'h',
    'dashboard.proposals.lead.status.mark_booked':        'Segna come prenotato',
    'dashboard.proposals.lead.status.mark_completed':     'Segna come completato',
    'dashboard.proposals.lead.status.marked_booked':      'Prenotato · aggiornamento',
    'dashboard.proposals.lead.status.marked_completed':   'Completato · aggiornamento',
    'dashboard.proposals.lead.status.notes_placeholder':  'Note opzionali — es. riferimento prenotazione, dettagli evento, follow-up…',
    'dashboard.proposals.lead.status.saving':             'Salvataggio…',
    'dashboard.proposals.lead.status.toast_booked':       'Lead segnato come prenotato',
    'dashboard.proposals.lead.status.toast_completed':    'Lead segnato come completato',
    'dashboard.proposals.lead.status_heading':            'Stato del lead',
    'dashboard.proposals.lead.submit_formal_link':        'Invia una proposta formale',
    'dashboard.proposals.modal.add_option':               'Aggiungi un’altra opzione di spazio',
    'dashboard.proposals.modal.ai_suggested_banner':      'Suggerito dall’IA in base alla richiesta del cliente — sentiti libero di aggiustare',
    'dashboard.proposals.modal.aria.close_modal':         'Chiudi modal',
    'dashboard.proposals.modal.aria.remove_date_slot':    'Rimuovi data',
    'dashboard.proposals.modal.aria.remove_option':       'Rimuovi opzione',
    'dashboard.proposals.modal.attachments_hint':         '(menu, termini e condizioni, ecc.)',
    'dashboard.proposals.modal.cross_location_warning':   'Questo spazio è in una sede diversa da quella richiesta',
    'dashboard.proposals.modal.label.attachments':        'Allegati',
    'dashboard.proposals.modal.label.available_dates':    'Date e orari disponibili',
    'dashboard.proposals.modal.label.booking_fee':        'Costo di prenotazione ($)',
    'dashboard.proposals.modal.label.capacity':           'Capacità',
    'dashboard.proposals.modal.label.min_spend':          'Spesa minima ($)',
    'dashboard.proposals.modal.label.notes':              'Note',
    'dashboard.proposals.modal.label.per_person':         'A persona ($)',
    'dashboard.proposals.modal.label.space':              'Spazio',
    'dashboard.proposals.modal.notes_placeholder':        'Dettagli aggiuntivi per il cliente...',
    'dashboard.proposals.modal.per_person_disclaimer':    'È una stima, non un preventivo vincolante. Il prezzo finale viene confermato dopo la conversazione.',
    'dashboard.proposals.modal.select_date_placeholder':  'Seleziona data',
    'dashboard.proposals.modal.select_space_placeholder': 'Seleziona spazio',
    'dashboard.proposals.modal.submit_multi':             'Invia {{count}} opzioni',
    'dashboard.proposals.modal.submitting':               'Invio in corso...',
    'dashboard.proposals.modal.up_to':                    'fino a {{n}}',
    'dashboard.proposals.results.clear':                  'Cancella filtri',
    'dashboard.proposals.results.showing':                'Mostrando {{shown}} di {{total}} proposte',
    'dashboard.proposals.search.placeholder':             'Cerca per evento, nome, data…',
    'dashboard.proposals.section.active':                 'Attive',
    'dashboard.proposals.section.forwarded_crm':          'Inoltrate a Tripleseat',
    'dashboard.proposals.section.in_negotiation':         'In trattativa',
    'dashboard.proposals.section.needs_review':           'Richiede revisione',
    'dashboard.proposals.section.past':                   'Passate',
    'dashboard.proposals.section.pending':                'Richieste in sospeso',
    'dashboard.proposals.subtitle.many':                  '{{count}} richieste in sospeso',
    'dashboard.proposals.subtitle.one':                   '1 richiesta in sospeso',
    'dashboard.proposals.subtitle.zero':                  'Nessuna richiesta in sospeso',
    'dashboard.proposals.toast.confirm_review.error':     'Errore nella conferma della revisione della proposta',
    'dashboard.proposals.toast.confirm_review.success':   'Proposta confermata',
    'dashboard.proposals.toast.dismiss.error':            'Errore nell’eliminare la proposta',
    'dashboard.proposals.toast.dismiss.success':          'Proposta eliminata dalla vista',
    'dashboard.proposals.toast.withdraw.error':           'Errore nel ritirare la proposta',
    'dashboard.proposals.toast.withdraw.success':         'Proposta ritirata',

    // Richieste generali (solo Members Only — IT).
    'dashboard.inquiries.title':            'Richieste',
    'dashboard.inquiries.subtitle.zero':    'Nessuna nuova domanda',
    'dashboard.inquiries.subtitle.one':     '1 nuova domanda',
    'dashboard.inquiries.subtitle.many':    '{{count}} nuove domande',
    'dashboard.inquiries.empty':            'Ancora nessuna domanda. Quando un ospite pone una domanda al team tramite il widget o per telefono, appare qui.',
    'dashboard.inquiries.mark_handled':     'Segna come gestita',
    'dashboard.inquiries.status.new':       'Nuova',
    'dashboard.inquiries.voiceEmailCaveat': 'Acquisita a voce — potrebbe essere trascritta male. Il numero di telefono è il contatto affidabile.',
    'dashboard.inquiries.status.handled':   'Gestita',
    'dashboard.inquiries.toast.handled.success': 'Richiesta segnata come gestita',
    'dashboard.inquiries.toast.handled.error':   'Errore nel segnare la richiesta',
    'dashboard.inquiries.category.reservation': 'Prenotazione',
    'dashboard.inquiries.category.hours':       'Orari',
    'dashboard.inquiries.category.menu':        'Menu',
    'dashboard.inquiries.category.directions':  'Indicazioni',
    'dashboard.inquiries.category.private_event': 'Evento privato',
    'dashboard.inquiries.category.catering':    'Catering',
    'dashboard.inquiries.category.general':     'Domanda generale',
    'dashboard.inquiries.category.other':       'Altro',

    // Pagina di conferma della richiesta generale (/inquiry/[id]) — IT.
    'inquiry.confirm.title':          'Hai posto una domanda al team di Members Only',
    'inquiry.confirm.question_label': 'La tua domanda',
    'inquiry.confirm.contact_label':  'Ti contatteremo a',
    'inquiry.confirm.response_time':  'Il team di solito risponde entro un giorno.',
    'inquiry.edit.button':            'Modifica',
    'inquiry.edit.name':              'Nome',
    'inquiry.edit.email':             'Email',
    'inquiry.edit.phone':             'Telefono',
    'inquiry.edit.add_email_hint':    'Aggiungi la tua email per ricevere una copia',
    'inquiry.edit.add_email_cta':     'Aggiungi la tua email per ricevere una copia',
    'inquiry.edit.voice_hint':        'Questa email è stata acquisita a voce — controlla che sia corretta.',
    'inquiry.edit.save':              'Salva le modifiche',
    'inquiry.edit.saving':            'Salvataggio\u2026',
    'inquiry.edit.cancel':            'Annulla',
    'inquiry.edit.saved':             'Salvato — il team vedrà i tuoi dati aggiornati.',
    'inquiry.edit.error':             'Impossibile salvare — controlla i dati e riprova.',
    'inquiry.confirm.loading':        'Caricamento della tua richiesta…',
    'inquiry.confirm.not_found':      'Non abbiamo trovato questa richiesta — scrivici a events@membersonly.com.',
    'inquiry.confirm.badge':          'Domanda ricevuta',

    // Multi-venue plan §1108-1134 — Modale di reindirizzamento a una sede sorella (IT).
    'proposals.routing.trigger':                          'Reindirizza a una sede sorella',
    'proposals.routing.title':                            'Reindirizza questa prenotazione da {{parent}}',
    'proposals.routing.choose_venue':                     'Scegli una sede',
    'proposals.routing.no_sisters':                       'Nessuna sede sorella disponibile nel tuo ambito.',
    'proposals.routing.customer_unknown':                 'Dettagli del cliente non disponibili',
    'proposals.routing.guests':                           '{{lo}}–{{hi}} ospiti',
    'proposals.routing.capacity':                         'capacità {{count}}',
    'proposals.routing.min_spend':                        'min {{amount}}',
    'proposals.routing.reason_label':                     'Motivo (opzionale)',
    'proposals.routing.reason_placeholder':               'Registrato nell’audit — perché questa sede?',
    'proposals.routing.cancel':                           'Annulla',
    'proposals.routing.confirm':                          'Reindirizza prenotazione',
    'proposals.routing.confirm_named':                    'Reindirizza a {{venue}}',
    'proposals.routing.success':                          'Reindirizzata a {{venue}}',
    'proposals.routing.failure':                          'Reindirizzamento non riuscito, riprova',
    'proposals.routing.tripleseat.disabled':              'Tripleseat: disattivato',
    'proposals.routing.tripleseat.shadow':                'Tripleseat: shadow',
    'proposals.routing.tripleseat.external_crm':          'Tripleseat: CRM',
    'proposals.routing.skip_tripleseat_label':            'Salta Tripleseat per questo reindirizzamento',
    'proposals.routing.skip_tripleseat_help_enabled':     'Il reindirizzamento viene registrato ma Tripleseat di {{venue}} non verrà attivato — gestiscilo manualmente.',
    'proposals.routing.skip_tripleseat_help_disabled':    'La sede selezionata non ha un’integrazione Tripleseat da saltare.',
    'proposals.routed_from':                              'Reindirizzata da {{venueName}}',

    // Multi-venue plan §1108-1175 — Modalità "Visualizza come" (IT).
    'dashboard.impersonate.view_as':                      'Visualizza come',
    'dashboard.impersonate.self':                         'Io (admin piattaforma)',
    'dashboard.impersonate.exit':                         'Esci',
    'dashboard.impersonate.banner':                       'Impersonificazione: {{role}} per {{scope}}.',
    'dashboard.impersonate.role.group_manager':           'Gestore del gruppo',
    'dashboard.impersonate.role.venue_manager':           'Gestore della sede',
    'dashboard.impersonate.role.location_manager':        'Gestore della sede locale',

    // R21 T2 batch 4 (2026-05-10) — dashboard.analytics + bookings +
    // feedback + settings (IT).
    'dashboard.analytics.subtitle':                       'Tieni traccia delle prestazioni della tua sede',
    'dashboard.analytics.empty.title':                    'Nessun dato ancora',
    'dashboard.analytics.empty.body':                     'Le analisi si popoleranno man mano che ricevi e rispondi alle richieste di prenotazione.',
    'dashboard.analytics.period.7d':                      '7g',
    'dashboard.analytics.period.30d':                     '30g',
    'dashboard.analytics.period.90d':                     '90g',
    'dashboard.analytics.period.all':                     'Sempre',
    'dashboard.analytics.stat.total_requests':            'Richieste totali',
    'dashboard.analytics.stat.submitted':                 'Inviate',
    'dashboard.analytics.stat.won':                       'Vinte',
    'dashboard.analytics.stat.lost':                      'Perse',
    'dashboard.analytics.stat.booked':                    'Prenotate',
    'dashboard.analytics.stat.completed':                 'Completate',
    'dashboard.analytics.stat.revenue':                   'Ricavi',
    'dashboard.analytics.stat.avg_party':                 'Dimensione media',
    'dashboard.analytics.stat.repeat':                    'Clienti abituali',
    'dashboard.analytics.funnel.title':                   'Funnel di conversione',
    'dashboard.analytics.funnel.received':                'Ricevute',
    'dashboard.analytics.funnel.submitted':               'Inviate',
    'dashboard.analytics.funnel.selected':                'Selezionate',
    'dashboard.analytics.funnel.completed':               'Completate',
    'dashboard.analytics.funnel.lost':                    'Perse',
    'dashboard.analytics.funnel.lost.reasons':            'Motivi',
    'dashboard.analytics.funnel.conversion':              'Tasso di conversione',
    'dashboard.analytics.lost_reasons.title':             'Perché i lead sono stati persi',
    'dashboard.analytics.lost_reasons.description':       'Include lead che non si sono convertiti (nessun follow-up, sono andati altrove) e prenotazioni annullate dopo la conferma.',
    'dashboard.analytics.lost_reasons.show_more':         'Mostra tutti i {{total}} motivi (+{{overflow}} in più)',
    'dashboard.analytics.lost_reasons.show_less':         'Mostra meno',
    'dashboard.analytics.detail.event_summary':           'Riepilogo evento',
    'dashboard.analytics.detail.event_notes':             'Note dell’evento',
    'dashboard.analytics.detail.min_spend':               'Spesa minima',
    'dashboard.analytics.detail.actual_spend':            'Spesa effettiva',
    'dashboard.analytics.detail.rental_fee':              'Acconto',
    'dashboard.analytics.detail.confirmed_for':           'Confermato per {{date}} · {{time}}{{duration}}',

    'dashboard.bookings.subtitle':                        '{{confirmed}} confermate, {{completed}} completate',
    'dashboard.bookings.empty.title':                     'Nessuna prenotazione ancora',
    'dashboard.bookings.empty.body':                      'Quando un cliente seleziona la tua proposta, la sua prenotazione apparirà qui con tutti i dettagli di contatto.',
    'dashboard.bookings.event_completed':                 'Evento segnato come completato',
    'dashboard.bookings.complete_error':                  'Errore nel segnare come completato',
    'dashboard.bookings.section.confirmed':               'Confermate ({{count}})',
    'dashboard.bookings.section.completed':               'Completate ({{count}})',
    'dashboard.bookings.view.list':                       'Lista',
    'dashboard.bookings.view.calendar':                   'Calendario',

    'dashboard.analytics.customer_list.heading_count':                'Clienti ({{count}})',
    'dashboard.analytics.customer_list.history.heading_count':        'Cronologia prenotazioni ({{count}})',
    'dashboard.analytics.customer_list.badge.re_engaged':             'RIATTIVATO',
    'dashboard.analytics.customer_list.badge.repeat_count':           'RICORRENTE · {{count}}',
    'dashboard.analytics.customer_list.badge.corporate_count':        'AZIENDALE × {{count}}',
    'dashboard.analytics.customer_list.badge.personal_count':         'PERSONALE × {{count}}',
    'dashboard.analytics.customer_list.status.completed':             'Completato',
    'dashboard.analytics.customer_list.status.booked':                'Prenotato',
    'dashboard.analytics.customer_list.status.lost':                  'Perso',
    'dashboard.analytics.customer_list.status.lost_with_reason':      'Perso · {{reason}}',
    'dashboard.analytics.customer_list.status.rejected':              'Non selezionato',
    'dashboard.analytics.customer_list.status.expired':               'Scaduto',
    'dashboard.analytics.customer_list.status.withdrawn':             'Ritirato',
    'dashboard.analytics.customer_list.cancel_reason.unspecified':    'Non specificato',
    'dashboard.analytics.customer_list.cancel_reason.no_response':    'Senza seguito',
    'dashboard.analytics.customer_list.cancel_reason.chose_elsewhere':'Ha scelto altrove',
    'dashboard.analytics.customer_list.cancel_reason.price_disagreement':'Disaccordo sul prezzo',
    'dashboard.analytics.customer_list.cancel_reason.date_unavailable':'Data non disponibile',
    'dashboard.analytics.customer_list.cancel_reason.event_postponed':'Evento posticipato',
    'dashboard.analytics.customer_list.cancel_reason.event_cancelled':'Cliente ha annullato',
    'dashboard.analytics.customer_list.cancel_reason.other':          'Altro',
    'dashboard.analytics.customer_list.last_corporate.label':         'Ultima prenotazione aziendale',
    'dashboard.analytics.customer_list.toggle.history':               'Cronologia',
    'dashboard.analytics.customer_list.toggle.hide':                  'Nascondi',

    'dashboard.feedback.title':                           'Recensioni dei clienti',
    'dashboard.feedback.highlights':                      'In evidenza (5★)',
    'dashboard.feedback.concerns':                        'Da migliorare (1-2★)',
    'dashboard.feedback.review_singular':                 'recensione',
    'dashboard.feedback.review_plural':                   'recensioni',
    'dashboard.feedback.view_all':                        'Vedi tutte le {{total}} recensioni',
    'dashboard.feedback.view_less':                       'Nascondi',

    'dashboard.settings.subtitle':                        'Gestisci i dettagli, gli spazi e la disponibilità della tua sede',
    'dashboard.settings.action.preview_public':           'Anteprima pubblica',
    'dashboard.settings.action.preview_customer':         'Anteprima cliente',
    'dashboard.settings.action.pause':                    'Sospendi',
    'dashboard.settings.action.resume':                   'Riprendi',
    'dashboard.settings.signout':                         'Esci',

    // Piano multi-sede — vista Impostazioni limitata al gestore della
    // sede (location_manager). Tutti i campi globali sono nascosti; resta
    // modificabile solo la sezione "dettagli di questa sede".
    'settings.location_manager_banner':                   'Sei il gestore della sede di {{locationName}}. Qui puoi modificare solo i campi specifici di {{locationName}}.',
    'settings.location_manager_section_title':            'Dettagli di {{locationName}}',
    'settings.location_only_fields_notice':               'La modifica delle impostazioni globali (brand, contatti, integrazioni) richiede un accesso da gestore sede o superiore.',
    'settings.location_manager.field.address':            'Indirizzo',
    'settings.location_manager.field.postal_code':        'CAP',
    'settings.location_manager.field.phone':              'Telefono',
    'settings.location_manager.field.latitude':           'Latitudine',
    'settings.location_manager.field.longitude':          'Longitudine',
    'settings.location_manager.field.website':            'URL del sito web',
    'settings.location_manager.field.instagram':          'URL Instagram',
    'settings.location_manager.action.save':              'Salva modifiche',
    'settings.location_manager.action.saving':            'Salvataggio…',
    'settings.location_manager.toast.saved':              'Sede aggiornata',
    'settings.location_manager.toast.save_failed':        'Errore durante il salvataggio',
    'settings.location_manager.no_location':              'Nessuna sede assegnata a questo account. Contatta il gestore della sede.',
    'dashboard.settings.toast.paused':                    'Sede sospesa',
    'dashboard.settings.toast.resumed':                   'Sede riattivata',
    'dashboard.settings.toast.toggle_failed':             'Errore nel cambiare lo stato',
    'dashboard.settings.toast.email_verified':            'Email verificata con successo!',
    'dashboard.settings.toast.email_link_expired':        'Il link di verifica è scaduto. Per favore richiedine uno nuovo.',
    'dashboard.settings.transfer.email_prompt':           'Per favore inserisci un indirizzo email',
    'dashboard.settings.transfer.phone_prompt':           'Per favore inserisci un numero di telefono',
    'dashboard.settings.transfer.success':                'Accesso alla sede trasferito! Il nuovo gestore riceverà un link di accesso tramite {{via}}.',
    'dashboard.settings.transfer.error':                  'Errore nel trasferire l’accesso alla sede',
    'dashboard.settings.cleanup.success':                 'Pulizia: {{proposals}} proposte e {{bookings}} richieste',
    'dashboard.settings.cleanup.error':                   'Errore nella pulizia',

    // R21 T2 batch 5 (2026-05-10) — settings.* sub-forms (IT).
    'settings.venue_info.title':                          'Informazioni sulla sede',
    'settings.venue_info.title_edit':                     'Modifica informazioni sulla sede',
    'settings.venue_info.action.edit':                    'Modifica',
    'settings.venue_info.action.cancel':                  'Annulla',
    'settings.venue_info.action.save':                    'Salva',
    'settings.venue_info.action.saving':                  'Salvataggio…',
    'settings.venue_info.hero.label':                     'Immagine principale',
    'settings.venue_info.hero.upload':                    'Carica',
    'settings.venue_info.hero.change':                    'Cambia',
    'settings.venue_info.hero.uploading':                 'Caricamento…',
    'settings.venue_info.hero.hint':                      'Consigliato: 2400 × 1200px o più grande',
    'settings.venue_info.hero.upload_failed':             'Errore nel caricare l’immagine principale',
    'settings.venue_info.field.name':                     'Nome',
    'settings.venue_info.field.name_full':                'Nome della sede',
    'settings.venue_info.field.contact':                  'Contatto',
    'settings.venue_info.field.contact_name':             'Nome del contatto',
    'settings.venue_info.field.email':                    'Email di contatto',
    'settings.venue_info.field.phone':                    'Telefono',
    'settings.venue_info.field.address':                  'Indirizzo',
    'settings.venue_info.field.tagline':                  'Slogan',
    'settings.venue_info.field.website':                  'Sito web',
    'settings.venue_info.field.instagram':                'Instagram',
    'settings.venue_info.field.notes':                    'Note',
    'settings.venue_info.placeholder.email':              'titolare@sede.com',
    'settings.venue_info.placeholder.website':            'miasede.com',
    'settings.venue_info.placeholder.instagram':          'tuasede',
    'settings.venue_info.tagline_empty':                  '—',
    'settings.venue_info.accessibility':                  'Accessibile con sedia a rotelle',
    'settings.venue_info.maps':                           'Vedi su Google Maps',
    'settings.venue_info.notifications':                  'Notifiche',
    'settings.venue_info.notifications.choose':           'Scegli come ricevere notifiche di prenotazione e codici di accesso.',
    'settings.venue_info.channel.sms':                    'SMS',
    'settings.venue_info.channel.whatsapp':               'WhatsApp',
    'settings.venue_info.channel.phone':                  'Telefono',
    'settings.venue_info.channel.email_toggle':           'Notifiche via email',
    'settings.venue_info.channel.add_email':              'Aggiungi un’email di contatto sopra per abilitare le notifiche via email.',
    'settings.venue_info.channel.none_email':             'Nessuno (solo email)',
    'settings.venue_info.channel.sending_to':             'Invio a {{email}}',
    'settings.venue_info.via_sms':                        '{{phone}} via SMS',
    'settings.venue_info.via_whatsapp':                   '{{phone}} via WhatsApp',
    'settings.venue_info.both':                           '{{email}} + {{phone}}',
    'settings.venue_info.no_email_label':                 'Nessuna email',
    'settings.venue_info.no_email_set':                   'Nessuna email impostata',
    'settings.venue_info.visibility.title':               'Visibilità del profilo pubblico',
    'settings.venue_info.visibility.contact':             'Mostra il contatto del cliente sulle proposte',
    'settings.venue_info.visibility.contact_help':        'Mostra telefono e email del cliente sulle richieste in arrivo',
    'settings.venue_info.visibility.menus':               'Mostra i menu sul profilo pubblico',
    'settings.venue_info.visibility.spend':               'Mostra la spesa minima sul profilo pubblico',
    'settings.venue_info.language':                       'Lingua delle notifiche',
    'settings.venue_info.language.auto':                  'Automatico (dal telefono)',
    'settings.venue_info.language.en':                    'English',
    'settings.venue_info.language.fr':                    'Français',
    'settings.venue_info.language.es':                    'Español',
    'settings.venue_info.language.it':                    'Italiano',
    'settings.venue_info.language.hint':                  'Lingua usata per le notifiche della dashboard (OTP, avvisi di prenotazione, email con magic link). Per impostazione predefinita rileva dal prefisso del telefono.',
    'settings.venue_info.toast.saved':                    'Informazioni sulla sede salvate',
    'settings.venue_info.toast.save_failed':              'Errore nel salvataggio',

    'settings.spaces.title':                              'Spazi ({{count}})',
    'settings.spaces.title_edit':                         'Modifica spazi',
    'settings.spaces.add':                                'Aggiungi spazio',
    'settings.spaces.empty':                              'Nessuno spazio configurato. Clicca Modifica per aggiungere il tuo primo spazio.',
    'settings.spaces.space_n':                            'Spazio {{n}}',
    'settings.spaces.no_location':                        'Nessuna sede specifica',
    'settings.spaces.field.name':                         'Nome',
    'settings.spaces.field.location':                     'Sede',
    'settings.spaces.field.capacity':                     'Capacità massima',
    'settings.spaces.field.min_spend':                    'Spesa minima ($)',
    'settings.spaces.field.booking_fee':                  'Costo di prenotazione ($)',
    'settings.spaces.field.description':                  'Descrizione',
    'settings.spaces.field.images':                       'Immagini',
    'settings.spaces.placeholder.name':                   'es. The Snug',
    'settings.spaces.placeholder.min_spend':              'es. 1000',
    'settings.spaces.placeholder.booking_fee':            'es. 50',
    'settings.spaces.toast.saved':                        'Spazi salvati',
    'settings.spaces.toast.save_failed':                  'Errore nel salvare gli spazi',
    'settings.spaces.toast.image_failed':                 'Errore nel caricare l’immagine',

    'settings.space_card.up_to':                          'Fino a {{capacity}}',
    'settings.space_card.up_to_guests':                   'Fino a {{capacity}} ospiti',
    'settings.space_card.min_short':                      '${{amount}} min.',
    'settings.space_card.min_long':                       '${{amount}} spesa minima',
    'settings.space_card.fee_short':                      '${{amount}} costo',
    'settings.space_card.fee_long':                       '${{amount}} di costo di prenotazione',

    'settings.locations.title':                           'Sedi ({{count}})',
    'settings.locations.title_edit':                      'Modifica sedi',
    'settings.locations.add':                             'Aggiungi sede',
    'settings.locations.empty':                           'Sede singola. Clicca Modifica per aggiungere altre sedi.',
    'settings.locations.location_n':                      'Sede {{n}}',
    'settings.locations.website_label':                   'Sito web',
    'settings.locations.field.name':                      'Nome',
    'settings.locations.field.city':                      'Città',
    'settings.locations.field.address':                   'Indirizzo',
    'settings.locations.field.phone':                     'Telefono',
    'settings.locations.field.website':                   'URL del sito web',
    'settings.locations.field.instagram':                 'URL Instagram',
    'settings.locations.placeholder.name':                'es. Sede centro',
    'settings.locations.placeholder.city':                'es. Milano',
    'settings.locations.placeholder.address':             'Cerca un indirizzo…',
    'settings.locations.placeholder.phone':               'es. (02) 1234-5678',
    'settings.locations.placeholder.website':             'es. https://miasede.com/centro',
    'settings.locations.placeholder.instagram':           'es. https://instagram.com/miasede',
    'settings.locations.toast.saved':                     'Sedi salvate',
    'settings.locations.toast.save_failed':               'Errore nel salvare le sedi',
    'settings.locations.toast.missing_address':           'La sede {{n}} richiede un indirizzo prima di salvare.',

    'settings.menus.title':                               'Menu / Dettagli aggiuntivi ({{count}})',
    'settings.menus.title_edit':                          'Modifica menu / dettagli aggiuntivi',
    'settings.menus.add':                                 'Aggiungi menu ({{count}}/12)',
    'settings.menus.empty':                               'Nessun menu o dettaglio caricato. Clicca Modifica per aggiungere menu o dettagli dell’evento.',
    'settings.menus.menu_n':                              'Menu {{n}}',
    'settings.menus.replace':                             'Sostituisci',
    'settings.menus.view_current':                        'Vedi file attuale',
    'settings.menus.no_location':                         'Nessuna sede specifica',
    'settings.menus.mode.upload':                         'Carica file',
    'settings.menus.mode.link':                           'Incolla link',
    'settings.menus.field.name':                          'Nome',
    'settings.menus.field.description':                   'Descrizione',
    'settings.menus.field.location':                      'Sede',
    'settings.menus.field.source':                        'Fonte',
    'settings.menus.placeholder.name':                    'es. Menu degustazione, Pacchetto evento',
    'settings.menus.placeholder.description':             'Descrizione opzionale',
    'settings.menus.placeholder.link':                    'https://tuosito.com/menu.pdf',
    'settings.menus.placeholder.link_help':               'Incolla un URL al tuo menu (pagina web, link PDF, ecc.)',
    'settings.menus.toast.saved':                         'Menu salvati',
    'settings.menus.toast.save_failed':                   'Errore nel salvare i menu',
    'settings.menus.error.untitled':                      'Senza titolo',
    'settings.menus.error.needs_link':                    'Il menu "{{name}}" richiede un URL',
    'settings.menus.error.too_large':                     'File troppo grande per "{{name}}". Massimo 10MB.',
    'settings.menus.error.bad_type':                      'Tipo di file non consentito per "{{name}}". Usa PDF, JPEG, PNG o WebP.',
    'settings.menus.error.upload_failed':                 'Errore nel caricare "{{name}}": {{msg}}',

    'settings.direct.title':                              'Prenotazione diretta',
    'settings.direct.code_label':                         'Codice della sede',
    'settings.direct.code_help':                          'I clienti possono inviare questo codice via SMS a BookOne per prenotare direttamente con te',
    'settings.direct.line_label_one':                     'Linea telefonica dedicata',
    'settings.direct.line_label_many':                    'Linee telefoniche dedicate',
    'settings.direct.no_line':                            'Vuoi un numero dedicato per la tua sede? Contatta BookOne per la configurazione.',
    'settings.direct.status.active':                      'Attiva',
    'settings.direct.status.inactive':                    'Inattiva',

    'settings.email_verify.verified':                     'Verificata',
    'settings.email_verify.not_verified':                 'Non verificata',
    'settings.email_verify.send':                         'Invia email di verifica',
    'settings.email_verify.sending':                      'Invio…',
    'settings.email_verify.toast.sent':                   'Email di verifica inviata. Controlla la tua casella di posta.',
    'settings.email_verify.toast.failed':                 'Errore nell’invio dell’email di verifica',

    // R21 T2 batch 6 (2026-05-10) — R21 namespaces (IT).
    'edit_booking.title':                                 'Modifica dettagli della prenotazione',
    'edit_booking.close':                                 'Chiudi',
    'edit_booking.cancel':                                'Annulla',
    'edit_booking.save':                                  'Salva modifiche',
    'edit_booking.saving':                                'Salvataggio…',
    'edit_booking.guest_count':                           'Numero di ospiti',
    'edit_booking.agreed_date':                           'Data dell’evento concordata',
    'edit_booking.start_time':                            'Ora di inizio',
    'edit_booking.duration_hrs':                          'Durata (h)',
    'edit_booking.notes':                                 'Note dell’evento',
    'edit_booking.notes_placeholder':                     'Feedback del cliente, follow-up, cose da ricordare la prossima volta…',
    'edit_booking.contact_locked':                        'Nome, telefono ed email del cliente sono bloccati. Per aggiornare i contatti, modifica in {{crmName}}.',
    'edit_booking.toast.saved':                           'Prenotazione aggiornata',
    'edit_booking.toast.error':                           'Errore nel salvare le modifiche',

    'catering_config.heading':                            'Configurazione catering',
    'catering_config.cuisine.heading':                    'Tipi di cucina',
    'catering_config.cuisine.placeholder':                'Scrivi il tipo e premi Invio',
    'catering_config.min_order':                          'Ordine minimo',
    'catering_config.service_area':                       'Area di servizio',
    'catering_config.service_area.placeholder':           'es. Area metropolitana',
    'catering_config.delivery_radius':                    'Raggio di consegna:',
    'catering_config.km':                                 '{{km}} km',
    'catering_config.radius_min':                         '1 km',
    'catering_config.radius_max':                         '100 km',
    'catering_config.show_service_area':                  'Mostra area di servizio sul profilo catering',
    'catering_config.show_service_area.hint':             'I clienti possono inserire il loro indirizzo per verificare se consegni lì',
    'catering_config.packages.heading':                   'Pacchetti menu',
    'catering_config.packages.add':                       '+ Aggiungi pacchetto',
    'catering_config.packages.name_placeholder':          'Nome del pacchetto',
    'catering_config.packages.desc_placeholder':          'Descrizione (piatti inclusi, info dietetiche, ecc.)',
    'catering_config.packages.per_person':                '$/persona',
    'catering_config.packages.empty':                     'Nessun pacchetto ancora. Aggiungine uno per mostrare le tue opzioni di catering.',
    'catering_config.photos.heading':                     'Foto del cibo',
    'catering_config.photos.caption_placeholder':         'Aggiungi una didascalia...',
    'catering_config.photos.uploading':                   'Caricamento...',
    'catering_config.photos.add_button':                  'Aggiungi foto del cibo',
    'catering_config.photos.hint':                        'Mostra i tuoi piatti. Le foto appaiono come slideshow sul tuo profilo catering.',
    'catering_config.toast.max_photos':                   'Massimo 20 foto',
    'catering_config.toast.file_type_invalid':            '{{filename}}: solo JPEG, PNG, WebP consentiti',
    'catering_config.toast.upload_success':               '{{count}} foto caricate',
    'catering_config.toast.saved':                        'Impostazioni catering salvate',

    'proposal_submit.customer_asked_for':                 'Il cliente ha chiesto:',
    'proposal_submit.fulfilling_from':                    'Servito da',
    'proposal_submit.location_placeholder':               'Scegli una sede…',
    'proposal_submit.photos.heading':                     'Foto della proposta',
    'proposal_submit.photos.label':                       'Foto della proposta',
    'proposal_submit.photos.default_hero':                'Immagine principale predefinita',
    'proposal_submit.photos.none_selected':               'Nessuna selezionata — l’immagine principale della sede sarà mostrata sulla proposta. Clicca sulle foto per includerle.',
    'proposal_submit.photos.deselect_hint':               'Clicca su una foto per deselezionare. Le foto selezionate appariranno sulla scheda di proposta che il cliente vede.',
    'proposal_submit.photos.empty_settings_hint':         'Aggiungi foto di catering nelle Impostazioni per scegliere immagini specifiche per ogni proposta.',

    'pricing.title':                                      'Prezzo per data (preventivo automatico)',
    'pricing.col.date':                                   'Data',
    'pricing.col.time_block':                             'Fascia oraria',
    'pricing.col.min_spend':                              'Spesa minima',
    'pricing.col.venue_rental_fee':                       'Affitto della sede',
    'pricing.closed':                                     'Sede chiusa — serve una data alternativa',
    'pricing.closest_match':                              'orario disponibile più vicino',
    'pricing.holiday':                                    'festivo',
    'pricing.holiday_surcharge':                          'include supplemento festivo',
    'pricing.rates_pending':                              'Tariffe in attesa — il team eventi confermerà',
    'pricing.dashboard_caveat':                           '',

    'quick_reply.heading.with_name':                      'Rispondi a {{name}}',
    'quick_reply.heading.anon':                           'Rispondi al cliente',
    'quick_reply.close':                                  'Chiudi compositore',
    'quick_reply.channel.email':                          'Email',
    'quick_reply.channel.sms':                            'SMS',
    'quick_reply.body_placeholder':                       'Ciao — grazie per la richiesta. Volevo condividere alcuni dettagli prima di chiudere nulla...',
    'quick_reply.send.email':                             'Invia email',
    'quick_reply.send.sms':                               'Invia SMS',
    'quick_reply.sending':                                'Invio…',
    'quick_reply.toast.email_sent':                       'Email inviata',
    'quick_reply.toast.sms_sent':                         'SMS inviato',
    'quick_reply.sent.email':                             'Inviata via email',
    'quick_reply.sent.sms':                               'Inviato via SMS',
    'quick_reply.sent.just_now':                          'ora',
    'quick_reply.sent.minutes_ago':                       '{{count}} min fa',
    'quick_reply.sent.hours_ago':                         '{{count}} h fa',

    // R21 T2 batch 7 (2026-05-10) — anonymous landing + venue signup +
    // public venue page error states (IT).
    'common.lang.synced_from_profile':                    'Lingua cambiata in base alla tua dashboard ({{lang}}).',

    'marketing.nav.call_now':                             'Chiama ora',
    'marketing.nav.venue_login':                          'Accesso sede',
    'marketing.hero.title.line1':                         'PRENOTA LA TUA',
    'marketing.hero.title.line2':                         'FESTA.',
    'marketing.hero.tagline':                             'Prenota spazi per eventi privati a {{city}}. Dicci cosa ti serve, ti abbineremo con le sedi.',
    'marketing.hero.cta.call':                            'Chiama',
    'marketing.hero.cta.text':                            'Messaggio',
    'marketing.hero.cta.subline':                         'CHIAMA O MANDA UN MESSAGGIO — STESSO ASSISTENTE, STESSO NUMERO',
    'marketing.hero.cta.browse':                          'Esplora spazi per eventi →',
    'marketing.hero.legal':                               'Chiamando o mandando un messaggio, accetti di ricevere SMS da BookOne.Party riguardo alla tua richiesta. La frequenza dei messaggi varia. Possono applicarsi tariffe per messaggi e dati. Rispondi HELP per aiuto, STOP per disiscriverti.',
    'marketing.hero.legal.privacy':                       'Informativa sulla privacy',
    'marketing.hero.legal.tos':                           'Termini di servizio',
    'marketing.help.lead':                                'Ho bisogno di uno spazio per',
    'marketing.help.subline':                             'TOCCA PER SFOGLIARE GLI SPAZI PER EVENTI',
    'marketing.help.phrase.birthday':                     'una festa di compleanno',
    'marketing.help.phrase.corporate':                    'una cena aziendale',
    'marketing.help.phrase.engagement':                   'una cena di fidanzamento',
    'marketing.help.phrase.anniversary':                  'un anniversario a sorpresa',
    'marketing.help.phrase.holiday':                      'una festa natalizia',
    'marketing.help.phrase.farewell':                     'una festa di addio',
    'marketing.help.phrase.shower':                       'un brunch baby shower',
    'marketing.help.phrase.graduation':                   'una festa di laurea',
    'marketing.help.phrase.rehearsal':                    'una cena di prova',
    'marketing.help.phrase.offsite':                      'un team offsite',
    'marketing.help.phrase.watch':                        'una serata per vedere la partita',
    'marketing.help.phrase.launch':                       'un lancio di prodotto',
    'marketing.statement.a':                              'Prima di BookOne, organizzare una festa privata significava 3 settimane e 12 email.',
    'marketing.statement.b':                              'Ora basta una ricerca.',
    'marketing.statement.c':                              'Questa è la scorciatoia.',
    'marketing.how.eyebrow':                              'COME FUNZIONA',
    'marketing.how.title.a':                              'Tre passaggi.',
    'marketing.how.title.b':                              'Una chiamata.',
    'marketing.how.step':                                 'PASSO',
    'marketing.how.step1.title':                          'Raccontaci del tuo evento',
    'marketing.how.step1.desc':                           'Sfoglia gli spazi per eventi e condividi i dettagli — data, numero di ospiti, atmosfera. Salva i locali che ti piacciono.',
    'marketing.how.step2.title':                          'Ti abbiniamo con le sedi',
    'marketing.how.step2.desc':                           'Troviamo gli spazi privati più adatti al tuo evento. Le sedi inviano proposte con dettagli dello spazio, capacità e prezzi così puoi confrontare.',
    'marketing.how.step3.title':                          'Scegli la tua preferita e prenota',
    'marketing.how.step3.desc':                           'Seleziona la proposta che ami e conferma la tua prenotazione. Ti colleghiamo direttamente con la sede per finalizzare tutto.',
    'marketing.partners.eyebrow':                         'PARTNER',
    'marketing.partners.title':                           'Le migliori sedi di {{city}} si fidano di noi',
    'marketing.partners.coming_soon':                     'Prossimamente',
    'marketing.partners.type.cocktail':                   'Cocktail lounge',
    'marketing.partners.type.dining':                     'Sala da pranzo privata',
    'marketing.partners.type.restaurant':                 'Ristorante e bar',
    'marketing.partners.type.rooftop':                    'Terrazza',
    'marketing.testimonial1.quote':                       'BookOne ha reso semplice prenotare la festa natalizia aziendale. Abbiamo ricevuto tre proposte entro un’ora.',
    'marketing.testimonial1.role':                        'Organizzatrice eventi',
    'marketing.testimonial2.quote':                       'Finalmente un servizio che capisce di cosa hanno bisogno le sedi. Lead qualificati direttamente sul telefono.',
    'marketing.testimonial2.role':                        'Proprietaria sede',
    'marketing.testimonial3.quote':                       'Ho organizzato l’intera cena di compleanno in un solo pomeriggio. La sede era perfetta per il nostro gruppo.',
    'marketing.testimonial3.role':                        'Cliente',
    'marketing.faq.eyebrow':                              'FAQ',
    'marketing.faq.title':                                'Domande comuni',
    'marketing.faq.q1.q':                                 'Quanto velocemente riceverò le proposte?',
    'marketing.faq.q1.a':                                 'La maggior parte dei clienti riceve le proposte entro 30 minuti. Abbiniamo il tuo evento con le sedi più adatte e rispondono velocemente perché ogni richiesta è qualificata.',
    'marketing.faq.q2.q':                                 'Quanto costa?',
    'marketing.faq.q2.a':                                 'Prenotare tramite BookOne.Party è gratis per i clienti. Paghi solo il costo di prenotazione della sede quando selezioni una proposta — nessun costo nascosto, nessuna commissione di piattaforma.',
    'marketing.faq.q3.q':                                 'Posso mandare un messaggio invece di chiamare?',
    'marketing.faq.q3.a':                                 'Sì. Lo stesso assistente IA gestisce voce e messaggi tramite un unico numero. Nessuna app richiesta.',
    'marketing.faq.q4.q':                                 'Posso modificare la mia prenotazione dopo averla selezionata?',
    'marketing.faq.q4.a':                                 'Una volta selezionata una sede, ti colleghiamo direttamente con il gestore della sede. Eventuali modifiche di date, numero di ospiti o dettagli passano da loro.',
    'marketing.cta.title.a':                              'Sfoglia gli spazi per la tua',
    'marketing.cta.title.b':                              'festa oggi.',
    'marketing.cta.tagline':                              'Nessuna app. Nessun account. Nessuna attesa.',
    'marketing.venue_cta.title':                          'Organizzi eventi privati?',
    'marketing.venue_cta.body':                           'Ricevi richieste di eventi privati direttamente sul tuo telefono. Registrazione gratis, nessuna app.',
    'marketing.venue_cta.button':                         'Registra la tua sede →',
    'marketing.footer.tag':                               'PRENOTAZIONE SEDE PRIVATA — {{city}}, {{country}}',
    'marketing.footer.tagline':                           'Nessuna app. Nessun account. Nessuna attesa.',
    'marketing.footer.legal':                             '© 2026 1001474668 ONTARIO INC.',

    'signup.page.title':                                  'Registra la tua sede',
    'signup.page.subtitle':                               'Eventi, catering o entrambi — ricevi prenotazioni via SMS. Nessuna app, nessun costo di registrazione.',
    'signup.quickfill.title':                             'Compilazione rapida da Google',
    'signup.quickfill.body':                              'Cerca la tua sede per riempire automaticamente nome, indirizzo, telefono e sito web.',
    'signup.quickfill.placeholder':                       'Cerca il nome o l’indirizzo della tua sede…',
    'signup.quickfill.success':                           'Modulo riempito automaticamente! Rivedi e modifica i dettagli sotto.',
    'signup.type.question':                               'Quali servizi offre la tua sede?',
    'signup.type.events.label':                           'Solo eventi privati',
    'signup.type.events.desc':                            'I clienti prenotano il tuo spazio per eventi',
    'signup.type.catering.label':                         'Solo catering',
    'signup.type.catering.desc':                          'Consegni cibo; nessun evento in sede',
    'signup.type.both.label':                             'Entrambi',
    'signup.type.both.desc':                              'Ospiti eventi in sede E consegni catering',
    'signup.venue.title':                                 'Dettagli della sede',
    'signup.venue.name_label':                            'Nome della sede *',
    'signup.venue.name_placeholder':                      'Taverna San Giovanni',
    'signup.venue.tagline_label':                         'Slogan',
    'signup.venue.tagline_placeholder':                   'Il pub di quartiere più accogliente',
    'signup.venue.address_label':                         'Indirizzo *',
    'signup.venue.accessibility':                         'La sede è accessibile con sedia a rotelle',
    'signup.contact.title':                               'Informazioni di contatto',
    'signup.contact.name_label':                          'Il tuo nome *',
    'signup.contact.phone_label':                         'Telefono (per aggiornamenti SMS) *',
    'signup.contact.phone_placeholder':                   '(02) 1234-5678',
    'signup.contact.email_label':                         'Email (opzionale)',
    'signup.photo.title':                                 'Foto della sede',
    'signup.photo.body':                                  'Questa sarà la foto principale sul profilo della tua sede',
    'signup.online.title':                                'Presenza online',
    'signup.online.body':                                 'Aiuta i clienti a trovarti online (opzionale)',
    'signup.online.website_label':                        'Sito web',
    'signup.online.website_placeholder':                  'miasede.com',
    'signup.online.instagram_label':                      'Instagram',
    'signup.online.instagram_placeholder':                'tuasede',
    'signup.spaces.title':                                'Spazi privati',
    'signup.spaces.body':                                 'Aggiungi ogni spazio privato disponibile per la prenotazione.',
    'signup.spaces.space_n':                              'Spazio {{n}}',
    'signup.spaces.name_placeholder':                     'Nome dello spazio (es. The Snug, Sala privata)',
    'signup.spaces.capacity_label':                       'Capacità massima *',
    'signup.spaces.capacity_placeholder':                 '50',
    'signup.spaces.min_spend_label':                      'Spesa minima ($)',
    'signup.spaces.min_spend_placeholder':                '2500',
    'signup.spaces.fee_label':                            'Costo di prenotazione ($)',
    'signup.spaces.fee_placeholder':                      '40',
    'signup.spaces.notes_placeholder':                    'Note aggiuntive (politica di cancellazione, ecc.)',
    'signup.spaces.photo_label':                          'Foto dello spazio',
    'signup.spaces.add':                                  'Aggiungi un altro spazio',
    'signup.catering.title':                              'Dettagli catering',
    'signup.catering.body':                               'Alcuni dettagli rapidi per abbinarti con richieste di catering. Puoi aggiungere foto, pacchetti menu e altro nelle Impostazioni dopo la registrazione.',
    'signup.catering.cuisines_label':                     'Tipi di cucina',
    'signup.catering.cuisines_placeholder':               'Scrivi un tipo e premi Invio (es. italiana, messicana, sushi)',
    'signup.catering.cuisines_remove':                    'Rimuovi {{tag}}',
    'signup.catering.radius_label':                       'Raggio di consegna (km)',
    'signup.catering.radius_placeholder':                 '25',
    'signup.catering.minimum_label':                      'Ordine minimo ($)',
    'signup.catering.minimum_placeholder':                'opzionale',
    'signup.catering.footer':                             'Tutti i campi di catering sono opzionali alla registrazione. Puoi aggiungere foto di catering, pacchetti menu e altro dalle Impostazioni dopo aver creato la tua sede.',
    'signup.menus.title':                                 'Menu / Dettagli aggiuntivi (opzionale)',
    'signup.menus.body':                                  'Carica menu o dettagli dell’evento — PDF o immagine, fino a 5.',
    'signup.menus.menu_n':                                'Menu {{n}}',
    'signup.menus.name_placeholder':                      'Nome (es. Menu degustazione, Pacchetto evento)',
    'signup.menus.description_placeholder':               'Descrizione (opzionale)',
    'signup.menus.add':                                   'Aggiungi un menu',
    'signup.notes.label':                                 'Note aggiuntive (opzionale)',
    'signup.notes.placeholder':                           'C’è altro che dobbiamo sapere?',
    'signup.consent.body':                                'Acconsento a ricevere SMS da BookOne.Party riguardo a richieste di prenotazione, aggiornamenti di eventi, codici di verifica (OTP) e messaggi di servizio. La frequenza dei messaggi varia. Possono applicarsi tariffe per messaggi e dati. Rispondi HELP per aiuto, STOP per disiscriverti. Ho letto e accetto i',
    'signup.consent.tos':                                 'Termini di servizio',
    'signup.consent.and':                                 'e l’',
    'signup.consent.privacy':                             'Informativa sulla privacy',
    'signup.submit':                                      'Registra la tua sede',
    'signup.error.generic':                               'Qualcosa è andato storto',
    'signup.error.consent':                               'Devi accettare di ricevere SMS per registrare una sede',
    'signup.error.name':                                  'Il nome della sede è obbligatorio',
    'signup.error.address':                               'L’indirizzo è obbligatorio',
    'signup.error.contact_name':                          'Il tuo nome è obbligatorio',
    'signup.error.phone_required':                        'Il numero di telefono è obbligatorio',
    'signup.error.phone_invalid':                         'Inserisci un numero di telefono valido del Nord America',
    'signup.error.hero_size':                             'L’immagine principale deve essere meno di 5 MB',
    'signup.error.space_name':                            'Il nome dello spazio è obbligatorio',
    'signup.error.space_capacity':                        'La capacità massima deve essere almeno 1',
    'signup.error.space_image':                           'L’immagine dello spazio deve essere meno di 5 MB',
    'signup.error.menu_name':                             'Il nome del menu è obbligatorio',
    'signup.error.menu_file':                             'Il file del menu è obbligatorio',
    'signup.error.menu_size':                             'Il file del menu deve essere meno di 10 MB',
    'signup.error.minimum_negative':                      'L’ordine minimo non può essere negativo',
    'signup.error.delivery_negative':                     'Il raggio di consegna non può essere negativo',
    'signup.success.title':                               'Sei registrato!',
    'signup.success.intro':                               'La tua sede è registrata su BookOne.Party. Ecco cosa succederà:',
    'signup.success.step1.title':                         'Richieste di prenotazione via SMS',
    'signup.success.step1.desc':                          'Quando l’evento di un cliente corrisponde al tuo spazio, riceverai un messaggio con i dettagli.',
    'signup.success.step2.title':                         'Invia la tua proposta',
    'signup.success.step2.desc':                          'Clicca sul link nella notifica SMS per inviare la tua proposta dalla dashboard.',
    'signup.success.step3.title':                         'Connettiti',
    'signup.success.step3.desc':                          'Se selezionato, ti colleghiamo direttamente con il cliente per finalizzare.',
    'signup.success.cta.dashboard':                       'Accedi alla dashboard',
    'signup.success.cta.home':                            'Torna alla home',

    'venue_profile.error.title':                          'Qualcosa è andato storto',
    'venue_profile.error.body':                           'Non siamo riusciti a caricare questa sede. Per favore riprova.',
    'venue_profile.error.retry':                          'Riprova',
    'venue_profile.error.home':                           'Torna alla home',
    'venue_profile.not_found.title':                      'Sede non trovata',
    'venue_profile.not_found.body':                       'Non abbiamo trovato una sede con questo ID. Potrebbe essere stata rimossa o il link potrebbe essere errato.',
    'venue_profile.not_found.home':                       'Torna alla home',

    'settings.booking_mgmt.toast_enabled':                'Gestione prenotazioni attivata',
    'settings.booking_mgmt.toast_disabled':               'Gestione prenotazioni disattivata',
    'settings.booking_mgmt.toast_save_failed':            'Impossibile salvare — {{error}}',
    'settings.booking_mgmt.error_save_default':           'Errore nel salvataggio',

    'dashboard.analytics.customer_list.toast.update_reminder_failed': 'Errore aggiornando il promemoria',
    'dashboard.analytics.customer_list.toast.save_notes_failed':      'Errore salvando le note',

    'catering_config.toast.upload_failed':                'Caricamento non riuscito',
    'catering_config.toast.max_menus':                    'Massimo 10 menu',
    'catering_config.toast.menu_uploaded':                'Menu caricato',
    'catering_config.toast.save_failed':                  'Errore nel salvataggio',

    'settings.tripleseat.toast.validation_errors':        'Per favore correggi gli errori di validazione prima di salvare.',
    'settings.tripleseat.toast.saved':                    'Impostazioni Tripleseat salvate',
    'settings.tripleseat.toast.save_failed':              'Errore nel salvare le impostazioni Tripleseat',
    'settings.tripleseat.confirm.switch_live':            'Passare agli invii Tripleseat in produzione?\n\nQuesto invierà lead reali al tuo account Tripleseat. Assicurati di aver testato prima con l’endpoint simulato.',

    'proposals.tripleseat_preview.toast.script_copied':    'Script di compilazione automatica copiato — incollalo nella console del browser del modulo',
    'proposals.tripleseat_preview.toast.copy_failed':      'Impossibile copiare — usa i pulsanti di copia individuali sotto',
    'proposals.tripleseat_preview.toast.marked_forwarded': 'Segnato come inoltrato. Il cliente è stato avvisato.',

    'booking.external_crm_tracker.toast.note_required':    'Aggiungi una breve nota che descriva cosa è successo',
    'booking.external_crm_tracker.toast.contact_logged':   'Contatto registrato',
    'booking.external_crm_tracker.toast.save_failed':      'Errore nel salvataggio',
    'booking.external_crm_tracker.toast.status_advanced':  'Stato: {{status}}',
    'booking.external_crm_tracker.toast.update_status_failed': 'Errore nell’aggiornamento dello stato',
    'booking.external_crm_tracker.toast.saved':            'Salvato',

    'menu_upload.toast.file_too_large':                    'File troppo grande ({{size}}MB). Massimo: 10MB.',
    'menu_upload.drop_or_click':                           'Trascina un PDF o un’immagine, o clicca per caricare',
    'menu_upload.file_hint':                               'PDF, JPEG, PNG o WebP — Max 10MB',

    'image_upload.drop_or_click':                          'Trascina un’immagine o clicca per caricare',
    'image_upload.file_hint':                              'JPEG, PNG o WebP — Max 5MB',

    'browse_nav.button':                                   'Esplora',
    'browse_nav.category.venues.label':                    'Sedi',
    'browse_nav.category.venues.desc':                     'Prenota spazi e sedi per eventi privati',
    'browse_nav.category.catering.label':                  'Catering',
    'browse_nav.category.catering.desc':                   'Ordina catering per il tuo prossimo evento',
    'browse_nav.category.experiences.label':               'Esperienze',
    'browse_nav.category.experiences.desc':                'Golf, gare, escape room e altro',

    'browse.hero.back_to_home':                            '← Torna alla home',
    'browse.venues.title':                                 'Esplora le sedi',
    'browse.venues.subtitle':                              'Esplora spazi e sedi per eventi privati.',
    'browse.experiences.title':                            'Esplora le esperienze',
    'browse.experiences.subtitle':                         'Spazi alternativi ed esperienze uniche per eventi.',
    'browse.catering.title':                               'Esplora il catering',
    'browse.catering.subtitle':                            'Cibo e bevande consegnati al tuo evento.',
    'browse.catering.address_placeholder':                 'Inserisci il tuo indirizzo per vedere chi consegna...',

    'venue_profile.back_to_venues':                        'Esplora sedi',
    'venue_profile.back_to_catering':                      'Torna al catering',
    'venue_profile.space.up_to':                           'Fino a {{n}}',
    'venue_profile.section.private_spaces':                'Spazi privati',
    'venue_profile.section.menu_additional':               'Menu / Dettagli aggiuntivi',
    'venue_profile.section.our_food':                      'Il nostro cibo',
    'venue_profile.section.our_locations':                 'Le nostre sedi',
    'venue_profile.section.catering_menus':                'Menu del catering',
    'venue_profile.section.delivery_range':                'Raggio di consegna',
    'venue_profile.widget.title_event':                    'Prenota un evento privato presso {{venueName}}',
    'venue_profile.widget.description_event':              'Chatta con il nostro assistente per pianificare la tua serata.',
    'venue_profile.widget.title_catering':                 'Pianifica un evento presso {{venueName}}',
    'venue_profile.widget.description_catering':           'Richiedi un preventivo — chiama o chatta con il team.',
    'venue_profile.cta.call_to_book':                      'Chiama per prenotare',
    'venue_profile.cta.text_to_book':                      'Manda un messaggio',
    'venue_profile.cta.ready_to_book':                     'Pronto a prenotare?',
    'venue_profile.cta.text_or_call_direct':               'Manda un messaggio o chiama direttamente {{venueName}}',
    'venue_profile.cta.text_to_book_direct':               'Manda un messaggio per prenotare questa sede direttamente',
    'venue_profile.locations.subtitle':                    '{{count}} sedi in {{region}} — scegli la più vicina, o verifica il tuo indirizzo di consegna sotto.',
    'venue_profile.locations.delivery_check':              'Verifica se consegniamo al tuo indirizzo',
    'venue_profile.menu.online_menu':                      'Menu online',

    'dashboard.settings.service_types.toast.updated':      'Tipi di servizio aggiornati',
    'dashboard.settings.service_types.toast.update_failed':'Errore nell’aggiornamento',

    'dashboard.live_transfers.toast.dismissed':            'Ignorato',
    'dashboard.live_transfers.toast.dismiss_failed':       'Errore nell’ignorare',
    'dashboard.live_transfers.toast.restored':             'Ripristinato',
    'dashboard.live_transfers.toast.restore_failed':       'Errore nel ripristino',

    // /admin/groups/new — Procedura guidata gruppi hospitality
    'admin.groups.new.title':              'Crea un nuovo gruppo',
    'admin.groups.new.subtitle':           'Configura un gruppo hospitality, sede principale, sedi sorelle e personale.',
    'admin.groups.new.progress':           'Passo {{current}} di {{total}}',
    'admin.groups.new.back':               'Indietro',
    'admin.groups.new.continue':           'Continua',
    'admin.groups.new.submit':             'Apri PR di staging',
    'admin.groups.new.draft.creating':     'Creazione bozza\u2026',
    'admin.groups.new.draft.saving':       'Salvataggio\u2026',
    'admin.groups.new.draft.error':        'Impossibile salvare i progressi. Riprovare?',
    'admin.groups.new.draft.load_error':   'Impossibile caricare la bozza.',
    'admin.groups.new.required':           'Obbligatorio',
    'admin.groups.new.step1.title':        'Scegli un archetipo',
    'admin.groups.new.step1.intro':        'Una sede o più? Questa scelta guida il resto della procedura.',
    'admin.groups.new.step1.standalone.label':       'Sede singola',
    'admin.groups.new.step1.standalone.description': 'Una sede, un telefono, un widget. Usa /admin/venues/new.',
    'admin.groups.new.step1.hospitality.label':      'Gruppo hospitality',
    'admin.groups.new.step1.hospitality.description':'Sede principale + sedi sorelle, team eventi condiviso. (Consigliato)',
    'admin.groups.new.step1.franchise.label':        'Franchising',
    'admin.groups.new.step1.franchise.description':  'Un marchio, più sedi, manager per sede. Usa /admin/venues/new (modalità franchising).',
    'admin.groups.new.step1.redirect_notice':        'Questo archetipo è gestito da /admin/venues/new. Ti reindirizzeremo.',
    'admin.groups.new.step2.title':        'Identità del gruppo',
    'admin.groups.new.step2.name.label':   'Nome del gruppo',
    'admin.groups.new.step2.name.help':    'Il nome legale o commerciale che i clienti vedranno nel breadcrumb.',
    'admin.groups.new.step2.slug.label':   'Identificatore (slug)',
    'admin.groups.new.step2.slug.help':    'Identificatore URL-safe. Derivato dal nome; modificabile.',
    'admin.groups.new.step2.parent.label': 'Sede principale',
    'admin.groups.new.step2.parent.yes':   'Sì — il gruppo ha una sede principale che riceve prenotazioni',
    'admin.groups.new.step2.parent.no':    'No — il gruppo è solo un contenitore organizzativo',
    'admin.groups.new.step2.error.slug':   'Lo slug può contenere solo minuscole, cifre o trattini.',
    'admin.groups.new.step2.error.name':   'Il nome è obbligatorio.',
    'admin.groups.new.step3.title':        'Sede principale',
    'admin.groups.new.step3.intro':        'Collega a una sede esistente o creane una più avanti.',
    'admin.groups.new.step3.link.label':   'Sede esistente',
    'admin.groups.new.step3.link.placeholder': 'Scegli una sede esistente\u2026',
    'admin.groups.new.step3.new.label':    'Crea una nuova sede principale dopo la procedura',
    'admin.groups.new.step3.skip':         'Saltato — il gruppo non ha sede principale.',
    'admin.groups.new.step4.title':        'Sedi sorelle',
    'admin.groups.new.step4.intro':        'Aggiungi ogni sede del gruppo. Ognuna eredita l\u2019ID del gruppo.',
    'admin.groups.new.step4.add':          'Aggiungi sede sorella',
    'admin.groups.new.step4.placeholder':  'Slug sede sorella (es. akiro)',
    'admin.groups.new.step4.existing.label':  'Collega a sede esistente',
    'admin.groups.new.step4.new.label':    'Segnaposto — creare in seguito',
    'admin.groups.new.step4.empty':        'Nessuna sede sorella aggiunta.',
    'admin.groups.new.step4.error.duplicate': 'Slug duplicato.',
    'admin.groups.new.step4.error.invalid':   'Lo slug può contenere solo minuscole, cifre o trattini.',
    'admin.groups.new.step4.remove.aria':  'Rimuovi sede sorella',
    'admin.groups.new.step5.title':        'Personale',
    'admin.groups.new.step5.intro':        'Chi gestisce questo gruppo? Aggiungi almeno un manager eventi.',
    'admin.groups.new.step5.add_group':    'Aggiungi manager eventi',
    'admin.groups.new.step5.add_venue':    'Aggiungi manager sede',
    'admin.groups.new.step5.name.label':   'Nome',
    'admin.groups.new.step5.phone.label':  'Telefono (E.164)',
    'admin.groups.new.step5.phone.placeholder': '+15551234567',
    'admin.groups.new.step5.email.label':  'Email',
    'admin.groups.new.step5.venue.label':  'Sede',
    'admin.groups.new.step5.channel.label':'Canale notifiche',
    'admin.groups.new.step5.channel.sms':  'SMS',
    'admin.groups.new.step5.channel.email':'Email',
    'admin.groups.new.step5.channel.both': 'Entrambi',
    'admin.groups.new.step5.empty':        'Nessun membro del personale aggiunto.',
    'admin.groups.new.step5.remove.aria':  'Rimuovi voce',
    'admin.groups.new.step5.role.group_manager':    'Manager eventi (gruppo)',
    'admin.groups.new.step5.role.venue_manager':    'Manager sede',
    'admin.groups.new.step5.role.location_manager': 'Manager ubicazione',
    'admin.groups.new.step5.error.required':        'È richiesto almeno un manager eventi.',
    'admin.groups.new.step5.error.phone':           'Il telefono deve essere in formato E.164 (+1\u2026).',
    'admin.groups.new.step5.error.duplicate_phone': 'Questo telefono è già in elenco.',
    'admin.groups.new.step6.title':        'Revisione',
    'admin.groups.new.step6.intro':        'Conferma il manifest generato qui sotto. Torna indietro per correggere.',
    'admin.groups.new.step6.preview':      'Manifest generato',
    'admin.groups.new.step6.validation.title': 'Validazione manifest',
    'admin.groups.new.step6.validation.ok':    'Manifest valido.',
    'admin.groups.new.step6.validation.errors':'Correggi quanto segue prima di inviare:',
    'admin.groups.new.step6.submit_hint':   'L\u2019invio apre una PR GitHub sul branch staging. Il merge attiva il deploy.',
    'admin.groups.new.step7.title':        'PR di staging',
    'admin.groups.new.step7.intro':        'La procedura ha aperto questa PR. Rivedi il diff, mergi, osserva la CI.',
    'admin.groups.new.step7.pr_url.label': 'Pull request',
    'admin.groups.new.step7.pr_url.open':  'Apri su GitHub',
    'admin.groups.new.step7.refresh':      'Aggiorna stato',
    'admin.groups.new.step7.checking':     'Verifica\u2026',
    'admin.groups.new.step7.error':        'L\u2019invio è fallito:',
    'admin.groups.new.step8.title':        'Produzione',
    'admin.groups.new.step8.intro':        'Una volta mergiata la PR di staging e verificata, lancia /go-live per promuovere in produzione.',
    'admin.groups.new.step8.gate':         'La procedura non promuove automaticamente in produzione — prima staging.',
    'admin.groups.new.step8.done':         'Fatto',

    // /admin/groups/[id]
    'admin.groups.detail.title':           '{{name}}',
    'admin.groups.detail.subtitle':        'Slug del gruppo: {{slug}}',
    'admin.groups.detail.archetype':       'Archetipo',
    'admin.groups.detail.parent':          'Sede principale',
    'admin.groups.detail.no_parent':       'Nessuna sede principale',
    'admin.groups.detail.loading':         'Caricamento gruppo…',
    'admin.groups.detail.error':           'Impossibile caricare il gruppo',
    'admin.groups.detail.back':            '← Tutti i gruppi',
    'admin.groups.detail.tab.members':     'Membri',
    'admin.groups.detail.tab.staff':       'Personale',
    'admin.groups.detail.tab.audit':       'Audit',
    'admin.groups.detail.tab.manifest':    'Manifesto',
    'admin.groups.detail.members.empty':   'Nessuna sede membro.',
    'admin.groups.detail.members.col.name':   'Nome',
    'admin.groups.detail.members.col.slug':   'Slug',
    'admin.groups.detail.members.col.status': 'Stato',
    'admin.groups.detail.members.manage':  'Gestisci',
    'admin.groups.detail.staff.empty':     'Nessun membro del personale collegato.',
    'admin.groups.detail.staff.col.name':    'Nome',
    'admin.groups.detail.staff.col.phone':   'Telefono',
    'admin.groups.detail.staff.col.role':    'Ruolo',
    'admin.groups.detail.staff.col.channel': 'Notifica via',
    'admin.groups.detail.staff.edit':      'Modifica',
    'admin.groups.detail.audit.empty':     'Nessun evento di audit per questo gruppo.',
    'admin.groups.detail.manifest.empty':  'Nessuna anteprima manifesto disponibile.',
    'admin.groups.detail.unlink_cta':      'Scollega sede dal gruppo',
    'admin.groups.detail.unlink.confirm.title': 'Scollegare questa sede?',
    'admin.groups.detail.unlink.confirm.body':  'Scollegando rimuovi la sede dal gruppo. I dati restano e potrai ricollegarla in seguito.',
    'admin.groups.detail.unlink.confirm.cta':   'Scollega',
    'admin.groups.detail.unlink.cancel':   'Annulla',

    // /admin/staff — elenco
    'admin.staff.list.add_button':         '+ Aggiungi membro',
    'admin.staff.list.row.tooltip':        'Apri membro',

    // /admin/staff/new
    'admin.staff.new.title':               'Aggiungi membro del personale',
    'admin.staff.new.subtitle':            'Crea una nuova riga admin. Potrai affinare le notifiche dalla pagina di dettaglio.',
    'admin.staff.new.field.name.label':    'Nome',
    'admin.staff.new.field.name.placeholder': 'Nome visualizzato',
    'admin.staff.new.field.phone.label':   'Telefono di accesso (E.164)',
    'admin.staff.new.field.phone.placeholder': '+15551234567',
    'admin.staff.new.field.phone.help':    'Formato E.164 — prefisso + cifre, senza spazi.',
    'admin.staff.new.field.notification_email.label': 'Email di notifica',
    'admin.staff.new.field.notification_email.placeholder': 'staff@example.com',
    'admin.staff.new.field.notification_channel.label': 'Canale di notifica',
    'admin.staff.new.channel.sms':         'SMS',
    'admin.staff.new.channel.email':       'Email',
    'admin.staff.new.channel.both':        'SMS + Email',
    'admin.staff.new.field.role.label':    'Ruolo',
    'admin.staff.new.role.group_manager':  'Responsabile gruppo',
    'admin.staff.new.role.venue_manager':  'Responsabile locale',
    'admin.staff.new.role.location_manager': 'Responsabile sede',
    'admin.staff.new.role.help.group_manager':    'Vede tutti i locali dei gruppi selezionati.',
    'admin.staff.new.role.help.venue_manager':    'Vede tutte le prenotazioni dei locali selezionati.',
    'admin.staff.new.role.help.location_manager': 'Vede solo le prenotazioni delle coppie indicate.',
    'admin.staff.new.field.scope.groups.label':   'Gruppi gestiti',
    'admin.staff.new.field.scope.groups.help':    'Seleziona uno o più gruppi. Il responsabile vede tutti i loro locali.',
    'admin.staff.new.field.scope.venues.label':   'Locali gestiti',
    'admin.staff.new.field.scope.venues.help':    'Seleziona uno o più locali. Il responsabile vede tutte le loro prenotazioni.',
    'admin.staff.new.field.scope.locations.label': 'Sedi gestite',
    'admin.staff.new.field.scope.locations.help':  'Scegli un locale, poi una sede, poi aggiungi la coppia. Ripeti per ogni sede.',
    'admin.staff.new.picker.search.groups':        'Cerca gruppi…',
    'admin.staff.new.picker.search.venues':        'Cerca locali…',
    'admin.staff.new.picker.loading.groups':       'Caricamento gruppi…',
    'admin.staff.new.picker.loading.venues':       'Caricamento locali…',
    'admin.staff.new.picker.error.groups':         'Impossibile caricare i gruppi. Aggiorna per riprovare.',
    'admin.staff.new.picker.error.venues':         'Impossibile caricare i locali. Aggiorna per riprovare.',
    'admin.staff.new.picker.empty.groups':         'Nessun gruppo trovato.',
    'admin.staff.new.picker.empty.venues':         'Nessun locale trovato.',
    'admin.staff.new.picker.empty.locations':      'Nessuno dei locali disponibili ha sedi configurate.',
    'admin.staff.new.picker.location.venue.label': 'Locale',
    'admin.staff.new.picker.location.venue.placeholder': 'Seleziona un locale…',
    'admin.staff.new.picker.location.slug.label':  'Sede',
    'admin.staff.new.picker.location.slug.placeholder': 'Seleziona una sede…',
    'admin.staff.new.picker.location.add':         '+ Aggiungi sede',
    'admin.staff.new.picker.location.added':       'Sedi aggiunte',
    'admin.staff.new.picker.location.remove.aria': 'Rimuovi sede',
    'admin.staff.new.picker.selected.count':       '{{count}} selezionati',
    'admin.staff.new.submit':              'Crea membro',
    'admin.staff.new.submitting':          'Creazione…',
    'admin.staff.new.cancel':              'Annulla',
    'admin.staff.new.error.generic':       'Impossibile creare il membro — controlla i campi.',
    'admin.staff.new.error.conflict':      'Esiste già un membro con quel telefono.',
    'admin.staff.new.error.scope_required.group_manager':    'Seleziona almeno un gruppo.',
    'admin.staff.new.error.scope_required.venue_manager':    'Seleziona almeno un locale.',
    'admin.staff.new.error.scope_required.location_manager': 'Aggiungi almeno una coppia {venueId, locationSlug}.',
    'admin.staff.new.error.invalid_phone':  'Il telefono deve essere in formato E.164 (es. +15551234567).',
    'admin.staff.new.error.invalid_locations_json': 'Le sedi devono essere JSON valido — array di oggetti {venueId, locationSlug}.',
    'admin.staff.new.success':              'Membro creato.',

    // /admin/staff/[id]
    'admin.staff.detail.title':            '{{name}}',
    'admin.staff.detail.subtitle':         'Dettaglio del membro del personale',
    'admin.staff.detail.loading':          'Caricamento personale…',
    'admin.staff.detail.error':            'Impossibile caricare il membro',
    'admin.staff.detail.back':             '← Tutto il personale',
    'admin.staff.detail.section.identity': 'Identità',
    'admin.staff.detail.section.access':   'Ruolo e ambito',
    'admin.staff.detail.section.notifications': 'Notifiche',
    'admin.staff.detail.section.login_history': 'Storico accessi',
    'admin.staff.detail.section.audit':    'Attività recente',
    'admin.staff.detail.section.actions':  'Azioni',
    'admin.staff.detail.field.name':       'Nome',
    'admin.staff.detail.field.phone':      'Telefono',
    'admin.staff.detail.field.email':      'Email',
    'admin.staff.detail.field.role':       'Ruolo',
    'admin.staff.detail.field.disabled':   'Disabilitato',
    'admin.staff.detail.field.scope.venues': 'Sedi gestite',
    'admin.staff.detail.field.scope.groups': 'Gruppi gestiti',
    'admin.staff.detail.field.scope.locations': 'Ubicazioni gestite',
    'admin.staff.detail.field.scope.empty':  'Nessun ambito assegnato',
    'admin.staff.detail.field.notify_phone': 'Telefono di notifica',
    'admin.staff.detail.field.notify_email': 'Email di notifica',
    'admin.staff.detail.field.notify_channel': 'Canale di notifica',
    'admin.staff.detail.field.last_login':  'Ultimo accesso',
    'admin.staff.detail.field.created':     'Creato',
    'admin.staff.detail.login_history.empty':   'Lo storico accessi non è ancora tracciato.',
    'admin.staff.detail.audit.empty':       'Nessuna attività recente.',
    'admin.staff.detail.role.platform_admin':  'Admin piattaforma',
    'admin.staff.detail.role.group_manager':   'Manager di gruppo',
    'admin.staff.detail.role.venue_manager':   'Manager sede',
    'admin.staff.detail.role.location_manager':'Manager ubicazione',
    'admin.staff.detail.channel.sms':       'SMS',
    'admin.staff.detail.channel.email':     'Email',
    'admin.staff.detail.channel.both':      'SMS + Email',
    'admin.staff.detail.save':              'Salva modifiche',
    'admin.staff.detail.saving':            'Salvataggio…',
    'admin.staff.detail.save_ok':           'Modifiche salvate',
    'admin.staff.detail.save_fail':         'Impossibile salvare le modifiche',
    'admin.staff.detail.disable':           'Disabilita',
    'admin.staff.detail.disable.confirm':   'Disabilitare questo membro? Perderà ogni accesso finché non viene riattivato.',
    'admin.staff.detail.enable':            'Abilita',
    'admin.staff.detail.rotate_phone':      'Cambia telefono',
    'admin.staff.detail.rotate_phone.confirm': 'Cambiare il telefono di accesso? Il membro dovrà autenticarsi con il nuovo numero.',
    'admin.staff.detail.rotate_phone.prompt':  'Nuovo telefono (formato E.164, es. +15551234567):',
    'admin.staff.detail.force_reauth':       'Forza riautenticazione',
    'admin.staff.detail.force_reauth.confirm': 'Forzare il riaccesso? Le sessioni attuali verranno invalidate.',
    'admin.staff.detail.force_reauth.ok':    'Sessioni invalidate',
    'admin.staff.detail.delete':             'Elimina',
    'admin.staff.detail.delete.confirm':     'Eliminare definitivamente questo membro? Azione irreversibile. Digita il suo telefono per confermare.',

    // /admin/audit — filtri + export CSV + modalità live
    'admin.audit.filter.title':            'Filtri',
    'admin.audit.filter.actor':            'Attore',
    'admin.audit.filter.actor.placeholder':'ID o ruolo dell’attore',
    'admin.audit.filter.action':           'Azione',
    'admin.audit.filter.action.placeholder':'es. submit_proposal',
    'admin.audit.filter.target_type':      'Tipo di destinazione',
    'admin.audit.filter.target_type.all':  'Tutti i tipi',
    'admin.audit.filter.date_from':        'Da',
    'admin.audit.filter.date_to':          'A',
    'admin.audit.filter.apply':            'Applica',
    'admin.audit.filter.reset':            'Reimposta',
    'admin.audit.filter.open':             'Filtra',
    'admin.audit.csv_export':              'Esporta CSV',
    'admin.audit.csv_export.empty':        'Niente da esportare.',
    'admin.audit.live_toggle':             'Live',
    'admin.audit.live_toggle.aria':        'Attiva il flusso in tempo reale',
    'admin.audit.row.expand':              'Vedi dettagli',
    'admin.audit.row.collapse':            'Chiudi',
    'admin.audit.row.payload':             'Payload',
    'admin.audit.row.impersonated':        'Impersonato come',
  },
};

// Detect the caller's preferred language from the browser. Returns 'fr' if
// the primary `navigator.language` starts with 'fr' (covers fr-CA, fr-FR,
// fr, etc.), otherwise falls back to English. Safe to call during SSR —
// returns DEFAULT_LANG when `navigator` is undefined. Not memoised; callers
// should snapshot the result into state once on mount.
export function detectBrowserLang(): SupportedLang {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  const primary = (navigator.language || (navigator.languages && navigator.languages[0]) || '').toLowerCase();
  if (primary.startsWith('fr')) return 'fr';
  if (primary.startsWith('es')) return 'es';
  if (primary.startsWith('it')) return 'it';
  return 'en';
}

// Persist the user's explicit choice so it survives page reloads. Separate
// from the browser auto-detect so a user who picks EN on a fr-CA browser
// stays in EN across sessions.
const STORAGE_KEY = 'bookone_widget_lang';

export function readStoredLang(): SupportedLang | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    // Round 21 (2026-05-10): extended from EN+FR to 4 langs.
    return v && (SUPPORTED_LANGS as readonly string[]).includes(v)
      ? (v as SupportedLang)
      : null;
  } catch {
    return null;
  }
}

export function writeStoredLang(lang: SupportedLang): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Silently drop — private mode or storage disabled. Runtime state
    // (parent component's useState) keeps the choice for the session.
  }
}

// Return the effective language. Resolution order:
//   1. Explicit override (from URL/page prop) — but only if it's in supportedLanguages
//   2. Stored choice (localStorage) — but only if it's in supportedLanguages
//   3. R22 (2026-05-10): venuePreferredLang — for the dashboard, the venue
//      owner's `preferred_language` column lands them in their language on
//      first login, before browser detection takes over. Public surfaces
//      pass undefined and behaviour is unchanged.
//   4. Browser detect — but only if it's in supportedLanguages
//   5. supportedLanguages[0] (when provided) or DEFAULT_LANG
//
// `supportedLanguages` is the source of truth for what the venue's agent
// can actually do. Without it (undefined), we fall back to the legacy
// "stored or detected" behavior — that path exists for callers who haven't
// adopted supportedLanguages yet. Always pass a value when you can.
//
// Why this matters: a stale `localStorage.bookone_widget_lang='fr'` from a
// previous bilingual session would otherwise leak into a single-language
// venue's widget, render French strings, and try to start an ElevenLabs
// session with overrides.language='fr' — which fails for agents without an
// FR preset (Akiro, Members Only).
export function resolveInitialLang(
  override?: SupportedLang,
  supportedLanguages?: SupportedLang[],
  venuePreferredLang?: SupportedLang | null,
): SupportedLang {
  const supported = supportedLanguages && supportedLanguages.length > 0 ? supportedLanguages : null;
  const isSupported = (lang: SupportedLang | null | undefined): lang is SupportedLang =>
    !!lang && (!supported || supported.includes(lang));

  if (override && SUPPORTED_LANGS.includes(override) && isSupported(override)) return override;
  const stored = readStoredLang();
  if (isSupported(stored)) return stored as SupportedLang;
  if (venuePreferredLang && SUPPORTED_LANGS.includes(venuePreferredLang) && isSupported(venuePreferredLang)) {
    return venuePreferredLang;
  }
  const detected = detectBrowserLang();
  if (isSupported(detected)) return detected;
  return supported ? supported[0]! : DEFAULT_LANG;
}

// Minimal interpolation — replaces `{{name}}` with values[name]. No escaping;
// the strings are developer-authored, not user input.
function interpolate(template: string, values?: Record<string, string | number>): string {
  if (!values) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const v = values[key];
    return v === undefined || v === null ? match : String(v);
  });
}

// Curried translator. Call `t = getT(lang)` once per render pass, then
// `t('widget.idle.call')`. Missing keys fall back to English, then to the
// raw key so dev sees the gap immediately.
export function getT(lang: SupportedLang) {
  return (key: string, values?: Record<string, string | number>): string => {
    const candidate = DICT[lang]?.[key] ?? DICT.en[key] ?? key;
    return interpolate(candidate, values);
  };
}

export type TranslateFn = ReturnType<typeof getT>;

// ── Intake-field helpers ─────────────────────────────────────────────────────
// Structured translations for known custom_answers keys + their common
// enum-ish values. These are the fields collected by the widget's intake
// (agent's data_collection) that show up on /booking/[id] as key/value rows.
// The real free-text fields (event_summary, budget descriptions) need
// Gemini to translate — a backend pass will persist bilingual versions on
// the booking_request. Until then, this dict covers the repeatable ones.

// Translate a custom_answers KEY (e.g. 'allergy_concerns') into a
// human-readable label in the caller's language. Falls back to the
// snake_case-stripped key if we don't have an entry yet.
const CUSTOM_ANSWER_LABELS: Record<SupportedLang, Record<string, string>> = {
  en: {
    allergy_concerns:     'Allergy Concerns',
    location_preference:  'Location Preference',
    accessibility_needs:  'Accessibility Needs',
    dietary_restrictions: 'Dietary Restrictions',
    special_requests:     'Special Requests',
    decoration_plans:     'Decoration Plans',
    byob_plans:           'Wine / Beer Plans',
  },
  fr: {
    allergy_concerns:     'Allergies',
    location_preference:  'Préférence d\u2019emplacement',
    accessibility_needs:  'Besoins d\u2019accessibilité',
    dietary_restrictions: 'Restrictions alimentaires',
    special_requests:     'Demandes spéciales',
    decoration_plans:     'Plans de décoration',
    byob_plans:           'Vin / bière',
  },
  // Round 21 (2026-05-10) — neutral Latin-American Spanish.
  es: {
    allergy_concerns:     'Alergias',
    location_preference:  'Preferencia de ubicación',
    accessibility_needs:  'Necesidades de accesibilidad',
    dietary_restrictions: 'Restricciones alimentarias',
    special_requests:     'Solicitudes especiales',
    decoration_plans:     'Planes de decoración',
    byob_plans:           'Vino / Cerveza',
  },
  // Round 21 (2026-05-10) — standard Italian.
  it: {
    allergy_concerns:     'Allergie',
    location_preference:  'Preferenza di sede',
    accessibility_needs:  'Esigenze di accessibilità',
    dietary_restrictions: 'Restrizioni alimentari',
    special_requests:     'Richieste speciali',
    decoration_plans:     'Decorazioni previste',
    byob_plans:           'Vino / Birra',
  },
};

export function translateCustomAnswerLabel(key: string, lang: SupportedLang): string {
  const dict = CUSTOM_ANSWER_LABELS[lang];
  if (dict[key]) return dict[key]!;
  // Fallback: humanize snake_case → capitalized words.
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Translate a common enum-like custom_answers VALUE. Normalizes case +
// separators so 'No Preference', 'no-preference', 'no_preference' all
// map. Falls back to the raw value when we don't have a translation —
// the Gemini backend pass will catch the free-text long tail later.
const CUSTOM_ANSWER_VALUES: Record<SupportedLang, Record<string, string>> = {
  en: {
    'no-preference':   'No preference',
    'no preference':   'No preference',
    'aucune preference': 'No preference',
    'aucune-preference': 'No preference',
    none:              'None',
    aucun:             'None',
    wheelchair:        'Wheelchair access',
    'fauteuil-roulant': 'Wheelchair access',
    peanuts:           'Peanuts',
    arachides:         'Peanuts',
    gluten:            'Gluten',
    'sans-gluten':     'Gluten-free',
    nuts:              'Nuts',
    noix:              'Nuts',
    dairy:             'Dairy',
    lactose:           'Dairy',
    vegan:             'Vegan',
    vegane:            'Vegan',
    laval:             'Laval',
    'st-laurent':      'St-Laurent',
    'saint-laurent':   'Saint-Laurent',
    'st-leo':          'St-Léonard',
    'st-leonard':      'Saint-Léonard',
    'saint-leonard':   'Saint-Léonard',
    gatineau:          'Gatineau',
  },
  fr: {
    'no-preference':   'Aucune préférence',
    'no preference':   'Aucune préférence',
    'aucune preference': 'Aucune préférence',
    'aucune-preference': 'Aucune préférence',
    none:              'Aucun',
    aucun:             'Aucun',
    wheelchair:        'Accès en fauteuil roulant',
    'fauteuil-roulant': 'Accès en fauteuil roulant',
    peanuts:           'Arachides',
    arachides:         'Arachides',
    gluten:            'Gluten',
    'sans-gluten':     'Sans gluten',
    nuts:              'Noix',
    noix:              'Noix',
    dairy:             'Produits laitiers',
    lactose:           'Lactose',
    vegan:             'Végane',
    vegane:            'Végane',
    laval:             'Laval',
    'st-laurent':      'St-Laurent',
    'saint-laurent':   'Saint-Laurent',
    'st-leo':          'St-Léonard',
    'st-leonard':      'Saint-Léonard',
    'saint-leonard':   'Saint-Léonard',
    gatineau:          'Gatineau',
  },
  // Round 21 (2026-05-10) — most CUSTOM_ANSWER_VALUES are venue-specific
  // (locations, dietary terms) that don't need translation per-language.
  // Common translatable terms only.
  es: {
    'no-preference':   'Sin preferencia',
    'no preference':   'Sin preferencia',
    none:              'Ninguno',
    wheelchair:        'Silla de ruedas',
    peanuts:           'Maní',
    gluten:            'Gluten',
    'sans-gluten':     'Sin gluten',
    nuts:              'Frutos secos',
    dairy:             'Lácteos',
    vegan:             'Vegano',
  },
  it: {
    'no-preference':   'Nessuna preferenza',
    'no preference':   'Nessuna preferenza',
    none:              'Nessuno',
    wheelchair:        'Sedia a rotelle',
    peanuts:           'Arachidi',
    gluten:            'Glutine',
    'sans-gluten':     'Senza glutine',
    nuts:              'Frutta a guscio',
    dairy:             'Latticini',
    vegan:             'Vegano',
  },
};

// Best-effort translate. If the value normalizes to a known key, return the
// translation; else return the raw value unchanged (long free-text answers
// fall through to the Gemini backend pass, not yet implemented).
export function translateCustomAnswerValue(value: string, lang: SupportedLang): string {
  if (!value) return value;
  const key = value.trim().toLowerCase();
  const dict = CUSTOM_ANSWER_VALUES[lang];
  return dict[key] ?? value;
}

// Multi-location venue slug → display name + street address per language.
// Used when the agent collects `location_preference` as a slug — the booking
// page header shows just the name (`resolveLocationName`) and the dashboard
// Event Details card shows name + address (`resolveLocationAddress`). New
// multi-location venues add an entry here and the helpers handle the rest.
// `no-preference` is intentionally rendered with the localized "no preference"
// label and no address.
const LOCATION_ADDRESSES: Record<string, { en: { name: string; address: string }; fr: { name: string; address: string } }> = {
  laval:           { en: { name: 'Ottavio Laval',         address: '1459 St. Martin O., Laval, QC H7S 1N1' },        fr: { name: 'Ottavio Laval',         address: '1459 boul. Saint-Martin O., Laval, QC H7S 1N1' } },
  'st-laurent':    { en: { name: 'Ottavio St-Laurent',    address: '1134 Marcel-Laurin, Saint-Laurent, QC H4R 1J7' }, fr: { name: 'Ottavio St-Laurent',    address: '1134 Marcel-Laurin, Saint-Laurent, QC H4R 1J7' } },
  'st-leo':        { en: { name: 'Ottavio St-Léonard',    address: '6880 Jean-Talon E., Saint-Léonard, QC H1S 1N1' }, fr: { name: 'Ottavio St-Léonard',    address: '6880 Jean-Talon E., Saint-Léonard, QC H1S 1N1' } },
  gatineau:        { en: { name: 'Ottavio Gatineau',      address: '56A Gréber, Gatineau, QC J8T 3P8' },              fr: { name: 'Ottavio Gatineau',      address: '56A Gréber, Gatineau, QC J8T 3P8' } },
  'bobby-bermudas':{ en: { name: "Bobby Bermuda's",       address: '235 Queens Quay West, Toronto, ON M5J2G8' },      fr: { name: "Bobby Bermuda's",       address: '235 Queens Quay West, Toronto, ON M5J2G8' } },
  harbourfront:    { en: { name: 'Boxcar Social Harbourfront', address: '235 Queens Quay West, Toronto, ON M5J2G8' }, fr: { name: 'Boxcar Social Harbourfront', address: '235 Queens Quay West, Toronto, ON M5J2G8' } },
  summerhill:      { en: { name: 'Boxcar Social Summerhill',   address: '1208 Yonge Street, Toronto, ON M4T1W1' },    fr: { name: 'Boxcar Social Summerhill',   address: '1208 Yonge Street, Toronto, ON M4T1W1' } },
  laneway:         { en: { name: 'Boxcar Social Laneway',      address: '298 Markham Street, Toronto, ON M6J2J6' },   fr: { name: 'Boxcar Social Laneway',      address: '298 Markham Street, Toronto, ON M6J2J6' } },
  // MáLà Project — 4 NYC locations. Addresses match the manifest at
  // reference/venues/mala-project/widget-manifest.json (keep in sync if the
  // venue moves). Names are English-only — they're venue-canonical NYC
  // place names, no FR translation needed beyond the Quebec convention.
  'east-village':  { en: { name: 'MáLà East Village',  address: '122 1st Ave, New York, NY 10009' },        fr: { name: 'MáLà East Village',  address: '122 1st Ave, New York, NY 10009' } },
  'bryant-park':   { en: { name: 'MáLà Bryant Park',   address: '41 W 46th St, New York, NY 10036' },       fr: { name: 'MáLà Bryant Park',   address: '41 W 46th St, New York, NY 10036' } },
  'midtown-east':  { en: { name: 'MáLà Midtown East',  address: '245 E 53rd St, New York, NY 10022' },      fr: { name: 'MáLà Midtown East',  address: '245 E 53rd St, New York, NY 10022' } },
  greenpoint:      { en: { name: 'MáLà Greenpoint',    address: '603 Manhattan Ave, Brooklyn, NY 11222' },  fr: { name: 'MáLà Greenpoint',    address: '603 Manhattan Ave, Brooklyn, NY 11222' } },
  'no-preference': { en: { name: 'No location preference', address: '' }, fr: { name: 'Aucune préférence d\u2019emplacement', address: '' } },
};

// Venue-scoped overrides for slugs that aren't globally unique. `gatineau` is
// shared by Ottavio AND Le Buffet des Continents — without a venue-aware lookup
// the customer's booking page renders a Le Buffet Gatineau booking as "Ottavio
// Gatineau" (and Ottavio's address). Entries here win over LOCATION_ADDRESSES
// for the matching venue id. Le Buffet street addresses aren't tracked yet, so
// these are name-only — the header uses the name and the address line stays
// empty rather than showing the wrong venue's address.
const LOCATION_ADDRESSES_BY_VENUE: Record<
  string,
  Record<string, { en: { name: string; address: string }; fr: { name: string; address: string } }>
> = {
  // Le Buffet des Continents — same venue id on staging + production.
  '019f0476-e237-78c2-a26f-eae4fff0aa7a': {
    brossard:         { en: { name: 'Le Buffet des Continents Brossard', address: '' },        fr: { name: 'Le Buffet des Continents Brossard', address: '' } },
    drummondville:    { en: { name: 'Le Buffet des Continents Drummondville', address: '' },   fr: { name: 'Le Buffet des Continents Drummondville', address: '' } },
    gatineau:         { en: { name: 'Le Buffet des Continents Gatineau', address: '' },        fr: { name: 'Le Buffet des Continents Gatineau', address: '' } },
    mascouche:        { en: { name: 'Le Buffet des Continents Mascouche', address: '' },       fr: { name: 'Le Buffet des Continents Mascouche', address: '' } },
    quebec:           { en: { name: 'Le Buffet des Continents Québec', address: '' },          fr: { name: 'Le Buffet des Continents Québec', address: '' } },
    'st-jerome':      { en: { name: 'Le Buffet des Continents Saint-Jérôme', address: '' },    fr: { name: 'Le Buffet des Continents Saint-Jérôme', address: '' } },
    'trois-rivieres': { en: { name: 'Le Buffet des Continents Trois-Rivières', address: '' },  fr: { name: 'Le Buffet des Continents Trois-Rivières', address: '' } },
  },
};

function normalizeLocationKey(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
}

// Look up a location entry, preferring a venue-scoped override (handles slug
// collisions like `gatineau`) before the global map.
function resolveLocationEntry(value: string, venueId?: string | null) {
  const key = normalizeLocationKey(value);
  if (venueId) {
    const scoped = LOCATION_ADDRESSES_BY_VENUE[venueId]?.[key];
    if (scoped) return scoped;
  }
  return LOCATION_ADDRESSES[key];
}

// Resolve a location slug (e.g. 'harbourfront') to "Name — Address" in the
// requested language. Used in the dashboard Event Details card. Falls back
// to the raw value when the slug isn't recognized.
export function resolveLocationAddress(value: string, lang: SupportedLang, venueId?: string | null): string {
  if (!value) return value;
  const entry = resolveLocationEntry(value, venueId);
  if (!entry) return value;
  // Round 21: ES/IT share EN copy — location names are venue-canonical, no
  // per-region translation. FR retained because Boxcar's Quebec locations
  // have French address conventions.
  const localized = lang === 'fr' ? entry.fr : entry.en;
  const { name, address } = localized;
  return address ? `${name} — ${address}` : name;
}

// Resolve a location slug to just the venue name (no address) — used in the
// consumer booking page header where the line is gold-uppercased and the full
// address would overflow.
export function resolveLocationName(value: string, lang: SupportedLang, venueId?: string | null): string {
  if (!value) return value;
  const entry = resolveLocationEntry(value, venueId);
  if (!entry) return value;
  return (lang === 'fr' ? entry.fr : entry.en).name;
}

// Per-slug Notion event-guide URLs for Boxcar Social's 4 locations. Read by
// the consumer booking page to render a "View event guide →" button and by
// the widget agent's `open_event_guide` tool. Updates here ship via Vercel;
// the agent reads the same map from `cateringConfig.eventGuideUrls` so DB
// updates can also override per-environment without a code redeploy.
export const BOXCAR_EVENT_GUIDE_URLS: Record<string, string> = {
  'bobby-bermudas': 'https://habitual-leo-5e7.notion.site/EVENTS-AT-BOBBY-BERMUDA-S-1d0f681415618035bb17ff7a1d7f6efb',
  harbourfront:     'https://habitual-leo-5e7.notion.site/EVENTS-AT-BOXCAR-SOCIAL-HARBOURFRONT-6b5223855f03450eafcbe9b166caccfe',
  summerhill:       'https://habitual-leo-5e7.notion.site/BOXCAR-SUMMERHILL-EVENTS-2e7bd1aff4d043e0b1df814b31ca4d34',
  laneway:          'https://habitual-leo-5e7.notion.site/BOXCAR-LANEWAY-EVENTS-c5f8dff096464b07a045f7e7bb012145',
};

// Resolve a location slug to its public event-guide URL. Returns null when
// the slug doesn't have one (single-location venues, unknown slugs). Booking
// page only renders the button when this returns a URL.
export function resolveEventGuideUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  return BOXCAR_EVENT_GUIDE_URLS[normalizeLocationKey(value)] ?? null;
}

// ── Locale-aware formatters ──────────────────────────────────────────────────
// Don't hard-code currency symbols or date formats inside translation strings
// — use these helpers so FR renders "123,45 $ CA" and "20 avril 2026" while
// EN renders "$123.45 CAD" and "April 20, 2026" without duplicate strings.

export const LOCALE_MAP: Record<SupportedLang, string> = {
  en: 'en-CA',
  fr: 'fr-CA',
  es: 'es',
  it: 'it-IT',
};

// Absolute date in the caller's language. `20 avril 2026` (fr) vs
// `April 20, 2026` (en). Input can be an ISO string or a Date.
export function formatDateLocal(
  date: string | Date | null | undefined,
  lang: SupportedLang,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
): string {
  if (!date) return '';
  // R42.28 (2026-05-13): YYYY-MM-DD strings are date-only and should render
  // as that calendar day in the viewer's local timezone — NOT as UTC midnight
  // (which renders as the previous day for any viewer west of UTC, e.g. NYC).
  // Detect the date-only shape and parse via `new Date("YYYY-MM-DDT00:00:00")`
  // which JavaScript treats as LOCAL midnight (no trailing Z = no UTC).
  let d: Date;
  if (typeof date === 'string') {
    const trimmed = date.trim();
    d = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? new Date(`${trimmed}T00:00:00`)
      : new Date(trimmed);
  } else {
    d = date;
  }
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE_MAP[lang], options).format(d);
}

// Short-form date for compact labels. `2026-04-20` → `20 avr.` (fr) / `Apr 20` (en).
export function formatDateShort(
  date: string | Date | null | undefined,
  lang: SupportedLang,
): string {
  return formatDateLocal(date, lang, { month: 'short', day: 'numeric' });
}

// Currency amount from integer cents. Defaults to CAD since BookOne only
// operates in Canada; callers can override for other currencies later.
// `123_45` cents → `$123.45 CAD` (en) / `123,45 $ CA` (fr).
export function formatCurrencyLocal(
  cents: number | null | undefined,
  lang: SupportedLang,
  currency = 'CAD',
): string {
  if (cents == null || Number.isNaN(cents)) return '';
  return new Intl.NumberFormat(LOCALE_MAP[lang], {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).format(cents / 100);
}

// Helper for the duration hour label. Handles pluralization via the
// `.plural` sibling key so we don't hard-code "hours" / "heures" in the
// component.
export function formatDurationHours(
  hours: number | null | undefined,
  lang: SupportedLang,
): string {
  if (hours == null) return '';
  const key = hours === 1 ? 'booking.details.duration_hours' : 'booking.details.duration_hours.plural';
  return getT(lang)(key, { hours });
}
